// ============================================
// PROPERTY FORM - Extrait de App.js
// Extraction: Étape 1 - Découpage monolithe
// Behavior: INCHANGÉ
// ============================================
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { TYPES_BIEN_BY_TRANSACTION, ACTIVITY_TYPES_BY_BIEN_TYPE_MAPPING } from '../../constants/propertyMappings';
import PropertyActions from '../properties/PropertyActions';
import ShareModal from '../shared/ShareModal';
import Header from '../Header';
import TaxonomySelector from './TaxonomySelector';
import AvisDeValeur from '../AvisDeValeur';
import ContactForm from '../../pages/ContactForm';
import ContactList from '../../pages/ContactList';
import { getAuthHeaders } from '../../utils/auth';
import ContactRequests from '../../pages/ContactRequests';
import ContactViewMode from '../../pages/ContactViewMode';
import Profile from '../../pages/Profile';
import Settings from '../../pages/Settings';
import SidebarLayout from '../layouts/SidebarLayout';
import { formatPriceFR, formatRentFR, formatSurfaceFR, formatPhoneFR, formatPercentFR, formatDateTimeFR } from '../../utils/formatFR';
import { getMediaUrl } from '../../utils/mediaUrl';
import RichTextEditor from './RichTextEditor';
import { htmlToPlainText, plainTextToHtml, renderDescriptionHtml, looksLikeHtml } from '../../utils/sanitizeHtml';
import { getNegociateurName } from '../../utils/negociateur';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';

// Import des sections refactorisées (Étape 1.6)
import {
  GeneralSection,
  PrestationsSection,
  DescriptifSection,
  CompositionSection,
  ConfidentialSection,
  VenteSection,
  MediasSection
} from './sections';

import { 
  $getRoot,
  $getSelection,
  $createParagraphNode,
  $createTextNode
} from 'lexical';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeNode } from '@lexical/code';
import { messageSuppression, messageGroupe, messageServeur } from '../../utils/retraitPortails';

// API Configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// AJ1 — aperçu du tableau de bord : 5 contacts sont affichés, on en charge 20 pour laisser
// de la marge au « voir plus », et le total vient du compteur serveur.
const CONTACTS_APERCU_DASHBOARD = 20;
const DEMANDES_APERCU_DASHBOARD = 20;

const PropertyForm = React.forwardRef(({ onStartWizard, propertyToEdit, onPropertyEditComplete, userProfile: userProfileProp, agencySettings: agencySettingsProp, hideHeader, isCreating = false }, ref) => {
  // Hook de thème pour conditionally render header
  const { isModerne, isJoueurs, switchTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState("general");
  const [properties, setProperties] = useState([]);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [showForm, setShowForm] = useState(!!propertyToEdit); // Si propertyToEdit fourni, afficher directement le formulaire
  const [configKey, setConfigKey] = useState(0); // Pour forcer le re-render quand l'activité change
  const [isUploading, setIsUploading] = useState(false); // État de chargement pour l'upload de fichiers
  
  // WIZARD géré maintenant au niveau App.js
  
  // FILTRES DE STATUT POUR LISTE DES BIENS
  const [currentFilter, setCurrentFilter] = useState("active");
  const [allPropertiesCounts, setAllPropertiesCounts] = useState({
    active: 0,
    draft: 0,
    archive: 0,
    sold: 0,
    shared: 0,
    all: 0
  });

  // Load property to edit when propertyToEdit changes
  useEffect(() => {
    if (propertyToEdit) {
      console.log("🔄 Loading property to edit:", propertyToEdit);
      loadPropertyForEdit(propertyToEdit);
    }
  }, [propertyToEdit]);
  // Assurer le bon état en mode création (propertyToEdit null)
  useEffect(() => {
    if (isCreating && !propertyToEdit) {
      setShowForm(true);
      setShowDashboard(false);
      setShowContacts(false);
      setActiveSection('properties');
    }
  }, [isCreating, propertyToEdit]);


  const loadPropertyForEdit = async (propertyId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${propertyId}`);
      console.log("✅ Property loaded for edit:", response.data);
      
      // Set both currentProperty AND formData to ensure config loads correctly
      setCurrentProperty(response.data);
      setFormData(response.data);
      
      setShowForm(true);
      setShowDashboard(false);
      setShowContacts(false);
      setActiveSection('properties');
      
      // Notify parent that we've started editing
      if (onPropertyEditComplete) {
        // We could call this when user closes the form, but for now just clear the state
      }
    } catch (error) {
      console.error("❌ Error loading property for edit:", error);
      alert("Erreur lors du chargement du bien");
    }
  };

  // Configuration data
  const [availablePrestations, setAvailablePrestations] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [formesJuridiques, setFormesJuridiques] = useState([]);
  
  // Address autocomplete
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressInputFocus, setAddressInputFocus] = useState('');
  
  // City autocomplete (for ville in localisation)
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);

  // Photo gallery state - moved to top to avoid temporal dead zone
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
  const [galleryProperty, setGalleryProperty] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  // Ref to prevent double-execution in StrictMode
  const galleryOpeningRef = useRef(false);

  // CONTACTS/ANNUAIRE STATE
  const [showContacts, setShowContacts] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [currentContact, setCurrentContact] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactViewMode, setContactViewMode] = useState('edit'); // 'view' ou 'edit'
  // Dashboard par défaut SAUF si propertyToEdit est fourni (mode édition)
  const [showDashboard, setShowDashboard] = useState(!propertyToEdit);
  // lot BY — « Supprimer » est réservé à l'admin (et à un bien déjà archivé).
  const estAdminBY = React.useMemo(() => {
    try {
      const role = JSON.parse(localStorage.getItem('user') || '{}')?.role;
      return role === 'admin_agence' || role === 'super_admin';
    } catch { return false; }
  }, []);
  const [activeSection, setActiveSection] = useState(propertyToEdit ? 'properties' : 'dashboard');
  const [contactTypes, setContactTypes] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  // AJ1 — total réel des contacts (compteur serveur) et état du carnet en mémoire :
  // le tableau de bord n'en charge que l'aperçu, la vue Contacts charge tout.
  const [contactsTotal, setContactsTotal] = useState(null);
  const [demandesContactTotal, setDemandesContactTotal] = useState(null);
  const [contactsCarnetComplet, setContactsCarnetComplet] = useState(false);
  const [availableContactCities, setAvailableContactCities] = useState([]);
  const [activeContactTab, setActiveContactTab] = useState("general");
  const [contactSearchFilters, setContactSearchFilters] = useState({
    type_contact: "",
    groupe: "",
    ville: ""
  });
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showArchivedContacts, setShowArchivedContacts] = useState(false);
  const [presentedProperties, setPresentedProperties] = useState([]);
  const [selectedPresentedProperties, setSelectedPresentedProperties] = useState([]);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [propertiesToAdd, setPropertiesToAdd] = useState([]);
  const [selectedPropertiesToAdd, setSelectedPropertiesToAdd] = useState([]);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  
  // Demandes de contact
  const [demandesContact, setDemandesContact] = useState([]);
  const [demandesArchivees, setDemandesArchivees] = useState([]);
  const [demandesContactFilter, setDemandesContactFilter] = useState('tous'); // 'tous', 'nouveau', 'traité'
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [showDemandeDetail, setShowDemandeDetail] = useState(false);
  const [demandesBien, setDemandesBien] = useState([]); // Demandes pour le bien actuel
  const [sharedCollectionView, setSharedCollectionView] = useState(null);
  const [sharedCollectionData, setSharedCollectionData] = useState(null);
  
  // DOCUMENTS STATE
  const [showDocuments, setShowDocuments] = useState(false);
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [favoriteProperties, setFavoriteProperties] = useState(() => {
    // Load favorites from localStorage
    const saved = localStorage.getItem('favoriteProperties');
    return saved ? JSON.parse(saved) : [];
  });
  const [showFavorites, setShowFavorites] = useState(false);
  const [favoritePropertiesData, setFavoritePropertiesData] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [selectedFavorites, setSelectedFavorites] = useState([]);
  const [showAffectationModal, setShowAffectationModal] = useState(false);
  const [selectedContactsForAffectation, setSelectedContactsForAffectation] = useState([]);
  const [demandeToAffect, setDemandeToAffect] = useState(null);
  const [affectationSearch, setAffectationSearch] = useState('');
  const [affectationResults, setAffectationResults] = useState([]);
  const [selectedContactForAffectation, setSelectedContactForAffectation] = useState(null);
  const [showAffectationBienModal, setShowAffectationBienModal] = useState(false);
  const [bienToAffect, setBienToAffect] = useState(null);
  const [showAffectationMultipleBiensModal, setShowAffectationMultipleBiensModal] = useState(false);
  
  // CONSULTATION STATE
  const [showConsultation, setShowConsultation] = useState(false);
  const [consultedProperty, setConsultedProperty] = useState(null);
  
  // State for property matching
  const [matchedProperties, setMatchedProperties] = useState(null);
  const [isMatchingLoading, setIsMatchingLoading] = useState(false);
  
  const [selectedPropertiesForContact, setSelectedPropertiesForContact] = useState([]);
  
  // States for proprietaire selection from contacts
  const [showContactSelection, setShowContactSelection] = useState(false);
  const [contactSelectionTarget, setContactSelectionTarget] = useState('proprietaire'); // 'proprietaire' or 'syndic'
  
  // MARKETING & SOCIAL MEDIA STATES
  const [showMarketing, setShowMarketing] = useState(false);
  const [marketingDashboard, setMarketingDashboard] = useState({
    analytics: [],
    campaigns: [],
    recent_posts: [],
    active_conversations: [],
    summary: {}
  });
  const [socialPosts, setSocialPosts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [chatbotConversations, setChatbotConversations] = useState([]);
  const [activeMarketingTab, setActiveMarketingTab] = useState("dashboard");
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    type: 'Property Listing',
    description: '',
    budget: '',
    target_audience: {
      age_range: '',
      interests: []
    },
    geographic_targeting: [],
    start_date: '',
    is_active: true
  });
  const [newPost, setNewPost] = useState({
    platform: 'Facebook',
    title: '',
    description: '',
    hashtags: [],
    status: 'Draft',
    media_urls: []
  });
  
  // PROPERTY HISTORY STATES  
  const [showPropertyHistory, setShowPropertyHistory] = useState(false);
  const [propertyHistory, setPropertyHistory] = useState([]);
  const [historyProperty, setHistoryProperty] = useState(null);
  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const [foundContacts, setFoundContacts] = useState([]);

  // SPEECH-TO-TEXT STATES
  const [isListening, setIsListening] = useState(false);
  const [speechRecognition, setSpeechRecognition] = useState(null);
  const [speechLanguage, setSpeechLanguage] = useState('fr-FR');
  const [speechSupported, setSpeechSupported] = useState(false);
  
  // OUTILS & CALCULETTES STATES
  const [showOutils, setShowOutils] = useState(false);
  const [activeOutilsTab, setActiveOutilsTab] = useState("dashboard");
  const [activeDocumentsTab, setActiveDocumentsTab] = useState("mes-documents");
  const [showAvisDeValeur, setShowAvisDeValeur] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  
  // DOCUMENT GENERATION STATES
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [generatingDocument, setGeneratingDocument] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [documentGenerationData, setDocumentGenerationData] = useState({
    property_id: '',
    custom_instructions: ''
  });
  
  // FICHE BIEN PDF STATES
  const [showFicheBienModal, setShowFicheBienModal] = useState(false);
  const [selectedPropertyForFiche, setSelectedPropertyForFiche] = useState(null);
  const [includeConfidential, setIncludeConfidential] = useState(false);

  // Pré-remplissage depuis un bien : quand on arrive avec `?prix=…&loyer=…`
  // (ex. via le bouton « Simuler » de la fiche bien), on injecte les valeurs
  // du bien dans les calculettes pour ouvrir directement sur des chiffres utiles.
  // `?calc=<id>` filtre pour n'afficher qu'une calculette (iter.75).
  const _presetFromUrl = React.useMemo(() => {
    const q = new URLSearchParams(location.search || '');
    return {
      prix: q.get('prix') || '',
      loyer: q.get('loyer') || '',
      calc: q.get('calc') || '',
    };
    // location.search change de référence à chaque navigation → re-evaluation.
  }, [location.search]);

  // Liste des IDs de calculettes valides (iter.75) — utilisé pour :
  //   - filtrer l'affichage quand `?calc=<id>` est présent
  //   - alimenter le menu « Nouvelle simulation »
  const CALCULETTES_MENU = React.useMemo(() => ([
    { id: 'pret',                 label: 'Calculette de prêt',                        icon: 'bi-calculator',        desc: 'Mensualité, coût total et capacité d\'emprunt.' },
    { id: 'rentabilite',          label: 'Calculette de rentabilité locative',        icon: 'bi-percent',            desc: 'Rendement brut, net et cash-flow locatif.' },
    { id: 'plus-value',           label: 'Calculette de plus-value',                  icon: 'bi-currency-euro',     desc: 'Impôt + prélèvements sociaux sur la plus-value.' },
    { id: 'droits-mutation',      label: 'Droits de mutation commerciaux',            icon: 'bi-file-earmark-text', desc: 'Cession de fonds (barème progressif) ou murs commerciaux.' },
    { id: 'surface-ponderee',     label: 'Surface pondérée commerciale',              icon: 'bi-rulers',            desc: 'RDC × 1, réserve × 0,5, sous-sol/mezzanine × 0,3.' },
    { id: 'valorisation-fonds',   label: 'Valorisation d\'un fonds de commerce',      icon: 'bi-cash-stack',        desc: 'Multiples CA et EBE — fourchette moyenne.' },
    { id: 'capitalisation-murs',  label: 'Valeur des murs par capitalisation',        icon: 'bi-graph-up-arrow',    desc: 'Valeur = loyer annuel ÷ taux de rendement.' },
  ]), []);
  const focusedCalc = _presetFromUrl.calc && CALCULETTES_MENU.some(c => c.id === _presetFromUrl.calc)
    ? _presetFromUrl.calc
    : null;

  // iter.75 : quand l'URL contient `?calc=<id>`, on bascule automatiquement
  // sur l'onglet Calculettes avec le rendu filtré sur la calculette demandée.
  // Cela permet aux boutons du dashboard et au menu « Nouvelle simulation »
  // d'ouvrir chacun une calculette bien précise dans une vue nettoyée.
  useEffect(() => {
    if (focusedCalc) {
      setActiveOutilsTab('calculettes');
    }
  }, [focusedCalc]);

  const [calculetteData, setCalculetteData] = useState({
    pret: {
      montant: _presetFromUrl.prix || '',
      duree: '',
      taux: '',
      apport: ''
    },
    rentabilite: {
      prix_achat: _presetFromUrl.prix || '',
      frais_achat: '',
      loyer_mensuel: _presetFromUrl.loyer || '',
      charges: '',
      travaux: ''
    },
    plus_value: {
      prix_achat: '',
      prix_vente: '',
      frais_achat: '',
      frais_vente: '',
      travaux: '',
      duree_detention: ''
    },
    // Droits de mutation commerciaux (iter.73) — remplace l'ancienne
    // « Frais de notaire » résidentielle (Ancien/Neuf/VEFA/TVA).
    // 2 modes : cession de fonds (barème progressif) ou murs commerciaux.
    droits_mutation: {
      type: 'fonds',              // 'fonds' | 'murs'
      prix: '',
      // Murs commerciaux — taux éditables par département
      taux_dep: '3.80',           // droit départemental
      taux_com: '1.20',           // taxe communale
      taux_annexes: '1.50'        // émoluments notaire + débours (estimation)
    },
    // Surface pondérée commerciale (iter.73) — remplace la loi Carrez
    // résidentielle. 3 zones avec coefficients standard éditables.
    surface_ponderee: {
      rdc: '',           // surface RDC / vitrine (coef 1)
      coef_rdc: '1',
      reserve: '',       // surface réserve / arrière-boutique (coef 0,5)
      coef_reserve: '0.5',
      annexe: '',        // surface sous-sol / mezzanine (coef 0,3)
      coef_annexe: '0.3'
    },
    // Valorisation d'un fonds de commerce (iter.73) — version standalone
    // de la carte « Valorisation du fonds » de ContextualSimulator.
    valorisation_fonds: {
      ca: '',
      ebe: '',
      bareme_ca: '80',   // multiple de CA en % (barème indicatif CHR / commerce)
      multiple_ebe: '4'  // multiple d'EBE typique
    },
    // Valeur des murs par capitalisation (iter.73) — version standalone
    // de la carte « Rendement & valorisation » de ContextualSimulator.
    capitalisation_murs: {
      loyer_annuel: '',
      taux: '7'          // taux de rendement cible en %
    }
  });
  const [calculResults, setCalculResults] = useState({});
  
  // Contact form data
  const [contactFormData, setContactFormData] = useState({
    type_contact: "",
    titre: "",
    nom: "",
    prenom: "",
    nom_societe: "",
    profession: "",
    telephones: [{ type: "mobile", numero: "", principal: true }],
    emails: [{ type: "professionnel", email: "", principal: true }],
    fax: "",
    adresse: "",
    code_postal: "",
    ville: "",
    pays: "France",
    negociateur_id: "",
    groupes: [],
    newsletter: false,
    informations_confidentielles: {},
    criteres_recherche: {}
  });

  // Form states - Complete structure with new tabs (Propriétaire déplacé vers Confidentiel)
  const [formData, setFormData] = useState({
    // Nouvelle taxonomie (Étape 2)
    operation: "",
    objet: "",
    profil: "",
    sous_profil: "",
    cible: "",
    bien_general: {
      type_bien: "Restaurant Pizzeria Snack",
      classement: "",
      murs_vente: "Non",
      identite_bailleur: "",
      prix_estime: "",
      location_mois_licence: "",
      travaux_prevoir: "",
      anciennete_activite: "",
      forme_juridique: ""
    },
    enseigne: {
      enseigne: "",
      superficie: ""
    },
    honoraires_calcul: {
      prix_vente: "",
      pourcentage: "",
      honoraires: "",
      net_vendeur: ""
    },
    informations_bail: {
      titre_locatif: "",
      loyer_soumis_tva: "",
      date_debut_bail: "",
      loyer_mois_charges_taxes: "",
      charges_mensuelles: "",
      impot_foncier: "",
      procedure: "",
      appartement_prive: ""
    },
    ouverture_fermeture: {
      horaires_ouverture_fermeture: "",
      fermeture_hebdomadaire: "",
      conges_fermeture_saisonniere: ""
    },
    conseils_vendeur: {
      avocat_notaire: "",
      expert_comptable: ""
    },
    negociateur: {
      negociateur: ""
    },
    documents_google_drive: {
      url_dossier: "",
      description: "Collez l'URL du dossier/fichier Google Drive"
    },
    composition: {
      brasserie: "",
      fournisseur_cafe: "",
      nombre_salles: 0,
      nombre_employes: 0,
      vitrine: "",
      fils_semaine: "",
      kms_cafe_semaine: "",
      facade: "",
      reserve: "",
      places_assises_interieur: 0,
      places_assises_terrasse: 0
    },
    prestations: {},
    commentaires_prestations: "",
    
    // New tabs data
    descriptif: {
      langue_active: "Français", // Langue actuellement sélectionnée pour édition
      descriptions: {
        "Fiche Technique": { 
          "Français": { content: "", optimized_by_ai: false },
          "Anglais": { content: "", optimized_by_ai: false },
          "Espagnol": { content: "", optimized_by_ai: false },
          "Italien": { content: "", optimized_by_ai: false }
        },
        "Affiche vitrine": { 
          "Français": { content: "", optimized_by_ai: false },
          "Anglais": { content: "", optimized_by_ai: false },
          "Espagnol": { content: "", optimized_by_ai: false },
          "Italien": { content: "", optimized_by_ai: false }
        },
        "Web": { 
          "Français": { content: "", optimized_by_ai: false },
          "Anglais": { content: "", optimized_by_ai: false },
          "Espagnol": { content: "", optimized_by_ai: false },
          "Italien": { content: "", optimized_by_ai: false }
        },
        "Pub": { 
          "Français": { content: "", optimized_by_ai: false },
          "Anglais": { content: "", optimized_by_ai: false },
          "Espagnol": { content: "", optimized_by_ai: false },
          "Italien": { content: "", optimized_by_ai: false }
        },
        "Mandat": { 
          "Français": { content: "", optimized_by_ai: false },
          "Anglais": { content: "", optimized_by_ai: false },
          "Espagnol": { content: "", optimized_by_ai: false },
          "Italien": { content: "", optimized_by_ai: false }
        },
        "Autre": { 
          "Français": { content: "", optimized_by_ai: false },
          "Anglais": { content: "", optimized_by_ai: false },
          "Espagnol": { content: "", optimized_by_ai: false },
          "Italien": { content: "", optimized_by_ai: false }
        }
      },
      proximite: {
        "Français": "",
        "Anglais": "",
        "Espagnol": "",
        "Italien": ""
      }
    },
    informations_confidentielles: {
      // Propriétaire (déplacé de Général)
      proprietaire: {
        societe: "",
        forme_juridique: "",
        activite: "",
        siret: "",
        nom: "",
        prenom: "",
        groupe: "",
        adresse: "",
        code_postal: "",
        ville: "",
        contacts: {
          telephones: [
            { type: "pro", value: "", label: "Professionnel" },
            { type: "perso", value: "", label: "Personnel" },
            { type: "mobile", value: "", label: "Mobile" }
          ],
          emails: [
            { type: "pro", value: "", label: "Professionnel" },
            { type: "perso", value: "", label: "Personnel" }
          ]
        }
      },
      
      // Localisation du bien (déplacée vers Confidentiel)
      adresse: "",
      code_postal: "",
      ville: "",
      coordonnees_gps: "",
      syndic_nom: "",
      syndic_adresse: "",
      syndic_contacts: {
        telephones: [
          { type: "pro", value: "", label: "Bureau" }
        ],
        emails: [
          { type: "pro", value: "", label: "Bureau" }
        ]
      },
      conditions_visites: "",
      horaires_visites: "",
      contact_visites: "",
      remarques_visites: "",
      informations_diverses: "",
      notes_particulieres: "",
      chiffre_affaires_annuel: "",
      benefices_annuels: "",
      charges_annuelles: "",
      banque: "",
      compte_bancaire: "",
      raison_vente: "",
      commentaires_confidentiels: ""
    },
    informations_vente: {
      prix_annonce: "",
      prix_minimum_accepte: "",
      date_disponibilite: "",
      delai_liberation: "",
      conditions_visite: "",
      contact_visite: "",
      documents_requis: "",
      statut_documents: ""
    },
    medias: {
      photos: [],
      documents: [],
      plans: []
    }
  });

  // Voice dictation states for Descriptif tab
  const [isRecordingDescriptif, setIsRecordingDescriptif] = useState({});

  // State for global search in header
  const [globalSearch, setGlobalSearch] = useState("");
  
  // State for search UI
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  // State for view mode (grid or list)
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'

  // State for table sorting
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Photo gallery functions
  const openPhotoGallery = useCallback((property, photoIndex = 0) => {
    // Prevent double-execution in StrictMode
    if (galleryOpeningRef.current) {
      return;
    }
    galleryOpeningRef.current = true;
    
    console.log("openPhotoGallery called", property?.id, property?.medias?.photos?.length);
    if (property.medias?.photos && property.medias.photos.length > 0) {
      console.log("Setting gallery state...");
      setGalleryProperty(property);
      setCurrentPhotoIndex(photoIndex);
      setPhotoGalleryOpen(true);
      console.log("Gallery state set to true");
    } else {
      console.log("No photos found in property");
    }
    
    // Reset the ref after a brief timeout
    setTimeout(() => {
      galleryOpeningRef.current = false;
    }, 100);
  }, []);

  const closePhotoGallery = useCallback(() => {
    setPhotoGalleryOpen(false);
    setGalleryProperty(null);
    setCurrentPhotoIndex(0);
    // Reset the ref when closing
    galleryOpeningRef.current = false;
  }, []);

  const nextPhoto = useCallback(() => {
    if (galleryProperty?.medias?.photos) {
      setCurrentPhotoIndex((prev) => 
        prev === galleryProperty.medias.photos.length - 1 ? 0 : prev + 1
      );
    }
  }, [galleryProperty]);

  const prevPhoto = useCallback(() => {
    if (galleryProperty?.medias?.photos) {
      setCurrentPhotoIndex((prev) => 
        prev === 0 ? galleryProperty.medias.photos.length - 1 : prev - 1
      );
    }
  }, [galleryProperty]);

  const goToPhoto = useCallback((index) => {
    setCurrentPhotoIndex(index);
  }, []);



  // Villes disponibles pour les filtres. Cette fonction retéléchargeait TOUT le portefeuille
  // (282 Ko en preview, 831 Ko en production) à chaque appel — dont un par chargement de
  // biens — uniquement pour en extraire des noms de villes. `/properties/criteria` les
  // agrège côté serveur, et sur les trois chemins possibles au lieu d'un seul.
  // Le paramètre `status` n'a plus d'objet : la route couvre tout le portefeuille de
  // l'agence, ce qui est un surensemble de ce que renvoyait chaque variante.
  const loadAvailableCities = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/properties/criteria`);
      setAvailableCities(data.cities || []);
    } catch (error) {
      console.error("Erreur lors du chargement des villes:", error);
      setAvailableCities([]);
    }
  }, []);

  // Initialize Speech Recognition
  const initializeSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = speechLanguage;
      
      setSpeechRecognition(recognition);
      setSpeechSupported(true);
      console.log('✅ Speech recognition initialized');
    } else {
      console.warn('❌ Speech recognition not supported in this browser');
      setSpeechSupported(false);
    }
  };

  // Speech-to-Text language options
  const speechLanguageOptions = [
    { code: 'fr-FR', name: 'Français', flag: '🇫🇷' },
    { code: 'en-US', name: 'English', flag: '🇺🇸' },
    { code: 'es-ES', name: 'Español', flag: '🇪🇸' },
    { code: 'it-IT', name: 'Italiano', flag: '🇮🇹' },
    { code: 'de-DE', name: 'Deutsch', flag: '🇩🇪' }
  ];

  // Start speech recognition for a specific editor
  const startSpeechForEditor = (targetElement, currentText = '') => {
    if (!speechRecognition || !speechSupported) {
      alert('La reconnaissance vocale n\'est pas supportée par votre navigateur');
      return;
    }

    setIsListening(true);
    
    // Configure recognition for current language
    speechRecognition.lang = speechLanguage;
    speechRecognition.continuous = true;
    speechRecognition.interimResults = true;
    
    let accumulatedTranscript = '';
    
    speechRecognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      
      // Accumulate final transcripts
      if (finalTranscript) {
        accumulatedTranscript += finalTranscript;
      }
      
      // Update the editor with accumulated + interim text
      const newText = currentText + accumulatedTranscript + interimTranscript;
      
      if (targetElement && targetElement.contentEditable === 'true') {
        // Find the associated React component and update through props
        // This avoids direct DOM manipulation and uses React's rendering cycle
        const editorId = targetElement.id;
        
        // Update content through React state instead of direct DOM manipulation
        targetElement.innerHTML = newText;
        
        // Trigger input event to update React state properly
        const inputEvent = new Event('input', { bubbles: true });
        targetElement.dispatchEvent(inputEvent);
        
        // Move cursor to end after state update
        setTimeout(() => {
          const selection = window.getSelection();
          const newRange = document.createRange();
          newRange.selectNodeContents(targetElement);
          newRange.collapse(false);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }, 0);
      }
    };

    speechRecognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      
      if (event.error === 'not-allowed') {
        alert('Veuillez autoriser l\'accès au microphone dans les paramètres de votre navigateur');
      } else if (event.error === 'no-speech') {
        alert('Aucune voix détectée. Parlez plus fort ou vérifiez votre microphone.');
      } else if (event.error === 'network') {
        alert('Erreur réseau. Vérifiez votre connexion internet et réessayez.');
      } else if (event.error === 'service-not-allowed') {
        alert('Service de reconnaissance vocale non autorisé. Vérifiez les paramètres de votre navigateur.');
      } else {
        alert(`Erreur de reconnaissance vocale: ${event.error}. Réessayez ou vérifiez vos paramètres de microphone.`);
      }
    };

    speechRecognition.onend = () => {
      setIsListening(false);
      console.log('Speech recognition ended');
    };

    try {
      speechRecognition.start();
      console.log('Speech recognition started for editor:', targetElement.id);
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      setIsListening(false);
    }
  };

  // Start speech recognition for simple textarea editor
  const startSpeechForSimpleEditor = (textareaElement, currentText = '') => {
    if (!speechRecognition || !speechSupported) {
      alert('La reconnaissance vocale n\'est pas supportée par votre navigateur');
      return;
    }

    setIsListening(true);
    
    // Configure recognition for current language
    speechRecognition.lang = speechLanguage;
    speechRecognition.continuous = true;
    speechRecognition.interimResults = true;
    
    let accumulatedTranscript = '';
    
    speechRecognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      
      // Accumulate final transcripts
      if (finalTranscript) {
        accumulatedTranscript += finalTranscript;
      }
      
      // Update the textarea with accumulated + interim text
      const newText = currentText + accumulatedTranscript + interimTranscript;
      
      if (textareaElement) {
        textareaElement.value = newText;
        // Trigger change event to update React state
        const event = new Event('input', { bubbles: true });
        textareaElement.dispatchEvent(event);
        
        // Move cursor to end
        textareaElement.setSelectionRange(newText.length, newText.length);
        textareaElement.focus();
      }
    };

    speechRecognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      
      if (event.error === 'not-allowed') {
        alert('Veuillez autoriser l\'accès au microphone dans les paramètres de votre navigateur');
      } else if (event.error === 'no-speech') {
        alert('Aucune voix détectée. Parlez plus fort ou vérifiez votre microphone.');
      } else if (event.error === 'network') {
        alert('Erreur réseau. Vérifiez votre connexion internet et réessayez.');
      } else if (event.error === 'service-not-allowed') {
        alert('Service de reconnaissance vocale non autorisé. Vérifiez les paramètres de votre navigateur.');
      } else {
        alert(`Erreur de reconnaissance vocale: ${event.error}. Réessayez ou vérifiez vos paramètres de microphone.`);
      }
    };

    speechRecognition.onend = () => {
      setIsListening(false);
      console.log('Speech recognition ended for simple editor');
    };

    try {
      speechRecognition.start();
      console.log('Speech recognition started for simple editor');
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      setIsListening(false);
      alert('Impossible de démarrer la reconnaissance vocale');
    }
  };

  // Start speech recognition for Lexical editor (legacy - keep for compatibility)
  const startSpeechForLexical = (editorInstance, currentText = '') => {
    if (!speechRecognition || !speechSupported) {
      alert('La reconnaissance vocale n\'est pas supportée par votre navigateur');
      return;
    }

    setIsListening(true);
    
    // Configure recognition for current language
    speechRecognition.lang = speechLanguage;
    speechRecognition.continuous = true;
    speechRecognition.interimResults = true;
    
    let accumulatedTranscript = '';
    
    speechRecognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      
      // Accumulate final transcripts
      if (finalTranscript) {
        accumulatedTranscript += finalTranscript;
      }
      
      // Update the Lexical editor with accumulated + interim text
      const newText = currentText + accumulatedTranscript + interimTranscript;
      
      if (editorInstance) {
        editorInstance.update(() => {
          const root = $getRoot();
          root.clear();
          const paragraph = $createParagraphNode();
          const textNode = $createTextNode(newText);
          paragraph.append(textNode);
          root.append(paragraph);
          
          // Move cursor to end of text
          setTimeout(() => {
            textNode.selectEnd();
          }, 0);
        });
      }
    };

    speechRecognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      
      if (event.error === 'not-allowed') {
        alert('Veuillez autoriser l\'accès au microphone dans les paramètres de votre navigateur');
      } else if (event.error === 'no-speech') {
        alert('Aucune voix détectée. Parlez plus fort ou vérifiez votre microphone.');
      } else if (event.error === 'network') {
        alert('Erreur réseau. Vérifiez votre connexion internet et réessayez.');
      } else if (event.error === 'service-not-allowed') {
        alert('Service de reconnaissance vocale non autorisé. Vérifiez les paramètres de votre navigateur.');
      } else {
        alert(`Erreur de reconnaissance vocale: ${event.error}. Réessayez ou vérifiez vos paramètres de microphone.`);
      }
    };

    speechRecognition.onend = () => {
      setIsListening(false);
      console.log('Speech recognition ended for Lexical editor');
    };

    try {
      speechRecognition.start();
      console.log('Speech recognition started for Lexical editor');
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      setIsListening(false);
      alert('Impossible de démarrer la reconnaissance vocale');
    }
  };

  // Stop speech recognition
  const stopSpeechRecognition = () => {
    if (speechRecognition && isListening) {
      speechRecognition.stop();
      setIsListening(false);
    }
  };

  // Change speech language
  const changeSpeechLanguage = (languageCode) => {
    setSpeechLanguage(languageCode);
    if (speechRecognition) {
      speechRecognition.lang = languageCode;
    }
  };

  // Speech-to-Text helper function for language change
  const handleSpeechLanguageChange = (languageCode) => {
    setSpeechLanguage(languageCode);
    if (speechRecognition) {
      speechRecognition.lang = languageCode;
    }
  };

  useEffect(() => {
    // NE PAS charger les propriétés si on est en mode édition (propertyToEdit existe)
    if (!propertyToEdit) {
      if (showDashboard) {
        // Pour le dashboard, charger toutes les propriétés (actives et archivées)
        loadAllPropertiesForDashboard();
      } else {
        loadProperties(currentFilter);
      }
      loadAllPropertiesCounts();
    }
    
    loadConfiguration();
    loadAvailableCities();
    loadDynamicCriteria(); // Load dynamic search criteria
    // AJ1 — aperçu borné pour le tableau de bord. Le thème legacy n'est PAS borné : sa carte
    // « +N ce mois » et son « Voir 5 de plus » raisonnent sur la liste entière, les borner
    // fausserait ses chiffres.
    if (isModerne) {
      loadContactsApercu();
    } else {
      loadContacts(); // Précharger les contacts pour le dashboard legacy
    }
    loadContactTypesAndGroups(); // Précharger les types de contacts
    initializeSpeechRecognition();
    
    // Charger les modèles de documents si on est dans la vue documents
    if (showDocuments) {
      loadDocumentTemplates();
    }
    
    // Initialiser les données du dashboard
    if (showDashboard) {
      initializeDashboardData();
    }
  }, [loadAvailableCities, showDashboard, showDocuments, propertyToEdit]);

  // Load all properties when entering favorites view
  useEffect(() => {
    if (showFavorites) {
      const loadFavoritesData = async () => {
        try {
          console.log('🔍 Loading all properties for favorites view...');
          const response = await axios.get(`${API}/properties?status=all`);
          console.log('✅ Loaded', response.data.length, 'properties');
          console.log('✅ Favorites IDs:', favoriteProperties);
          
          // Log first 5 property IDs to compare
          if (response.data.length > 0) {
            const sampleIds = response.data.slice(0, 5).map(p => p.id);
            console.log('📋 Sample property IDs:', sampleIds);
          }
          
          setFavoritePropertiesData(response.data);
          
          // Log filtered result with details
          const filtered = response.data.filter(p => favoriteProperties.includes(p.id));
          console.log('✅ Filtered favorites:', filtered.length, 'properties');
          
          if (filtered.length === 0 && favoriteProperties.length > 0) {
            console.warn('⚠️ No matching properties found!');
            console.log('🔍 Checking if any property has matching ID...');
            const firstFavId = favoriteProperties[0];
            const match = response.data.find(p => p.id === firstFavId);
            console.log('Match for first fav ID:', match ? 'FOUND' : 'NOT FOUND');
          }
        } catch (error) {
          console.error('❌ Error loading properties for favorites:', error);
        }
      };
      loadFavoritesData();
    }
  }, [showFavorites, favoriteProperties]);
  // useEffect pour déboguer les changements d'état Documents
  useEffect(() => {
    console.log('🔍 État Documents changé:', {
      showDocuments,
      showDashboard, 
      showForm,
      showContacts,
      activeSection
    });
  }, [showDocuments, showDashboard, showForm, showContacts, activeSection]);

  // useEffect to populate formData when editing an existing property
  useEffect(() => {
    if (currentProperty && showForm) {
      console.log('🔄 Chargement des données du bien pour édition:', currentProperty.id);
      console.log('📊 Type de bien dans currentProperty:', currentProperty.bien_general?.type_bien);
      console.log('📊 Activité principale dans currentProperty:', currentProperty.bien_general?.activite_principale);
      
      // Migrate old type_bien values to new system
      let typeBien = currentProperty.bien_general?.type_bien || '';
      let activitePrincipale = currentProperty.bien_general?.activite_principale || '';
      
      // If type_bien is empty but subcategory exists, migrate from subcategory
      if (!typeBien && currentProperty.subcategory) {
        console.log(`🔄 Migration depuis subcategory: ${currentProperty.subcategory}`);
        const deducedTypeBien = getTypeBienFromActivite(currentProperty.subcategory);
        if (deducedTypeBien) {
          typeBien = deducedTypeBien;
          activitePrincipale = currentProperty.subcategory;
          console.log(`✅ Migré depuis subcategory: type_bien=${typeBien}, activite=${activitePrincipale}`);
        }
      }
      
      // Check if migration is needed for old system values
      const validTypeBiens = ["Fonds de commerce", "Droit au bail", "Locaux commerciaux", "Bureaux", "Industriel", "Murs", "Terrain"];
      if (typeBien && !validTypeBiens.includes(typeBien)) {
        console.log(`🔄 Migration nécessaire pour type_bien: ${typeBien}`);
        const migrated = migrateOldTypeBien(typeBien);
        if (migrated) {
          typeBien = migrated.type_bien;
          activitePrincipale = migrated.activite_principale;
          console.log(`✅ Migré vers: type_bien=${typeBien}, activite_principale=${activitePrincipale}`);
        }
      }
      
      // Default descriptif structure
      const defaultDescriptif = {
        langue_active: "Français",
        descriptions: {
          "Fiche Technique": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Affiche vitrine": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Web": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Pub": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Mandat": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Autre": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          }
        },
        proximite: {
          "Français": "",
          "Anglais": "",
          "Espagnol": "",
          "Italien": ""
        }
      };
      
      // Populate formData with currentProperty values (with migrated type_bien/activite)
      const rawFormData = {
        // Nouvelle taxonomie (Étape 2)
        operation: currentProperty.operation || "",
        objet: currentProperty.objet || "",
        profil: currentProperty.profil || "",
        sous_profil: currentProperty.sous_profil || "",
        cible: currentProperty.cible || "",
        // Ancien champ conservé pour compatibilité éventuelle
        transaction_type: currentProperty.transaction_type || "Vente", // NOUVEAU - par défaut "Vente"
        bien_general: {
          ...(currentProperty.bien_general || {}),
          type_bien: typeBien,
          activite_principale: activitePrincipale,
          classement: currentProperty.bien_general?.classement || "",
          murs_vente: currentProperty.bien_general?.murs_vente || "Non",
          identite_bailleur: currentProperty.bien_general?.identite_bailleur || "",
          prix_estime: currentProperty.bien_general?.prix_estime || "",
          location_mois_licence: currentProperty.bien_general?.location_mois_licence || "",
          travaux_prevoir: currentProperty.bien_general?.travaux_prevoir || "",
          anciennete_activite: currentProperty.bien_general?.anciennete_activite || "",
          forme_juridique: currentProperty.bien_general?.forme_juridique || ""
        },
        enseigne: currentProperty.enseigne || {
          enseigne: "",
          superficie: ""
        },
        honoraires_calcul: currentProperty.honoraires_calcul || {
          prix_vente: "",
          pourcentage: "",
          honoraires: "",
          net_vendeur: ""
        },
        informations_bail: currentProperty.informations_bail || {
          titre_locatif: "",
          loyer_soumis_tva: "",
          date_debut_bail: "",
          loyer_mois_charges_taxes: "",
          charges_mensuelles: "",
          impot_foncier: "",
          procedure: "",
          appartement_prive: ""
        },
        ouverture_fermeture: currentProperty.ouverture_fermeture || {
          horaires_ouverture_fermeture: "",
          fermeture_hebdomadaire: "",
          conges_fermeture_saisonniere: ""
        },
        conseils_vendeur: currentProperty.conseils_vendeur || {
          avocat_notaire: "",
          expert_comptable: ""
        },
        negociateur: currentProperty.negociateur || {
          negociateur: ""
        },
        documents_google_drive: currentProperty.documents_google_drive || {
          url_dossier: "",
          description: "Collez l'URL du dossier/fichier Google Drive"
        },
        composition: currentProperty.composition || {
          brasserie: "",
          fournisseur_cafe: "",
          nombre_salles: 0,
          nombre_employes: 0,
          vitrine: "",
          fils_semaine: "",
          kms_cafe_semaine: "",
          facade: "",
          reserve: "",
          places_assises_interieur: 0,
          places_assises_terrasse: 0
        },
        prestations: (() => {
          // Nettoyer prestations pour ne garder QUE les boolean
          const rawPrestations = currentProperty.prestations || {};
          const cleanedPrestations = {};
          Object.keys(rawPrestations).forEach(key => {
            if (typeof rawPrestations[key] === 'boolean') {
              cleanedPrestations[key] = rawPrestations[key];
            } else {
              console.warn(`⚠️ Champ non-boolean "${key}" ignoré dans prestations lors du chargement`);
            }
          });
          return cleanedPrestations;
        })(),
        packs_actifs: currentProperty.packs_actifs || {}, // AJOUT: charger packs actifs
        commentaires_prestations: currentProperty.commentaires_prestations || "",
        descriptif: currentProperty.descriptif || defaultDescriptif,
        informations_confidentielles: currentProperty.informations_confidentielles || {},
        informations_vente: currentProperty.informations_vente || {},
        medias: currentProperty.medias || { photos: [], documents: [], plans: [], video_url: "", visite_virtuelle_url: "" }
      };
      
      // ✅ CRITICAL FIX: Clean loaded data to convert empty strings to null
      const newFormData = cleanDataRecursive(rawFormData);
      
      console.log('✅ Données chargées et nettoyées - type_bien:', newFormData.bien_general.type_bien);
      console.log('✅ Données chargées et nettoyées - activite_principale:', newFormData.bien_general.activite_principale);
      console.log('🧹 Composition après nettoyage:', newFormData.composition);
      
      setFormData(newFormData);
      
      // Force re-render of dynamic fields
      setConfigKey(prev => prev + 1);
    } else if (!currentProperty && showForm) {
      // Reset to default empty form when creating new property
      console.log('🆕 Initialisation formulaire vide pour nouveau bien');
      // Keep default formData initialization
    }
  }, [currentProperty, showForm]);


  const initializeDashboardData = () => {
    // Charger l'aperçu des demandes pour le tableau de bord (pas les 55 demandes + archivées)
    loadDemandesContactApercu();
    
    // Mock data pour la section News (à remplacer par des données réelles depuis une API)
    setNews([
      {
        id: 1,
        title: "Nouvelle fonctionnalité : Dashboard interactif",
        content: "Le nouveau dashboard offre des statistiques en temps réel et des graphiques interactifs pour un meilleur suivi de votre activité.",
        date: new Date().toISOString(),
        author: "Super Admin",
        type: "feature",
        priority: "high"
      },
      {
        id: 2,
        title: "Mise à jour système - Performance améliorée",
        content: "Optimisation des temps de chargement et amélioration de la synchronisation des données.",
        date: new Date(Date.now() - 86400000).toISOString(), // Hier
        author: "Super Admin",
        type: "update",
        priority: "medium"
      }
    ]);

    // Mock data pour les tâches
    setTasks([
      {
        id: 1,
        title: "Relancer M. Dupont pour la visite",
        description: "Restaurant Central - Rdv fixé pour demain 14h30",
        due_date: new Date(Date.now() + 86400000).toISOString(), // Demain
        priority: "high",
        status: "pending",
        assigned_to: "Yvan GRIMAZ"
      },
      {
        id: 2,
        title: "Préparer dossier Commerce Monaco",
        description: "Rassembler tous les documents pour signature mandat",
        due_date: new Date(Date.now() + 172800000).toISOString(), // Après-demain
        priority: "medium",
        status: "in_progress",
        assigned_to: "Yvan GRIMAZ"
      },
      {
        id: 3,
        title: "Mise à jour prix Pizzeria Nice",
        description: "Ajuster le prix selon l'évaluation expert",
        due_date: new Date(Date.now() + 259200000).toISOString(), // Dans 3 jours
        priority: "medium",
        status: "pending",
        assigned_to: "Yvan GRIMAZ"
      }
    ]);

    // Mock data pour l'agenda
    setAgendaEvents([
      {
        id: 1,
        title: "Visite - Restaurant Central",
        description: "Visite avec M. Dupont",
        start_time: new Date(Date.now() + 86400000 + 14.5 * 3600 * 1000).toISOString(), // Demain 14h30
        end_time: new Date(Date.now() + 86400000 + 15.5 * 3600 * 1000).toISOString(), // Demain 15h30
        type: "visite",
        client: "M. Dupont",
        property_ref: "#ca8296"
      },
      {
        id: 2,
        title: "Signature mandat - Commerce Monaco",
        description: "Signature contrat avec Mme Martin",
        start_time: new Date(Date.now() + 172800000 + 10 * 3600 * 1000).toISOString(), // Après-demain 10h00
        end_time: new Date(Date.now() + 172800000 + 11 * 3600 * 1000).toISOString(), // Après-demain 11h00
        type: "signature",
        client: "Mme Martin",
        property_ref: "#d42943"
      },
      {
        id: 3,
        title: "Présentation biens - Acquéreur",
        description: "Présentation sélection biens M. Durant",
        start_time: new Date(Date.now() + 172800000 + 16 * 3600 * 1000).toISOString(), // Après-demain 16h00
        end_time: new Date(Date.now() + 172800000 + 17 * 3600 * 1000).toISOString(), // Après-demain 17h00
        type: "presentation",
        client: "M. Durant",
        property_ref: "Multiple"
      }
    ]);

    // Mock data pour les emails récents (en attendant l'intégration Google Workspace)
    setRecentEmails([
      {
        id: 1,
        from: "martin.dupont@example.com",
        subject: "Intérêt pour Restaurant Central - Demande d'informations",
        preview: "Bonjour, je suis très intéressé par le restaurant central que vous proposez. Pourriez-vous me faire parvenir plus d'informations sur...",
        date: new Date(Date.now() - 3600000).toISOString(), // Il y a 1h
        read: false,
        priority: "high",
        property_ref: "#ca8296"
      },
      {
        id: 2,
        from: "sophie.bernard@workspace.com",
        subject: "RE: Visite Commerce Monaco - Confirmation",
        preview: "Parfait pour demain 14h30. Je confirme ma présence pour la visite du commerce. Merci de me faire parvenir l'adresse exacte...",
        date: new Date(Date.now() - 7200000).toISOString(), // Il y a 2h
        read: true,
        priority: "medium",
        property_ref: "#d42943"
      },
      {
        id: 3,
        from: "jean.martin@immo.fr",
        subject: "Partenariat inter-agences - Proposition",
        preview: "Bonjour, nous avons plusieurs acquéreurs recherchant des biens commerciaux sur Monaco. Seriez-vous intéressé par un partenariat...",
        date: new Date(Date.now() - 14400000).toISOString(), // Il y a 4h
        read: false,
        priority: "medium",
        property_ref: null
      },
      {
        id: 4,
        from: "contact@expert-compta.fr",
        subject: "Évaluation Pizza Express Nice - Rapport final",
        preview: "Veuillez trouver ci-joint le rapport d'évaluation pour la pizzeria située rue de France à Nice. La valeur estimée...",
        date: new Date(Date.now() - 21600000).toISOString(), // Il y a 6h
        read: true,
        priority: "low",
        property_ref: "#f83142"
      }
    ]);
  };

  // Keyboard navigation for photo gallery
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!photoGalleryOpen) return;
      
      switch(event.key) {
        case 'Escape':
          closePhotoGallery();
          break;
        case 'ArrowLeft':
          prevPhoto();
          break;
        case 'ArrowRight':
          nextPhoto();
          break;
        default:
          break;
      }
    };

    if (photoGalleryOpen) {
      document.addEventListener('keydown', handleKeyPress);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [photoGalleryOpen, closePhotoGallery, prevPhoto, nextPhoto]);

  const loadProperties = async (status = "active") => {
    try {
      setCurrentFilter(status);
      
      // Mettre à jour l'URL SEULEMENT si on affiche déjà la page biens
      if (showForm && !showDashboard && !showContacts && !showMarketing && !showOutils && !showDocuments) {
        navigate(`/biens-v2?filter=${status}`, { replace: true });
      }
      
      // Map frontend filter names to backend status values
      const statusMapping = {
        'archive': 'archived',
        'active': 'active',
        'draft': 'draft',
        'sold': 'sold',
        'all': 'all',
        'shared': 'all' // Load all for shared, then filter
      };
      
      const backendStatus = statusMapping[status] || status;
      console.log(`📋 Loading properties: frontend filter="${status}" → backend status="${backendStatus}"`);
      
      const response = await axios.get(`${API}/properties?status=${backendStatus}`);
      
      let propertiesData = response.data;
      
      // If filter is "shared", only keep properties with is_public = true
      if (status === 'shared') {
        propertiesData = propertiesData.filter(p => p.is_public && p.public_code);
        console.log(`✅ Filtered to ${propertiesData.length} shared properties`);
      }
      
      setProperties(propertiesData);
      
      console.log(`✅ Loaded ${propertiesData.length} properties with filter="${status}"`);
      
      // Charger les liens de partage existants
      loadExistingLinks(propertiesData);
      
      // Also reload available cities for the current status
      await loadAvailableCities(status);
      
      // Reload counts after loading properties
      await loadAllPropertiesCounts();
    } catch (error) {
      console.error("Erreur lors du chargement des biens:", error);
    }
  };

  const loadAllPropertiesCounts = async () => {
    try {
      // Utiliser l'endpoint /count pour ne charger que les compteurs (pas les données)
      const [activeRes, draftRes, archivedRes, soldRes, allRes] = await Promise.all([
        axios.get(`${API}/properties/count?status=active`),
        axios.get(`${API}/properties/count?status=draft`),
        axios.get(`${API}/properties/count?status=archived`),
        axios.get(`${API}/properties/count?status=sold`),
        axios.get(`${API}/properties/count?status=all`)
      ]);
      
      const activeCount = activeRes.data.count;
      const draftCount = draftRes.data.count;
      const archivedCount = archivedRes.data.count;
      const soldCount = soldRes.data.count;
      const sharedCount = allRes.data.shared || 0;
      
      const counts = {
        active: activeCount,
        draft: draftCount,
        archive: archivedCount,
        archived: archivedCount, // Ajouter 'archived' pour compatibilité avec ModernDashboard
        sold: soldCount,
        shared: sharedCount,
        all: activeCount + draftCount + archivedCount + soldCount
      };
      
      console.log("📊 Compteurs chargés:", counts);
      console.log("📊 Total calculé:", counts.all, "=", activeCount, "+", draftCount, "+", archivedCount, "+", soldCount);
      console.log("📊 Biens partagés:", sharedCount);
      setAllPropertiesCounts(counts);
    } catch (error) {
      console.error("Erreur lors du chargement des compteurs:", error);
    }
  };

  // Navigation vers liste filtrée depuis dashboard
  // ⚠️ Doit naviguer vers la NOUVELLE page /properties (PropertiesListPage v2),
  // pas afficher la liste legacy interne à PropertyForm.
  const goToPropertiesList = (status = 'active') => {
    navigate(`/properties?filter=${status}`);
  };

  // WIZARD FUNCTIONS (délégation vers App)
  const startWizard = () => {
    console.log("🚀 startWizard appelée - délégation vers App");
    if (onStartWizard) {
      onStartWizard();
    }
    console.log("✅ Wizard démarré via App.js");
  };

  // FICHE BIEN PDF FUNCTIONS
  const openFicheBienModal = (property) => {
    console.log("🔵 openFicheBienModal appelée avec:", property?.id);
    setSelectedPropertyForFiche(property);
    setIncludeConfidential(false);
    setShowFicheBienModal(true);
    console.log("🔵 Modal état changé à true");
  };

  const closeFicheBienModal = () => {
    setShowFicheBienModal(false);
    setSelectedPropertyForFiche(null);
    setIncludeConfidential(false);
  };

  const downloadFicheBien = async () => {
    if (!selectedPropertyForFiche) return;

    const version = includeConfidential ? 'confidential' : 'public';
    const propertyId = selectedPropertyForFiche.id;
    const ref = selectedPropertyForFiche.reference || propertyId?.slice(0, 8);
    closeFicheBienModal();

    // iter131 — la route est authentifiée : téléchargement en blob (window.open ne peut
    // pas porter l'en-tête Authorization).
    try {
      const response = await axios.get(`${API}/properties/${propertyId}/fiche-bien`, {
        params: { version },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fiche_bien_${ref}_${version === 'confidential' ? 'agent' : 'client'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur génération fiche bien:', err);
      alert('Erreur lors de la génération de la fiche. Veuillez réessayer.');
    }
  };

  // DOCUMENT GENERATION FUNCTIONS
  const openDocumentGenerator = (documentType) => {
    setSelectedDocumentType(documentType);
    setGeneratedDocument(null);
    setDocumentGenerationData({
      property_id: '',
      custom_instructions: ''
    });
    setShowDocumentModal(true);
  };

  const closeDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocumentType(null);
    setGeneratedDocument(null);
    setGeneratingDocument(false);
    setDocumentGenerationData({
      property_id: '',
      custom_instructions: ''
    });
  };

  const generateDocument = async () => {
    try {
      setGeneratingDocument(true);
      setGeneratedDocument(null);
      
      const requestData = {
        document_type: selectedDocumentType,
        custom_instructions: documentGenerationData.custom_instructions
      };
      
      // Add property_id if selected
      if (documentGenerationData.property_id) {
        requestData.property_id = documentGenerationData.property_id;
      }
      
      const response = await axios.post(`${API}/documents/generate`, requestData);
      
      if (response.data.success) {
        setGeneratedDocument(response.data);
      } else {
        alert('Erreur lors de la génération du document');
      }
    } catch (error) {
      console.error("Erreur lors de la génération du document:", error);
      alert('Erreur lors de la génération du document: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingDocument(false);
    }
  };

  const loadAllPropertiesForDashboard = async () => {
    try {
      const response = await axios.get(`${API}/properties?status=all`);
      setProperties(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement de tous les biens:", error);
    }
  };

  const deleteProperty = async (propertyId) => {
    // lot BX — suppression douce : ni « DÉFINITIVEMENT », ni « irréversible ». La diffusion
    // n'est pas chargée dans cet écran (legacy) : phrase générique.
    if (!window.confirm(messageSuppression(undefined))) {
      return;
    }    
    try {
      await axios.delete(`${API}/properties/${propertyId}`);
      // Reload all properties for dashboard statistics
      if (showDashboard) {
        await loadAllPropertiesForDashboard();
      } else {
        await loadProperties(currentFilter);
      }
      // Reload counts after deletion
      await loadAllPropertiesCounts();
      alert("Bien supprimé avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert(messageServeur(error, "Erreur lors de la suppression"));
    }
  };

  // Supprimer plusieurs biens sélectionnés
  const handleDeleteSelectedProperties = async () => {
    try {
      const promises = selectedProperties.map(propertyId =>
        axios.delete(`${API}/properties/${propertyId}`)
      );
      
      await Promise.all(promises);
      console.log(`✅ ${selectedProperties.length} bien(s) supprimé(s) avec succès`);
      
      // Recharger la liste ET les compteurs
      if (showDashboard) {
        await loadAllPropertiesForDashboard();
      } else {
        await loadProperties(currentFilter);
      }
      
      // Réinitialiser la sélection
      setSelectedProperties([]);
      
      alert(`${selectedProperties.length} bien(s) supprimé(s) avec succès`);
    } catch (error) {
      console.error('❌ Erreur lors de la suppression multiple:', error);
      alert('Erreur lors de la suppression des biens');
    }
  };

  // Fonction pour charger les liens existants des biens
  const loadExistingLinks = (properties) => {
    const links = {};
    properties.forEach(prop => {
      if (prop.is_public && prop.public_code) {
        links[prop.id] = {
          code: prop.public_code,
          url: `${window.location.origin}/p/${prop.public_code}`,
          loading: false
        };
      }
    });
    setPropertyLinks(links);
  };

  // Fonction pour générer le lien de partage public
  const generatePublicLink = async (propertyId) => {
    try {
      setPropertyLinks(prev => ({...prev, [propertyId]: {loading: true}}));
      
      const response = await axios.post(`${API}/properties/${propertyId}/public-link`);
      
      const linkData = {
        code: response.data.public_code,
        url: `${window.location.origin}/p/${response.data.public_code}`,
        loading: false
      };
      
      setPropertyLinks(prev => ({...prev, [propertyId]: linkData}));
      setExpandedShareProperty(propertyId); // Ouvre la section
      
      // Recharger les propriétés pour mettre à jour is_public
      await loadProperties(currentFilter);
      
    } catch (error) {
      console.error('Erreur génération lien:', error);
      alert('Erreur lors de la génération du lien public');
      setPropertyLinks(prev => ({...prev, [propertyId]: {loading: false, error: true}}));
    }
  };

  // Fonction pour désactiver le partage public
  const disablePublicLink = async (propertyId) => {
    try {
      await axios.delete(`${API}/properties/${propertyId}/public-link`);
      setPropertyLinks(prev => {
        const newLinks = {...prev};
        delete newLinks[propertyId];
        return newLinks;
      });
      setExpandedShareProperty(null);
      await loadProperties(currentFilter); // Recharger pour mettre à jour is_public
      alert('Partage désactivé avec succès');
    } catch (error) {
      console.error('Erreur désactivation:', error);
      alert('Erreur lors de la désactivation du partage');
    }
  };

  const archiveProperty = async (propertyId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir archiver ce bien ?")) {
      return;
    }
    
    try {
      await axios.patch(`${API}/properties/${propertyId}/archive`);
      // Reload all properties for dashboard statistics
      if (showDashboard) {
        await loadAllPropertiesForDashboard();
      } else {
        await loadProperties(currentFilter);
      }
      // Reload counts after archiving
      await loadAllPropertiesCounts();
      alert("Bien archivé avec succès");
    } catch (error) {
      console.error("Erreur lors de l'archivage:", error);
      console.error("Détails:", error.response?.data);
      alert("Erreur lors de l'archivage: " + (error.response?.data?.detail || error.message));
    }
  };

  const restoreProperty = async (propertyId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir restaurer ce bien ?")) {
      return;
    }
    
    try {
      await axios.patch(`${API}/properties/${propertyId}/restore`);
      // Reload all properties for dashboard statistics
      if (showDashboard) {
        await loadAllPropertiesForDashboard();
      } else {
        await loadProperties(currentFilter);
      }
      // Reload counts after restoring
      await loadAllPropertiesCounts();
      alert("Bien restauré avec succès");
    } catch (error) {
      console.error("Erreur lors de la restauration:", error);
      console.error("Détails:", error.response?.data);
      alert("Erreur lors de la restauration: " + (error.response?.data?.detail || error.message));
    }
  };

  const loadConfiguration = async () => {
    try {
      // Load prestations
      const prestationsResponse = await axios.get(`${API}/config/prestations`);
      setAvailablePrestations(prestationsResponse.data.prestations);
      
      // Initialize prestations state
      const prestationsObj = {};
      prestationsResponse.data.prestations.forEach(prestation => {
        prestationsObj[prestation] = false;
      });
      setFormData(prev => ({
        ...prev,
        prestations: prestationsObj
      }));

      // Load property types
      const typesResponse = await axios.get(`${API}/config/property-types`);
      setPropertyTypes(typesResponse.data.property_types);

      // Load formes juridiques
      const formesResponse = await axios.get(`${API}/config/forme-juridique`);
      setFormesJuridiques(formesResponse.data.formes_juridiques);

    } catch (error) {
      console.error("Erreur lors du chargement de la configuration:", error);
    }
  };

  // CONTACTS MANAGEMENT FUNCTIONS
  // Search contacts for proprietaire selection
  const searchContactsForProprietaire = async (query) => {
    if (!query || query.length < 2) {
      setFoundContacts([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/contacts/search/duplicates?nom=${query}&prenom=${query}`);
      setFoundContacts(response.data);
    } catch (error) {
      console.error("Erreur lors de la recherche de contacts:", error);
      setFoundContacts([]);
    }
  };

  // UNIFIED: Select contact and update property form
  const selectContactForProperty = (contact, targetType) => {
    if (targetType === 'proprietaire') {
      const proprietaireData = {
        societe: contact.nom_societe || "",
        nom: contact.nom || "",
        prenom: contact.prenom || "",
        adresse: contact.adresse || "",
        code_postal: contact.code_postal || "",
        ville: contact.ville || "",
        forme_juridique: null,
        activite: "",
        siret: "",
        groupe: "",
        contacts: {
          telephones: contact.telephones || [],
          emails: contact.emails || []
        }
      };
      
      setFormData(prev => ({
        ...prev,
        informations_confidentielles: {
          ...prev.informations_confidentielles,
          proprietaire: proprietaireData
        }
      }));
      
      setShowContactSelection(false);
      setContactSearchQuery("");
      setFoundContacts([]);
      alert(`Contact "${contact.prenom} ${contact.nom}" sélectionné comme propriétaire`);
      
    } else if (targetType === 'syndic') {
      const firstTelephone = contact.telephones?.[0]?.numero || contact.telephones?.[0]?.value || "";
      const firstEmail = contact.emails?.[0]?.email || contact.emails?.[0]?.value || "";
      
      setFormData(prev => ({
        ...prev,
        informations_confidentielles: {
          ...prev.informations_confidentielles,
          syndic_nom: contact.nom_societe || `${contact.prenom || ''} ${contact.nom || ''}`.trim(),
          syndic_telephone: firstTelephone,
          syndic_email: firstEmail,
          syndic_adresse: contact.adresse || ""
        }
      }));
      
      setShowContactSelection(false);
      setContactSearchQuery("");
      setFoundContacts([]);
      alert(`Contact "${contact.nom_societe || contact.nom}" sélectionné comme syndic`);
    }
  };

  // UNIFIED: Create contact from property info
  const createContactFromPropertyInfo = async (contactData, type) => {
    try {
      let nomToCheck = "";
      let prenomToCheck = "";
      let newContact = {};
      
      if (type === "Propriétaire") {
        if (!contactData.nom || !contactData.nom.trim()) {
          console.log(`⏭️ Skipping ${type} creation: no name provided`);
          return null;
        }
        nomToCheck = contactData.nom;
        prenomToCheck = contactData.prenom || "";
        
        newContact = {
          type_contact: "Propriétaire",
          nom: contactData.nom,
          prenom: contactData.prenom || "",
          nom_societe: contactData.societe || "",
          adresse: contactData.adresse || "",
          code_postal: contactData.code_postal || "",
          ville: contactData.ville || "",
          telephones: contactData.telephones || [], // FIX: accès direct à telephones
          emails: contactData.emails || [], // FIX: accès direct à emails
          groupes: ["Propriétaires"],
          newsletter: false
        };
        
      } else if (type === "Syndic") {
        if (!contactData.syndic_nom || !contactData.syndic_nom.trim()) {
          console.log(`⏭️ Skipping ${type} creation: no name provided`);
          return null;
        }
        nomToCheck = contactData.syndic_nom;
        
        newContact = {
          type_contact: "Syndic",
          nom: contactData.syndic_nom,
          prenom: "",
          nom_societe: contactData.syndic_nom,
          adresse: contactData.syndic_adresse || "",
          telephones: contactData.syndic_telephone ? [{ type: "pro", numero: contactData.syndic_telephone, principal: true }] : [],
          emails: contactData.syndic_email ? [{ type: "pro", email: contactData.syndic_email, principal: true }] : [],
          groupes: ["Syndics"],
          newsletter: false
        };
      } else {
        console.log(`⚠️ Type de contact non supporté: ${type}`);
        return null;
      }
      
      // Check for duplicates
      const params = new URLSearchParams();
      params.append('nom', nomToCheck);
      if (prenomToCheck) {
        params.append('prenom', prenomToCheck);
      }
      
      const duplicateResponse = await axios.get(`${API}/contacts/search/duplicates?${params}`);
      
      if (duplicateResponse.data && duplicateResponse.data.length > 0) {
        console.log(`ℹ️ Contact ${type} déjà existant, création ignorée`);
        return null;
      }
      
      // Create new contact
      console.log(`📤 Création du contact ${type}:`, newContact);
      const response = await axios.post(`${API}/contacts`, newContact);
      console.log(`✅ Contact ${type} créé avec succès:`, response.data);
      alert(`Contact "${nomToCheck}" ajouté automatiquement à l'annuaire (catégorie: ${type}) !`);
      return response.data;
      
    } catch (error) {
      console.error(`❌ Erreur lors de la création automatique du contact ${type}:`, error);
      console.error(`❌ Détails:`, error.response?.data);
      alert(`⚠️ Erreur lors de l'ajout du contact ${type} à l'annuaire. Veuillez vérifier les logs.`);
      return null;
    }
  };

  // Helper functions to extract data from properties (handles both old wizard structure and new structure)
  const getPropertyType = (property) => {
    // Prioritize activite_principale, then subcategory/category, then type_bien
    let type = property.bien_general?.activite_principale ||
               property.subcategory ||
               property.category ||
               property.bien_general?.type_bien || 
               'Non défini';
    
    // Shorten long types
    if (type.includes('(')) {
      type = type.split('(')[0].trim(); // "Licence IV (débit...)" → "Licence IV"
    }
    
    return type;
  };

  const getPropertyTransaction = (property) => {
    return property.transaction_type || 'Vente';
  };

  const getPropertyPrice = (property) => {
    const price = property.honoraires_calcul?.prix_vente || 
                  property.bien_general?.prix_estime;
    return price ? `${price.toLocaleString()} €` : 'Prix à définir';
  };

  const getPropertyAddress = (property, mode = 'city') => {
    const ville = property.informations_confidentielles?.ville || 
                  property.ville ||
                  'Non définie';
    
    if (mode === 'full') {
      const adresse = property.informations_confidentielles?.adresse || 
                     property.adresse ||
                     'Non définie';
      return adresse;
    }
    return ville;
  };

  const getPropertyPostalCode = (property) => {
    return property.informations_confidentielles?.code_postal || '-';
  };

  const getPropertyOwner = (property) => {
    const owner = property.informations_confidentielles?.proprietaire;
    
    if (!owner) return { name: 'Non défini', company: '' };
    
    const name = `${owner.prenom || ''} ${owner.nom || ''}`.trim() || 'Non défini';
    const company = owner.societe || '';
    
    return { name, company };
  };

  const getPropertyRooms = (property) => {
    return property.composition?.nombre_salles || '-';
  };

  const getPropertySurface = (property) => {
    return property.composition?.superficie_totale || 
           property.bien_general?.superficie ||
           '-';
  };

  // ==========================================
  // SYSTÈME DE CONFIGURATION DYNAMIQUE V2
  // ==========================================
  
  // Import des configurations de fiches - STATIC IMPORTS pour forcer le rechargement
  let baseConfigJson = null;
  let restaurationConfigJson = null;
  let bureauxConfigJson = null;
  let retailConfigJson = null;
  let industrielConfigJson = null;
  let licencesBoissonsConfigJson = null;
  
  try {
    baseConfigJson = require('../../configs/fiches/base-config.json');
    console.log('✅ base-config chargé, sections:', Object.keys(baseConfigJson?.onglets?.general?.sections || {}));
    console.log('✅ Champs identification:', baseConfigJson?.onglets?.general?.sections?.identification?.champs?.length);
  } catch (e) {
    console.error('❌ Erreur chargement base-config:', e);
  }
  
  try {
    restaurationConfigJson = require('../../configs/fiches/restauration-config.json');
  } catch (e) {
    console.warn('Config restauration non trouvée');
  }
  
  try {
    bureauxConfigJson = require('../../configs/fiches/bureaux-config.json');
  } catch (e) {
    console.warn('Config bureaux non trouvée');
  }
  
  try {
    retailConfigJson = require('../../configs/fiches/retail-config.json');
  } catch (e) {
    console.warn('Config retail non trouvée');
  }
  
  try {
    industrielConfigJson = require('../../configs/fiches/industriel-config.json');
  } catch (e) {
    console.warn('Config industriel non trouvée');
  }
  
  try {
    licencesBoissonsConfigJson = require('../../configs/fiches/licences-boissons-config.json');
    console.log('✅ licences-boissons-config chargé');
  } catch (e) {
    console.warn('Config licences boissons non trouvée');
  }
  
  // Import des packs d'activité
  const packRestauration = require('../../configs/packs/pack-restauration-complet.json');
  const packRetail = require('../../configs/packs/pack-retail-complet.json');
  const packEnseignes = require('../../configs/packs/pack-enseignes.json');
  
  // Import ancienne config licence (à migrer)
  const licenceVenteConfig = require('../../configs/licence-vente-config.json');
  
  /**
   * Charger la configuration de fiche selon le type
   */
  const loadFicheConfig = (typeConfig) => {
    const configs = {
      'FDC': fdcConfig,
      'FONDS_COMMERCE': fdcConfig,
      'LOCAUX_COMMERCIAUX': locauxComConfig,
      'LOCAUX_COMMERCIAL': locauxComConfig,
      // Ancienne config licence (fallback)
      'LICENCE': licenceVenteConfig
    };
    
    return configs[typeConfig] || null;
  };
  
  /**
   * Charger un pack d'activité
   */
  const loadPack = (packId) => {
    const packs = {
      'restauration': packRestauration,
      'retail': packRetail,
      'enseignes': packEnseignes
    };
    
    return packs[packId] || null;
  };
  
  /**
   * Fusionner config de base + packs actifs
   */
  const mergeConfigWithPacks = (baseConfig, packsActifs = {}) => {
    if (!baseConfig) return null;
    
    // Cloner la config de base
    const mergedConfig = JSON.parse(JSON.stringify(baseConfig));
    
    // S'assurer que la structure onglets.prestations.sections existe
    if (!mergedConfig.onglets) mergedConfig.onglets = {};
    if (!mergedConfig.onglets.prestations) mergedConfig.onglets.prestations = {};
    if (!mergedConfig.onglets.prestations.sections) mergedConfig.onglets.prestations.sections = {};
    
    // Ajouter les champs des packs actifs
    Object.keys(packsActifs).forEach(packId => {
      if (packsActifs[packId] === true) {
        const pack = loadPack(packId);
        if (pack && pack.champs) {
          // Ajouter un groupe pour ce pack dans prestations
          mergedConfig.onglets.prestations.sections[`pack_${packId}`] = {
            label: pack.label || `Pack ${packId}`,
            icon: pack.icon || 'bi-star',
            color: pack.color || 'blue',
            champs: pack.champs
          };
        }
      }
    });
    
    return mergedConfig;
  };
  
  /**
   * Rendre un champ dynamiquement selon son type
   */
  /**
   * Helper pour obtenir la valeur d'un champ avec le bon type par défaut
   */
  const getFieldValue = (data, groupeData, champType) => {
    const parts = groupeData?.split('.');
    if (!parts) return getDefaultValue(champType);
    
    // Helper pour vérifier si une valeur est considérée comme "vide"
    const isEmpty = (value) => {
      // Pour les booléens, false est une valeur valide, pas vide
      if (typeof value === 'boolean') return false;
      // Pour les autres types, vérifier undefined, null et string vide
      return value === undefined || value === null || value === '';
    };
    
    // Champ racine (ex: transaction_type)
    if (parts.length === 1) {
      const value = data[parts[0]];
      if (!isEmpty(value)) {
        return value;
      }
      return getDefaultValue(champType);
    }
    
    // Champ imbriqué (ex: bien_general.type_bien)
    if (parts.length === 2) {
      const value = data[parts[0]]?.[parts[1]];
      if (!isEmpty(value)) {
        return value;
      }
      return getDefaultValue(champType);
    }
    
    // Plus de 2 niveaux (ex: descriptif.proximite.Français)
    if (parts.length > 2) {
      let value = data;
      for (const part of parts) {
        value = value?.[part];
        if (value === undefined) break;
      }
      if (!isEmpty(value)) {
        return value;
      }
    }
    
    // Sinon, retourner la valeur par défaut selon le type
    return getDefaultValue(champType);
  };
  
  const getDefaultValue = (champType) => {
    switch (champType) {
      case 'boolean':
        return null; // null au lieu de '' pour éviter les erreurs de validation
      case 'number':
        return null; // null au lieu de '' pour les nombres
      case 'array_string':
        return [];
      default:
        return ''; // string vide pour les types text/string/enum
    }
  };

  const renderDynamicField = (champ, value, onChange) => {
    const commonClasses = "w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm";
    
    // Pour activite_principale, vérifier si type_bien est sélectionné
    const isActivitePrincipaleDisabled = champ.id === 'activite_principale' && !formData.bien_general?.type_bien;
    
    switch (champ.type) {
      case 'string':
      case 'text':
        if (champ.type === 'text') {
          return (
            <textarea
              className={commonClasses + " resize-none"}
              rows="3"
              placeholder={champ.placeholder || ''}
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
            />
          );
        }
        return (
          <input
            type="text"
            className={commonClasses}
            placeholder={champ.placeholder || ''}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      
      case 'number':
        return (
          <div className="relative">
            <input
              type="number"
              className={commonClasses}
              placeholder={champ.placeholder || ''}
              value={value || ''}
              onChange={(e) => onChange(parseFloat(e.target.value) || '')}
            />
            {champ.unite && (
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">{champ.unite}</span>
            )}
          </div>
        );
      
      case 'boolean':
        return (
          <select
            className={commonClasses}
            value={value === true ? 'true' : value === false ? 'false' : ''}
            onChange={(e) => onChange(e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)}
          >
            <option value="">Non spécifié</option>
            <option value="true">Oui</option>
            <option value="false">Non</option>
          </select>
        );
      
      case 'enum':
        return (
          <div className="relative">
            <select
              className={commonClasses + (isActivitePrincipaleDisabled ? ' opacity-50 cursor-not-allowed' : '')}
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              disabled={isActivitePrincipaleDisabled}
            >
              <option value="">
                {isActivitePrincipaleDisabled ? 'Sélectionner d\'abord un Type de bien' : 'Sélectionner...'}
              </option>
              {champ.options && champ.options.map((option, idx) => (
                <option key={idx} value={option}>{option}</option>
              ))}
            </select>
            {isActivitePrincipaleDisabled && (
              <div className="absolute -bottom-6 left-0 text-xs text-orange-600">
                ⚠️ Veuillez d'abord sélectionner un Type de bien
              </div>
            )}
          </div>
        );
      
      case 'date':
        return (
          <input
            type="date"
            className={commonClasses}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      
      case 'array_string':
        return (
          <input
            type="text"
            className={commonClasses}
            placeholder={champ.placeholder || 'Séparés par des virgules'}
            value={Array.isArray(value) ? value.join(', ') : value || ''}
            onChange={(e) => onChange(e.target.value.split(',').map(v => v.trim()))}
          />
        );
      
      case 'info':
        return (
          <div className={`p-4 rounded-xl ${champ.style === 'info-box' ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
            <div className="flex items-start">
              <i className="bi bi-info-circle text-blue-600 text-xl mr-3 mt-0.5"></i>
              <p className="text-sm text-gray-700 leading-relaxed">{champ.content}</p>
            </div>
          </div>
        );
      
      default:
        return (
          <input
            type="text"
            className={commonClasses}
            placeholder={champ.placeholder || ''}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };
  
  /**
   * Migration des anciennes valeurs de type_bien vers le nouveau système
   */
  const migrateOldTypeBien = (oldTypeBien) => {
    // Mapping des anciennes valeurs vers le nouveau système
    const migrations = {
      "Restaurant Pizzeria Snack": { type_bien: "Fonds de commerce", activite_principale: "Restaurant / Brasserie / Pizzeria / Snack / Fast-food" },
      "Bar": { type_bien: "Fonds de commerce", activite_principale: "Bar / Café / Pub / Lounge" },
      "Restaurant": { type_bien: "Fonds de commerce", activite_principale: "Restaurant / Brasserie / Pizzeria / Snack / Fast-food" },
      "Boulangerie": { type_bien: "Fonds de commerce", activite_principale: "Boulangerie / Pâtisserie" },
      "Bureaux": { type_bien: "Bureaux", activite_principale: "Libres / occupés" },
      // Ajoutez d'autres migrations si nécessaire
    };
    
    return migrations[oldTypeBien] || null;
  };
  
  /**
   * NOUVEAU: Déduire le Type de bien depuis l'Activité principale
   */
  const getTypeBienFromActivite = (activite) => {
    if (!activite) return null;
    
    const reverseMapping = {
      "Restaurant / Brasserie / Pizzeria / Snack / Fast-food": "Fonds de commerce",
      "Bar / Café / Pub / Lounge": "Fonds de commerce",
      "Boulangerie / Pâtisserie": "Fonds de commerce",
      "Épicerie / Supérette / Primeur": "Fonds de commerce",
      "Salon de coiffure / Institut de beauté / Spa": "Fonds de commerce",
      "Pharmacie / Parapharmacie": "Fonds de commerce",
      "Tabac / Presse / PMU": "Fonds de commerce",
      "Magasin de prêt-à-porter / Chaussures / Bijouterie": "Fonds de commerce",
      "Garage / Station-service / Centre auto": "Fonds de commerce",
      "Pressing / Blanchisserie": "Fonds de commerce",
      "Autres commerces (librairie, fleuriste, animalerie, etc.)": "Fonds de commerce",
      
      "Libres / occupés": "Bureaux",
      "En open-space / cloisonnés": "Bureaux",
      "Coworking / bureaux partagés": "Bureaux",
      "Plateaux divisibles": "Bureaux",
      
      "Entrepôts / dépôts / hangars": "Industriel",
      "Ateliers de production / artisanaux": "Industriel",
      "Sites logistiques / plateformes de distribution": "Industriel",
      
      "Hôtel / Résidence hôtelière / Chambre d'hôtes": "Murs",
      
      "Terrain industriel": "Terrain",
      "Terrain commercial": "Terrain",
      "Terrain artisanal": "Terrain"
    };
    
    return reverseMapping[activite] || null;
  };
  
  /**
   * NOUVEAU: Mapping Type de bien → Activités correspondantes
   */
  // ============================================
  // CALCULS AUTOMATIQUES : Prix & Honoraires
  // ============================================
  const calculatePriceFields = (updatedField, value, currentData) => {
    const data = { ...currentData };
    const honoraires_type = data.honoraires_calcul?.honoraires_type || "€";
    
    console.log(`💰 Calcul prix - Champ modifié: ${updatedField}, Valeur: ${value}, Type honoraires: ${honoraires_type}`);
    
    // Récupérer les valeurs actuelles
    let prix_vente = parseFloat(data.honoraires_calcul?.prix_vente) || 0;
    let net_vendeur = parseFloat(data.honoraires_calcul?.net_vendeur) || 0;
    let honoraires = parseFloat(data.honoraires_calcul?.honoraires) || 0;
    
    // Mettre à jour le champ modifié
    if (updatedField === 'prix_vente') prix_vente = parseFloat(value) || 0;
    if (updatedField === 'net_vendeur') net_vendeur = parseFloat(value) || 0;
    if (updatedField === 'honoraires') honoraires = parseFloat(value) || 0;
    if (updatedField === 'honoraires_type') {
      // Type changé, recalculer Net vendeur basé sur Prix et Honoraires
      if (prix_vente > 0 && honoraires > 0) {
        if (value === "€") {
          net_vendeur = prix_vente - honoraires;
        } else if (value === "%") {
          net_vendeur = prix_vente / (1 + (honoraires / 100));
        }
        
        data.honoraires_calcul = {
          ...data.honoraires_calcul,
          honoraires_type: value,
          net_vendeur: net_vendeur
        };
        
        console.log(`✅ Type changé vers ${value}, Net recalculé: ${net_vendeur}€`);
        return data;
      }
      
      data.honoraires_calcul = {
        ...data.honoraires_calcul,
        honoraires_type: value
      };
      return data;
    }
    
    // LOGIQUE DE CALCUL CORRIGÉE
    // Règle : Prix affiché est la référence principale
    
    if (updatedField === 'prix_vente') {
      // Prix affiché modifié
      if (honoraires > 0) {
        // Si honoraires existe, recalculer Net vendeur
        if (honoraires_type === "€") {
          net_vendeur = prix_vente - honoraires;
        } else if (honoraires_type === "%") {
          net_vendeur = prix_vente / (1 + (honoraires / 100));
        }
        console.log(`✅ Net vendeur calculé: ${net_vendeur}€ (Prix: ${prix_vente}€ - Honoraires: ${honoraires}${honoraires_type})`);
      } else if (net_vendeur > 0 && net_vendeur < prix_vente) {
        // Si Net existe mais pas Honoraires, calculer Honoraires
        honoraires = prix_vente - net_vendeur;
        console.log(`✅ Honoraires calculés: ${honoraires}€ (Prix: ${prix_vente}€ - Net: ${net_vendeur}€)`);
      }
    } 
    else if (updatedField === 'net_vendeur') {
      // Net vendeur modifié
      if (prix_vente > 0 && net_vendeur < prix_vente) {
        // Calculer Honoraires = Prix - Net (toujours en €)
        const montant_honoraires = prix_vente - net_vendeur;
        
        if (honoraires_type === "€") {
          honoraires = montant_honoraires;
        } else if (honoraires_type === "%") {
          // Calculer le % : Honoraires% = (Prix - Net) / Net * 100
          honoraires = (montant_honoraires / net_vendeur) * 100;
        }
        
        console.log(`✅ Honoraires calculés: ${honoraires}${honoraires_type} (Prix: ${prix_vente}€ - Net: ${net_vendeur}€)`);
      } else if (honoraires > 0) {
        // Si Honoraires existe, recalculer Prix
        if (honoraires_type === "€") {
          prix_vente = net_vendeur + honoraires;
        } else if (honoraires_type === "%") {
          prix_vente = net_vendeur * (1 + (honoraires / 100));
        }
        console.log(`✅ Prix calculé: ${prix_vente}€ (Net: ${net_vendeur}€ + Honoraires: ${honoraires}${honoraires_type})`);
      }
    } 
    else if (updatedField === 'honoraires') {
      // Honoraires modifié - PRIORITÉ au Prix affiché
      if (prix_vente > 0) {
        // Calculer Net = Prix - Honoraires (référence = Prix)
        if (honoraires_type === "€") {
          net_vendeur = prix_vente - honoraires;
        } else if (honoraires_type === "%") {
          net_vendeur = prix_vente / (1 + (honoraires / 100));
        }
        console.log(`✅ Net vendeur recalculé: ${net_vendeur}€ (Prix: ${prix_vente}€ - Honoraires: ${honoraires}${honoraires_type})`);
      } else if (net_vendeur > 0) {
        // Si pas de Prix mais Net existe, calculer Prix
        if (honoraires_type === "€") {
          prix_vente = net_vendeur + honoraires;
        } else if (honoraires_type === "%") {
          prix_vente = net_vendeur * (1 + (honoraires / 100));
        }
        console.log(`✅ Prix calculé: ${prix_vente}€ (Net: ${net_vendeur}€ + Honoraires: ${honoraires}${honoraires_type})`);
      }
    }
    
    data.honoraires_calcul = {
      ...data.honoraires_calcul,
      prix_vente: Math.round(prix_vente * 100) / 100,
      net_vendeur: Math.round(net_vendeur * 100) / 100,
      honoraires: Math.round(honoraires * 100) / 100
    };
    
    return data;
  };

  // CALCULS AUTOMATIQUES : Location - Loyer & Honoraires
  // ============================================
  const calculateRentalPriceFields = (updatedField, value, currentData) => {
    const data = { ...currentData };
    
    console.log(`🏠 Calcul location - Champ modifié: ${updatedField}, Valeur: ${value}`);
    
    // Récupérer les valeurs actuelles
    let loyer_mensuel = parseFloat(data.informations_bail?.loyer_mensuel) || 0;
    let loyer_annuel = parseFloat(data.informations_bail?.loyer_annuel) || 0;
    let charges = parseFloat(data.informations_bail?.charges) || 0;
    let honoraires_location_fixe = parseFloat(data.informations_bail?.honoraires_location_fixe) || 0;
    
    // Mettre à jour le champ modifié
    if (updatedField === 'loyer_mensuel') loyer_mensuel = parseFloat(value) || 0;
    if (updatedField === 'loyer_annuel') loyer_annuel = parseFloat(value) || 0;
    if (updatedField === 'charges') charges = parseFloat(value) || 0;
    if (updatedField === 'honoraires_location_fixe') honoraires_location_fixe = parseFloat(value) || 0;
    
    // LOGIQUE DE CALCUL
    if (updatedField === 'loyer_mensuel' && loyer_mensuel > 0) {
      // Loyer mensuel modifié -> calculer loyer annuel
      loyer_annuel = loyer_mensuel * 12;
      console.log(`✅ Loyer annuel calculé: ${loyer_annuel}€ (Mensuel: ${loyer_mensuel}€ x 12)`);
    } 
    else if (updatedField === 'loyer_annuel' && loyer_annuel > 0) {
      // Loyer annuel modifié -> calculer loyer mensuel
      loyer_mensuel = loyer_annuel / 12;
      console.log(`✅ Loyer mensuel calculé: ${loyer_mensuel}€ (Annuel: ${loyer_annuel}€ / 12)`);
    }
    
    data.informations_bail = {
      ...data.informations_bail,
      loyer_mensuel: Math.round(loyer_mensuel * 100) / 100,
      loyer_annuel: Math.round(loyer_annuel * 100) / 100,
      charges: Math.round(charges * 100) / 100,
      honoraires_location_fixe: Math.round(honoraires_location_fixe * 100) / 100
    };
    
    console.log(`📊 Récapitulatif location:`, {
      loyer_mensuel: data.informations_bail.loyer_mensuel,
      loyer_annuel: data.informations_bail.loyer_annuel,
      charges: data.informations_bail.charges,
      honoraires_location_fixe: data.informations_bail.honoraires_location_fixe
    });
    
    return data;
  };

  const getActivitiesByTypeBien = (typeBien) => {
    return ACTIVITY_TYPES_BY_BIEN_TYPE_MAPPING[typeBien] || [];
  };
  
  const getTypesBienByTransaction = (transactionType) => {
    return TYPES_BIEN_BY_TRANSACTION[transactionType] || [];
  };
  
  /**
   * NOUVEAU SYSTÈME: Mapping Activité → Type de Config
   */
  const getConfigTypeByActivity = (activityType) => {
    if (!activityType) return 'base';
    
    const activityMappings = {
      // Restauration
      'Restaurant / Brasserie / Pizzeria / Snack / Fast-food': 'restauration',
      'Bar / Café / Pub / Lounge': 'restauration',
      
      // Retail / Commerces
      'Boulangerie / Pâtisserie': 'retail',
      'Épicerie / Supérette / Primeur': 'retail',
      'Salon de coiffure / Institut de beauté / Spa': 'retail',
      'Pharmacie / Parapharmacie': 'retail',
      'Tabac / Presse / PMU': 'retail',
      'Magasin de prêt-à-porter / Chaussures / Bijouterie': 'retail',
      'Pressing / Blanchisserie': 'retail',
      'Garage / Station-service / Centre auto': 'retail',
      'Autres commerces (librairie, fleuriste, animalerie, etc.)': 'retail',
      
      // Bureaux
      'Libres / occupés': 'bureaux',
      'En open-space / cloisonnés': 'bureaux',
      'Coworking / bureaux partagés': 'bureaux',
      'Plateaux divisibles': 'bureaux',
      
      // Industriel
      'Entrepôts / dépôts / hangars': 'industriel',
      'Ateliers de production / artisanaux': 'industriel',
      'Sites logistiques / plateformes de distribution': 'industriel',
      
      // Licences de boissons
      'Licence IV (débit de boissons)': 'licences-boissons',
      'Licence III': 'licences-boissons',
      'Licence restaurant': 'licences-boissons',
      'Licence petite restauration': 'licences-boissons',
      'Autres autorisations spécifiques (casino, tabac, etc.)': 'licences-boissons',
      'Autres autorisations spécifiques': 'licences-boissons',
      
      // Hotel (pour futur)
      'Hôtel / Résidence hôtelière / Chambre d\'hôtes': 'hotel',
      
      // Terrains (pour futur)
      'Terrain industriel': 'terrains',
      'Terrain commercial': 'terrains',
      'Terrain artisanal': 'terrains',
      'Terrain constructible (zone d\'activité)': 'terrains'
    };
    
    return activityMappings[activityType] || 'base';
  };
  
  /**
   * NOUVEAU: Charger une config JSON par type
   */
  const loadConfigByType = (configType) => {
    console.log('📂 Loading config type:', configType);
    
    switch(configType) {
      case 'restauration':
        return restaurationConfigJson;
      case 'bureaux':
        return bureauxConfigJson;
      case 'retail':
        return retailConfigJson;
      case 'industriel':
        return industrielConfigJson;
      case 'licences-boissons':
        return licencesBoissonsConfigJson;
      case 'base':
      default:
        console.log('📂 Returning base config, sections:', Object.keys(baseConfigJson?.onglets?.general?.sections || {}));
        return baseConfigJson;
    }
  };
  
  /**
   * NOUVEAU: Fusionner config spécifique avec base (héritage)
   */
  const mergeConfigWithBase = (specificConfig) => {
    if (!specificConfig || !specificConfig.extends || specificConfig.extends !== 'base') {
      return specificConfig;
    }
    
    // Utiliser la config de base déjà chargée
    if (!baseConfigJson) {
      console.error('❌ base-config non chargé!');
      return specificConfig;
    }
    
    // Cloner la config de base
    const mergedConfig = JSON.parse(JSON.stringify(baseConfigJson));
    console.log('🔀 Merging specific config with base for type:', specificConfig.type);
    
    // Fusionner TOUS les onglets
    if (specificConfig.onglets) {
      Object.keys(specificConfig.onglets).forEach(ongletKey => {
        const specificOnglet = specificConfig.onglets[ongletKey];
        
        // Si l'onglet existe dans la config spécifique
        if (specificOnglet && specificOnglet.sections) {
          
          // Pour l'onglet GENERAL : fusionner les champs_supplementaires
          if (ongletKey === 'general') {
            Object.keys(specificOnglet.sections || {}).forEach(sectionKey => {
              const specificSection = specificOnglet.sections[sectionKey];
              const baseSection = mergedConfig.onglets.general?.sections?.[sectionKey];
              
              if (specificSection.champs_supplementaires && baseSection) {
                console.log(`  ✅ Fusion champs_supplementaires dans general.${sectionKey}`);
                // Insérer les champs supplémentaires aux bons endroits
                specificSection.champs_supplementaires.forEach(newChamp => {
                  const insertAfter = newChamp.insert_after;
                  if (insertAfter) {
                    const insertIndex = baseSection.champs.findIndex(c => c.id === insertAfter);
                    if (insertIndex >= 0) {
                      baseSection.champs.splice(insertIndex + 1, 0, newChamp);
                    } else {
                      // Si pas trouvé, ajouter à la fin
                      baseSection.champs.push(newChamp);
                    }
                  } else {
                    baseSection.champs.push(newChamp);
                  }
                });
              }
            });
          }
          // Pour les AUTRES onglets (composition, prestations, etc.) : remplacer complètement
          else if (Object.keys(specificOnglet.sections).length > 0) {
            console.log(`  ✅ Remplacement complet de l'onglet "${ongletKey}" par config spécifique`);
            mergedConfig.onglets[ongletKey] = specificOnglet;
          }
        }
      });
    }
    
    // Conserver les métadonnées de la config spécifique
    mergedConfig.type = specificConfig.type;
    mergedConfig.label = specificConfig.label;
    mergedConfig.description = specificConfig.description;
    mergedConfig.packs_recommandes = specificConfig.packs_recommandes || [];
    
    console.log('✅ Config fusionnée, onglets disponibles:', Object.keys(mergedConfig.onglets));
    
    return mergedConfig;
  };
  
  /**
   * Fonction principale - Charger config + packs
   */
  const getFieldsConfig = () => {
    // Déterminer l'activité pour charger la bonne config
    // IMPORTANT: Prioriser formData (valeurs en cours d'édition) sur currentProperty (valeurs sauvegardées)
    const activityType = formData?.bien_general?.activite_principale ||
                         currentProperty?.bien_general?.activite_principale || 
                         currentProperty?.category || 
                         formData?.type_config;
    
    console.log('🔍 Activité détectée (formData prioritaire):', activityType);
    console.log('🔍 formData.bien_general:', formData?.bien_general);
    console.log('🔍 currentProperty.bien_general:', currentProperty?.bien_general);
    
    // Déterminer le type de config à charger selon l'activité
    const configType = getConfigTypeByActivity(activityType);
    console.log('📄 Type de config à charger:', configType);
    
    // Charger la config spécifique
    const specificConfig = loadConfigByType(configType);
    
    // Fusionner avec base si nécessaire
    const baseConfig = mergeConfigWithBase(specificConfig);
    
    const transactionType = currentProperty?.transaction_type || formData?.transaction_type || 'Vente';
    const packsActifs = currentProperty?.packs_actifs || formData?.packs_actifs || {};
    
    console.log('🔧 getFieldsConfig - Config:', configType, 'Transaction:', transactionType, 'Packs:', packsActifs);
    
    if (baseConfig) {
      // Fusionner avec les packs actifs
      const fullConfig = mergeConfigWithPacks(baseConfig, packsActifs);
      
      return {
        fullConfig: fullConfig,
        baseConfig: baseConfig,
        packsActifs: packsActifs,
        transactionType: transactionType,
        configType: configType,
        // Pour compatibilité avec ancien code
        specificFields: {},
        general: {
          enseigne: true,
          superficie: true,
          type_bien: true,
          classement: true,
          murs_vente: true,
          identite_bailleur: true,
          secteur: true,
          ville_generale: true,
          zone_attractivite: true
        }
      };
    }
    
    // FALLBACK : Configuration par défaut pour ancien système
    console.warn('⚠️ Aucune config trouvée pour:', typeConfig, '- Utilisation du fallback');
    
    const type = currentProperty?.subcategory || currentProperty?.category || currentProperty?.bien_general?.type_bien || '';
    const category = currentProperty?.category || '';
    
    // Configuration par défaut (tous les champs visibles)
    const defaultConfig = {
      general: {
        enseigne: true,
        superficie: true,
        type_bien: true,
        classement: true,
        murs_vente: true,
        identite_bailleur: true,
        secteur: true,
        ville_generale: true,
        zone_attractivite: true
      },
      composition: {}
    };
    
    // Restaurant / Bar / Café (fallback)
    if (type.toLowerCase().includes('restaurant') || 
        type.toLowerCase().includes('bar') || 
        type.toLowerCase().includes('brasserie') ||
        type.toLowerCase().includes('café')) {
      return {
        ...defaultConfig,
        specificFields: {
          couverts: true,
          terrasse: true,
          cuisine_equipee: true,
          licence_type: true
        }
      };
    }
    
    // Licences (fallback)
    if (category.includes('Licences') || type.toLowerCase().includes('licence')) {
      return {
        ...defaultConfig,
        general: {
          ...defaultConfig.general,
          enseigne: false,
          superficie: false
        },
        specificFields: {
          type_licence: true,
          numero_licence: true,
          validite_licence: true,
          transferable: true,
          conditions_transfert: true
        }
      };
    }
    
    // Bureaux
    if (category.includes('Bureaux') || type.toLowerCase().includes('bureau')) {
      return {
        ...defaultConfig,
        general: {
          ...defaultConfig.general,
          enseigne: false,
          superficie: true,
          type_bien: true,
          classement: false,
          murs_vente: true,
          identite_bailleur: true
        },
        specificFields: {
          postes_travail: true,
          salles_reunion: true,
          parking_inclus: true,
          espaces_communs: true,
          etage: true
        }
      };
    }
    
    // Par défaut, tout afficher
    return defaultConfig;
  };

  // Dashboard states
  const [chartType, setChartType] = useState('bar');
  const [showMoreProperties, setShowMoreProperties] = useState(5);
  const [showMoreContacts, setShowMoreContacts] = useState(5);
  const [showMoreModified, setShowMoreModified] = useState(5);
  
  // News, agenda, tasks and emails states
  const [news, setNews] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [agendaEvents, setAgendaEvents] = useState([]);
  const [recentEmails, setRecentEmails] = useState([]);

  const generatePropertyPDF = async (property, templateType = 'complete') => {
    try {
      setGeneratingPDF(true);
      const response = await fetch(`${API}/properties/${property.id}/pdf?template_type=${templateType}`);
      
      if (!response.ok) {
        throw new Error('Erreur lors de la génération du PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${property.bien_general?.type_bien || 'Bien'}_${property.id.slice(0,8)}_${templateType}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('PDF généré avec succès !');
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      alert('Erreur lors de la génération du PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const generateBulkPDF = async (templateType = 'summary') => {
    try {
      if (selectedProperties.length === 0) {
        alert('Veuillez sélectionner au moins un bien');
        return;
      }
      
      setGeneratingPDF(true);
      const response = await fetch(`${API}/properties/bulk-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          property_ids: selectedProperties,
          template_type: templateType
        })
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la génération du PDF groupé');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Biens_groupe_${selectedProperties.length}_${templateType}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert(`PDF groupé généré avec succès pour ${selectedProperties.length} bien(s) !`);
      setSelectedProperties([]); // Reset selection
    } catch (error) {
      console.error('Erreur génération PDF groupé:', error);
      alert('Erreur lors de la génération du PDF groupé');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const renderDocuments = () => {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
        {/* Background decoration moderne */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-purple-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-emerald-400/10 to-teal-600/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 container mx-auto px-4 py-6">
          {/* Header moderne avec glassmorphism */}
          <div className="backdrop-blur-xl bg-white/80 border border-white/20 rounded-2xl shadow-2xl shadow-blue-500/10 mb-8 overflow-hidden">
            {/* Hero section */}
            <div className="relative bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 text-white overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
              <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <pattern id="docs-grid" width="10" height="10" patternUnits="userSpaceOnUse">
                    <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#docs-grid)"/>
              </svg>
              
              <div className="relative z-10 p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="relative">
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                      <i className="bi bi-folder text-3xl text-blue-300"></i>
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full animate-pulse"></div>
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                      Documents & Générateurs
                    </h1>
                    <p className="text-blue-200/80 font-medium">Gestion et création automatique de documents</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Toolbar */}
            <div className="bg-gradient-to-r from-gray-50/80 to-white/80 backdrop-blur-sm border-t border-gray-200/50 p-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => showPropertiesView()}
                    className="group inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gray-500/25 active:scale-95"
                  >
                    <i className="bi bi-arrow-left mr-2 group-hover:-translate-x-0.5 transition-transform duration-200"></i>
                    <span>Retour</span>
                  </button>
                  
                  <button 
                    onClick={() => setActiveDocumentsTab('generer')}
                    className="group inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-green-500/25 active:scale-95"
                  >
                    <i className="bi bi-file-earmark-plus mr-2 group-hover:scale-110 transition-transform duration-200"></i>
                    <span>Générer document</span>
                  </button>
                  
                  <button 
                    onClick={() => setActiveDocumentsTab('modeles')}
                    className="group inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25 active:scale-95"
                  >
                    <i className="bi bi-file-earmark-text mr-2 group-hover:rotate-12 transition-transform duration-200"></i>
                    <span>Modèles</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs navigation */}
          <div className="mb-8">
            <nav className="flex space-x-1 bg-white/60 backdrop-blur-sm p-1 rounded-xl border border-white/20 shadow-lg">
              {[
                { id: 'mes-documents', label: 'Mes Documents', icon: 'bi-folder' },
                { id: 'generer', label: 'Générer Nouveau', icon: 'bi-file-earmark-plus' },
                { id: 'modeles', label: 'Modèles', icon: 'bi-file-earmark-text' },
                { id: 'archives', label: 'Archives', icon: 'bi-archive' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveDocumentsTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                    activeDocumentsTab === tab.id
                      ? 'bg-white text-blue-600 shadow-lg shadow-blue-500/25'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-white/50'
                  }`}
                >
                  <i className={`${tab.icon} text-lg`}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content based on active tab */}
          <div className="space-y-6">
            {activeDocumentsTab === 'mes-documents' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                <div className="text-center py-12 text-gray-500">
                  <i className="bi bi-folder text-6xl mb-4 block text-gray-300"></i>
                  <h4 className="text-xl font-medium mb-2">Vos documents</h4>
                  <p className="text-gray-400 mb-6">Organisez et consultez tous vos documents immobiliers</p>
                  <button 
                    onClick={() => setActiveDocumentsTab('generer')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
                  >
                    Générer votre premier document
                  </button>
                </div>
              </div>
            )}

            {activeDocumentsTab === 'generer' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Générateurs de documents commerciaux */}
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i className="bi bi-briefcase mr-2 text-green-500"></i>
                    Documents Commerciaux
                  </h3>
                  <div className="space-y-3">
                    <button 
                      onClick={() => openDocumentGenerator('rapport_visite')}
                      className="w-full text-left p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors border border-green-200"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="bi bi-camera text-green-600 text-xl"></i>
                        <div>
                          <h4 className="font-medium text-green-900">Rapport de Visite</h4>
                          <p className="text-sm text-green-700">Avec photos et observations</p>
                        </div>
                      </div>
                    </button>
                    
                    <button 
                      onClick={() => openDocumentGenerator('annonce')}
                      className="w-full text-left p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="bi bi-megaphone text-blue-600 text-xl"></i>
                        <div>
                          <h4 className="font-medium text-blue-900">Annonce Immobilière</h4>
                          <p className="text-sm text-blue-700">Description automatique</p>
                        </div>
                      </div>
                    </button>
                    
                    <button 
                      onClick={() => openDocumentGenerator('dossier_presentation')}
                      className="w-full text-left p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors border border-purple-200"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="bi bi-file-earmark-slides text-purple-600 text-xl"></i>
                        <div>
                          <h4 className="font-medium text-purple-900">Dossier Présentation</h4>
                          <p className="text-sm text-purple-700">Pour propriétaires/acquéreurs</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Générateurs de documents légaux */}
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i className="bi bi-shield-check mr-2 text-orange-500"></i>
                    Documents Légaux
                  </h3>
                  <div className="space-y-3">
                    <button 
                      onClick={() => openDocumentGenerator('mandat_vente')}
                      className="w-full text-left p-4 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors border border-orange-200"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="bi bi-file-earmark-medical text-orange-600 text-xl"></i>
                        <div>
                          <h4 className="font-medium text-orange-900">Mandat de Vente</h4>
                          <p className="text-sm text-orange-700">Pré-rempli automatiquement</p>
                        </div>
                      </div>
                    </button>
                    
                    <button 
                      onClick={() => openDocumentGenerator('contrat_location')}
                      className="w-full text-left p-4 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="bi bi-house-door text-red-600 text-xl"></i>
                        <div>
                          <h4 className="font-medium text-red-900">Contrat de Location</h4>
                          <p className="text-sm text-red-700">Clauses personnalisées</p>
                        </div>
                      </div>
                    </button>
                    
                    <button 
                      onClick={() => openDocumentGenerator('etat_lieux')}
                      className="w-full text-left p-4 bg-yellow-50 hover:bg-yellow-100 rounded-lg transition-colors border border-yellow-200"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="bi bi-clipboard-check text-yellow-600 text-xl"></i>
                        <div>
                          <h4 className="font-medium text-yellow-900">État des Lieux</h4>
                          <p className="text-sm text-yellow-700">Entrée et sortie</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Générateurs de documents marketing */}
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i className="bi bi-palette mr-2 text-pink-500"></i>
                    Documents Marketing
                  </h3>
                  <div className="space-y-3">
                    <button 
                      onClick={() => openDocumentGenerator('flyer')}
                      className="w-full text-left p-4 bg-pink-50 hover:bg-pink-100 rounded-lg transition-colors border border-pink-200"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="bi bi-file-earmark-image text-pink-600 text-xl"></i>
                        <div>
                          <h4 className="font-medium text-pink-900">Flyer Propriété</h4>
                          <p className="text-sm text-pink-700">Avec visuels automatiques</p>
                        </div>
                      </div>
                    </button>
                    
                    <button 
                      onClick={() => openDocumentGenerator('brochure')}
                      className="w-full text-left p-4 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors border border-teal-200"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="bi bi-speedometer2 text-teal-600 text-xl"></i>
                        <div>
                          <h4 className="font-medium text-teal-900">Brochure Marketing</h4>
                          <p className="text-sm text-teal-700">Présentation professionnelle</p>
                        </div>
                      </div>
                    </button>
                    
                    <button 
                      onClick={() => openDocumentGenerator('campagne_emailing')}
                      className="w-full text-left p-4 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors border border-indigo-200"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="bi bi-graph-up text-indigo-600 text-xl"></i>
                        <div>
                          <h4 className="font-medium text-indigo-900">Campagne Emailing</h4>
                          <p className="text-sm text-indigo-700">Messages personnalisés</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeDocumentsTab === 'modeles' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                <div className="text-center py-12 text-gray-500">
                  <i className="bi bi-file-earmark-text text-6xl mb-4 block text-gray-300"></i>
                  <h4 className="text-xl font-medium mb-2">Modèles disponibles</h4>
                  <p className="text-gray-400 mb-6">Templates pré-conçus pour vos documents récurrents</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
                    <div className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                      <i className="bi bi-file-earmark-medical text-2xl text-blue-500 mb-2"></i>
                      <h5 className="font-medium">Mandat Standard</h5>
                      <p className="text-sm text-gray-500">Template mandat de vente</p>
                    </div>
                    <div className="p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-colors">
                      <i className="bi bi-camera text-2xl text-green-500 mb-2"></i>
                      <h5 className="font-medium">Rapport Visite</h5>
                      <p className="text-sm text-gray-500">Template rapport standardisé</p>
                    </div>
                    <div className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
                      <i className="bi bi-file-earmark-slides text-2xl text-purple-500 mb-2"></i>
                      <h5 className="font-medium">Présentation Bien</h5>
                      <p className="text-sm text-gray-500">Template présentation client</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeDocumentsTab === 'archives' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                <div className="text-center py-12 text-gray-500">
                  <i className="bi bi-archive text-6xl mb-4 block text-gray-300"></i>
                  <h4 className="text-xl font-medium mb-2">Archives</h4>
                  <p className="text-gray-400">Documents archivés et anciens mandats</p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Modal de génération de documents */}
        {showDocumentModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold">Générer un Document</h3>
                    <p className="text-blue-100 mt-1">
                      {selectedDocumentType && DOCUMENT_TEMPLATES[selectedDocumentType]?.name}
                    </p>
                  </div>
                  <button 
                    onClick={closeDocumentModal}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    <i className="bi bi-x-lg text-2xl"></i>
                  </button>
                </div>
              </div>
              
              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {!generatedDocument ? (
                  <>
                    {/* Selection du bien (optionnel) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bien concerné (optionnel)
                      </label>
                      <select
                        value={documentGenerationData.property_id}
                        onChange={(e) => setDocumentGenerationData({
                          ...documentGenerationData,
                          property_id: e.target.value
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Sélectionnez un bien...</option>
                        {properties.map((property) => (
                          <option key={property.id} value={property.id}>
                            {property.bien_general?.type_bien || 'Sans type'} - 
                            {property.enseigne?.enseigne || property.id.substring(0, 8)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Instructions personnalisées */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Instructions supplémentaires (optionnel)
                      </label>
                      <textarea
                        value={documentGenerationData.custom_instructions}
                        onChange={(e) => setDocumentGenerationData({
                          ...documentGenerationData,
                          custom_instructions: e.target.value
                        })}
                        rows="4"
                        placeholder="Ajoutez des détails spécifiques, points à mettre en avant, style souhaité..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>
                    
                    {/* Bouton de génération */}
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={closeDocumentModal}
                        className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={generateDocument}
                        disabled={generatingDocument}
                        className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {generatingDocument ? (
                          <>
                            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                            <span>Génération en cours...</span>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-magic"></i>
                            <span>Générer</span>
                          </>
                        )}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Document généré */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-2 text-green-700">
                        <i className="bi bi-check-circle-fill text-xl"></i>
                        <span className="font-medium">Document généré avec succès!</span>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contenu du document:
                      </label>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">
                          {generatedDocument.content}
                        </pre>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(generatedDocument.content);
                          alert('Document copié dans le presse-papiers!');
                        }}
                        className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
                      >
                        <i className="bi bi-clipboard"></i>
                        <span>Copier</span>
                      </button>
                      <button
                        onClick={closeDocumentModal}
                        className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
                      >
                        Fermer
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Définition des templates de documents pour le modal
  const DOCUMENT_TEMPLATES = {
    'rapport_visite': { name: 'Rapport de Visite' },
    'annonce': { name: 'Annonce Immobilière' },
    'dossier_presentation': { name: 'Dossier de Présentation' },
    'mandat_vente': { name: 'Mandat de Vente' },
    'contrat_location': { name: 'Contrat de Location' },
    'etat_lieux': { name: 'État des Lieux' },
    'flyer': { name: 'Flyer Propriété' },
    'brochure': { name: 'Brochure Marketing' },
    'campagne_emailing': { name: 'Campagne Emailing' }
  };

  const renderMarketingDashboard = () => {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
        {/* Background decoration moderne */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-purple-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-emerald-400/10 to-teal-600/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 container mx-auto px-4 py-6">
          {/* Header moderne avec glassmorphism */}
          <div className="backdrop-blur-xl bg-white/80 border border-white/20 rounded-2xl shadow-2xl shadow-blue-500/10 mb-8 overflow-hidden">
            {/* Hero section avec gradient et effet glass */}
            <div className="relative bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 text-white overflow-hidden">
              {/* Pattern decoration */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
              <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                    <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#grid)"/>
              </svg>
              
              <div className="relative z-10 p-8">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between space-y-6 lg:space-y-0">
                  <div className="flex-1">
                    {/* Badge status avec animation */}
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="relative">
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                          <i className="bi bi-megaphone-fill text-3xl text-blue-300"></i>
                        </div>
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full animate-pulse"></div>
                      </div>
                      <div>
                        <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                          Marketing Dashboard
                        </h1>
                        <p className="text-blue-200/80 font-medium">Gestion des campagnes et réseaux sociaux</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Toolbar moderne avec micro-interactions */}
            <div className="bg-gradient-to-r from-gray-50/80 to-white/80 backdrop-blur-sm border-t border-gray-200/50 p-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                {/* Actions principales avec design system */}
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => showPropertiesView()}
                    className="group inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gray-500/25 active:scale-95"
                  >
                    <i className="bi bi-arrow-left mr-2 group-hover:-translate-x-0.5 transition-transform duration-200"></i>
                    <span>Retour</span>
                  </button>
                  
                  <button
                    onClick={() => setShowCampaignModal(true)}
                    className="group inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25 active:scale-95"
                  >
                    <i className="bi bi-plus-circle mr-2 group-hover:rotate-90 transition-transform duration-200"></i>
                    <span>Nouvelle campagne</span>
                  </button>
                  
                  <button 
                    onClick={() => setShowPostModal(true)}
                    className="group inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-emerald-500/25 active:scale-95"
                  >
                    <i className="bi bi-share mr-2 group-hover:scale-110 transition-transform duration-200"></i>
                    <span>Nouveau post</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs navigation */}
          <div className="mb-8">
            <nav className="flex space-x-1 bg-white/60 backdrop-blur-sm p-1 rounded-xl border border-white/20 shadow-lg">
              {[
                { id: 'dashboard', label: 'Tableau de bord', icon: 'bi-speedometer2' },
                { id: 'campaigns', label: 'Campagnes', icon: 'bi-megaphone' },
                { id: 'social', label: 'Réseaux sociaux', icon: 'bi-share' },
                { id: 'analytics', label: 'Analytics', icon: 'bi-graph-up' },
                { id: 'chatbot', label: 'Chatbot', icon: 'bi-robot' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveMarketingTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                    activeMarketingTab === tab.id
                      ? 'bg-white text-blue-600 shadow-lg shadow-blue-500/25'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-white/50'
                  }`}
                >
                  <i className={`${tab.icon} text-lg`}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content based on active tab */}
          <div className="space-y-6">
            {activeMarketingTab === 'dashboard' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Summary Cards */}
                <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Campagnes actives</p>
                        <p className="text-2xl font-bold text-gray-900">{campaigns.length}</p>
                      </div>
                      <i className="bi bi-megaphone text-2xl text-blue-500"></i>
                    </div>
                  </div>
                  
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Posts publiés</p>
                        <p className="text-2xl font-bold text-gray-900">{socialPosts.filter(p => p.status === 'PUBLISHED').length}</p>
                      </div>
                      <i className="bi bi-share text-2xl text-green-500"></i>
                    </div>
                  </div>
                  
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Conversations</p>
                        <p className="text-2xl font-bold text-gray-900">{chatbotConversations.filter(c => c.is_active).length}</p>
                      </div>
                      <i className="bi bi-chat-dots text-2xl text-purple-500"></i>
                    </div>
                  </div>
                  
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Leads générés</p>
                        <p className="text-2xl font-bold text-gray-900">0</p>
                      </div>
                      <i className="bi bi-person-plus text-2xl text-orange-500"></i>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i className="bi bi-clock-history mr-2 text-blue-500"></i>
                    Activité récente
                  </h3>
                  <div className="space-y-3">
                    {socialPosts.slice(0, 5).map((post) => (
                      <div key={post.id} className="flex items-start space-x-3 p-3 bg-gray-50/80 rounded-lg">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          post.status === 'PUBLISHED' ? 'bg-green-500' : 
                          post.status === 'SCHEDULED' ? 'bg-blue-500' : 'bg-gray-400'
                        }`}></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{post.title}</p>
                          <p className="text-xs text-gray-500">{post.platform} • {post.status}</p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(post.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    ))}
                    {socialPosts.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <i className="bi bi-inbox text-4xl mb-2 block text-gray-300"></i>
                        <p>Aucune activité récente</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i className="bi bi-lightning mr-2 text-yellow-500"></i>
                    Actions rapides
                  </h3>
                  <div className="space-y-3">
                    <button 
                      onClick={() => setActiveMarketingTab('campaigns')}
                      className="w-full text-left p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="bi bi-plus-circle text-blue-600"></i>
                        <span className="font-medium text-blue-900">Nouvelle campagne</span>
                      </div>
                    </button>
                    <button 
                      onClick={() => setActiveMarketingTab('social')}
                      className="w-full text-left p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="bi bi-share text-green-600"></i>
                        <span className="font-medium text-green-900">Publier un post</span>
                      </div>
                    </button>
                    <button 
                      onClick={() => setActiveMarketingTab('analytics')}
                      className="w-full text-left p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <i className="bi bi-graph-up text-purple-600"></i>
                        <span className="font-medium text-purple-900">Voir les stats</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeMarketingTab === 'campaigns' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <i className="bi bi-megaphone mr-2 text-blue-500"></i>
                    Campagnes marketing
                  </h3>
                  <button 
                    onClick={() => setShowCampaignModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <i className="bi bi-plus mr-2"></i>
                    Nouvelle campagne
                  </button>
                </div>

                {campaigns.length > 0 ? (
                  <div className="grid gap-4">
                    {campaigns.map((campaign) => (
                      <div key={campaign.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="font-semibold text-gray-900">{campaign.name}</h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                campaign.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {campaign.is_active ? 'Active' : 'Inactive'}
                              </span>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                {campaign.type}
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm mb-3">{campaign.description}</p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>Budget: {campaign.budget}€</span>
                              <span>Créé le: {new Date(campaign.created_at).toLocaleDateString('fr-FR')}</span>
                              {campaign.geographic_targeting && campaign.geographic_targeting.length > 0 && (
                                <span>Zones: {campaign.geographic_targeting.join(', ')}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className="p-2 text-gray-400 hover:text-red-600 transition-colors">
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <i className="bi bi-megaphone text-6xl mb-4 block text-gray-300"></i>
                    <h4 className="text-xl font-medium mb-2">Aucune campagne</h4>
                    <p className="text-gray-400 mb-6">Commencez par créer votre première campagne marketing</p>
                    <button 
                      onClick={() => setShowCampaignModal(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
                    >
                      Créer une campagne
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeMarketingTab === 'social' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <i className="bi bi-share mr-2 text-green-500"></i>
                    Publications réseaux sociaux
                  </h3>
                  <button 
                    onClick={() => setShowPostModal(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <i className="bi bi-plus mr-2"></i>
                    Nouveau post
                  </button>
                </div>

                {socialPosts.length > 0 ? (
                  <div className="grid gap-4">
                    {socialPosts.map((post) => (
                      <div key={post.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="font-semibold text-gray-900">{post.title}</h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                post.status === 'Published' ? 'bg-green-100 text-green-800' :
                                post.status === 'Scheduled' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {post.status === 'Published' ? 'Publié' :
                                 post.status === 'Scheduled' ? 'Programmé' :
                                 'Brouillon'}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                post.platform === 'Facebook' ? 'bg-blue-100 text-blue-800' :
                                post.platform === 'Instagram' ? 'bg-pink-100 text-pink-800' :
                                post.platform === 'LinkedIn' ? 'bg-indigo-100 text-indigo-800' :
                                post.platform === 'Twitter' ? 'bg-cyan-100 text-cyan-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {post.platform}
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm mb-3 line-clamp-2">{post.description}</p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500 mb-2">
                              <span>Créé le: {new Date(post.created_at).toLocaleDateString('fr-FR')}</span>
                              {post.hashtags && post.hashtags.length > 0 && (
                                <span>Hashtags: {post.hashtags.slice(0, 3).join(' ')}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className="p-2 text-gray-400 hover:text-red-600 transition-colors">
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <i className="bi bi-share text-6xl mb-4 block text-gray-300"></i>
                    <h4 className="text-xl font-medium mb-2">Aucun post</h4>
                    <p className="text-gray-400 mb-6">Commencez par publier votre premier post sur les réseaux sociaux</p>
                    <button 
                      onClick={() => setShowPostModal(true)}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors"
                    >
                      Créer un post
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeMarketingTab === 'analytics' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <i className="bi bi-graph-up mr-2 text-purple-500"></i>
                    Analytics et performances
                  </h3>
                </div>
                <div className="text-center py-12 text-gray-500">
                  <i className="bi bi-graph-up text-6xl mb-4 block text-gray-300"></i>
                  <h4 className="text-xl font-medium mb-2">Données bientôt disponibles</h4>
                  <p className="text-gray-400">Les statistiques apparaîtront ici une fois vos campagnes lancées</p>
                </div>
              </div>
            )}

            {activeMarketingTab === 'chatbot' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <i className="bi bi-robot mr-2 text-orange-500"></i>
                    Chatbot et conversations
                  </h3>
                  <button className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors">
                    <i className="bi bi-gear mr-2"></i>
                    Configuration
                  </button>
                </div>
                <div className="text-center py-12 text-gray-500">
                  <i className="bi bi-robot text-6xl mb-4 block text-gray-300"></i>
                  <h4 className="text-xl font-medium mb-2">Chatbot en préparation</h4>
                  <p className="text-gray-400 mb-6">Configuration et intégration du chatbot intelligent en cours</p>
                  <button className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg transition-colors">
                    Configurer le chatbot
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Campaign Creation Modal - INSIDE Marketing Dashboard */}
        {showCampaignModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" style={{zIndex: 10000}}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                    <i className="bi bi-megaphone mr-2 text-blue-500"></i>
                    Nouvelle Campagne Marketing
                  </h3>
                  <button
                    onClick={() => setShowCampaignModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <i className="bi bi-x-lg text-xl"></i>
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom de la campagne *
                    </label>
                    <input
                      type="text"
                      value={newCampaign.name}
                      onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ex: Promotion Restaurant Monaco"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de campagne
                    </label>
                    <select
                      value={newCampaign.type}
                      onChange={(e) => setNewCampaign({...newCampaign, type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Property Listing">Annonce immobilière</option>
                      <option value="Brand Awareness">Notoriété de marque</option>
                      <option value="Lead Generation">Génération de leads</option>
                      <option value="Event Promotion">Promotion d'événement</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newCampaign.description}
                    onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Décrivez l'objectif et le contenu de votre campagne..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget (€)
                    </label>
                    <input
                      type="number"
                      value={newCampaign.budget}
                      onChange={(e) => setNewCampaign({...newCampaign, budget: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ex: 5000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date de début
                    </label>
                    <input
                      type="date"
                      value={newCampaign.start_date ? new Date(newCampaign.start_date).toISOString().split('T')[0] : ''}
                      onChange={(e) => setNewCampaign({...newCampaign, start_date: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ciblage géographique
                  </label>
                  <input
                    type="text"
                    value={newCampaign.geographic_targeting.join(', ')}
                    onChange={(e) => setNewCampaign({...newCampaign, geographic_targeting: e.target.value.split(',').map(s => s.trim()).filter(s => s)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex: Monaco, Nice, Cannes (séparez par des virgules)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tranche d'âge cible
                  </label>
                  <input
                    type="text"
                    value={newCampaign.target_audience.age_range}
                    onChange={(e) => setNewCampaign({...newCampaign, target_audience: {...newCampaign.target_audience, age_range: e.target.value}})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex: 25-45"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCampaignModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={createCampaign}
                  disabled={!newCampaign.name.trim()}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors"
                >
                  Créer la campagne
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Social Post Creation Modal - INSIDE Marketing Dashboard */}
        {showPostModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" style={{zIndex: 10000}}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                    <i className="bi bi-share mr-2 text-green-500"></i>
                    Nouveau Post Réseaux Sociaux
                  </h3>
                  <button
                    onClick={() => setShowPostModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <i className="bi bi-x-lg text-xl"></i>
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plateforme
                    </label>
                    <select
                      value={newPost.platform}
                      onChange={(e) => setNewPost({...newPost, platform: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Facebook">Facebook</option>
                      <option value="Instagram">Instagram</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Twitter">Twitter</option>
                      <option value="TikTok">TikTok</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Statut
                    </label>
                    <select
                      value={newPost.status}
                      onChange={(e) => setNewPost({...newPost, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Draft">Brouillon</option>
                      <option value="Scheduled">Programmé</option>
                      <option value="Published">Publié</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre du post *
                  </label>
                  <input
                    type="text"
                    value={newPost.title}
                    onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex: Nouveau restaurant disponible à Monaco"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contenu du post
                  </label>
                  <textarea
                    value={newPost.description}
                    onChange={(e) => setNewPost({...newPost, description: e.target.value})}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Rédigez votre post..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hashtags
                  </label>
                  <input
                    type="text"
                    value={newPost.hashtags.join(' ')}
                    onChange={(e) => setNewPost({...newPost, hashtags: e.target.value.split(/\s+/).filter(tag => tag.trim() !== '')})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex: #restaurant #monaco #immobilier #pizzeria"
                  />
                  <p className="text-xs text-gray-500 mt-1">Séparez les hashtags par des espaces</p>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowPostModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={createPost}
                  disabled={!newPost.title.trim() || !newPost.description.trim()}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors"
                >
                  Créer le post
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderOutilsDashboard = () => {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="container mx-auto px-4 py-6">
          {/* Header sobre — design system Komerces (plus de gradient vert, plus de glassmorphism) */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm mb-6 overflow-hidden" data-testid="outils-hero">
            <div className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                  <i className="bi bi-calculator text-2xl"></i>
                </div>
                <div className="min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Simulateurs
                  </h1>
                  <p className="text-sm text-slate-500">Calculette de prêt et de rentabilité — projections indicatives, hors fiscalité.</p>
                </div>
              </div>
            </div>

            {/* Toolbar sobre — action primaire bleue, retour neutre. Bouton « Statistiques » retiré (iter.72). */}
            <div className="bg-slate-50 border-t border-slate-200 px-6 py-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => showPropertiesView()}
                  data-testid="outils-btn-back"
                  className="inline-flex items-center h-10 px-4 border border-slate-200 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/40 text-slate-700 rounded-lg text-sm font-semibold transition"
                >
                  <i className="bi bi-arrow-left mr-2"></i>
                  Retour
                </button>
                <button
                  onClick={() => setActiveOutilsTab('calculettes')}
                  data-testid="outils-btn-calculettes"
                  className="inline-flex items-center h-10 px-4 bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 text-white rounded-lg text-sm font-semibold shadow-sm transition"
                >
                  <i className="bi bi-calculator mr-2"></i>
                  Ouvrir les calculettes
                </button>
              </div>
            </div>
          </div>

          {/* Tabs navigation — pills sobres, primary blue */}
          <div className="mb-6">
            <nav className="inline-flex bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
              {[
                { id: 'dashboard', label: 'Tableau de bord', icon: 'bi-speedometer2' },
                { id: 'calculettes', label: 'Calculettes', icon: 'bi-calculator' },
                // Onglets « Statistiques » et « Rapports » retirés (démo-ready).
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveOutilsTab(tab.id)}
                  data-testid={`outils-tab-${tab.id}`}
                  className={`flex items-center gap-2 h-9 px-4 rounded-lg font-medium text-sm transition ${
                    activeOutilsTab === tab.id
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content based on active tab */}
          <div className="space-y-6">
            {activeOutilsTab === 'dashboard' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6" data-testid="outils-dashboard">
                {/* Carte principale : accès direct aux 2 calculettes principales.
                    Chaque bouton ouvre une VUE DÉDIÉE à la calculette via
                    `?calc=<id>` (iter.75), pas la grille des 7 outils. */}
                <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                  <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    <i className="bi bi-calculator text-blue-600"></i>
                    Simulateurs disponibles
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/outils/calculettes?calc=pret')}
                      data-testid="outils-card-pret"
                      className="text-left p-4 bg-slate-50 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/40 rounded-xl border border-slate-200 transition"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                          <i className="bi bi-calculator text-lg"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">Calculette de prêt</h4>
                          <p className="text-xs text-slate-500 mt-0.5">Mensualité, coût total et capacité d&apos;emprunt.</p>
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => navigate('/outils/calculettes?calc=rentabilite')}
                      data-testid="outils-card-rentabilite"
                      className="text-left p-4 bg-slate-50 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/40 rounded-xl border border-slate-200 transition"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                          <i className="bi bi-percent text-lg"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">Calculette de rentabilité</h4>
                          <p className="text-xs text-slate-500 mt-0.5">Rendement brut, net et cash-flow locatif.</p>
                        </div>
                      </div>
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-4">
                    <i className="bi bi-info-circle mr-1"></i>
                    Les simulations sont indicatives : elles ne remplacent pas une étude bancaire ou un audit fiscal.
                  </p>
                </div>

                {/* Actions rapides — Dropdown listant les 7 calculettes (iter.75).
                    Chaque item ouvre sa calculette dédiée via `?calc=<id>`. */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                  <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    <i className="bi bi-lightning-charge text-amber-500"></i>
                    Actions rapides
                  </h3>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        data-testid="outils-action-new"
                        className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 text-white rounded-lg text-sm font-semibold shadow-sm transition"
                      >
                        <i className="bi bi-plus-lg"></i>
                        Nouvelle simulation
                        <i className="bi bi-chevron-down text-xs ml-1"></i>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-80 max-w-[90vw]"
                      data-testid="outils-action-new-menu"
                    >
                      <DropdownMenuLabel className="text-[11px] tracking-wider uppercase text-slate-500">
                        Choisir une simulation
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {CALCULETTES_MENU.map((c) => (
                        <DropdownMenuItem
                          key={c.id}
                          data-testid={`outils-action-item-${c.id}`}
                          onSelect={() => navigate(`/outils/calculettes?calc=${c.id}`)}
                          className="flex items-start gap-3 px-2 py-2 cursor-pointer focus:bg-blue-50"
                        >
                          <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className={`bi ${c.icon} text-base`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-slate-900 leading-tight">{c.label}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{c.desc}</div>
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <p className="text-[11px] text-slate-500 mt-3 leading-snug">
                    7 outils disponibles : prêt, rentabilité locative, plus-value, droits de mutation, surface pondérée, valorisation d&apos;un fonds, valeur des murs par capitalisation.
                  </p>
                </div>
              </div>
            )}

            {/* Tab Calculettes */}
            {activeOutilsTab === 'calculettes' && (
              <div
                className={focusedCalc ? "max-w-3xl mx-auto" : "grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6"}
                data-testid="outils-calculettes"
                data-focused-calc={focusedCalc || ''}
              >
                {focusedCalc && (
                  /* Bandeau retour vers la vue « toutes les calculettes »
                     (visible uniquement en mode ciblé, iter.75). */
                  <div className="mb-4 flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="outils-calc-back-all"
                      onClick={() => navigate('/outils/calculettes')}
                      className="inline-flex items-center gap-2 h-9 px-3 border border-slate-200 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/40 text-slate-700 rounded-lg text-sm font-medium transition"
                    >
                      <i className="bi bi-grid" />
                      Voir toutes les calculettes
                    </button>
                  </div>
                )}
                {/* Calculette Prêt Immobilier */}
                {(!focusedCalc || focusedCalc === 'pret') && (
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="calc-pret">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <i className="bi bi-calculator text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Calculette de prêt</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">Projection de la mensualité et du coût total d'un prêt amortissable.</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Prix d'achat FAI (€)</label>
                        <input
                          type="number"
                          value={calculetteData.pret.montant}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              pret: { ...calculetteData.pret, montant: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (e.target.value && calculetteData.pret.duree && calculetteData.pret.taux) {
                              const result = calculatePret(
                                e.target.value,
                                calculetteData.pret.duree,
                                calculetteData.pret.taux,
                                calculetteData.pret.apport
                              );
                              setCalculResults(prev => ({ ...prev, pret: result }));
                            }
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="300 000"
                          data-testid="calc-pret-montant"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Prix FAI (Frais d'Agence Inclus). Le montant emprunté sera : Prix − Apport.</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Durée (années)</label>
                        <input
                          type="number"
                          value={calculetteData.pret.duree}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              pret: { ...calculetteData.pret, duree: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (calculetteData.pret.montant && e.target.value && calculetteData.pret.taux) {
                              const result = calculatePret(
                                calculetteData.pret.montant,
                                e.target.value,
                                calculetteData.pret.taux,
                                calculetteData.pret.apport
                              );
                              setCalculResults(prev => ({ ...prev, pret: result }));
                            }
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="20"
                          data-testid="calc-pret-duree"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Durée totale du crédit (15 à 25 ans en pratique commerciale).</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Taux nominal (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={calculetteData.pret.taux}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              pret: { ...calculetteData.pret, taux: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (calculetteData.pret.montant && calculetteData.pret.duree && e.target.value) {
                              const result = calculatePret(
                                calculetteData.pret.montant,
                                calculetteData.pret.duree,
                                e.target.value,
                                calculetteData.pret.apport
                              );
                              setCalculResults(prev => ({ ...prev, pret: result }));
                            }
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="4,20"
                          data-testid="calc-pret-taux"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Taux d'intérêt annuel hors assurance emprunteur.</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Apport (€)</label>
                        <input
                          type="number"
                          value={calculetteData.pret.apport}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              pret: { ...calculetteData.pret, apport: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (calculetteData.pret.montant && calculetteData.pret.duree && calculetteData.pret.taux) {
                              const result = calculatePret(
                                calculetteData.pret.montant,
                                calculetteData.pret.duree,
                                calculetteData.pret.taux,
                                e.target.value
                              );
                              setCalculResults(prev => ({ ...prev, pret: result }));
                            }
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="60 000"
                          data-testid="calc-pret-apport"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Fonds propres injectés. Diminue le capital emprunté et donc les intérêts.</p>
                      </div>
                    </div>

                    {/* Calcul inversé - Capacité d'emprunt */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                        <i className="bi bi-arrow-left-right text-blue-600"></i>
                        Calcul inversé — capacité d'emprunt
                      </h4>
                      <p className="text-[11px] text-slate-500 mb-3">Combien puis-je emprunter pour une mensualité donnée ? Utilise le taux et la durée saisis ci-dessus.</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Mensualité souhaitée (€)</label>
                          <input
                            type="number"
                            className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                            placeholder="1 500"
                            data-testid="calc-pret-mensualite-cible"
                            onChange={(e) => {
                              if (e.target.value && calculetteData.pret.duree && calculetteData.pret.taux) {
                                const mensualite = parseFloat(e.target.value);
                                const tauxMensuel = parseFloat(calculetteData.pret.taux) / 100 / 12;
                                const nbMois = parseFloat(calculetteData.pret.duree) * 12;
                                let capaciteEmprunt;
                                if (tauxMensuel === 0) {
                                  capaciteEmprunt = mensualite * nbMois;
                                } else {
                                  capaciteEmprunt = mensualite * (1 - Math.pow(1 + tauxMensuel, -nbMois)) / tauxMensuel;
                                }
                                setCalculResults(prev => ({
                                  ...prev,
                                  pret: {
                                    ...prev.pret,
                                    capaciteEmprunt: capaciteEmprunt.toFixed(2),
                                    prixAchatMax: (capaciteEmprunt + parseFloat(calculetteData.pret.apport || 0)).toFixed(2)
                                  }
                                }));
                              }
                            }}
                          />
                        </div>
                        {calculResults.pret?.capaciteEmprunt && (
                          <div className="bg-white p-3 rounded-lg border border-slate-200" data-testid="calc-pret-capacite-result">
                            <div className="text-sm space-y-1.5">
                              <div className="flex justify-between gap-2">
                                <span className="text-slate-600">Capacité d'emprunt</span>
                                <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.pret.capaciteEmprunt)}</strong>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-slate-600">Prix d'achat max</span>
                                <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.pret.prixAchatMax)}</strong>
                              </div>
                              <p className="text-[11px] text-slate-500 pt-1">Prix max = capacité d'emprunt + apport.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {calculResults.pret && calculResults.pret.mensualite && (
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid="calc-pret-results">
                        <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <i className="bi bi-check2-circle text-emerald-600"></i>
                          Résultats
                        </h4>
                        <div className="space-y-1.5">
                          <div className="flex justify-between gap-2 text-sm">
                            <span className="text-slate-600">Mensualité</span>
                            <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.pret.mensualite, { decimals: 2 })}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Échéance mensuelle hors assurance emprunteur.</p>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Montant emprunté</span>
                            <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.pret.montantEmprunte)}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Montant emprunté = prix d'achat FAI − apport.</p>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Coût total du crédit</span>
                            <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.pret.coutTotal)}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Somme des mensualités sur toute la durée.</p>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Intérêts payés</span>
                            <strong className="tabular-nums text-amber-700">{formatPriceFR(calculResults.pret.interets)}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Intérêts = coût total − montant emprunté.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                )}

                {/* Calculette Rentabilité */}
                {(!focusedCalc || focusedCalc === 'rentabilite') && (
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="calc-rentabilite">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <i className="bi bi-percent text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Calculette de rentabilité</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">Rendement locatif brut, net et cash-flow — pour investissement en murs commerciaux.</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Prix d'achat FAI (€)</label>
                        <input
                          type="number"
                          value={calculetteData.rentabilite.prix_achat}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              rentabilite: { ...calculetteData.rentabilite, prix_achat: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (e.target.value && calculetteData.rentabilite.loyer_mensuel) {
                              const result = calculateRentabilite(
                                e.target.value,
                                calculetteData.rentabilite.frais_achat,
                                calculetteData.rentabilite.loyer_mensuel,
                                calculetteData.rentabilite.charges,
                                calculetteData.rentabilite.travaux
                              );
                              setCalculResults(prev => ({ ...prev, rentabilite: result }));
                            }
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="200 000"
                          data-testid="calc-rent-prix"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Prix FAI = Frais d'Agence Inclus.</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Frais d'acquisition (€)</label>
                        <input
                          type="number"
                          value={calculetteData.rentabilite.frais_achat}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              rentabilite: { ...calculetteData.rentabilite, frais_achat: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (calculetteData.rentabilite.prix_achat && calculetteData.rentabilite.loyer_mensuel) {
                              const result = calculateRentabilite(
                                calculetteData.rentabilite.prix_achat,
                                e.target.value,
                                calculetteData.rentabilite.loyer_mensuel,
                                calculetteData.rentabilite.charges,
                                calculetteData.rentabilite.travaux
                              );
                              setCalculResults(prev => ({ ...prev, rentabilite: result }));
                            }
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="16 000"
                          data-testid="calc-rent-frais"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Notaire + droits d'enregistrement (≈ 7 à 8 % en ancien).</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Loyer mensuel HT/HC (€)</label>
                        <input
                          type="number"
                          value={calculetteData.rentabilite.loyer_mensuel}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              rentabilite: { ...calculetteData.rentabilite, loyer_mensuel: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (calculetteData.rentabilite.prix_achat && e.target.value) {
                              const result = calculateRentabilite(
                                calculetteData.rentabilite.prix_achat,
                                calculetteData.rentabilite.frais_achat,
                                e.target.value,
                                calculetteData.rentabilite.charges,
                                calculetteData.rentabilite.travaux
                              );
                              setCalculResults(prev => ({ ...prev, rentabilite: result }));
                            }
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="1 200"
                          data-testid="calc-rent-loyer"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Loyer hors taxes et hors charges perçu par le bailleur.</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Charges mensuelles (€)</label>
                        <input
                          type="number"
                          value={calculetteData.rentabilite.charges}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              rentabilite: { ...calculetteData.rentabilite, charges: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (calculetteData.rentabilite.prix_achat && calculetteData.rentabilite.loyer_mensuel) {
                              const result = calculateRentabilite(
                                calculetteData.rentabilite.prix_achat,
                                calculetteData.rentabilite.frais_achat,
                                calculetteData.rentabilite.loyer_mensuel,
                                e.target.value,
                                calculetteData.rentabilite.travaux
                              );
                              setCalculResults(prev => ({ ...prev, rentabilite: result }));
                            }
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="200"
                          data-testid="calc-rent-charges"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Charges non récupérables (copropriété, gestion, assurances).</p>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Travaux (€)</label>
                        <input
                          type="number"
                          value={calculetteData.rentabilite.travaux}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              rentabilite: { ...calculetteData.rentabilite, travaux: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (calculetteData.rentabilite.prix_achat && calculetteData.rentabilite.loyer_mensuel) {
                              const result = calculateRentabilite(
                                calculetteData.rentabilite.prix_achat,
                                calculetteData.rentabilite.frais_achat,
                                calculetteData.rentabilite.loyer_mensuel,
                                calculetteData.rentabilite.charges,
                                e.target.value
                              );
                              setCalculResults(prev => ({ ...prev, rentabilite: result }));
                            }
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="5 000"
                          data-testid="calc-rent-travaux"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Rénovations à réaliser avant mise en location (mise aux normes, agencement…).</p>
                      </div>
                    </div>

                    {/* Calcul inversé - Prix max selon rentabilité */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                        <i className="bi bi-arrow-left-right text-blue-600"></i>
                        Calcul inversé — prix max selon rendement cible
                      </h4>
                      <p className="text-[11px] text-slate-500 mb-3">Quel prix maximum pour atteindre le rendement net souhaité ?</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Rendement net cible (%)</label>
                          <input
                            type="number"
                            step="0.1"
                            className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                            placeholder="8,0"
                            data-testid="calc-rent-cible"
                            onChange={(e) => {
                              if (e.target.value && calculetteData.rentabilite.loyer_mensuel) {
                                const rentabiliteCible = parseFloat(e.target.value) / 100;
                                const loyerAnnuel = parseFloat(calculetteData.rentabilite.loyer_mensuel) * 12;
                                const chargesAnnuelles = parseFloat(calculetteData.rentabilite.charges || 0) * 12;
                                const revenusNets = loyerAnnuel - chargesAnnuelles;
                                const prixAchatMax = (revenusNets / rentabiliteCible) - parseFloat(calculetteData.rentabilite.frais_achat || 0) - parseFloat(calculetteData.rentabilite.travaux || 0);
                                setCalculResults(prev => ({
                                  ...prev,
                                  rentabilite: {
                                    ...prev.rentabilite,
                                    prixAchatMax: prixAchatMax.toFixed(2)
                                  }
                                }));
                              }
                            }}
                          />
                        </div>
                        {calculResults.rentabilite?.prixAchatMax && (
                          <div className="bg-white p-3 rounded-lg border border-slate-200" data-testid="calc-rent-prixmax-result">
                            <div className="flex justify-between gap-2 text-sm">
                              <span className="text-slate-600">Prix d'achat max</span>
                              <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.rentabilite.prixAchatMax)}</strong>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-1">Au-delà, le rendement net descend sous la cible saisie.</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {calculResults.rentabilite && calculResults.rentabilite.rendementBrut && (
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid="calc-rent-results">
                        <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <i className="bi bi-check2-circle text-emerald-600"></i>
                          Résultats
                        </h4>
                        <div className="space-y-1.5">
                          <div className="flex justify-between gap-2 text-sm">
                            <span className="text-slate-600">Rendement brut</span>
                            <strong className={`tabular-nums ${parseFloat(calculResults.rentabilite.rendementBrut) >= 6 ? 'text-emerald-700' : 'text-slate-900'}`}>
                              {formatPercentFR(calculResults.rentabilite.rendementBrut)}
                            </strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Rendement brut = loyer annuel ÷ investissement total. Repère marché : 5 à 8 %.</p>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Rendement net</span>
                            <strong className={`tabular-nums ${parseFloat(calculResults.rentabilite.rendementNet) >= 5 ? 'text-emerald-700' : 'text-slate-900'}`}>
                              {formatPercentFR(calculResults.rentabilite.rendementNet)}
                            </strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Net = brut après charges non récupérables. Toujours inférieur au brut.</p>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Cash-flow mensuel</span>
                            <strong className={`tabular-nums ${parseFloat(calculResults.rentabilite.cashflowMensuel) >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                              {formatPriceFR(calculResults.rentabilite.cashflowMensuel, { decimals: 2 })}
                            </strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Trésorerie mensuelle après charges (hors mensualité de prêt).</p>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Revenus nets annuels</span>
                            <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.rentabilite.revenusNetsAnnuels)}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Loyer annuel − charges annuelles.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                )}

                {/* Calculette Plus-Value */}
                {(!focusedCalc || focusedCalc === 'plus-value') && (
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="calc-plus-value">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <i className="bi bi-currency-euro text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Calculette de plus-value immobilière</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">Estimation de l'imposition sur la plus-value (impôt + prélèvements sociaux) — hors résidence principale.</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prix d'achat (€)</label>
                        <input
                          type="number"
                          value={calculetteData.plus_value.prix_achat}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              plus_value: { ...calculetteData.plus_value, prix_achat: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (e.target.value && calculetteData.plus_value.prix_vente && calculetteData.plus_value.duree_detention) {
                              const result = calculatePlusValue(
                                e.target.value,
                                calculetteData.plus_value.prix_vente,
                                calculetteData.plus_value.frais_achat,
                                calculetteData.plus_value.frais_vente,
                                calculetteData.plus_value.travaux,
                                calculetteData.plus_value.duree_detention
                              );
                              setCalculResults(prev => ({ ...prev, plus_value: result }));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="250000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prix de vente (€)</label>
                        <input
                          type="number"
                          value={calculetteData.plus_value.prix_vente}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              plus_value: { ...calculetteData.plus_value, prix_vente: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (calculetteData.plus_value.prix_achat && e.target.value && calculetteData.plus_value.duree_detention) {
                              const result = calculatePlusValue(
                                calculetteData.plus_value.prix_achat,
                                e.target.value,
                                calculetteData.plus_value.frais_achat,
                                calculetteData.plus_value.frais_vente,
                                calculetteData.plus_value.travaux,
                                calculetteData.plus_value.duree_detention
                              );
                              setCalculResults(prev => ({ ...prev, plus_value: result }));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="320000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Frais d'achat (€)</label>
                        <input
                          type="number"
                          value={calculetteData.plus_value.frais_achat}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              plus_value: { ...calculetteData.plus_value, frais_achat: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (calculetteData.plus_value.prix_achat && calculetteData.plus_value.prix_vente && calculetteData.plus_value.duree_detention) {
                              const result = calculatePlusValue(
                                calculetteData.plus_value.prix_achat,
                                calculetteData.plus_value.prix_vente,
                                e.target.value,
                                calculetteData.plus_value.frais_vente,
                                calculetteData.plus_value.travaux,
                                calculetteData.plus_value.duree_detention
                              );
                              setCalculResults(prev => ({ ...prev, plus_value: result }));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="20000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Frais de vente (€)</label>
                        <input
                          type="number"
                          value={calculetteData.plus_value.frais_vente}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              plus_value: { ...calculetteData.plus_value, frais_vente: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (calculetteData.plus_value.prix_achat && calculetteData.plus_value.prix_vente && calculetteData.plus_value.duree_detention) {
                              const result = calculatePlusValue(
                                calculetteData.plus_value.prix_achat,
                                calculetteData.plus_value.prix_vente,
                                calculetteData.plus_value.frais_achat,
                                e.target.value,
                                calculetteData.plus_value.travaux,
                                calculetteData.plus_value.duree_detention
                              );
                              setCalculResults(prev => ({ ...prev, plus_value: result }));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="15000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Travaux (€)</label>
                        <input
                          type="number"
                          value={calculetteData.plus_value.travaux}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              plus_value: { ...calculetteData.plus_value, travaux: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (calculetteData.plus_value.prix_achat && calculetteData.plus_value.prix_vente && calculetteData.plus_value.duree_detention) {
                              const result = calculatePlusValue(
                                calculetteData.plus_value.prix_achat,
                                calculetteData.plus_value.prix_vente,
                                calculetteData.plus_value.frais_achat,
                                calculetteData.plus_value.frais_vente,
                                e.target.value,
                                calculetteData.plus_value.duree_detention
                              );
                              setCalculResults(prev => ({ ...prev, plus_value: result }));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="10000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Durée détention (années)</label>
                        <input
                          type="number"
                          value={calculetteData.plus_value.duree_detention}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              plus_value: { ...calculetteData.plus_value, duree_detention: e.target.value }
                            };
                            setCalculetteData(newData);
                            if (calculetteData.plus_value.prix_achat && calculetteData.plus_value.prix_vente && e.target.value) {
                              const result = calculatePlusValue(
                                calculetteData.plus_value.prix_achat,
                                calculetteData.plus_value.prix_vente,
                                calculetteData.plus_value.frais_achat,
                                calculetteData.plus_value.frais_vente,
                                calculetteData.plus_value.travaux,
                                e.target.value
                              );
                              setCalculResults(prev => ({ ...prev, plus_value: result }));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="8"
                        />
                      </div>
                    </div>
                    
                    {calculResults.plus_value && calculResults.plus_value.plusValueBrute && (
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid="calc-pv-results">
                        <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <i className="bi bi-check2-circle text-emerald-600"></i>
                          Résultats
                        </h4>
                        <div className="space-y-1.5">
                          <div className="flex justify-between gap-2 text-sm">
                            <span className="text-slate-600">Plus-value brute</span>
                            <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.plus_value.plusValueBrute)}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Prix de vente − (prix d'achat + frais + travaux).</p>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Abattement pour durée de détention</span>
                            <strong className="tabular-nums text-slate-900">{formatPercentFR(calculResults.plus_value.abattement, { decimals: 0 })}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Exonération d'impôt après 22 ans, de prélèvements sociaux après 30 ans.</p>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Plus-value imposable</span>
                            <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.plus_value.plusValueImposable)}</strong>
                          </div>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Impôt sur le revenu (19 %)</span>
                            <strong className="tabular-nums text-amber-700">{formatPriceFR(calculResults.plus_value.impots)}</strong>
                          </div>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Prélèvements sociaux (17,2 %)</span>
                            <strong className="tabular-nums text-amber-700">{formatPriceFR(calculResults.plus_value.prelevementsSociaux)}</strong>
                          </div>
                          <div className="flex justify-between gap-2 text-sm pt-2 mt-1 border-t border-slate-200">
                            <span className="font-semibold text-slate-900">Plus-value nette</span>
                            <strong className="tabular-nums text-emerald-700">{formatPriceFR(calculResults.plus_value.plusValueNette)}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500">Ce que le vendeur perçoit réellement après impôts.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                )}

                {/* Calculette Droits de mutation commerciaux (iter.73) */}
                {(!focusedCalc || focusedCalc === 'droits-mutation') && (
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="calc-droits-mutation">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <i className="bi bi-file-earmark-text text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Droits de mutation commerciaux</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">Cession de fonds (barème progressif) ou acquisition de murs commerciaux (droits d'enregistrement).</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {/* Sélecteur du type d'opération — segmented control DS */}
                    <div>
                      <div className="inline-flex bg-slate-100 p-1 rounded-lg" role="tablist">
                        {[
                          { id: 'fonds', label: 'Fonds de commerce' },
                          { id: 'murs', label: 'Murs commerciaux' },
                        ].map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            data-testid={`calc-dm-type-${opt.id}`}
                            onClick={() => {
                              const newData = {
                                ...calculetteData,
                                droits_mutation: { ...calculetteData.droits_mutation, type: opt.id }
                              };
                              setCalculetteData(newData);
                              if (calculetteData.droits_mutation.prix) {
                                const result = calculateDroitsMutation(newData.droits_mutation);
                                setCalculResults(prev => ({ ...prev, droits_mutation: result }));
                              }
                            }}
                            className={`h-9 px-3 rounded-md text-sm font-medium transition ${
                              calculetteData.droits_mutation.type === opt.id
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1.5">
                        {calculetteData.droits_mutation.type === 'fonds'
                          ? 'Barème progressif de cession de fonds : 0 % jusqu\'à 23 000 €, 3 % de 23 000 à 200 000 €, 5 % au-delà.'
                          : 'Droits d\'enregistrement + taxe communale + émoluments et débours (taux éditables selon département).'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Prix de cession (€)</label>
                      <input
                        type="number"
                        value={calculetteData.droits_mutation.prix}
                        onChange={(e) => {
                          const newData = {
                            ...calculetteData,
                            droits_mutation: { ...calculetteData.droits_mutation, prix: e.target.value }
                          };
                          setCalculetteData(newData);
                          const result = calculateDroitsMutation(newData.droits_mutation);
                          setCalculResults(prev => ({ ...prev, droits_mutation: result }));
                        }}
                        className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                        placeholder="250 000"
                        data-testid="calc-dm-prix"
                      />
                      <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                        {calculetteData.droits_mutation.type === 'fonds'
                          ? 'Prix de cession du fonds de commerce (hors stock).'
                          : 'Prix d\'acquisition FAI des murs.'}
                      </p>
                    </div>

                    {calculetteData.droits_mutation.type === 'murs' && (
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Droit dép. (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={calculetteData.droits_mutation.taux_dep}
                            onChange={(e) => {
                              const newData = {
                                ...calculetteData,
                                droits_mutation: { ...calculetteData.droits_mutation, taux_dep: e.target.value }
                              };
                              setCalculetteData(newData);
                              const result = calculateDroitsMutation(newData.droits_mutation);
                              setCalculResults(prev => ({ ...prev, droits_mutation: result }));
                            }}
                            className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                            data-testid="calc-dm-taux-dep"
                          />
                          <p className="text-[11px] text-slate-500 mt-1 leading-snug">Droit départemental d'enregistrement.</p>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Taxe com. (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={calculetteData.droits_mutation.taux_com}
                            onChange={(e) => {
                              const newData = {
                                ...calculetteData,
                                droits_mutation: { ...calculetteData.droits_mutation, taux_com: e.target.value }
                              };
                              setCalculetteData(newData);
                              const result = calculateDroitsMutation(newData.droits_mutation);
                              setCalculResults(prev => ({ ...prev, droits_mutation: result }));
                            }}
                            className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                            data-testid="calc-dm-taux-com"
                          />
                          <p className="text-[11px] text-slate-500 mt-1 leading-snug">Taxe communale additionnelle.</p>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Annexes (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={calculetteData.droits_mutation.taux_annexes}
                            onChange={(e) => {
                              const newData = {
                                ...calculetteData,
                                droits_mutation: { ...calculetteData.droits_mutation, taux_annexes: e.target.value }
                              };
                              setCalculetteData(newData);
                              const result = calculateDroitsMutation(newData.droits_mutation);
                              setCalculResults(prev => ({ ...prev, droits_mutation: result }));
                            }}
                            className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                            data-testid="calc-dm-taux-annexes"
                          />
                          <p className="text-[11px] text-slate-500 mt-1 leading-snug">Émoluments notaire + débours (estimation).</p>
                        </div>
                      </div>
                    )}

                    {calculResults.droits_mutation && (
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid="calc-dm-results">
                        <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <i className="bi bi-check2-circle text-emerald-600"></i>
                          Résultats
                        </h4>
                        <div className="space-y-1.5">
                          {calculResults.droits_mutation.type === 'fonds' ? (
                            <>
                              <div className="flex justify-between gap-2 text-sm">
                                <span className="text-slate-600">Tranche 0 – 23 000 €</span>
                                <strong className="tabular-nums text-slate-900">{formatPriceFR(0)}</strong>
                              </div>
                              <p className="text-[11px] text-slate-500 -mt-1">Franchise de 23 000 € (0 %).</p>
                              <div className="flex justify-between gap-2 text-sm pt-1">
                                <span className="text-slate-600">Tranche 23 000 – 200 000 €</span>
                                <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.droits_mutation.detail.tranche_23_200k, { decimals: 2 })}</strong>
                              </div>
                              <p className="text-[11px] text-slate-500 -mt-1">Cette tranche est taxée à 3 %.</p>
                              <div className="flex justify-between gap-2 text-sm pt-1">
                                <span className="text-slate-600">Tranche &gt; 200 000 €</span>
                                <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.droits_mutation.detail.tranche_gt_200k, { decimals: 2 })}</strong>
                              </div>
                              <p className="text-[11px] text-slate-500 -mt-1">Au-delà de 200 000 €, taux de 5 %.</p>
                            </>
                          ) : (
                            <>
                              <div className="flex justify-between gap-2 text-sm">
                                <span className="text-slate-600">Droit départemental</span>
                                <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.droits_mutation.detail.droitsDep, { decimals: 2 })}</strong>
                              </div>
                              <div className="flex justify-between gap-2 text-sm pt-1">
                                <span className="text-slate-600">Taxe communale</span>
                                <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.droits_mutation.detail.droitsCom, { decimals: 2 })}</strong>
                              </div>
                              <div className="flex justify-between gap-2 text-sm pt-1">
                                <span className="text-slate-600">Émoluments + débours</span>
                                <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.droits_mutation.detail.annexes, { decimals: 2 })}</strong>
                              </div>
                              <p className="text-[11px] text-slate-500 -mt-1">Estimation ; les émoluments notaire suivent en réalité un barème dégressif.</p>
                            </>
                          )}
                          <div className="flex justify-between gap-2 text-sm pt-2 mt-1 border-t border-slate-200">
                            <span className="text-slate-600">Total droits</span>
                            <strong className="tabular-nums text-amber-700">{formatPriceFR(calculResults.droits_mutation.droits, { decimals: 2 })}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Taux effectif : {formatPercentFR(calculResults.droits_mutation.tauxEffectif)}.</p>
                          <div className="flex justify-between gap-2 text-sm pt-2 mt-1 border-t border-slate-200">
                            <span className="font-semibold text-slate-900">Coût total acquéreur</span>
                            <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.droits_mutation.prixTotal, { decimals: 2 })}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500">Prix de cession + droits.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                )}

                {/* Calculette Surface pondérée commerciale (iter.73) */}
                {(!focusedCalc || focusedCalc === 'surface-ponderee') && (
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="calc-surface-ponderee">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <i className="bi bi-rulers text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Surface pondérée commerciale</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">Pondération standard du bail commercial : RDC × 1, réserve × 0,5, sous-sol/mezzanine × 0,3. Coefficients éditables.</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {/* 3 zones : RDC, réserve, annexe */}
                    {[
                      { key: 'rdc', coefKey: 'coef_rdc', label: 'RDC / vitrine', hint: 'Surface commerciale principale, visible depuis la rue.', defCoef: '1', testid: 'calc-sp-rdc' },
                      { key: 'reserve', coefKey: 'coef_reserve', label: 'Réserve / arrière-boutique', hint: 'Surface de stockage ou de préparation, non visible du public.', defCoef: '0,5', testid: 'calc-sp-reserve' },
                      { key: 'annexe', coefKey: 'coef_annexe', label: 'Sous-sol / mezzanine', hint: 'Surface annexe (sous-sol, mezzanine, extérieur couvert).', defCoef: '0,3', testid: 'calc-sp-annexe' },
                    ].map((zone) => (
                      <div key={zone.key} className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                          <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">{zone.label}</label>
                          <input
                            type="number"
                            step="0.1"
                            value={calculetteData.surface_ponderee[zone.key]}
                            onChange={(e) => {
                              const newData = {
                                ...calculetteData,
                                surface_ponderee: { ...calculetteData.surface_ponderee, [zone.key]: e.target.value }
                              };
                              setCalculetteData(newData);
                              const result = calculateSurfacePonderee(newData.surface_ponderee);
                              setCalculResults(prev => ({ ...prev, surface_ponderee: result }));
                            }}
                            className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                            placeholder={`Surface en m² (${zone.defCoef}× par défaut)`}
                            data-testid={`${zone.testid}-surface`}
                          />
                          <p className="text-[11px] text-slate-500 mt-1 leading-snug">{zone.hint}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Coefficient</label>
                          <input
                            type="number"
                            step="0.01"
                            value={calculetteData.surface_ponderee[zone.coefKey]}
                            onChange={(e) => {
                              const newData = {
                                ...calculetteData,
                                surface_ponderee: { ...calculetteData.surface_ponderee, [zone.coefKey]: e.target.value }
                              };
                              setCalculetteData(newData);
                              const result = calculateSurfacePonderee(newData.surface_ponderee);
                              setCalculResults(prev => ({ ...prev, surface_ponderee: result }));
                            }}
                            className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                            data-testid={`${zone.testid}-coef`}
                          />
                          <p className="text-[11px] text-slate-500 mt-1 leading-snug">Défaut : {zone.defCoef}.</p>
                        </div>
                      </div>
                    ))}

                    {calculResults.surface_ponderee && calculResults.surface_ponderee.surfacePonderee && (
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid="calc-sp-results">
                        <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <i className="bi bi-check2-circle text-emerald-600"></i>
                          Résultats
                        </h4>
                        <div className="space-y-1.5">
                          <div className="flex justify-between gap-2 text-sm">
                            <span className="text-slate-600">Surface brute totale</span>
                            <strong className="tabular-nums text-slate-900">{formatSurfaceFR(calculResults.surface_ponderee.surfaceBrute)}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Somme des 3 zones (RDC + réserve + annexe) sans pondération.</p>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Contribution RDC</span>
                            <strong className="tabular-nums text-slate-900">{formatSurfaceFR(calculResults.surface_ponderee.details.rdc)}</strong>
                          </div>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Contribution réserve</span>
                            <strong className="tabular-nums text-slate-900">{formatSurfaceFR(calculResults.surface_ponderee.details.reserve)}</strong>
                          </div>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Contribution annexe</span>
                            <strong className="tabular-nums text-slate-900">{formatSurfaceFR(calculResults.surface_ponderee.details.annexe)}</strong>
                          </div>
                          <div className="flex justify-between gap-2 text-sm pt-2 mt-1 border-t border-slate-200">
                            <span className="font-semibold text-slate-900">Surface pondérée totale</span>
                            <strong className="tabular-nums text-emerald-700">{formatSurfaceFR(calculResults.surface_ponderee.surfacePonderee)}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500">Base commerciale de référence — utilisée pour comparer des locaux et fixer un loyer/m² pondéré.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                )}

                {/* Calculette Valorisation d'un fonds de commerce (iter.73) */}
                {(!focusedCalc || focusedCalc === 'valorisation-fonds') && (
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="calc-valorisation-fonds">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <i className="bi bi-cash-stack text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Valorisation d'un fonds de commerce</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">Deux approches croisées : barème par CA (usage sectoriel) et multiple d'EBE (rentabilité).</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">CA HT annuel (€)</label>
                        <input
                          type="number"
                          value={calculetteData.valorisation_fonds.ca}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              valorisation_fonds: { ...calculetteData.valorisation_fonds, ca: e.target.value }
                            };
                            setCalculetteData(newData);
                            const result = calculateValorisationFonds(newData.valorisation_fonds);
                            setCalculResults(prev => ({ ...prev, valorisation_fonds: result }));
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="250 000"
                          data-testid="calc-vf-ca"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">CA HT annuel constaté sur le dernier exercice.</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">EBE annuel (€)</label>
                        <input
                          type="number"
                          value={calculetteData.valorisation_fonds.ebe}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              valorisation_fonds: { ...calculetteData.valorisation_fonds, ebe: e.target.value }
                            };
                            setCalculetteData(newData);
                            const result = calculateValorisationFonds(newData.valorisation_fonds);
                            setCalculResults(prev => ({ ...prev, valorisation_fonds: result }));
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="60 000"
                          data-testid="calc-vf-ebe"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Résultat d'exploitation avant amortissements et impôts.</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Barème CA (%)</label>
                        <input
                          type="number"
                          step="1"
                          value={calculetteData.valorisation_fonds.bareme_ca}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              valorisation_fonds: { ...calculetteData.valorisation_fonds, bareme_ca: e.target.value }
                            };
                            setCalculetteData(newData);
                            const result = calculateValorisationFonds(newData.valorisation_fonds);
                            setCalculResults(prev => ({ ...prev, valorisation_fonds: result }));
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="80"
                          data-testid="calc-vf-bareme-ca"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">% du CA HT retenu (barème sectoriel : CHR 50–100 %, commerce 30–80 %).</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Multiple d'EBE</label>
                        <input
                          type="number"
                          step="0.1"
                          value={calculetteData.valorisation_fonds.multiple_ebe}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              valorisation_fonds: { ...calculetteData.valorisation_fonds, multiple_ebe: e.target.value }
                            };
                            setCalculetteData(newData);
                            const result = calculateValorisationFonds(newData.valorisation_fonds);
                            setCalculResults(prev => ({ ...prev, valorisation_fonds: result }));
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="4"
                          data-testid="calc-vf-multiple-ebe"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Repère marché : 3 à 7 selon rentabilité et pérennité du fonds.</p>
                      </div>
                    </div>

                    {calculResults.valorisation_fonds && (calculResults.valorisation_fonds.valeurParCa !== null || calculResults.valorisation_fonds.valeurParEbe !== null) && (
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid="calc-vf-results">
                        <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <i className="bi bi-check2-circle text-emerald-600"></i>
                          Résultats
                        </h4>
                        <div className="space-y-1.5">
                          <div className="flex justify-between gap-2 text-sm">
                            <span className="text-slate-600">Valeur par barème CA</span>
                            <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.valorisation_fonds.valeurParCa, { decimals: 2, fallback: '—' })}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Valeur = CA × barème sectoriel (%).</p>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Valeur par multiple d'EBE</span>
                            <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.valorisation_fonds.valeurParEbe, { decimals: 2, fallback: '—' })}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500 -mt-1">Valeur = EBE × multiple retenu.</p>
                          {calculResults.valorisation_fonds.valeurMoyenne && (
                            <>
                              <div className="flex justify-between gap-2 text-sm pt-2 mt-1 border-t border-slate-200">
                                <span className="font-semibold text-slate-900">Fourchette moyenne</span>
                                <strong className="tabular-nums text-emerald-700">{formatPriceFR(calculResults.valorisation_fonds.valeurMoyenne, { decimals: 2 })}</strong>
                              </div>
                              <p className="text-[11px] text-slate-500">Moyenne arithmétique des 2 approches — indication de fourchette, pas d'expertise.</p>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                )}

                {/* Calculette Valeur des murs par capitalisation (iter.73) */}
                {(!focusedCalc || focusedCalc === 'capitalisation-murs') && (
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="calc-capitalisation-murs">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <i className="bi bi-graph-up-arrow text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Valeur des murs par capitalisation</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">Estimation d'un local commercial à partir du loyer perçu et du taux de rendement attendu.</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Loyer annuel HT (€)</label>
                        <input
                          type="number"
                          value={calculetteData.capitalisation_murs.loyer_annuel}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              capitalisation_murs: { ...calculetteData.capitalisation_murs, loyer_annuel: e.target.value }
                            };
                            setCalculetteData(newData);
                            const result = calculateCapitalisationMurs(newData.capitalisation_murs);
                            setCalculResults(prev => ({ ...prev, capitalisation_murs: result }));
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="24 000"
                          data-testid="calc-cm-loyer"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Loyer annuel HT/HC perçu par le bailleur (bail commercial).</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">Taux de rendement cible (%)</label>
                        <input
                          type="number"
                          step="0.1"
                          value={calculetteData.capitalisation_murs.taux}
                          onChange={(e) => {
                            const newData = {
                              ...calculetteData,
                              capitalisation_murs: { ...calculetteData.capitalisation_murs, taux: e.target.value }
                            };
                            setCalculetteData(newData);
                            const result = calculateCapitalisationMurs(newData.capitalisation_murs);
                            setCalculResults(prev => ({ ...prev, capitalisation_murs: result }));
                          }}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
                          placeholder="7"
                          data-testid="calc-cm-taux"
                        />
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">Rendement attendu par l'investisseur (marché commercial : 5 à 8 %).</p>
                      </div>
                    </div>

                    {calculResults.capitalisation_murs && (
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid="calc-cm-results">
                        <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <i className="bi bi-check2-circle text-emerald-600"></i>
                          Résultats
                        </h4>
                        <div className="space-y-1.5">
                          <div className="flex justify-between gap-2 text-sm">
                            <span className="text-slate-600">Loyer annuel retenu</span>
                            <strong className="tabular-nums text-slate-900">{formatPriceFR(calculResults.capitalisation_murs.loyerAnnuel)}</strong>
                          </div>
                          <div className="flex justify-between gap-2 text-sm pt-1">
                            <span className="text-slate-600">Taux appliqué</span>
                            <strong className="tabular-nums text-slate-900">{formatPercentFR(calculResults.capitalisation_murs.tauxApplique)}</strong>
                          </div>
                          <div className="flex justify-between gap-2 text-sm pt-2 mt-1 border-t border-slate-200">
                            <span className="font-semibold text-slate-900">Valeur des murs</span>
                            <strong className="tabular-nums text-emerald-700">{formatPriceFR(calculResults.capitalisation_murs.valeur, { decimals: 2 })}</strong>
                          </div>
                          <p className="text-[11px] text-slate-500">Valeur = loyer annuel ÷ taux de rendement. Approche par capitalisation.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
            )}

            {/* Tabs autres à implémenter */}
            {/* Sections « statistiques » et « rapports » retirées :
                onglets non prêts, placeholders démo-not-ready. */}
          </div>
        </div>
      </div>
    );
  };

  const renderConsultation = () => {
    if (!consultedProperty) {
      return (
        <div className="p-6 bg-gray-50 min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Aucun bien sélectionné</h2>
            <p className="text-gray-600">Veuillez sélectionner un bien à consulter.</p>
          </div>
        </div>
      );
    }

    // Interface CUTTING-EDGE 2025 avec dernières tendances
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
        {/* Background decoration moderne */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-purple-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-emerald-400/10 to-teal-600/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 container mx-auto px-4 py-6">
          {/* Header moderne avec glassmorphism */}
          <div className="backdrop-blur-xl bg-white/80 border border-white/20 rounded-2xl shadow-2xl shadow-blue-500/10 mb-8 overflow-hidden">
            {/* Hero section avec gradient et effet glass */}
            <div className="relative bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 text-white overflow-hidden">
              {/* Pattern decoration */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
              <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                    <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#grid)"/>
              </svg>
              
              <div className="relative z-10 p-8">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between space-y-6 lg:space-y-0">
                  <div className="flex-1">
                    {/* Badge status avec animation */}
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="relative">
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                          <i className="bi bi-eye text-3xl text-blue-300"></i>
                        </div>
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full animate-pulse"></div>
                      </div>
                      <div>
                        <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                          Mode Consultation
                        </h1>
                        <p className="text-blue-200/80 font-medium">Réf: CASASOFT{consultedProperty.id?.slice(0,8)}</p>
                      </div>
                    </div>
                    
                    {/* Info principale avec typography moderne */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-4">
                        <span className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold backdrop-blur-sm border transition-all duration-300 hover:scale-105 ${
                          consultedProperty.status === 'active' 
                            ? 'bg-emerald-500/20 text-emerald-100 border-emerald-400/30 shadow-emerald-500/20' 
                            : 'bg-amber-500/20 text-amber-100 border-amber-400/30 shadow-amber-500/20'
                        } shadow-lg`}>
                          <div className={`w-2 h-2 rounded-full mr-2 animate-pulse ${
                            consultedProperty.status === 'active' ? 'bg-emerald-400' : 
                            consultedProperty.status === 'draft' ? 'bg-blue-400' : 
                            'bg-amber-400'
                          }`}></div>
                          {consultedProperty.status === 'active' ? 'Bien actif' : 
                           consultedProperty.status === 'draft' ? 'Brouillon' : 
                           'Bien archivé'}
                        </span>
                        <span className="text-blue-200/70 text-sm font-medium">
                          Créé le {new Date(consultedProperty.created_at).toLocaleDateString('fr-FR', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                      
                      {/* Property type avec effet neumorphic */}
                      <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-3 border border-white/20">
                        <i className="bi bi-building text-blue-300 mr-3 text-lg"></i>
                        <span className="text-white font-semibold text-lg">
                          {consultedProperty.bien_general?.activite_principale || consultedProperty.subcategory || consultedProperty.bien_general?.type_bien || 'Commerce'}
                        </span>
                        {consultedProperty.informations_confidentielles?.ville && (
                          <>
                            <span className="text-blue-200 mx-3">•</span>
                            <span className="text-blue-200 font-medium">
                              {consultedProperty.informations_confidentielles.ville}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Prix avec effet glass et animation */}
                  {consultedProperty.honoraires_calcul?.prix_vente && (
                    <div className="lg:text-right">
                      <div className="bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-sm rounded-3xl p-6 border border-white/20 shadow-2xl hover:shadow-emerald-500/20 transition-all duration-500 hover:scale-105">
                        <div className="text-4xl lg:text-5xl font-semibold bg-gradient-to-r from-emerald-300 via-teal-300 to-blue-300 bg-clip-text text-transparent mb-2 tabular-nums">
                          {formatPriceFR(consultedProperty.honoraires_calcul.prix_vente)}
                          {consultedProperty.transaction_type === 'Location' && (
                            <span className="text-sm ml-1">/mois</span>
                          )}
                        </div>
                        <div className="text-blue-200/80 font-medium text-lg">
                          {consultedProperty.transaction_type === 'Location' ? 'Montant de location' : 'Prix de vente'}
                        </div>
                        <div className="mt-2 text-xs text-blue-300/60 font-medium uppercase tracking-wide">
                          TTC • Honoraires inclus
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Toolbar moderne avec micro-interactions */}
            <div className="bg-gradient-to-r from-gray-50/80 to-white/80 backdrop-blur-sm border-t border-gray-200/50 p-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                {/* Actions principales avec design system */}
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => {
                      setShowConsultation(false);
                      setConsultedProperty(null);
                      showPropertiesView();
                    }}
                    className="group inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gray-500/25 active:scale-95"
                  >
                    <i className="bi bi-arrow-left mr-2 group-hover:-translate-x-0.5 transition-transform duration-200"></i>
                    <span>Retour</span>
                  </button>
                  
                  <div className="w-px h-8 bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
                  
                  <div className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-semibold text-sm border border-blue-400/20 shadow-lg shadow-blue-500/25">
                    <i className="bi bi-eye mr-2"></i>
                    <span>Consultation active</span>
                    <div className="ml-2 w-2 h-2 bg-blue-200 rounded-full animate-pulse"></div>
                  </div>
                  
                  <button
                    onClick={() => {
                      setCurrentProperty(consultedProperty);
                      setShowConsultation(false);
                      setShowForm(true);
                    }}
                    className="group inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-orange-500/25 active:scale-95"
                  >
                    <i className="bi bi-pencil mr-2 group-hover:rotate-12 transition-transform duration-200"></i>
                    <span>Modifier</span>
                  </button>
                </div>

                {/* Actions secondaires avec animations */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => openEmailModal(consultedProperty)}
                    className="group relative overflow-hidden inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-emerald-500/25 active:scale-95"
                    title="E-Mailing"
                  >
                    <i className="bi bi-envelope relative z-10 group-hover:scale-110 transition-transform duration-200"></i>
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                  </button>
                  
                  <button
                    onClick={() => openClientAffectation(consultedProperty)}
                    className="group relative overflow-hidden inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-purple-500/25 active:scale-95"
                    title="Affecter à un client"
                  >
                    <i className="bi bi-person-plus relative z-10 group-hover:scale-110 transition-transform duration-200"></i>
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openFicheBienModal(consultedProperty);
                    }}
                    className="group relative overflow-hidden inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-orange-500/25 active:scale-95"
                    title="Fiche Bien PDF"
                  >
                    <i className="bi bi-file-earmark-pdf relative z-10 group-hover:scale-110 transition-transform duration-200"></i>
                  </button>
                  
                  <button
                    onClick={() => openHistorique(consultedProperty)}
                    className="group relative overflow-hidden inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-indigo-500/25 active:scale-95"
                    title="Historique"
                  >
                    <i className="bi bi-clock-history relative z-10 group-hover:rotate-12 transition-transform duration-200"></i>
                  </button>
                  
                  {/* Dropdown moderne avec glassmorphism */}
                  <div className="relative group">
                    <button className="group relative overflow-hidden inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-red-500/25 active:scale-95">
                      <i className="bi bi-printer relative z-10 group-hover:scale-110 transition-transform duration-200"></i>
                    </button>
                    <div className="absolute right-0 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-50">
                      <div className="bg-white/95 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-2xl shadow-gray-500/10 min-w-48 overflow-hidden">
                        <div className="p-2 space-y-1">
                          <button
                            onClick={() => generatePropertyPDF(consultedProperty, 'complete')}
                            className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-red-50 rounded-xl transition-all duration-200 flex items-center space-x-3 group/item"
                          >
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center group-hover/item:bg-red-200 transition-colors">
                              <i className="bi bi-file-earmark-pdf text-red-600 text-sm"></i>
                            </div>
                            <span className="font-medium">PDF Complet</span>
                          </button>
                          <button
                            onClick={() => printPropertyWeb(consultedProperty, 'fiche_technique')}
                            className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 rounded-xl transition-all duration-200 flex items-center space-x-3 group/item"
                          >
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center group-hover/item:bg-blue-200 transition-colors">
                              <i className="bi bi-file-earmark-text text-blue-600 text-sm"></i>
                            </div>
                            <span className="font-medium">Fiche technique</span>
                          </button>
                          <button
                            onClick={() => printPropertyWeb(consultedProperty, 'vitrine')}
                            className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 rounded-xl transition-all duration-200 flex items-center space-x-3 group/item"
                          >
                            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center group-hover/item:bg-orange-200 transition-colors">
                              <i className="bi bi-window text-orange-600 text-sm"></i>
                            </div>
                            <span className="font-medium">Affichage vitrine</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => openAjouterEvenement(consultedProperty)}
                    className="group relative overflow-hidden inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-teal-500/25 active:scale-95"
                    title="Ajouter événement"
                  >
                    <i className="bi bi-calendar-event relative z-10 group-hover:scale-110 transition-transform duration-200"></i>
                  </button>
                  
                  <button
                    onClick={() => openAjouterTache(consultedProperty)}
                    className="group relative overflow-hidden inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-amber-500/25 active:scale-95"
                    title="Ajouter tâche"
                  >
                    <i className="bi bi-check-square relative z-10 group-hover:scale-110 transition-transform duration-200"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Contenu principal avec design system avancé */}
          <div className="backdrop-blur-xl bg-white/80 border border-white/20 rounded-2xl shadow-2xl shadow-blue-500/5 overflow-hidden">
            {/* Navigation onglets avec morphism */}
            <div className="border-b border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white/50 backdrop-blur-sm">
              <nav className="flex" aria-label="Tabs">
                {[
                  { id: 'general', name: 'Général', icon: 'bi-info-circle', gradient: 'from-blue-500 to-blue-600', hover: 'hover:bg-blue-50' },
                  { id: 'prestations', name: 'Prestations', icon: 'bi-star', gradient: 'from-purple-500 to-purple-600', hover: 'hover:bg-purple-50' },
                  { id: 'composition', name: 'Composition', icon: 'bi-diagram-3', gradient: 'from-amber-500 to-orange-600', hover: 'hover:bg-amber-50' },
                  { id: 'descriptif', name: 'Descriptif', icon: 'bi-text-paragraph', gradient: 'from-emerald-500 to-emerald-600', hover: 'hover:bg-emerald-50' },
                  { id: 'confidentiel', name: 'Confidentiel', icon: 'bi-lock', gradient: 'from-red-500 to-red-600', hover: 'hover:bg-red-50' },
                  { id: 'photos', name: 'Photos', icon: 'bi-images', gradient: 'from-orange-500 to-orange-600', hover: 'hover:bg-orange-50' },
                  { id: 'vente', name: 'Vente', icon: 'bi-currency-euro', gradient: 'from-teal-500 to-teal-600', hover: 'hover:bg-teal-50' },
                  { id: 'historique', name: 'Historique', icon: 'bi-clock-history', gradient: 'from-indigo-500 to-indigo-600', hover: 'hover:bg-indigo-50' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative flex-1 px-4 py-4 text-sm font-semibold flex items-center justify-center space-x-2 transition-all duration-300 group ${
                      activeTab === tab.id
                        ? `bg-gradient-to-r ${tab.gradient} text-white shadow-lg hover:shadow-xl transform -translate-y-0.5`
                        : `text-gray-600 ${tab.hover} hover:text-gray-800`
                    }`}
                  >
                    <i className={`${tab.icon} text-lg ${activeTab === tab.id ? 'animate-pulse' : 'group-hover:scale-110'} transition-transform duration-200`}></i>
                    <span className="hidden sm:inline font-medium">{tab.name}</span>
                    {activeTab === tab.id && (
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-white/30 rounded-full"></div>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Contenu de l'onglet avec animations - NOUVEAU: Réutilise le formulaire modernisé */}
            <div className="p-8">
              <div className="animate-fade-in">
                {/* Réutilisation du formulaire modernisé en mode consultation */}
                <div className="consultation-mode">
                  {activeTab === "general" && renderGeneralTabReadOnly()}
                  {activeTab === "prestations" && renderPrestationsTabReadOnly()}
                  {activeTab === "composition" && renderCompositionTabReadOnly()}
                  {activeTab === "descriptif" && renderDescriptifTabReadOnly()}
                  {activeTab === "confidentiel" && renderConfidentielTabReadOnly()}
                  {activeTab === "vente" && renderVenteTabReadOnly()}
                  {activeTab === "photos" && renderMediasTabReadOnly()}
                  {activeTab === "historique" && renderHistorique()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDashboard = () => {
    // Si thème Moderne, utiliser le nouveau dashboard v4 (refonte design 20/04/2026)
    if (isModerne) {
      const Dashboard = require('../Dashboard').default;
      return (
        <Dashboard 
          allPropertiesCounts={allPropertiesCounts}
          contactsCount={contactsTotal ?? contacts.length}
          contacts={contacts}
          demandesContactCount={demandesContactTotal ?? demandesContact.length}
          demandesContact={demandesContact}
          properties={properties}
          goToPropertiesList={goToPropertiesList}
          startNewProperty={startNewProperty}
          startNewContact={startNewContact}
          showContactsView={showContactsView}
          showDemandesContactView={showDemandesContactView}
          setSelectedDemande={setSelectedDemande}
          setShowDemandeDetail={setShowDemandeDetail}
          userProfile={userProfile}
        />
      );
    }
    
    return (
      <div className="min-h-screen bg-white relative">
        <div className="relative p-6">
          {/* Ultra-Modern Header */}
          <div className="ultra-modern-card glassmorphism rounded-3xl p-4 sm:p-6 md:p-8 mb-8 md:mb-12 border border-white/20 hover:shadow-2xl transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-purple-600/5 to-pink-600/5"></div>
            <div className="relative z-10 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 lg:gap-0">
              <div className="flex items-center space-x-3 sm:space-x-4 md:space-x-6">
                <div className="flex items-center space-x-3 sm:space-x-4 md:space-x-6">
                  <div className="relative">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500 rounded-2xl sm:rounded-3xl flex items-center justify-center text-white text-xl sm:text-2xl md:text-3xl font-bold shadow-2xl shadow-purple-500/25 neon-glow">
                      <i className="bi bi-speedometer2"></i>
                    </div>
                    <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full flex items-center justify-center animate-pulse">
                      <i className="bi bi-lightning-fill text-white text-xs sm:text-sm"></i>
                    </div>
                  </div>
                  <div>
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 bg-clip-text text-transparent mb-1">
                      Bienvenue {userProfile?.civilite || ''} {userProfile?.prenom || ''} {userProfile?.nom || ''}
                    </h1>
                    <p className="text-xs sm:text-sm md:text-base lg:text-xl text-gray-600 font-medium">Tableau de Bord - Intelligence immobilière</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 w-full sm:w-auto">
                <button 
                  onClick={() => startNewProperty()}
                  className="cutting-edge-button relative px-4 py-2 sm:px-6 sm:py-3 md:px-8 md:py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl sm:rounded-2xl text-sm sm:text-base font-bold shadow-2xl shadow-blue-500/25 hover:shadow-purple-500/40 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105"
                >
                  <i className="bi bi-plus-circle mr-1 sm:mr-2"></i>
                  Nouveau Bien
                </button>
                <button 
                  onClick={() => startNewContact()}
                  className="cutting-edge-button relative px-4 py-2 sm:px-6 sm:py-3 md:px-8 md:py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl sm:rounded-2xl text-sm sm:text-base font-bold shadow-2xl shadow-emerald-500/25 hover:shadow-teal-500/40 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105"
                >
                  <i className="bi bi-person-plus mr-1 sm:mr-2"></i>
                  Nouveau Contact
                </button>
              </div>
            </div>
          </div>

          {/* Ultra-Modern Statistics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 md:gap-8 mb-8 md:mb-12 stagger-container">
            {/* Biens actifs - Cliquable */}
            <div 
              onClick={() => goToPropertiesList('active')}
              className="ultra-modern-card group relative overflow-hidden glassmorphism rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-white/20 hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-500 cursor-pointer hover:scale-105 transform"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-blue-500/5 to-transparent"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-2xl shadow-blue-500/25 group-hover:scale-110 transition-transform duration-300">
                    <i className="bi bi-buildings text-lg sm:text-xl md:text-2xl"></i>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-blue-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
                <div className="space-y-2 sm:space-y-3">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 tabular-nums">
                    {allPropertiesCounts.active}
                  </div>
                  <div className="text-gray-600 text-sm font-semibold uppercase tracking-wider">Biens Actifs</div>
                  <div className="flex items-center text-emerald-600 text-sm font-medium">
                    <i className="bi bi-arrow-up-right mr-1"></i>
                    <span>+12% ce mois</span>
                  </div>
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>

            {/* Biens en brouillon - Cliquable */}
            <div 
              onClick={() => goToPropertiesList('draft')}
              className="ultra-modern-card group relative overflow-hidden glassmorphism rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-white/20 hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-500 cursor-pointer hover:scale-105 transform"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 via-purple-500/5 to-transparent"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-2xl shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                    <i className="bi bi-pencil-square text-xl sm:text-2xl"></i>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-purple-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
                <div className="space-y-2 sm:space-y-3">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 tabular-nums">
                    {allPropertiesCounts.draft}
                  </div>
                  <div className="text-gray-600 text-sm font-semibold uppercase tracking-wider">Brouillons</div>
                  <div className="flex items-center text-purple-600 text-sm font-medium">
                    <i className="bi bi-clock-history mr-1"></i>
                    <span>À compléter</span>
                  </div>
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>

            {/* Biens archivés - Cliquable */}
            <div 
              onClick={() => goToPropertiesList('archived')}
              className="ultra-modern-card group relative overflow-hidden glassmorphism rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-white/20 hover:shadow-2xl hover:shadow-amber-500/20 transition-all duration-500 cursor-pointer hover:scale-105 transform"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-amber-600/10 via-amber-500/5 to-transparent"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-2xl shadow-amber-500/25 group-hover:scale-110 transition-transform duration-300">
                    <i className="bi bi-archive text-2xl"></i>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="text-4xl font-bold text-gray-900 tabular-nums">
                    {allPropertiesCounts.archived}
                  </div>
                  <div className="text-gray-600 text-sm font-semibold uppercase tracking-wider">Biens Archivés</div>
                  <div className="flex items-center text-amber-600 text-sm font-medium">
                    <i className="bi bi-clock mr-1"></i>
                    <span>Archive: 2j</span>
                  </div>
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-amber-500 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>

            {/* Biens vendus - Cliquable */}
            <div 
              onClick={() => goToPropertiesList('sold')}
              className="ultra-modern-card group relative overflow-hidden glassmorphism rounded-3xl p-8 border border-white/20 hover:shadow-2xl hover:shadow-emerald-500/20 transition-all duration-500 cursor-pointer hover:scale-105 transform"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/10 via-emerald-500/5 to-transparent"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-2xl shadow-emerald-500/25 group-hover:scale-110 transition-transform duration-300">
                    <i className="bi bi-check-circle text-2xl"></i>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="text-4xl font-bold text-gray-900 tabular-nums">
                    {allPropertiesCounts.sold}
                  </div>
                  <div className="text-gray-600 text-sm font-semibold uppercase tracking-wider">Biens Vendus</div>
                  <div className="flex items-center text-emerald-600 text-sm font-medium">
                    <i className="bi bi-arrow-up-right mr-1"></i>
                    <span>+3 ce mois</span>
                  </div>
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>

            {/* Total contacts */}
            <div className="ultra-modern-card group relative overflow-hidden glassmorphism rounded-3xl p-8 border border-white/20 hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 via-purple-500/5 to-transparent"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-2xl shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                    <i className="bi bi-people text-2xl"></i>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="text-4xl font-bold text-gray-900 tabular-nums">{contacts.length}</div>
                  <div className="text-gray-600 text-sm font-semibold uppercase tracking-wider">Contacts</div>
                  <div className="flex items-center text-purple-600 text-sm font-medium">
                    <i className="bi bi-arrow-up-right mr-1"></i>
                    <span>+{contacts.filter(c => {
                      const createdDate = new Date(c.created_at);
                      const now = new Date();
                      const diffTime = Math.abs(now - createdDate);
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      return diffDays <= 30;
                    }).length} ce mois</span>
                  </div>
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-purple-500 to-violet-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>

            {/* Chiffre d'affaires */}
            <div className="ultra-modern-card group relative overflow-hidden glassmorphism rounded-3xl p-8 border border-white/20 hover:shadow-2xl hover:shadow-pink-500/20 transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-600/10 via-pink-500/5 to-transparent"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-2xl shadow-pink-500/25 group-hover:scale-110 transition-transform duration-300">
                    <i className="bi bi-currency-euro text-2xl"></i>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-3 h-3 bg-pink-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="text-4xl font-bold text-gray-900 tabular-nums">12.5M€</div>
                  <div className="text-gray-600 text-sm font-semibold uppercase tracking-wider">CA Potentiel</div>
                  <div className="flex items-center text-pink-600 text-sm font-medium">
                    <i className="bi bi-graph-up mr-1"></i>
                    <span>Projection 2025</span>
                  </div>
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-pink-500 to-rose-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
          </div>

          {/* Ultra-Modern Charts Section */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-12">
            {/* Advanced Property Type Distribution */}
            <div className="ultra-modern-card glassmorphism rounded-3xl p-8 border border-white/20 hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 via-purple-600/5 to-pink-600/5"></div>
              <div className="relative z-10">
                <div className="flex justify-between items-center mb-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-500/25">
                      <i className="bi bi-pie-chart text-xl"></i>
                    </div>
                    <div>
                      <h2 className="text-2xl font-semibold text-gray-900">Répartition par Type</h2>
                      <p className="text-gray-600 text-sm">Distribution intelligente</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {['bar', 'pie', 'doughnut'].map((type) => (
                      <button
                        key={type}
                        onClick={() => setChartType(type)}
                        className={`cutting-edge-button px-4 py-2 rounded-xl transition-all duration-300 ${
                          chartType === type 
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25' 
                            : 'bg-white/10 text-indigo-200 hover:bg-white/20'
                        }`}
                      >
                        <i className={`bi bi-${type === 'bar' ? 'bar-chart' : type === 'pie' ? 'pie-chart' : 'circle'}`}></i>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Ultra-modern chart visualization */}
                <div className="space-y-6">
                  {[
                    { name: 'Restaurant', count: properties.filter(p => p.bien_general?.type_bien?.includes('Restaurant')).length, color: 'from-blue-500 to-cyan-500', bg: 'bg-blue-500/10' },
                    { name: 'Commerce', count: properties.filter(p => p.bien_general?.type_bien?.includes('Commerce')).length, color: 'from-emerald-500 to-teal-500', bg: 'bg-emerald-500/10' },
                    { name: 'Bureau', count: properties.filter(p => p.bien_general?.type_bien?.includes('Bureau')).length, color: 'from-amber-500 to-orange-500', bg: 'bg-amber-500/10' }
                  ].map((item, index) => (
                    <div key={item.name} className={`group relative overflow-hidden ${item.bg} rounded-2xl p-6 border border-white/10 hover:shadow-xl transition-all duration-500`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 bg-gradient-to-r ${item.color} rounded-xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                            <span className="font-bold text-lg">{item.count}</span>
                          </div>
                          <div>
                            <div className="text-gray-900 font-bold text-lg">{item.name}</div>
                            <div className="text-gray-600 text-sm">{item.count} propriétés</div>
                          </div>
                        </div>
                        <div className="flex-1 mx-6">
                          <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                            <div 
                              className={`data-bar-fill h-full bg-gradient-to-r ${item.color} rounded-full transition-all duration-2000`}
                              style={{width: `${Math.max((item.count / Math.max(properties.length, 1)) * 100, 5)}%`}}
                            ></div>
                          </div>
                        </div>
                        <div className="text-2xl font-semibold text-gray-400 group-hover:text-gray-600 transition-colors duration-300">
                          {Math.round((item.count / Math.max(properties.length, 1)) * 100)}%
                        </div>
                      </div>
                      <div className={`absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r ${item.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Geographic Heat Map */}
            <div className="ultra-modern-card glassmorphism rounded-3xl p-8 border border-white/20 hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-rose-600/5 via-pink-600/5 to-orange-600/5"></div>
              <div className="relative z-10">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-orange-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-rose-500/25">
                    <i className="bi bi-geo-alt text-xl"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Heatmap Géographique</h2>
                    <p className="text-gray-600 text-sm">Zones de concentration</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {availableCities.slice(0, 5).map((city, index) => {
                    const cityCount = properties.filter(p => p.informations_confidentielles?.ville === city).length;
                    const percentage = properties.length > 0 ? (cityCount / properties.length * 100) : 0;
                    const colors = [
                      'from-red-500 to-rose-600',
                      'from-blue-500 to-indigo-600', 
                      'from-emerald-500 to-teal-600',
                      'from-amber-500 to-orange-600',
                      'from-purple-500 to-violet-600'
                    ];
                    const bgColors = [
                      'bg-red-500/10',
                      'bg-blue-500/10',
                      'bg-emerald-500/10', 
                      'bg-amber-500/10',
                      'bg-purple-500/10'
                    ];
                    
                    return (
                      <div key={city} className={`group relative overflow-hidden ${bgColors[index]} rounded-2xl p-6 border border-white/10 hover:shadow-xl transition-all duration-500 hover:-translate-y-1`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className={`w-12 h-12 bg-gradient-to-r ${colors[index]} rounded-xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                              <i className="bi bi-building text-lg"></i>
                            </div>
                            <div>
                              <div className="text-gray-900 font-bold text-lg">{city}</div>
                              <div className="text-gray-600 text-sm">{cityCount} biens</div>
                            </div>
                          </div>
                          <div className="flex-1 mx-6">
                            <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                              <div 
                                className={`data-bar-fill h-full bg-gradient-to-r ${colors[index]} rounded-full transition-all duration-2000`}
                                style={{width: `${Math.max(percentage, 5)}%`}}
                              ></div>
                            </div>
                          </div>
                          <div className="text-2xl font-semibold text-white opacity-20 group-hover:opacity-40 transition-opacity duration-300">
                            {Math.round(percentage)}%
                          </div>
                        </div>
                        <div className={`absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r ${colors[index]} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* AI-Powered Insights & News */}
          <div className="ultra-modern-card glassmorphism rounded-3xl p-8 border border-white/20 hover:shadow-2xl transition-all duration-500 mb-12">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/5 via-blue-600/5 to-indigo-600/5"></div>
            <div className="relative z-10">
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-2xl shadow-cyan-500/25 neon-glow">
                    <i className="bi bi-robot text-2xl"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Intelligence Artificielle</h2>
                    <p className="text-gray-600 text-sm">Insights automatisés et tendances</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 px-4 py-2 rounded-full border border-emerald-500/30">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                  <span className="text-emerald-600 text-sm font-semibold">IA Active</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="group bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl p-6 border border-blue-500/20 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-500">
                  <div className="flex items-center space-x-3 mb-4">
                    <i className="bi bi-trending-up text-blue-400 text-2xl"></i>
                    <h3 className="text-gray-900 font-medium">Tendance Marché</h3>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-4">Les prix des restaurants ont augmenté de 8% ce trimestre. Opportunité d'investissement détectée.</p>
                  <div className="flex items-center text-blue-400 text-sm font-medium group-hover:text-blue-300 transition-colors duration-300">
                    <i className="bi bi-arrow-right mr-1"></i>
                    <span>Voir l'analyse complète</span>
                  </div>
                </div>

                <div className="group bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-6 border border-purple-500/20 hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-500">
                  <div className="flex items-center space-x-3 mb-4">
                    <i className="bi bi-bullseye text-purple-400 text-2xl"></i>
                    <h3 className="text-gray-900 font-medium">Recommandation</h3>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-4">3 contacts correspondent parfaitement aux critères du bien #149d2d. Match automatique suggéré.</p>
                  <div className="flex items-center text-purple-400 text-sm font-medium group-hover:text-purple-300 transition-colors duration-300">
                    <i className="bi bi-arrow-right mr-1"></i>
                    <span>Lancer le matching</span>
                  </div>
                </div>

                <div className="group bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-2xl p-6 border border-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-500">
                  <div className="flex items-center space-x-3 mb-4">
                    <i className="bi bi-lightning text-emerald-400 text-2xl"></i>
                    <h3 className="text-gray-900 font-medium">Action Rapide</h3>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-4">Relancer le contact Jean Dupont - Dernière interaction il y a 15 jours. Taux de conversion +65%.</p>
                  <div className="flex items-center text-emerald-600 text-sm font-medium group-hover:text-emerald-700 transition-colors duration-300">
                    <i className="bi bi-arrow-right mr-1"></i>
                    <span>Envoyer un message</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Widget Dernières demandes de contact */}
          <div className="mb-8">
            <div className="ultra-modern-card glassmorphism rounded-3xl p-6 border border-white/20 hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 via-pink-600/5 to-rose-600/5"></div>
              <div className="relative z-10">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-purple-500/25">
                      <i className="bi bi-envelope-heart text-lg"></i>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Dernières demandes de contact</h2>
                      <p className="text-gray-600 text-xs">Prospects récents</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="bg-purple-500/20 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold border border-purple-500/30">
                      {demandesContact.filter(d => d.statut === 'en_attente' || d.statut === 'nouveau').length} nouvelles
                    </span>
                    <button
                      onClick={showDemandesContactView}
                      className="text-purple-600 hover:text-purple-700 text-sm font-semibold flex items-center gap-1"
                    >
                      Voir tout
                      <i className="bi bi-arrow-right"></i>
                    </button>
                  </div>
                </div>
                
                {demandesContact.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-2xl">
                    <i className="bi bi-inbox text-3xl text-gray-400 mb-2 block"></i>
                    <p className="text-gray-500 text-sm font-medium">Aucune demande pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {demandesContact.slice(0, 3).map((demande) => (
                      <div 
                        key={demande.id} 
                        className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-white/40 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 cursor-pointer"
                        onClick={() => {
                          setSelectedDemande(demande);
                          setShowDemandeDetail(true);
                        }}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                {demande.nom.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 text-sm">{demande.nom}</h4>
                                <p className="text-xs text-gray-500">
                                  {new Date(demande.created_at).toLocaleDateString('fr-FR', {
                                    day: '2-digit',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                            
                            <div className="ml-10 space-y-1">
                              <p className="text-xs text-gray-700 truncate">
                                <i className="bi bi-building mr-1 text-blue-600"></i>
                                {demande.bien_titre}
                              </p>
                              <p className="text-xs text-gray-600">
                                <i className="bi bi-tag mr-1"></i>
                                {demande.type_demande}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex flex-col items-end gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              demande.statut === 'nouveau' || demande.statut === 'en_attente'
                                ? 'bg-green-100 text-green-700 border border-green-300'
                                : demande.statut === 'en_cours'
                                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                : 'bg-gray-100 text-gray-700 border border-gray-300'
                            }`}>
                              {demande.statut === 'nouveau' || demande.statut === 'en_attente' ? 'Nouveau' : 
                               demande.statut === 'en_cours' ? 'En cours' : 'Traité'}
                            </span>
                            
                            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                              {demande.telephone && (
                                <a
                                  href={`tel:${demande.telephone}`}
                                  className="w-7 h-7 bg-green-500 hover:bg-green-600 rounded-lg flex items-center justify-center text-white text-xs transition-colors"
                                  title="Appeler"
                                >
                                  <i className="bi bi-telephone-fill"></i>
                                </a>
                              )}
                              <a
                                href={`mailto:${demande.email}`}
                                className="w-7 h-7 bg-[#0062CC] hover:bg-[#0056b3] rounded-lg flex items-center justify-center text-white text-xs transition-colors"
                                title="Email"
                              >
                                <i className="bi bi-envelope-fill"></i>
                              </a>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDemandeToAffect(demande);
                                  setShowAffectationModal(true);
                                  setAffectationSearch(demande.email);
                                  searchContactForAffectation(demande.email);
                                }}
                                className="w-7 h-7 bg-purple-500 hover:bg-purple-600 rounded-lg flex items-center justify-center text-white text-xs transition-colors"
                                title="Affecter à un contact"
                              >
                                <i className="bi bi-person-check-fill"></i>
                              </button>
                              {(demande.statut === 'nouveau' || demande.statut === 'en_attente') && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await marquerDemandeTraitee(demande.id, 'traité');
                                  }}
                                  className="w-7 h-7 bg-emerald-500 hover:bg-emerald-600 rounded-lg flex items-center justify-center text-white text-xs transition-colors"
                                  title="Marquer comme traité"
                                >
                                  <i className="bi bi-check-lg"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-purple-500 to-pink-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Dashboard Grid - Tâches, Agenda, Emails, Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-12">
            {/* Tâches à faire */}
            <div className="ultra-modern-card glassmorphism rounded-3xl p-6 border border-white/20 hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-indigo-600/5 to-purple-600/5"></div>
              <div className="relative z-10">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-500/25">
                      <i className="bi bi-check2-square text-lg"></i>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Mes tâches</h2>
                      <p className="text-gray-600 text-xs">Actions prioritaires</p>
                    </div>
                  </div>
                  <span className="bg-blue-500/20 text-blue-700 px-2 py-1 rounded-full text-xs font-semibold border border-blue-500/30">
                    {tasks.filter(t => t.status === 'pending').length} en attente
                  </span>
                </div>
                <div className="space-y-3">
                  {tasks.slice(0, 3).map((task) => (
                    <div key={task.id} className={`group relative overflow-hidden rounded-2xl p-4 border transition-all duration-500 hover:shadow-lg ${
                      task.priority === 'high' ? 'border-red-500/30 bg-red-500/10 hover:shadow-red-500/10' :
                      task.priority === 'medium' ? 'border-yellow-500/30 bg-yellow-500/10 hover:shadow-yellow-500/10' :
                      'border-gray-500/30 bg-gray-500/10 hover:shadow-gray-500/10'
                    }`}>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-gray-900 text-sm leading-tight">{task.title}</h4>
                        <span className={`w-3 h-3 rounded-full flex-shrink-0 ${
                          task.status === 'pending' ? 'bg-yellow-400 animate-pulse' :
                          task.status === 'in_progress' ? 'bg-blue-400' :
                          'bg-green-400'
                        }`}></span>
                      </div>
                      <p className="text-xs text-gray-700 mb-3 leading-relaxed">{task.description}</p>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-600">Échéance: {new Date(task.due_date).toLocaleDateString('fr-FR')}</span>
                        <span className={`px-2 py-1 rounded-full font-semibold ${
                          task.priority === 'high' ? 'bg-red-500/20 text-red-700 border border-red-500/30' :
                          task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-700 border border-yellow-500/30' :
                          'bg-gray-500/20 text-gray-700 border border-gray-500/30'
                        }`}>
                          {task.priority === 'high' ? 'Urgent' : 
                           task.priority === 'medium' ? 'Normal' : 'Faible'}
                        </span>
                      </div>
                      <div className={`absolute inset-x-0 bottom-0 h-1 transition-transform duration-500 transform scale-x-0 group-hover:scale-x-100 ${
                        task.priority === 'high' ? 'bg-gradient-to-r from-red-500 to-red-600' :
                        task.priority === 'medium' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                        'bg-gradient-to-r from-gray-500 to-gray-600'
                      }`}></div>
                    </div>
                  ))}
                </div>
                <button className="w-full mt-4 text-center text-blue-600 hover:text-blue-800 text-sm font-semibold transition-colors duration-300">
                  <i className="bi bi-plus-circle mr-1"></i>
                  Voir toutes les tâches
                </button>
              </div>
            </div>

            {/* Agenda - Prochains rendez-vous */}
            <div className="ultra-modern-card glassmorphism rounded-3xl p-6 border border-white/20 hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-green-600/5 via-emerald-600/5 to-teal-600/5"></div>
              <div className="relative z-10">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-green-500/25">
                      <i className="bi bi-calendar-check text-lg"></i>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Agenda</h2>
                      <p className="text-gray-600 text-xs">Prochains RDV</p>
                    </div>
                  </div>
                  <span className="bg-green-500/20 text-green-700 px-2 py-1 rounded-full text-xs font-semibold border border-green-500/30">
                    {agendaEvents.length} RDV
                  </span>
                </div>
                <div className="space-y-3">
                  {agendaEvents.slice(0, 3).map((event) => (
                    <div key={event.id} className={`group relative overflow-hidden rounded-2xl p-4 border transition-all duration-500 hover:shadow-lg ${
                      event.type === 'visite' ? 'border-green-500/30 bg-green-500/10 hover:shadow-green-500/10' :
                      event.type === 'signature' ? 'border-blue-500/30 bg-blue-500/10 hover:shadow-blue-500/10' :
                      'border-yellow-500/30 bg-yellow-500/10 hover:shadow-yellow-500/10'
                    }`}>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-gray-900 text-sm leading-tight">{event.title}</h4>
                        <i className={`text-lg ${
                          event.type === 'visite' ? 'bi-eye text-green-400' :
                          event.type === 'signature' ? 'bi-pen text-blue-400' :
                          'bi-presentation text-yellow-400'
                        }`}></i>
                      </div>
                      <p className="text-xs text-gray-700 mb-3 leading-relaxed">{event.description}</p>
                      <div className="flex justify-between items-center text-xs text-gray-600 mb-2">
                        <span>{new Date(event.start_time).toLocaleDateString('fr-FR')} à {new Date(event.start_time).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}</span>
                        <span className="font-semibold text-gray-900">{event.client}</span>
                      </div>
                      {event.property_ref !== 'Multiple' && (
                        <div className="text-xs text-gray-600">
                          Réf: {event.property_ref}
                        </div>
                      )}
                      <div className={`absolute inset-x-0 bottom-0 h-1 transition-transform duration-500 transform scale-x-0 group-hover:scale-x-100 ${
                        event.type === 'visite' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                        event.type === 'signature' ? 'bg-gradient-to-r from-blue-500 to-indigo-500' :
                        'bg-gradient-to-r from-yellow-500 to-orange-500'
                      }`}></div>
                    </div>
                  ))}
                </div>
                <button className="w-full mt-4 text-center text-green-600 hover:text-green-800 text-sm font-semibold transition-colors duration-300">
                  <i className="bi bi-calendar-plus mr-1"></i>
                  Voir l'agenda complet
                </button>
              </div>
            </div>

            {/* Derniers emails reçus */}
            <div className="ultra-modern-card glassmorphism rounded-3xl p-6 border border-white/20 hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 via-purple-600/5 to-pink-600/5"></div>
              <div className="relative z-10">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-500/25">
                      <i className="bi bi-envelope text-lg"></i>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Emails</h2>
                      <p className="text-gray-600 text-xs">Messages récents</p>
                    </div>
                  </div>
                  <span className="bg-indigo-500/20 text-indigo-700 px-2 py-1 rounded-full text-xs font-semibold border border-indigo-500/30">
                    {recentEmails.filter(e => !e.read).length} non lus
                  </span>
                </div>
                <div className="space-y-3">
                  {recentEmails.slice(0, 4).map((email) => (
                    <div key={email.id} className={`group relative overflow-hidden rounded-2xl p-4 border cursor-pointer transition-all duration-500 hover:shadow-lg ${
                      !email.read ? 'border-indigo-500/30 bg-indigo-500/10 hover:shadow-indigo-500/10' : 'border-gray-500/30 bg-gray-500/10 hover:shadow-gray-500/10'
                    }`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${
                            !email.read ? 'bg-indigo-400 animate-pulse' : 'bg-gray-400'
                          }`}></div>
                          <h4 className={`text-sm leading-tight ${
                            !email.read ? 'font-bold text-gray-900' : 'font-medium text-gray-700'
                          }`}>
                            {email.from.split('@')[0]}
                          </h4>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                          email.priority === 'high' ? 'bg-red-500/20 text-red-700 border border-red-500/30' :
                          email.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-700 border border-yellow-500/30' :
                          'bg-gray-500/20 text-gray-700 border border-gray-500/30'
                        }`}>
                          {email.priority === 'high' ? '!' : email.priority === 'medium' ? '•' : '·'}
                        </span>
                      </div>
                      <h5 className={`text-sm mb-1 leading-tight ${
                        !email.read ? 'font-bold text-gray-900' : 'font-medium text-gray-700'
                      }`}>
                        {email.subject.length > 25 ? email.subject.slice(0, 25) + '...' : email.subject}
                      </h5>
                      <p className="text-xs text-gray-600 leading-relaxed mb-2">
                        {email.preview.length > 50 ? email.preview.slice(0, 50) + '...' : email.preview}
                      </p>
                      <div className="flex justify-between items-center text-xs text-gray-600">
                        <span>{new Date(email.date).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}</span>
                        {email.property_ref && (
                          <span className="font-semibold text-blue-400">{email.property_ref}</span>
                        )}
                      </div>
                      <div className={`absolute inset-x-0 bottom-0 h-1 transition-transform duration-500 transform scale-x-0 group-hover:scale-x-100 ${
                        !email.read ? 'bg-gradient-to-r from-indigo-500 to-purple-500' : 'bg-gradient-to-r from-gray-500 to-gray-600'
                      }`}></div>
                    </div>
                  ))}
                </div>
                <button className="w-full mt-4 text-center text-indigo-600 hover:text-indigo-800 text-sm font-semibold transition-colors duration-300">
                  <i className="bi bi-envelope-open mr-1"></i>
                  Voir tous les emails
                </button>
              </div>
            </div>

            {/* Performance et statistiques */}
            <div className="ultra-modern-card glassmorphism rounded-3xl p-6 border border-white/20 hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 via-pink-600/5 to-rose-600/5"></div>
              <div className="relative z-10">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-purple-500/25">
                      <i className="bi bi-graph-up text-lg"></i>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Performance</h2>
                      <p className="text-gray-600 text-xs">Ce mois</p>
                    </div>
                  </div>
                  <span className="bg-purple-500/20 text-purple-700 px-2 py-1 rounded-full text-xs font-semibold border border-purple-500/30">
                    Mois actuel
                  </span>
                </div>
                <div className="space-y-4">
                  <div className="group flex items-center justify-between p-3 bg-purple-500/10 rounded-2xl border border-purple-500/20 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-500">
                    <div className="flex items-center space-x-3">
                      <i className="bi bi-eye text-purple-400 text-lg group-hover:scale-110 transition-transform duration-300"></i>
                      <span className="text-sm font-semibold text-gray-700">Visites programmées</span>
                    </div>
                    <span className="text-lg font-semibold text-purple-600 tabular-nums">12</span>
                  </div>
                  <div className="group flex items-center justify-between p-3 bg-green-500/10 rounded-2xl border border-green-500/20 hover:shadow-lg hover:shadow-green-500/10 transition-all duration-500">
                    <div className="flex items-center space-x-3">
                      <i className="bi bi-handshake text-green-400 text-lg group-hover:scale-110 transition-transform duration-300"></i>
                      <span className="text-sm font-semibold text-gray-700">Mandats signés</span>
                    </div>
                    <span className="text-lg font-semibold text-green-600 tabular-nums">3</span>
                  </div>
                  <div className="group flex items-center justify-between p-3 bg-blue-500/10 rounded-2xl border border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-500">
                    <div className="flex items-center space-x-3">
                      <i className="bi bi-telephone text-blue-400 text-lg group-hover:scale-110 transition-transform duration-300"></i>
                      <span className="text-sm font-semibold text-gray-700">Appels effectués</span>
                    </div>
                    <span className="text-lg font-semibold text-blue-600 tabular-nums">47</span>
                  </div>
                  <div className="group flex items-center justify-between p-3 bg-yellow-500/10 rounded-2xl border border-yellow-500/20 hover:shadow-lg hover:shadow-yellow-500/10 transition-all duration-500">
                    <div className="flex items-center space-x-3">
                      <i className="bi bi-star text-yellow-400 text-lg group-hover:scale-110 transition-transform duration-300"></i>
                      <span className="text-sm font-semibold text-gray-700">Score satisfaction</span>
                    </div>
                    <span className="text-lg font-semibold text-yellow-600 tabular-nums">4.8/5</span>
                  </div>
                </div>
                <button className="w-full mt-4 text-center text-purple-600 hover:text-purple-800 text-sm font-semibold transition-colors duration-300">
                  <i className="bi bi-bar-chart mr-1"></i>
                  Voir le rapport complet
                </button>
              </div>
            </div>
          </div>

          {/* Actualités et Messages Super Admin */}
          <div className="ultra-modern-card glassmorphism rounded-3xl p-8 border border-white/20 hover:shadow-2xl transition-all duration-500 mb-12">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-600/5 via-amber-600/5 to-yellow-600/5"></div>
            <div className="relative z-10">
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-2xl shadow-orange-500/25">
                    <i className="bi bi-megaphone text-2xl"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Actualités & Messages</h2>
                    <p className="text-gray-600 text-sm">Communications importantes</p>
                  </div>
                </div>
                <span className="bg-orange-500/20 text-orange-700 px-3 py-2 rounded-full text-sm font-semibold border border-orange-500/30">
                  {news.length} nouveaux
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {news.slice(0, 3).map((item) => (
                  <div key={item.id} className={`group relative overflow-hidden rounded-2xl p-6 border transition-all duration-500 hover:shadow-xl ${
                    item.priority === 'high' ? 'border-red-500/30 bg-red-500/10 hover:shadow-red-500/10' :
                    item.priority === 'medium' ? 'border-yellow-500/30 bg-yellow-500/10 hover:shadow-yellow-500/10' :
                    'border-blue-500/30 bg-blue-500/10 hover:shadow-blue-500/10'
                  }`}>
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-semibold text-gray-900 text-lg leading-tight">{item.title}</h3>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          item.type === 'feature' ? 'bg-green-500/20 text-green-700 border border-green-500/30' :
                          item.type === 'update' ? 'bg-blue-500/20 text-blue-700 border border-blue-500/30' :
                          'bg-gray-500/20 text-gray-700 border border-gray-500/30'
                        }`}>
                          {item.type === 'feature' ? 'Nouveauté' : 
                           item.type === 'update' ? 'Mise à jour' : 'Info'}
                        </span>
                        <span className="text-xs text-gray-600">
                          {new Date(item.date).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed mb-4">{item.content}</p>
                    <div className="text-xs text-gray-600">
                      Par {item.author}
                    </div>
                    <div className={`absolute inset-x-0 bottom-0 h-1 transition-transform duration-500 transform scale-x-0 group-hover:scale-x-100 ${
                      item.priority === 'high' ? 'bg-gradient-to-r from-red-500 to-red-600' :
                      item.priority === 'medium' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                      'bg-gradient-to-r from-blue-500 to-indigo-500'
                    }`}></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section rapide: derniers biens ajoutés et modifications avec "voir plus" */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-12">
            <div className="ultra-modern-card glassmorphism rounded-3xl p-8 border border-white/20 hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-indigo-600/5 to-purple-600/5"></div>
              <div className="relative z-10">
                <div className="flex justify-between items-center mb-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-500/25">
                      <i className="bi bi-building text-xl"></i>
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Derniers biens ajoutés</h2>
                      <p className="text-gray-600 text-sm">Nouveaux dans le système</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-700 bg-blue-500/10 px-3 py-2 rounded-full border border-blue-500/20">{properties.length} total</span>
                </div>
                <div className="space-y-4">
                  {properties.slice(0, showMoreProperties).map((property, index) => (
                    <div key={property.id} className="group relative overflow-hidden bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-500 cursor-pointer hover:-translate-y-1">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          {property.medias?.photos && property.medias.photos.length > 0 ? (
                            <img
                              src={getMediaUrl(property.medias.photos[0])}
                              alt="Propriété"
                              className="w-12 h-12 object-cover rounded-xl group-hover:scale-110 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <i className="bi bi-building text-blue-400 text-xl"></i>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-gray-900 text-sm truncate">
                            {property.enseigne?.enseigne || property.bien_general?.type_bien || 'Sans nom'}
                          </div>
                          <div className="text-sm text-gray-700 truncate">
                            {property.bien_general?.type_bien} • {property.informations_confidentielles?.ville || 'Ville non renseignée'}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Ajouté le {new Date(property.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-lg font-semibold text-emerald-600 tabular-nums">
                            {property.honoraires_calcul?.prix_vente ? `${property.honoraires_calcul.prix_vente.toLocaleString()}€` : 'Prix NC'}
                          </div>
                          <div className="text-xs text-gray-600">
                            Réf: {property.reference || property.id.slice(-6)}
                          </div>
                        </div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                    </div>
                  ))}
                </div>
                {properties.length > showMoreProperties && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={() => setShowMoreProperties(prev => prev + 5)}
                      className="cutting-edge-button bg-gradient-to-r from-blue-500 to-indigo-600 hover:shadow-lg hover:shadow-blue-500/25 text-white px-6 py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <i className="bi bi-plus-circle mr-2"></i>
                      Voir 5 de plus ({properties.length - showMoreProperties} restants)
                    </button>
                  </div>
                )}
                {showMoreProperties > 5 && (
                  <div className="mt-3 text-center">
                    <button
                      onClick={() => setShowMoreProperties(5)}
                      className="text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors duration-300"
                    >
                      Réduire la liste
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Derniers contacts avec "voir plus" */}
            <div className="ultra-modern-card glassmorphism rounded-3xl p-8 border border-white/20 hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/5 via-teal-600/5 to-green-600/5"></div>
              <div className="relative z-10">
                <div className="flex justify-between items-center mb-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-emerald-500/25">
                      <i className="bi bi-people text-xl"></i>
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Derniers contacts</h2>
                      <p className="text-gray-600 text-sm">Nouveaux prospects</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-700 bg-emerald-500/10 px-3 py-2 rounded-full border border-emerald-500/20">{contacts.length} total</span>
                </div>
                <div className="space-y-4">
                  {contacts.slice(0, showMoreContacts).map((contact, index) => (
                    <div key={contact.id} className="group relative overflow-hidden bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-500 cursor-pointer hover:-translate-y-1">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <i className="bi bi-person text-emerald-400 text-xl"></i>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-gray-900 text-sm">
                            {contact.prenom} {contact.nom}
                          </div>
                          <div className="text-sm text-gray-700 truncate">
                            {contact.type_contact} • {contact.ville || 'Ville non renseignée'}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Ajouté le {new Date(contact.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-bold text-emerald-600">
                            {contact.criteres_recherche?.budget_max ? `Budget: ${contact.criteres_recherche.budget_max.toLocaleString()}€` : 'Budget NC'}
                          </div>
                          <div className="text-xs text-gray-600">
                            {contact.negociateur_id || 'Non assigné'}
                          </div>
                        </div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                    </div>
                  ))}
                </div>
                {contacts.length > showMoreContacts && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={() => setShowMoreContacts(prev => prev + 5)}
                      className="cutting-edge-button bg-gradient-to-r from-emerald-500 to-teal-600 hover:shadow-lg hover:shadow-emerald-500/25 text-white px-6 py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <i className="bi bi-plus-circle mr-2"></i>
                      Voir 5 de plus ({contacts.length - showMoreContacts} restants)
                    </button>
                  </div>
                )}
                {showMoreContacts > 5 && (
                  <div className="mt-3 text-center">
                    <button
                      onClick={() => setShowMoreContacts(5)}
                      className="text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors duration-300"
                    >
                      Réduire la liste
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Dernières modifications de biens */}
          <div className="ultra-modern-card glassmorphism rounded-3xl p-8 border border-white/20 hover:shadow-2xl transition-all duration-500 mb-12">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 via-pink-600/5 to-rose-600/5"></div>
            <div className="relative z-10">
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-2xl shadow-purple-500/25">
                    <i className="bi bi-pencil-square text-2xl"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Dernières modifications</h2>
                    <p className="text-gray-600 text-sm">Biens récemment modifiés</p>
                  </div>
                </div>
                <span className="text-sm text-gray-700 bg-purple-500/10 px-3 py-2 rounded-full border border-purple-500/20">
                  {properties.filter(p => p.updated_at).length} modifiés
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties
                  .filter(p => p.updated_at) // Filtrer les biens qui ont été modifiés
                  .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)) // Trier par date de modification
                  .slice(0, showMoreModified)
                  .map((property) => (
                  <div key={property.id} className="group relative overflow-hidden bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-500 cursor-pointer hover:-translate-y-2" onClick={() => showPropertyForm(property)}>
                    <div className="flex items-start justify-between mb-4">
                      <h4 className="font-bold text-gray-900 text-lg leading-tight">
                        {property.enseigne?.enseigne || property.bien_general?.type_bien || 'Sans nom'}
                      </h4>
                      <span className="text-xs text-purple-700 bg-purple-500/20 px-3 py-1 rounded-full font-semibold border border-purple-500/30">
                        Modifié
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 mb-4">
                      {property.bien_general?.type_bien} • {property.informations_confidentielles?.ville || 'Ville NC'}
                    </div>
                    <div className="flex justify-between items-center text-sm mb-4">
                      <span className="text-gray-600">Modifié le {new Date(property.updated_at).toLocaleDateString('fr-FR')}</span>
                      <span className="font-semibold text-purple-600 tabular-nums">
                        {property.honoraires_calcul?.prix_vente ? `${property.honoraires_calcul.prix_vente.toLocaleString()}€` : 'Prix NC'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">
                      Réf: {property.reference || property.id.slice(-6)}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-purple-500 to-pink-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                  </div>
                ))}
              </div>
              {properties.filter(p => p.updated_at).length > showMoreModified && (
                <div className="mt-8 text-center">
                  <button
                    onClick={() => setShowMoreModified(prev => prev + 6)}
                    className="cutting-edge-button bg-gradient-to-r from-purple-500 to-pink-600 hover:shadow-lg hover:shadow-purple-500/25 text-white px-6 py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <i className="bi bi-plus-circle mr-2"></i>
                    Voir plus de modifications
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Carousel */}
          <div className="ultra-modern-card glassmorphism rounded-3xl p-8 border border-white/20">
            <div className="absolute inset-0 bg-gradient-to-br from-violet-600/5 via-fuchsia-600/5 to-pink-600/5"></div>
            <div className="relative z-10">
              <div className="flex items-center space-x-4 mb-8">
                <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-violet-500/25">
                  <i className="bi bi-lightning-charge text-xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-white">Actions Rapides</h2>
                  <p className="text-violet-200 text-sm">Raccourcis intelligents</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {[
                  { icon: 'bi-list-ul', label: 'Liste Biens', color: 'from-blue-500 to-cyan-500', action: () => {setShowContacts(false); setShowForm(false); setShowDashboard(false); setActiveSection('biens');} },
                  { icon: 'bi-people', label: 'Annuaire', color: 'from-emerald-500 to-teal-500', action: showContactsView },
                  { icon: 'bi-file-pdf', label: 'Documents', color: 'from-purple-500 to-violet-500', action: showDocumentsView },
                  { icon: 'bi-bar-chart', label: 'Rapports', color: 'from-amber-500 to-orange-500', action: () => {} },
                  { icon: 'bi-calendar', label: 'Agenda', color: 'from-pink-500 to-rose-500', action: () => {} },
                  { icon: 'bi-gear', label: 'Paramètres', color: 'from-indigo-500 to-purple-500', action: () => {} }
                ].map((item, index) => (
                  <button
                    key={index}
                    onClick={item.action}
                    className={`cutting-edge-button group relative overflow-hidden bg-gradient-to-r ${item.color} rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105`}
                  >
                    <div className="flex flex-col items-center space-y-3">
                      <i className={`${item.icon} text-3xl group-hover:scale-110 transition-transform duration-300`}></i>
                      <span className="text-sm font-bold">{item.label}</span>
                    </div>
                    <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Navigation helpers
  const showDashboardView = () => {
    setShowDashboard(true);
    setShowContacts(false);
    setShowForm(false);
    setShowConsultation(false);
    setShowDocuments(false);
    setShowMarketing(false);
    setShowOutils(false);
    setShowAvisDeValeur(false);  // Reset avis de valeur view
    setShowSettings(false);  // Reset settings view
    setShowProfile(false);  // Reset profile view
    setActiveDropdown(null);
    setActiveSection('dashboard');
    navigate('/dashboard'); // Update URL
    
    // Charger les demandes de contact si pas déjà chargées
    if (demandesContact.length === 0) {
      loadDemandesContactApercu();
    }
  };

  const showContactsView = () => {
    console.log('🔧 Contacts button clicked');
    setShowArchivedContacts(false); // Always show active contacts when clicking header button
    loadContacts("active"); // Reload active contacts
    setShowContacts(true);
    setShowContactForm(false); // Fermer le formulaire de contact
    setCurrentContact(null); // Réinitialiser le contact actuel
    setActiveContactTab('general'); // Réinitialiser l'onglet actif
    setSelectedPresentedProperties([]); // Vider la sélection des biens présentés
    setShowDashboard(false);
    setShowForm(false);
    setShowMarketing(false);
    setShowOutils(false);
    setShowAvisDeValeur(false);  // Reset avis de valeur view
    setShowSettings(false);  // Reset settings view
    setShowProfile(false);  // Reset profile view
    setActiveSection('contacts'); // IMPORTANT : Sortir de la section 'demandes-contact'
    navigate('/contacts'); // Update URL
  };
  
  const showDemandesContactView = () => {
    console.log('📧 Demandes de contact button clicked → navigate /demandes-contact');
    // ⚠️ On utilise le router pour aller sur la NOUVELLE page ContactRequests
    // (rendue par App.js au path /demandes-contact), pas l'ancienne vue interne.
    navigate('/demandes-contact');
  };

  const showDocumentsView = () => {
    console.log('🔧 Documents button clicked');
    setShowDocuments(true);
    setShowDashboard(false);
    setShowContacts(false);
    setShowForm(false);
    setShowConsultation(false);
    setShowMarketing(false);
    setShowOutils(false);
    setShowAvisDeValeur(false);  // Reset avis de valeur view
    setActiveDropdown(null);
    setActiveSection('documents');
  };

  // AJ1 — `limite` borne le chargement à ce que l'écran affiche. Sans limite, comportement
  // d'avant : carnet complet (c'est ce dont la vue Contacts de ce composant a besoin).
  const loadContacts = async (status = "active", limite = null) => {
    try {
      console.log('Loading contacts with status:', status, limite ? `(limite ${limite})` : '(carnet complet)');
      const response = await axios.get(
        API + `/contacts?status=${status}` + (limite ? `&limit=${limite}` : ''));
      const contactsData = response.data;
      console.log('Contacts loaded:', contactsData.length);
      
      // Sort contacts by creation date (newest first)
      const sortedContacts = contactsData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setContacts(sortedContacts);
      setContactsCarnetComplet(!limite);
      if (limite) {
        return sortedContacts;  // aperçu : on ne touche pas aux villes, calculées sur le carnet
      }
      
      // Extract unique cities from contacts for filtering
      const cities = new Set();
      contactsData.forEach(contact => {
        if (contact.ville && contact.ville.trim()) {
          cities.add(contact.ville.trim());
        }
        // Also check criteres_recherche.villes
        if (contact.criteres_recherche && contact.criteres_recherche.villes) {
          contact.criteres_recherche.villes.forEach(ville => {
            if (ville && ville.trim()) {
              cities.add(ville.trim());
            }
          });
        }
      });
      
      // Convert to sorted array and update available cities
      const cityArray = Array.from(cities).sort();
      setAvailableContactCities(cityArray);
      console.log('Available contact cities loaded:', cityArray);
      return sortedContacts;
    } catch (error) {
      console.error("Erreur lors du chargement des contacts:", error);
      // Set empty arrays on error to prevent undefined states
      setContacts([]);
      setAvailableContactCities([]);
      return [];
    }
  };

  // AJ1 — aperçu du tableau de bord : 20 contacts (5 affichés, marge pour « voir plus ») et
  // le total demandé au serveur. Le carnet entier faisait 215 Ko et 11 s en production pour
  // afficher cinq noms, et ce préchargement part sur CHAQUE écran (composant monté par la
  // route attrape-tout). La vue Contacts, elle, continue de charger le carnet complet.
  const loadContactsApercu = async () => {
    await loadContacts("active", CONTACTS_APERCU_DASHBOARD);
    try {
      const { data } = await axios.get(`${API}/contacts/count?status=active`);
      setContactsTotal(data?.count ?? null);
    } catch (error) {
      console.error("Erreur lors du comptage des contacts:", error);
      setContactsTotal(null);
    }
  };

  const loadContactTypesAndGroups = async () => {
    try {
      const response = await axios.get(`${API}/contacts/types/groupes`);
      setContactTypes(response.data.contact_types);
      setContactGroups(response.data.groupes);
    } catch (error) {
      console.error("Erreur lors du chargement des types et groupes:", error);
    }
  };
  
  // Charger les demandes de contact
  const loadDemandesContact = async () => {
    try {
      console.log('📧 Chargement des demandes de contact...');
      // Charger les demandes actives (non archivées)
      const response = await axios.get(`${API}/contacts-agence`, { headers: getAuthHeaders() });
      const demandes = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setDemandesContact(demandes);
      console.log('📧 Demandes actives chargées:', demandes.length);
      
      // Charger les demandes archivées
      const responseArchived = await axios.get(`${API}/contacts-agence?include_archived=true`, { headers: getAuthHeaders() });
      const archived = responseArchived.data
        .filter(d => d.archived === true)
        .sort((a, b) => new Date(b.archived_at || b.created_at) - new Date(a.archived_at || a.created_at));
      setDemandesArchivees(archived);
      console.log('📧 Demandes archivées chargées:', archived.length);
    } catch (error) {
      console.error("❌ Erreur lors du chargement des demandes:", error);
      setDemandesContact([]);
      setDemandesArchivees([]);
    }
  };
  
  // Aperçu borné pour le tableau de bord : trois demandes sont affichées, on en charge 20,
  // le total vient du compteur serveur, et les archivées ne sont PAS chargées ici (elles ne
  // servent qu'à la vue Demandes). Deux appels non bornés partaient sur chaque écran.
  const loadDemandesContactApercu = async () => {
    try {
      const { data } = await axios.get(`${API}/contacts-agence?limit=${DEMANDES_APERCU_DASHBOARD}`,
        { headers: getAuthHeaders() });
      setDemandesContact((data || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      const compte = await axios.get(`${API}/contacts-agence/count`, { headers: getAuthHeaders() });
      setDemandesContactTotal(compte.data?.count ?? null);
    } catch (error) {
      console.error("❌ Erreur lors du chargement de l'aperçu des demandes:", error);
      setDemandesContact([]);
    }
  };

  // Rechercher un contact pour affectation (par email ou nom)
  const searchContactForAffectation = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setAffectationResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/contacts?status=active`);
      const allContacts = response.data;
      
      // Filtrer par email ou nom/prénom
      const results = allContacts.filter(contact => {
        const email = contact.emails?.[0]?.email?.toLowerCase() || '';
        const nom = contact.nom?.toLowerCase() || '';
        const prenom = contact.prenom?.toLowerCase() || '';
        const fullName = `${prenom} ${nom}`.toLowerCase();
        const search = searchTerm.toLowerCase();
        
        return email.includes(search) || nom.includes(search) || prenom.includes(search) || fullName.includes(search);
      });
      
      setAffectationResults(results.slice(0, 10)); // Limiter à 10 résultats
    } catch (error) {
      console.error("Erreur recherche contact:", error);
      setAffectationResults([]);
    }
  };
  
  // Affecter le bien au contact (nouveau ou existant)
  const affecterBienAContact = async () => {
    if (!demandeToAffect) return;
    
    try {
      // Préparer le payload avec le contact_id si un contact est sélectionné
      const payload = selectedContactForAffectation 
        ? { contact_id: selectedContactForAffectation.id }
        : {};
      
      const response = await axios.post(
        `${API}/contacts-agence/${demandeToAffect.id}/creer-contact`,
        payload,
        { headers: getAuthHeaders() }
      );
      
      if (response.data.action === 'existing') {
        alert(`✅ Bien affecté à ${selectedContactForAffectation.prenom} ${selectedContactForAffectation.nom} !\n\nLe bien a été ajouté à ses biens présentés.`);
      } else {
        alert(`✅ Nouveau contact créé et bien affecté !`);
      }
      
      // Recharger les demandes
      await loadDemandesContact();
      
      // Fermer le modal
      setShowAffectationModal(false);
      setDemandeToAffect(null);
      setAffectationSearch('');
      setAffectationResults([]);
      setSelectedContactForAffectation(null);
      
      // Ouvrir le contact dans l'onglet "Biens"
      const contact = await axios.get(`${API}/contacts/${response.data.contact_id}`);
      setCurrentContact(contact.data);
      setShowContactForm(true);
      setShowContacts(true);
      setShowDashboard(false);
      setActiveContactTab("biens-presentes");
      setActiveSection('contacts');
    } catch (error) {
      console.error('Erreur affectation:', error);
      alert('❌ Erreur lors de l\'affectation du bien');
    }
  };
  
  // Marquer une demande avec un statut
  const marquerDemandeTraitee = async (demandeId, statut = 'traité') => {
    try {
      await axios.put(`${API}/contacts-agence/${demandeId}/statut`, { statut }, { headers: getAuthHeaders() });
      // Recharger les demandes
      await loadDemandesContact();
      // Recharger aussi les demandes du bien si on est sur l'onglet
      if (currentProperty) {
        await loadDemandesBien(currentProperty.numero_mandat || currentProperty.id);
      }
    } catch (error) {
      console.error("❌ Erreur:", error);
      alert('Erreur lors de la mise à jour');
    }
  };
  

  // Supprimer une demande
  const supprimerDemande = async (demandeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement cette demande ?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/contacts-agence/${demandeId}`, { headers: getAuthHeaders() });
      alert('✅ Demande supprimée');
      // Recharger les demandes
      await loadDemandesContact();
    } catch (error) {
      console.error('Erreur suppression demande:', error);
      alert('❌ Erreur lors de la suppression');
    }
  };
  
  // Archiver une demande
  const archiverDemande = async (demandeId) => {
    if (!window.confirm('Archiver cette demande ? Elle sera masquée de la liste.')) {
      return;
    }
    
    try {
      await axios.patch(`${API}/contacts-agence/${demandeId}/archiver`, null, { headers: getAuthHeaders() });
      alert('✅ Demande archivée');
      // Recharger les demandes
      await loadDemandesContact();
    } catch (error) {
      console.error('Erreur archivage demande:', error);
      alert('❌ Erreur lors de l\'archivage');
    }
  };
  
  // Désarchiver une demande
  const desarchiverDemande = async (demandeId) => {
    if (!window.confirm('Désarchiver cette demande ?')) {
      return;
    }
    
    try {
      await axios.patch(`${API}/contacts-agence/${demandeId}/desarchiver`, null, { headers: getAuthHeaders() });
      alert('✅ Demande désarchivée');
      // Recharger les demandes
      await loadDemandesContact();
    } catch (error) {
      console.error('Erreur désarchivage demande:', error);
      alert('❌ Erreur lors du désarchivage');
    }
  };

  // Charger les demandes pour un bien spécifique
  const loadDemandesBien = async (propertyReference) => {
    try {
      console.log('📧 Chargement des demandes pour le bien:', propertyReference);
      const response = await axios.get(`${API}/contacts-agence/bien/${propertyReference}`, { headers: getAuthHeaders() });
      setDemandesBien(response.data);
      console.log('📧 Demandes du bien chargées:', response.data.length);
    } catch (error) {
      console.error("❌ Erreur chargement demandes bien:", error);
      setDemandesBien([]);
    }
  };
  
  // Rendu de l'onglet Demandes dans la fiche bien
  const renderDemandesTab = () => (
    <div className="p-6 bg-white">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Demandes reçues pour ce bien</h3>
        <p className="text-sm text-gray-600">
          Historique de toutes les demandes de contact concernant ce bien
        </p>
      </div>
      
      <button
        onClick={() => loadDemandesBien(currentProperty.numero_mandat || currentProperty.id)}
        className="mb-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
      >
        <i className="bi bi-arrow-clockwise mr-2"></i>
        Actualiser
      </button>
      
      {demandesBien.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <i className="bi bi-inbox text-4xl text-gray-400 mb-3 block"></i>
          <p className="text-gray-600">Aucune demande reçue pour ce bien</p>
        </div>
      ) : (
        <div className="space-y-4">
          {demandesBien.map(demande => (
            <div key={demande.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-gray-900">{demande.nom}</h4>
                  <p className="text-sm text-gray-600">
                    {new Date(demande.created_at).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded text-xs font-semibold ${
                  demande.statut === 'en_attente' || demande.statut === 'nouveau'
                    ? 'bg-orange-100 text-orange-700'
                    : demande.statut === 'en_cours'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {demande.statut === 'en_attente' || demande.statut === 'nouveau' ? 'En attente' :
                   demande.statut === 'en_cours' ? 'En cours' : 'Traité'}
                </span>
              </div>
              
              <div className="flex items-center gap-4 mb-2 text-sm text-gray-600">
                <span><i className="bi bi-envelope mr-1"></i>{demande.email}</span>
                {demande.telephone && <span><i className="bi bi-telephone mr-1"></i>{formatPhoneFR(demande.telephone)}</span>}
              </div>
              
              <p className="text-sm text-gray-700 mb-3 bg-white p-2 rounded">
                {demande.message}
              </p>
              
              <div className="flex items-center gap-2">
                {demande.telephone && (
                  <a
                    href={`tel:${demande.telephone}`}
                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded"
                  >
                    <i className="bi bi-telephone-fill mr-1"></i>
                    Appeler
                  </a>
                )}
                <a
                  href={`mailto:${demande.email}`}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded"
                >
                  <i className="bi bi-envelope-fill mr-1"></i>
                  Email
                </a>
                <button
                  onClick={() => {
                    setContactForm({
                      ...contactForm,
                      nom: demande.nom,
                      email: demande.email,
                      telephone: demande.telephone || ''
                    });
                    startNewContact();
                  }}
                  className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded"
                >
                  <i className="bi bi-person-plus-fill mr-1"></i>
                  Créer contact
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const loadPresentedProperties = async (contactId) => {
    try {
      console.log('🔍 Chargement des biens présentés pour le contact:', contactId);
      const response = await axios.get(`${API}/contacts/${contactId}/biens-presentes`);
      const biensPresentes = response.data;
      console.log('✅ Biens présentés chargés:', biensPresentes.length);
      setPresentedProperties(biensPresentes);
    } catch (error) {
      console.error("❌ Erreur lors du chargement des biens présentés:", error);
      setPresentedProperties([]);
    }
  };

  const removePresentedProperty = async (presentationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer ce bien de l\'affectation ?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/contacts/biens-presentes/${presentationId}`);
      // Recharger la liste
      if (currentContact?.id) {
        loadPresentedProperties(currentContact.id);
      }
      alert('✅ Bien retiré de l\'affectation avec succès');
    } catch (error) {
      console.error("❌ Erreur lors du retrait du bien:", error);
      alert('❌ Erreur lors du retrait du bien');
    }
  };

  const loadDocumentTemplates = async () => {
    try {
      const response = await axios.get(`${API}/documents/templates`);
      setDocumentTemplates(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des modèles de documents:", error);
      setDocumentTemplates([]);
    }
  };

  // MARKETING FUNCTIONS
  const loadMarketingDashboard = async () => {
    try {
      console.log("Loading marketing dashboard...");
      const response = await axios.get(`${API}/marketing/dashboard`);
      setMarketingDashboard(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement du dashboard marketing:", error);
      setMarketingDashboard({
        analytics: [],
        campaigns: [],
        recent_posts: [],
        active_conversations: [],
        summary: {}
      });
    }
  };

  const loadSocialPosts = async (platform = null, status = null) => {
    try {
      let url = `${API}/marketing/social-posts`;
      const params = [];
      if (platform) params.push(`platform=${platform}`);
      if (status) params.push(`status=${status}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setSocialPosts(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des posts:", error);
      setSocialPosts([]);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/marketing/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des campagnes:", error);
      setCampaigns([]);
    }
  };

  const loadChatbotConversations = async () => {
    try {
      const response = await axios.get(`${API}/chatbot/conversations`);
      setChatbotConversations(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des conversations:", error);
      setChatbotConversations([]);
    }
  };

  const loadPropertyHistory = async (propertyId, clientView = false) => {
    if (!propertyId) return;
    
    try {
      const response = await axios.get(`${API}/properties/${propertyId}/history?client_view=${clientView}`, { headers: getAuthHeaders() });
      setPropertyHistory(response.data);
    } catch (error) {
      console.error('Error loading property history:', error);
    }
  };

  // MARKETING FUNCTIONS
  const createCampaign = async () => {
    try {
      const campaignData = {
        ...newCampaign,
        budget: parseFloat(newCampaign.budget) || 0,
        start_date: newCampaign.start_date || new Date().toISOString(),
        properties: [],
        created_by: "current_user"
      };

      const response = await axios.post(`${API}/marketing/campaigns`, campaignData);
      
      setCampaigns(prev => [response.data, ...prev]);
      setShowCampaignModal(false);
      setNewCampaign({
        name: '',
        type: 'Property Listing',
        description: '',
        budget: '',
        target_audience: { age_range: '', interests: [] },
        geographic_targeting: [],
        start_date: '',
        is_active: true
      });
      alert('Campagne créée avec succès !');
      
      // Reload campaigns to show the update
      loadCampaigns();
      
    } catch (error) {
      console.error('Error creating campaign:', error);
      alert('Erreur lors de la création de la campagne');
    }
  };

  const createPost = async () => {
    try {
      const postData = {
        ...newPost,
        hashtags: newPost.hashtags.filter(tag => tag.trim() !== ''),
        created_by: "current_user"
      };

      const response = await axios.post(`${API}/marketing/social-posts`, postData, { headers: getAuthHeaders() });
      
      setSocialPosts(prev => [response.data, ...prev]);
      setShowPostModal(false);
      setNewPost({
        platform: 'Facebook',
        title: '',
        description: '',
        hashtags: [],
        status: 'Draft',
        media_urls: []
      });
      alert('Post créé avec succès !');
      
      // Reload posts to show the update
      loadSocialPosts();
      
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Erreur lors de la création du post');
    }
  };

  const generateSocialPost = async (propertyId = null, platform = "Facebook") => {
    try {
      const response = await axios.post(`${API}/marketing/generate-post`, {
        property_id: propertyId,
        platform: platform
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la génération du post:", error);
      return null;
    }
  };

  // OUTILS & CALCULETTES FUNCTIONS
  const calculatePret = (montant, duree, taux, apport) => {
    const montantEmprunte = parseFloat(montant) - parseFloat(apport || 0);
    const tauxMensuel = parseFloat(taux) / 100 / 12;
    const nbMois = parseFloat(duree) * 12;
    
    if (tauxMensuel === 0) {
      return {
        mensualite: (montantEmprunte / nbMois).toFixed(2),
        coutTotal: montantEmprunte.toFixed(2),
        interets: 0
      };
    }
    
    const mensualite = (montantEmprunte * tauxMensuel) / (1 - Math.pow(1 + tauxMensuel, -nbMois));
    const coutTotal = mensualite * nbMois;
    const interets = coutTotal - montantEmprunte;
    
    return {
      mensualite: mensualite.toFixed(2),
      coutTotal: coutTotal.toFixed(2),
      interets: interets.toFixed(2),
      montantEmprunte: montantEmprunte.toFixed(2)
    };
  };

  const calculateRentabilite = (prixAchat, fraisAchat, loyerMensuel, charges, travaux) => {
    const investissementTotal = parseFloat(prixAchat) + parseFloat(fraisAchat || 0) + parseFloat(travaux || 0);
    const loyerAnnuel = parseFloat(loyerMensuel) * 12;
    const chargesAnnuelles = parseFloat(charges || 0) * 12;
    const revenusNets = loyerAnnuel - chargesAnnuelles;
    
    const rendementBrut = ((loyerAnnuel / investissementTotal) * 100);
    const rendementNet = ((revenusNets / investissementTotal) * 100);
    const cashflowMensuel = (revenusNets / 12);
    
    return {
      rendementBrut: rendementBrut.toFixed(2),
      rendementNet: rendementNet.toFixed(2),
      cashflowMensuel: cashflowMensuel.toFixed(2),
      revenusNetsAnnuels: revenusNets.toFixed(2),
      investissementTotal: investissementTotal.toFixed(2)
    };
  };

  const calculatePlusValue = (prixAchat, prixVente, fraisAchat, fraisVente, travaux, dureeDetention) => {
    const prixRevient = parseFloat(prixAchat) + parseFloat(fraisAchat || 0) + parseFloat(travaux || 0);
    const prixVenteNet = parseFloat(prixVente) - parseFloat(fraisVente || 0);
    const plusValueBrute = prixVenteNet - prixRevient;
    
    // Calcul abattement (6% par an après 5 ans de détention)
    let abattement = 0;
    if (parseFloat(dureeDetention) > 5) {
      abattement = Math.min((parseFloat(dureeDetention) - 5) * 6, 100);
    }
    
    const plusValueImposable = plusValueBrute * (1 - abattement / 100);
    const impots = plusValueImposable > 0 ? plusValueImposable * 0.19 : 0; // 19% d'impôt
    const prelevementsSociaux = plusValueImposable > 0 ? plusValueImposable * 0.172 : 0; // 17.2%
    const plusValueNette = plusValueBrute - impots - prelevementsSociaux;
    
    return {
      plusValueBrute: plusValueBrute.toFixed(2),
      plusValueImposable: plusValueImposable.toFixed(2),
      abattement: abattement.toFixed(1),
      impots: impots.toFixed(2),
      prelevementsSociaux: prelevementsSociaux.toFixed(2),
      plusValueNette: plusValueNette.toFixed(2)
    };
  };

  // ═══════════════════════════════════════════════════════════════
  // Droits de mutation commerciaux (iter.73)
  // Remplace l'ancien `calculateFraisNotaire` résidentiel (Ancien/Neuf/TVA).
  //
  //  • type='fonds' → barème progressif de cession de fonds :
  //        0 %  jusqu'à 23 000 €
  //        3 %  entre 23 000 et 200 000 €
  //        5 %  au-delà de 200 000 €
  //  • type='murs' → droits d'enregistrement + taxes locales + annexes,
  //        taux éditables par département (défauts : 3,80 + 1,20 + 1,50 %).
  // ═══════════════════════════════════════════════════════════════
  const calculateDroitsMutation = ({ type, prix, taux_dep, taux_com, taux_annexes }) => {
    const p = parseFloat(prix);
    if (!Number.isFinite(p) || p <= 0) return null;

    if (type === 'fonds') {
      // Barème progressif (art. 719 CGI)
      const t1 = Math.min(p, 23000);
      const t2 = Math.max(0, Math.min(p, 200000) - 23000);
      const t3 = Math.max(0, p - 200000);
      const droits = t1 * 0 + t2 * 0.03 + t3 * 0.05;
      const tauxEffectif = p > 0 ? (droits / p) * 100 : 0;
      return {
        type: 'fonds',
        droits: droits.toFixed(2),
        prixTotal: (p + droits).toFixed(2),
        tauxEffectif: tauxEffectif.toFixed(2),
        detail: {
          tranche_0_23k: 0,
          tranche_23_200k: t2 * 0.03,
          tranche_gt_200k: t3 * 0.05,
        },
      };
    }

    // type === 'murs'
    const rDep = parseFloat(taux_dep) || 0;
    const rCom = parseFloat(taux_com) || 0;
    const rAnn = parseFloat(taux_annexes) || 0;
    const droitsDep = p * (rDep / 100);
    const droitsCom = p * (rCom / 100);
    const annexes = p * (rAnn / 100);
    const total = droitsDep + droitsCom + annexes;
    return {
      type: 'murs',
      droits: total.toFixed(2),
      prixTotal: (p + total).toFixed(2),
      tauxEffectif: (rDep + rCom + rAnn).toFixed(2),
      detail: {
        droitsDep: droitsDep.toFixed(2),
        droitsCom: droitsCom.toFixed(2),
        annexes: annexes.toFixed(2),
      },
    };
  };

  // ═══════════════════════════════════════════════════════════════
  // Surface pondérée commerciale (iter.73)
  // Remplace la loi Carrez (usage résidentiel) par la pondération
  // standard du bail commercial :
  //   • RDC / vitrine     → coef 1
  //   • Réserve / arrière → coef 0,5
  //   • Sous-sol / mezz.  → coef 0,3
  // Coefficients éditables pour s'adapter aux usages locaux.
  // ═══════════════════════════════════════════════════════════════
  const calculateSurfacePonderee = ({ rdc, coef_rdc, reserve, coef_reserve, annexe, coef_annexe }) => {
    const s1 = parseFloat(rdc) || 0;
    const c1 = parseFloat(coef_rdc) || 0;
    const s2 = parseFloat(reserve) || 0;
    const c2 = parseFloat(coef_reserve) || 0;
    const s3 = parseFloat(annexe) || 0;
    const c3 = parseFloat(coef_annexe) || 0;
    const brute = s1 + s2 + s3;
    const ponderee = s1 * c1 + s2 * c2 + s3 * c3;
    if (brute <= 0) return null;
    return {
      surfaceBrute: brute.toFixed(2),
      surfacePonderee: ponderee.toFixed(2),
      details: {
        rdc: (s1 * c1).toFixed(2),
        reserve: (s2 * c2).toFixed(2),
        annexe: (s3 * c3).toFixed(2),
      },
    };
  };

  // ═══════════════════════════════════════════════════════════════
  // Valorisation d'un fonds de commerce (standalone — iter.73)
  // Version autonome de la carte « Valorisation du fonds » du
  // simulateur contextuel (ContextualSimulator.jsx).
  //   • Valeur par barème CA = CA × (barème_ca %)
  //   • Valeur par multiple d'EBE = EBE × multiple_ebe
  // ═══════════════════════════════════════════════════════════════
  const calculateValorisationFonds = ({ ca, ebe, bareme_ca, multiple_ebe }) => {
    const c = parseFloat(ca);
    const e = parseFloat(ebe);
    const b = parseFloat(bareme_ca) || 0;
    const m = parseFloat(multiple_ebe) || 0;
    const valCa = Number.isFinite(c) ? c * (b / 100) : null;
    const valEbe = Number.isFinite(e) ? e * m : null;
    // Moyenne des 2 approches si les 2 sont disponibles
    const both = [valCa, valEbe].filter((v) => v !== null);
    const moyenne = both.length === 2 ? (both[0] + both[1]) / 2 : null;
    return {
      valeurParCa: valCa === null ? null : valCa.toFixed(2),
      valeurParEbe: valEbe === null ? null : valEbe.toFixed(2),
      valeurMoyenne: moyenne === null ? null : moyenne.toFixed(2),
    };
  };

  // ═══════════════════════════════════════════════════════════════
  // Valeur des murs par capitalisation (standalone — iter.73)
  // Version autonome de la carte « Rendement & valorisation » du
  // simulateur contextuel.
  //   valeur = loyer_annuel / (taux / 100)
  // ═══════════════════════════════════════════════════════════════
  const calculateCapitalisationMurs = ({ loyer_annuel, taux }) => {
    const l = parseFloat(loyer_annuel);
    const t = parseFloat(taux);
    if (!Number.isFinite(l) || !Number.isFinite(t) || t <= 0) return null;
    const valeur = l / (t / 100);
    return {
      valeur: valeur.toFixed(2),
      loyerAnnuel: l.toFixed(2),
      tauxApplique: t.toFixed(2),
    };
  };

  const showMarketingView = () => {
    setShowDashboard(false);
    setShowForm(false);
    setShowContacts(false);
    setShowDocuments(false);
    setShowConsultation(false);
    setShowMarketing(true);
    setShowOutils(false);
    setShowAvisDeValeur(false);  // Reset avis de valeur view
    setActiveSection('marketing');
    
    // Load marketing dashboard - backend ObjectId issue is now fixed
    loadMarketingDashboard();
    loadCampaigns();
    loadSocialPosts();
    loadChatbotConversations();
  };

  const showOutilsView = () => {
    setShowDashboard(false);
    setShowForm(false);
    setShowContacts(false);
    setShowDocuments(false);
    setShowConsultation(false);
    setShowMarketing(false);
    setShowOutils(true);
    setShowAvisDeValeur(false);  // Reset avis de valeur view when going to outils dashboard
    setActiveSection('outils');
    setActiveOutilsTab('dashboard');
  };

  const showAvisDeValeurView = () => {
    setShowDashboard(false);
    setShowForm(false);
    setShowContacts(false);
    setShowDocuments(false);
    setShowConsultation(false);
    setShowMarketing(false);
    setShowOutils(true);  // Keep Outils TRUE since Avis de Valeur is a sub-section
    setShowAvisDeValeur(true);
    setShowSettings(false);
    setActiveSection('avis-de-valeur');
  };

  const showSettingsView = () => {
    setShowDashboard(false);
    setShowForm(false);
    setShowContacts(false);
    setShowDocuments(false);
    setShowConsultation(false);
    setShowMarketing(false);
    setShowOutils(false);
    setShowAvisDeValeur(false);
    setShowFavorites(false);
    setShowProfile(false);
    setShowSettings(true);
    setActiveSection('settings');
  };

  const showProfileView = () => {
    setShowDashboard(false);
    setShowForm(false);
    setShowContacts(false);
    setShowDocuments(false);
    setShowConsultation(false);
    setShowMarketing(false);
    setShowOutils(false);
    setShowAvisDeValeur(false);
    setShowFavorites(false);
    setShowSettings(false);
    setShowProfile(true);
    setActiveSection('profile');
  };

  const showPropertyHistoryView = (property) => {
    setShowDashboard(false);
    setShowForm(false);
    setShowContacts(false);
    setShowDocuments(false);
    setShowConsultation(false);
    setShowMarketing(false);
    setShowPropertyHistory(true);
    setHistoryProperty(property);
    setActiveSection('property-history');
    if (property && property.id) {
      loadPropertyHistory(property.id, false); // Load admin view by default
    }
  };

  const showPropertiesView = () => {
    setShowDashboard(false);
    setShowForm(false);
    setShowContacts(false);
    setShowDocuments(false);
    setShowConsultation(false);
    setShowMarketing(false);
    setShowOutils(false);
    setShowFavorites(false);  // Reset favorites view
    setShowAvisDeValeur(false);  // Reset avis de valeur view
    setShowSettings(false);  // Reset settings view
    setShowProfile(false);  // Reset profile view
    setConsultedProperty(null);
    setActiveSection('properties');
    loadProperties(currentFilter); // Reload properties when showing the view
    navigate('/biens-v2'); // Update URL
  };

  const showPropertiesList = () => {
    setShowForm(false);
    setCurrentProperty(null);
    setShowContacts(false);
    setShowContactForm(false);
    setShowDashboard(false);
    setActiveSection('biens');
  };

  const startNewContact = () => {
    console.log("startNewContact → navigate /contacts/new");
    navigate('/contacts/new');
  };

  const createContact = async (contactData) => {
    try {
      const response = await axios.post(`${API}/contacts`, contactData);
      await loadContacts(showArchivedContacts ? "archived" : "active");
      // Rester sur le formulaire en mode édition avec le contact créé
      setCurrentContact(response.data);
      alert("Contact créé avec succès ! Vous pouvez continuer à le modifier.");
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création du contact:", error);
      alert("Erreur lors de la création du contact");
    }
  };

  const updateContact = async (contactId, contactData) => {
    try {
      const response = await axios.put(`${API}/contacts/${contactId}`, contactData);
      await loadContacts(showArchivedContacts ? "archived" : "active");
      // Rester sur le formulaire avec les données mises à jour
      setCurrentContact(response.data);
      alert("Contact mis à jour avec succès ! Vous pouvez continuer à le modifier.");
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour du contact:", error);
      alert("Erreur lors de la mise à jour du contact");
    }
  };

  const deleteContact = async (contactId) => {
    try {
      // Check if contact is linked to properties
      const checkResponse = await axios.get(`${API}/contacts/${contactId}/check-links`);
      const { is_linked, linked_properties, count } = checkResponse.data;
      
      if (is_linked) {
        const propertyRefs = linked_properties.map(p => p.reference).join(", ");
        alert(`❌ Impossible de supprimer ce contact.\n\nCe contact est lié à ${count} bien(s) : ${propertyRefs}\n\nVeuillez d'abord retirer ce contact des biens concernés.`);
        return;
      }
      
      if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce contact ?")) {
        return;
      }
      
      await axios.delete(`${API}/contacts/${contactId}`);
      await loadContacts(showArchivedContacts ? "archived" : "active");
      alert("Contact supprimé avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert("Erreur lors de la suppression");
    }
  };

  const archiveContact = async (contactId) => {
    try {
      // Check if contact is linked to properties
      const checkResponse = await axios.get(`${API}/contacts/${contactId}/check-links`);
      const { is_linked, linked_properties, count } = checkResponse.data;
      
      if (is_linked) {
        const propertyRefs = linked_properties.map(p => p.reference).join(", ");
        alert(`❌ Impossible d'archiver ce contact.\n\nCe contact est lié à ${count} bien(s) : ${propertyRefs}\n\nVeuillez d'abord retirer ce contact des biens concernés.`);
        return;
      }
      
      await axios.post(`${API}/contacts/${contactId}/archive`);
      await loadContacts(showArchivedContacts ? "archived" : "active");
      setSelectedContacts([]);
      alert("Contact archivé avec succès");
    } catch (error) {
      console.error("Erreur lors de l'archivage:", error);
      alert("Erreur lors de l'archivage");
    }
  };

  const unarchiveContact = async (contactId) => {
    try {
      await axios.post(`${API}/contacts/${contactId}/unarchive`);
      await loadContacts(showArchivedContacts ? "archived" : "active");
      setSelectedContacts([]);
      alert("Contact désarchivé avec succès");
    } catch (error) {
      console.error("Erreur lors du désarchivage:", error);
      alert("Erreur lors du désarchivage");
    }
  };

  const bulkArchiveContacts = async () => {
    if (selectedContacts.length === 0) return;
    
    try {
      // Check each contact for links
      const linkedContacts = [];
      for (const contactId of selectedContacts) {
        const checkResponse = await axios.get(`${API}/contacts/${contactId}/check-links`);
        if (checkResponse.data.is_linked) {
          const contact = contacts.find(c => c.id === contactId);
          linkedContacts.push({
            name: `${contact.prenom} ${contact.nom}`,
            properties: checkResponse.data.linked_properties
          });
        }
      }
      
      if (linkedContacts.length > 0) {
        const contactNames = linkedContacts.map(c => c.name).join(", ");
        alert(`❌ Impossible d'archiver ${linkedContacts.length} contact(s).\n\nLes contacts suivants sont liés à des biens : ${contactNames}\n\nVeuillez d'abord retirer ces contacts des biens concernés.`);
        return;
      }
      
      await axios.post(`${API}/contacts/bulk-archive`, selectedContacts);
      setSelectedContacts([]);
      // Force reload active contacts after archiving
      await loadContacts("active");
      alert(`${selectedContacts.length} contact(s) archivé(s) avec succès`);
    } catch (error) {
      console.error("Erreur lors de l'archivage groupé:", error);
      alert("Erreur lors de l'archivage groupé");
    }
  };

  const bulkUnarchiveContacts = async () => {
    if (selectedContacts.length === 0) return;
    
    try {
      await axios.post(`${API}/contacts/bulk-unarchive`, selectedContacts);
      setSelectedContacts([]);
      // Force reload archived contacts after unarchiving
      await loadContacts("archived");
      alert(`${selectedContacts.length} contact(s) désarchivé(s) avec succès`);
    } catch (error) {
      console.error("Erreur lors du désarchivage groupé:", error);
      alert("Erreur lors du désarchivage groupé");
    }
  };

  const bulkDeleteContacts = async () => {
    if (selectedContacts.length === 0) return;
    
    try {
      // Check each contact for links
      const linkedContacts = [];
      for (const contactId of selectedContacts) {
        const checkResponse = await axios.get(`${API}/contacts/${contactId}/check-links`);
        if (checkResponse.data.is_linked) {
          const contact = contacts.find(c => c.id === contactId);
          linkedContacts.push({
            name: `${contact.prenom} ${contact.nom}`,
            properties: checkResponse.data.linked_properties
          });
        }
      }
      
      if (linkedContacts.length > 0) {
        const contactNames = linkedContacts.map(c => c.name).join(", ");
        alert(`❌ Impossible de supprimer ${linkedContacts.length} contact(s).\n\nLes contacts suivants sont liés à des biens : ${contactNames}\n\nVeuillez d'abord retirer ces contacts des biens concernés.`);
        return;
      }
      
      if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedContacts.length} contact(s) ?`)) {
        return;
      }
      
      await axios.post(`${API}/contacts/bulk-delete`, selectedContacts);
      setSelectedContacts([]);
      // Force reload with current status
      await loadContacts(showArchivedContacts ? "archived" : "active");
      alert(`${selectedContacts.length} contact(s) supprimé(s) avec succès`);
    } catch (error) {
      console.error("Erreur lors de la suppression groupée:", error);
      alert("Erreur lors de la suppression groupée");
    }
  };

  const viewContact = (contact) => {
    setCurrentContact(contact);
    
    // Charger les données du contact dans le formulaire (même en mode consultation)
    setContactFormData({
      type_contact: contact.type_contact || "",
      titre: contact.titre || "",
      nom: contact.nom || "",
      prenom: contact.prenom || "",
      nom_societe: contact.nom_societe || "",
      profession: contact.profession || "",
      telephones: contact.telephones && contact.telephones.length > 0 
        ? contact.telephones 
        : [{ type: "mobile", numero: "", principal: true }],
      emails: contact.emails && contact.emails.length > 0 
        ? contact.emails 
        : [{ type: "professionnel", email: "", principal: true }],
      fax: contact.fax || "",
      adresse: contact.adresse || "",
      code_postal: contact.code_postal || "",
      ville: contact.ville || "",
      pays: contact.pays || "France",
      negociateur_id: contact.negociateur_id || "",
      groupes: contact.groupes || [],
      newsletter: contact.newsletter || false,
      informations_confidentielles: contact.informations_confidentielles || {},
      criteres_recherche: contact.criteres_recherche || {}
    });
    
    // Charger les biens présentés pour ce contact
    if (contact.id) {
      loadPresentedProperties(contact.id);
    }
    
    setContactViewMode('view');
    setShowContactForm(true);
    setShowContacts(true);
    setActiveContactTab("general");
  };

  const editContact = (contact) => {
    setCurrentContact(contact);
    
    // Charger les données du contact dans le formulaire
    setContactFormData({
      type_contact: contact.type_contact || "",
      titre: contact.titre || "",
      nom: contact.nom || "",
      prenom: contact.prenom || "",
      nom_societe: contact.nom_societe || "",
      profession: contact.profession || "",
      telephones: contact.telephones && contact.telephones.length > 0 
        ? contact.telephones 
        : [{ type: "mobile", numero: "", principal: true }],
      emails: contact.emails && contact.emails.length > 0 
        ? contact.emails 
        : [{ type: "professionnel", email: "", principal: true }],
      fax: contact.fax || "",
      adresse: contact.adresse || "",
      code_postal: contact.code_postal || "",
      ville: contact.ville || "",
      pays: contact.pays || "France",
      negociateur_id: contact.negociateur_id || "",
      groupes: contact.groupes || [],
      newsletter: contact.newsletter || false,
      informations_confidentielles: contact.informations_confidentielles || {},
      criteres_recherche: contact.criteres_recherche || {}
    });
    
    // Charger les biens présentés pour ce contact
    if (contact.id) {
      loadPresentedProperties(contact.id);
    }
    
    setContactViewMode('edit');
    setShowContactForm(true);
    setShowContacts(true);
    setActiveContactTab("general");
  };

  // Filter contacts based on search criteria
  const filteredContacts = contacts.filter(contact => {
    // Filter by type
    if (contactSearchFilters.type_contact && contact.type_contact !== contactSearchFilters.type_contact) {
      return false;
    }
    
    // Filter by group
    if (contactSearchFilters.groupe && !contact.groupes?.includes(contactSearchFilters.groupe)) {
      return false;
    }
    
    // Filter by city
    if (contactSearchFilters.ville && contact.ville !== contactSearchFilters.ville) {
      return false;
    }
    
    return true;
  });

  const showPropertyForm = (property = null) => {
    setCurrentProperty(property);
    setShowForm(true);
    setShowContacts(false);
    setShowContactForm(false);
    setShowDashboard(false);
  };

  // Contact form handlers
  const handleContactFormChange = (field, value) => {
    setContactFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContactPhoneChange = (index, field, value) => {
    setContactFormData(prev => ({
      ...prev,
      telephones: prev.telephones.map((phone, i) => 
        i === index ? { ...phone, [field]: value } : phone
      )
    }));
  };

  const handleContactEmailChange = (index, field, value) => {
    setContactFormData(prev => ({
      ...prev,
      emails: prev.emails.map((email, i) => 
        i === index ? { ...email, [field]: value } : email
      )
    }));
  };

  const addContactPhone = () => {
    setContactFormData(prev => ({
      ...prev,
      telephones: [...prev.telephones, { type: "mobile", numero: "", principal: false }]
    }));
  };

  const addContactEmail = () => {
    setContactFormData(prev => ({
      ...prev,
      emails: [...prev.emails, { type: "personnel", email: "", principal: false }]
    }));
  };

  const removeContactPhone = (index) => {
    setContactFormData(prev => ({
      ...prev,
      telephones: prev.telephones.filter((_, i) => i !== index)
    }));
  };

  const removeContactEmail = (index) => {
    setContactFormData(prev => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index)
    }));
  };

  const saveContact = async () => {
    // Validation
    if (!contactFormData.nom.trim()) {
      alert("Le nom est obligatoire");
      return;
    }
    if (!contactFormData.type_contact) {
      alert("Le type de contact est obligatoire");
      return;
    }

    // Filter out empty phones and emails
    const contactData = {
      ...contactFormData,
      telephones: contactFormData.telephones.filter(phone => phone.numero.trim()),
      emails: contactFormData.emails.filter(email => email.email.trim())
    };

    try {
      if (currentContact) {
        await updateContact(currentContact.id, contactData);
      } else {
        await createContact(contactData);
      }
      
      // Reset form
      setContactFormData({
        type_contact: "",
        titre: "",
        nom: "",
        prenom: "",
        nom_societe: "",
        profession: "",
        telephones: [{ type: "mobile", numero: "", principal: true }],
        emails: [{ type: "professionnel", email: "", principal: true }],
        fax: "",
        adresse: "",
        code_postal: "",
        ville: "",
        pays: "France",
        negociateur_id: "",
        groupes: [],
        newsletter: false,
        informations_confidentielles: {},
        criteres_recherche: {}
      });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
    }
  };

  // Handler pour le nouveau ContactForm
  const handleContactFormSave = async (contactData) => {
    // Validation
    if (!contactData.nom.trim()) {
      alert("Le nom est obligatoire");
      return;
    }
    if (!contactData.type_contact) {
      alert("Le type de contact est obligatoire");
      return;
    }

    // Nettoyer les données avant l'envoi
    const cleanedData = {
      ...contactData,
      // Filtrer les téléphones et emails vides
      telephones: contactData.telephones.filter(phone => phone.numero && phone.numero.trim()),
      emails: contactData.emails.filter(email => email.email && email.email.trim()),
      // Fusionner groupes (checkboxes) et tags (libres) dans le champ groupes
      groupes: [
        ...(contactData.groupes || []),
        ...(contactData.tags || [])
      ].filter((value, index, self) => self.indexOf(value) === index), // Supprimer les doublons
      // Nettoyer criteres_recherche
      criteres_recherche: contactData.criteres_recherche ? {
        budget_min: contactData.criteres_recherche.budget_min || null,
        budget_max: contactData.criteres_recherche.budget_max || null,
        surface_min: contactData.criteres_recherche.surface_min || null,
        surface_max: contactData.criteres_recherche.surface_max || null,
        types_biens: contactData.criteres_recherche.types_biens || [],
        types_activites: contactData.criteres_recherche.types_activites || [],
        villes: contactData.criteres_recherche.villes || [],
        secteurs: contactData.criteres_recherche.secteurs || [],
        notes: contactData.criteres_recherche.notes || null
      } : null,
      // Nettoyer informations_confidentielles
      informations_confidentielles: contactData.informations_confidentielles ? {
        apport_disponible: contactData.informations_confidentielles.apport_disponible || null,
        capacite_emprunt: contactData.informations_confidentielles.capacite_emprunt || null,
        revenus_annuels: contactData.informations_confidentielles.revenus_annuels || null,
        banque: contactData.informations_confidentielles.banque || null,
        nom_banquier: contactData.informations_confidentielles.nom_banquier || null
      } : null
    };

    // Supprimer les champs qui n'existent pas dans le backend
    delete cleanedData.tags;
    if (cleanedData.criteres_recherche) {
      delete cleanedData.criteres_recherche.type_transaction;
      delete cleanedData.criteres_recherche.caracteristiques;
      delete cleanedData.criteres_recherche.notes_ia;
      // types_activites est maintenant supporté par le backend
    }

    try {
      if (currentContact) {
        await updateContact(currentContact.id, cleanedData);
      } else {
        await createContact(cleanedData);
      }
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      alert("Erreur lors de la sauvegarde: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleContactFormCancel = () => {
    setShowContactForm(false);
    setCurrentContact(null);
  };

  // Property matching functions
  const performPropertyMatching = async () => {
    if (!contactFormData.criteres_recherche) {
      alert("Aucun critère de recherche défini pour ce contact");
      return;
    }

    try {
      setIsMatchingLoading(true); // Start loading
      setMatchedProperties(null); // Reset results
      
      // Use existing backend endpoint to get properties and filter them
      const propertiesResponse = await axios.get(`${API}/properties`);
      const allProperties = propertiesResponse.data;
      
      // Simulate processing time for user feedback
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Filter properties based on contact's criteria
      const matchedResults = allProperties.filter(property => {
        const criteres = contactFormData.criteres_recherche;
        
        // Check budget
        if (criteres.budget_min && property.honoraires_calcul?.prix_vente < criteres.budget_min) {
          return false;
        }
        if (criteres.budget_max && property.honoraires_calcul?.prix_vente > criteres.budget_max) {
          return false;
        }
        
        // Check surface
        if (criteres.surface_min && property.enseigne?.superficie < criteres.surface_min) {
          return false;
        }
        if (criteres.surface_max && property.enseigne?.superficie > criteres.surface_max) {
          return false;
        }
        
        // Check cities
        if (criteres.villes && criteres.villes.length > 0) {
          if (!criteres.villes.includes(property.informations_confidentielles?.ville)) {
            return false;
          }
        }
        
        // Check property types
        if (criteres.types_biens && criteres.types_biens.length > 0) {
          if (!criteres.types_biens.includes(property.bien_general?.type_bien)) {
            return false;
          }
        }
        
        return true;
      });
      
      setMatchedProperties(matchedResults);
      console.log(`Matching completed: ${matchedResults.length} properties found`);
    } catch (error) {
      console.error("Erreur lors du matching:", error);
      alert("Erreur lors de la recherche de biens correspondants");
      setMatchedProperties([]);
    } finally {
      setIsMatchingLoading(false); // Stop loading
    }
  };

  const selectPropertyForContact = async (property) => {
    try {
      // Add the property to the selected list
      setSelectedPropertiesForContact(prev => {
        const isAlreadySelected = prev.some(p => p.id === property.id);
        if (isAlreadySelected) {
          return prev.filter(p => p.id !== property.id); // Remove if already selected
        } else {
          return [...prev, property]; // Add if not selected
        }
      });
      
      const propertyName = property.enseigne?.enseigne || 'Sans nom';
      const isAlreadySelected = selectedPropertiesForContact.some(p => p.id === property.id);
      
      if (isAlreadySelected) {
        alert(`Bien "${propertyName}" retiré de la sélection`);
      } else {
        alert(`Bien "${propertyName}" ajouté à la sélection`);
      }
      
    } catch (error) {
      console.error("Erreur lors de la sélection:", error);
      alert("Erreur lors de la sélection du bien");
    }
  };

  const viewPropertyDetails = (property) => {
    // Switch to property view and show details
    setCurrentProperty(property);
    setShowForm(true);
    setShowContacts(false);
    setShowContactForm(false);
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Render bottom action buttons (update + scroll to top)
  const renderBottomActionButtons = () => {
    return (
      <div className="sticky bottom-4 z-10 flex justify-center gap-3 mt-8">
        <button
          onClick={scrollToTop}
          className="inline-flex items-center px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-lg hover:shadow-xl"
          title="Remonter en haut"
        >
          <i className="bi bi-arrow-up text-lg"></i>
        </button>
        
        <button
          onClick={updateProperty}
          disabled={isSaving}
          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Enregistrement...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle mr-2"></i>
              Mettre à jour le bien
            </>
          )}
        </button>
      </div>
    );
  };

  // Handle file upload for images, documents, and plans
  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    try {
      // Convert file to base64 for storage
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        
        // Create media object with correct property names matching backend MediaFile model
        const mediaObject = {
          id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          url: base64String,  // Base64 data URL format
          type: file.type,
          size: file.size,
          uploadDate: new Date().toISOString()  // Changed from 'uploaded_at' to 'uploadDate'
        };
        
        // Update formData with new media
        setFormData(prev => ({
          ...prev,
          medias: {
            ...prev.medias,
            [type]: [...(prev.medias?.[type] || []), mediaObject]
          }
        }));
        
        console.log(`✅ ${type} uploaded:`, file.name);
      };
      
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        alert('Erreur lors de la lecture du fichier');
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Erreur lors du téléchargement du fichier');
    }
  };

  // Handle media deletion
  // lot U2 — les photos issues de la migration ne portent PAS de clé `id` (seulement
  // `asset_id`). Filtrer sur `m.id` supprimait alors TOUTES les photos migrées d'un coup
  // (undefined === undefined). Clé de repli explicite, même expression des deux côtés.
  const mediaKey = (m) => m?.id || m?.asset_id || m?.url || m?.name;

  const handleDeleteMedia = (type, mediaId) => {
    setFormData(prev => ({
      ...prev,
      medias: {
        ...prev.medias,
        [type]: (prev.medias?.[type] || []).filter(m => mediaKey(m) !== mediaId)
      }
    }));
  };

  // Fonction pour supprimer un média (alias de handleDeleteMedia)
  const removeMediaFile = (type, mediaId) => {
    handleDeleteMedia(type, mediaId);
  };

  // Fonction pour réorganiser les photos
  const reorderPhotos = (fromIndex, toIndex) => {
    setFormData(prev => {
      const photos = [...(prev.medias?.photos || [])];
      
      // Vérifier les indices valides
      if (fromIndex < 0 || fromIndex >= photos.length || toIndex < 0 || toIndex >= photos.length) {
        return prev;
      }
      
      // Déplacer l'élément
      const [movedPhoto] = photos.splice(fromIndex, 1);
      photos.splice(toIndex, 0, movedPhoto);
      
      return {
        ...prev,
        medias: {
          ...prev.medias,
          photos: photos
        }
      };
    });
  };

  // Helper function to handle nested path updates in formData
  const handleDynamicFieldChange = (path, value) => {
    const parts = path.split('.');
    
    // Si c'est un chemin simple (1 niveau), utiliser la fonction standard
    if (parts.length === 1) {
      setFormData(prev => ({
        ...prev,
        [parts[0]]: value
      }));
      return;
    }
    
    // Si c'est un chemin à 2 niveaux, utiliser handleInputChange
    if (parts.length === 2) {
      handleInputChange(parts[0], parts[1], value);
      return;
    }
    
    // Pour les chemins à 3+ niveaux, utiliser une mise à jour imbriquée
    if (parts.length === 3) {
      handleNestedInputChange(parts[0], parts[1], parts[2], value);
      return;
    }
    
    // Fallback pour chemins très imbriqués (4+ niveaux) - rare mais possible
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      
      // Parcourir tous les niveaux sauf le dernier
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) {
          current[parts[i]] = {};
        } else {
          current[parts[i]] = { ...current[parts[i]] };
        }
        current = current[parts[i]];
      }
      
      // Définir la valeur finale
      current[parts[parts.length - 1]] = value;
      
      return newData;
    });
  };

  const handleInputChange = (section, field, value) => {
    console.log(`🔧 handleInputChange appelé: section="${section}", field="${field}", value="${value}"`);
    
    // CAS SPÉCIAL : Champs à la racine de formData (pas de field, juste section)
    if (field === undefined || field === null) {
      console.log(`🔄 Champ racine "${section}" changé vers: ${value}`);
      
      // Gérer le changement de transaction_type
      if (section === 'transaction_type') {
        console.log(`🔄 Transaction type changé vers: ${value}`);
        const newFormData = {
          ...formData,
          transaction_type: value,
          bien_general: {
            ...formData.bien_general,
            type_bien: '', // Réinitialiser
            activite_principale: '' // Réinitialiser
          }
        };
        setFormData(newFormData);
        setTimeout(() => {
          setConfigKey(prev => prev + 1);
          console.log('✅ ConfigKey incrémenté après changement transaction_type');
        }, 0);
        return;
      }
      
      // Autres champs racine
      setFormData({
        ...formData,
        [section]: value
      });
      return;
    }
    
    // CAS NORMAL : Champs imbriqués
    // Mettre à jour formData
    let newFormData = {
      ...formData,
      [section]: {
        ...formData[section],
        [field]: value
      }
    };
    
    console.log(`📝 Nouvelle valeur dans formData[${section}][${field}]:`, newFormData[section][field]);
    
    // CALCULS AUTOMATIQUES : Prix & Honoraires (VENTE)
    if (section === 'honoraires_calcul' && (field === 'prix_vente' || field === 'net_vendeur' || field === 'honoraires' || field === 'honoraires_type')) {
      console.log(`💰 Déclenchement calcul prix vente pour ${field}`);
      newFormData = calculatePriceFields(field, value, newFormData);
    }
    
    // CALCULS AUTOMATIQUES : Loyer & Honoraires (LOCATION)
    if (section === 'informations_bail' && (field === 'loyer_mensuel' || field === 'loyer_annuel' || field === 'charges' || field === 'honoraires_location_fixe')) {
      console.log(`🏠 Déclenchement calcul location pour ${field}`);
      newFormData = calculateRentalPriceFields(field, value, newFormData);
    }

    // CALCULS AUTOMATIQUES : Murs occupés (VENTE) → loyer en place + rendement brut
    if (section === 'informations_vente' && ['loyer_mensuel_actuel', 'loyer_annuel_actuel', 'prix_annonce', 'occupation_murs'].includes(field)) {
      const toNum = (v) => {
        if (v === null || v === undefined || v === '') return 0;
        const n = parseFloat(String(v).replace(/[^\d.,-]/g, '').replace(/\s/g, '').replace(',', '.'));
        return isNaN(n) ? 0 : n;
      };
      const iv = { ...newFormData.informations_vente };
      // Synchronisation loyer mensuel <-> annuel
      if (field === 'loyer_mensuel_actuel') {
        const m = toNum(value);
        iv.loyer_annuel_actuel = m > 0 ? Math.round(m * 12) : '';
      } else if (field === 'loyer_annuel_actuel') {
        const a = toNum(value);
        iv.loyer_mensuel_actuel = a > 0 ? Math.round(a / 12) : '';
      }
      // Si libre : on remet à zéro loyer + rendement
      if (field === 'occupation_murs' && value !== 'occupe') {
        iv.loyer_mensuel_actuel = '';
        iv.loyer_annuel_actuel = '';
        iv.taux_rendement_brut = '';
      } else {
        const loyerAnnuel = toNum(iv.loyer_annuel_actuel);
        const prixFai = toNum(iv.prix_annonce);
        iv.taux_rendement_brut = (loyerAnnuel > 0 && prixFai > 0)
          ? Math.round((loyerAnnuel / prixFai) * 10000) / 100
          : (loyerAnnuel === 0 ? '' : iv.taux_rendement_brut);
      }
      newFormData.informations_vente = iv;
    }
    
    // Si c'est le champ activite_principale OU type_bien, gérer la logique spéciale
    if (section === 'bien_general' && (field === 'activite_principale' || field === 'type_bien')) {
      console.log(`🔄 ${field} changé vers:`, value);
      
      // Si c'est le type_bien qui change, réinitialiser l'activité
      if (field === 'type_bien') {
        newFormData.bien_general = {
          ...newFormData.bien_general,
          activite_principale: '' // Réinitialiser l'activité
        };
      }
      
      // Si c'est l'activite_principale qui change, déduire et remplir le type_bien
      if (field === 'activite_principale' && value) {
        const deducedTypeBien = getTypeBienFromActivite(value);
        if (deducedTypeBien && !formData.bien_general?.type_bien) {
          console.log(`💡 Type de bien déduit: ${deducedTypeBien} depuis activité: ${value}`);
          newFormData.bien_general = {
            ...newFormData.bien_general,
            type_bien: deducedTypeBien
          };
        }
      }
    }
    
    // Mettre à jour le state avec toutes les modifications
    setFormData(newFormData);
    
    // Forcer le re-render des onglets si type_bien ou activite_principale ou transaction_type a changé
    if (section === 'bien_general' && (field === 'activite_principale' || field === 'type_bien')) {
      // Utiliser setTimeout pour s'assurer que formData est mis à jour avant le re-render
      setTimeout(() => {
        setConfigKey(prev => prev + 1);
        console.log('✅ ConfigKey incrémenté, re-render forcé de tous les onglets');
      }, 0);
    }
  };

  // Handle nested input changes (for proprietaire in confidentiel)
  const handleNestedInputChange = (section, subsection, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  // Handle multi-contact changes
  const handleContactChange = (section, subsection, contactType, index, field, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      if (!newData[section][subsection].contacts) {
        newData[section][subsection].contacts = { telephones: [], emails: [] };
      }
      
      const contacts = [...newData[section][subsection].contacts[contactType]];
      contacts[index] = { ...contacts[index], [field]: value };
      
      newData[section][subsection].contacts[contactType] = contacts;
      return newData;
    });
  };

  const addContact = (section, subsection, contactType) => {
    setFormData(prev => {
      const newData = { ...prev };
      if (!newData[section][subsection].contacts) {
        newData[section][subsection].contacts = { telephones: [], emails: [] };
      }
      
      const newContact = contactType === 'telephones' 
        ? { type: "autre", value: "", label: "Autre" }
        : { type: "autre", value: "", label: "Autre" };
        
      newData[section][subsection].contacts[contactType].push(newContact);
      return newData;
    });
  };

  const removeContact = (section, subsection, contactType, index) => {
    setFormData(prev => {
      const newData = { ...prev };
      const contacts = [...newData[section][subsection].contacts[contactType]];
      contacts.splice(index, 1);
      newData[section][subsection].contacts[contactType] = contacts;
      return newData;
    });
  };

  // Handle description changes for multiple types and languages
  const handleDescriptionChange = (descriptionType, language, content) => {
    setFormData(prev => ({
      ...prev,
      descriptif: {
        ...prev.descriptif,
        descriptions: {
          ...prev.descriptif.descriptions,
          [descriptionType]: {
            ...prev.descriptif.descriptions[descriptionType],
            [language]: {
              ...prev.descriptif.descriptions[descriptionType][language],
              content: content
            }
          }
        }
      }
    }));
  };

  // Handle proximite changes for multiple languages
  const handleProximiteChange = (language, content) => {
    setFormData(prev => ({
      ...prev,
      descriptif: {
        ...prev.descriptif,
        proximite: {
          ...prev.descriptif.proximite,
          [language]: content
        }
      }
    }));
  };

  // Speech plugin for Lexical editor
  const SpeechPlugin = ({ onSpeechToggle }) => {
    const [editor] = useLexicalComposerContext();

    const handleSpeechClick = () => {
      if (isListening) {
        stopSpeechRecognition();
      } else {
        // Get current text from Lexical editor
        let currentText = '';
        editor.getEditorState().read(() => {
          const root = $getRoot();
          currentText = root.getTextContent();
        });
        startSpeechForLexical(editor, currentText);
      }
    };

    return (
      <div className="absolute top-2 right-2 flex items-center gap-1 z-20 bg-white rounded-lg shadow-sm border p-1">
        {/* Language selector (compact) */}
        <select
          value={speechLanguage}
          onChange={(e) => handleSpeechLanguageChange(e.target.value)}
          className="text-xs bg-white border border-gray-300 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          disabled={isListening}
          title="Langue de reconnaissance vocale"
        >
          {speechLanguageOptions.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.flag} {lang.name}
            </option>
          ))}
        </select>
        
        {/* Microphone button */}
        <button
          type="button"
          onClick={handleSpeechClick}
          className={`relative inline-flex items-center justify-center w-6 h-6 rounded border transition-all duration-300 ${
            isListening
              ? 'bg-red-500 border-red-500 text-white shadow-lg shadow-red-500/25'
              : 'bg-white border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50'
          }`}
          title={isListening ? 'Arrêter la dictée vocale' : 'Commencer la dictée vocale'}
        >
          <i className={`bi ${isListening ? 'bi-mic-fill' : 'bi-mic'} text-xs`}></i>
          
          {/* Pulsing ring animation when listening */}
          {isListening && (
            <div className="absolute inset-0 rounded bg-red-500 opacity-25 animate-ping"></div>
          )}
        </button>
      </div>
    );
  };

  // Ultra-Simple Rich Text Editor - Anti cursor jumping
  const RichTextEditor = ({ value, onChange, placeholder, height = "200px" }) => {
    const editorRef = useRef(null);

    // Handle speech-to-text
    const handleSpeechClick = () => {
      if (isListening) {
        stopSpeechRecognition();
      } else {
        startSpeechForSimpleEditor(editorRef.current, editorRef.current?.value || '');
      }
    };

    return (
      <div className="relative border border-gray-300 rounded-md" style={{ minHeight: height }}>
        <textarea
          ref={editorRef}
          defaultValue={value || ''}
          onChange={(e) => onChange && onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full p-3 min-h-32 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none border-0"
          style={{ 
            minHeight: height, 
            textAlign: 'left',
            fontFamily: 'inherit'
          }}
        />
        
        {/* Speech controls */}
        {speechSupported && (
          <div className="absolute top-2 right-2 flex items-center gap-1 z-20 bg-white rounded-lg shadow-sm border p-1">
            {/* Language selector */}
            <select
              value={speechLanguage}
              onChange={(e) => handleSpeechLanguageChange(e.target.value)}
              className="text-xs bg-white border border-gray-300 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              disabled={isListening}
              title="Langue de reconnaissance vocale"
            >
              {speechLanguageOptions.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
            
            {/* Microphone button */}
            <button
              type="button"
              onClick={handleSpeechClick}
              className={`relative inline-flex items-center justify-center w-6 h-6 rounded border transition-all duration-300 ${
                isListening
                  ? 'bg-red-500 border-red-500 text-white shadow-lg shadow-red-500/25'
                  : 'bg-white border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50'
              }`}
              title={isListening ? 'Arrêter la dictée vocale' : 'Commencer la dictée vocale'}
            >
              <i className={`bi ${isListening ? 'bi-mic-fill' : 'bi-mic'} text-xs`}></i>
              
              {/* Pulsing ring animation when listening */}
              {isListening && (
                <div className="absolute inset-0 rounded bg-red-500 opacity-25 animate-ping"></div>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  // AI Optimization for descriptions with language support
  const optimizeDescription = async (descriptionType, language) => {
    let content = formData.descriptif.descriptions[descriptionType]?.[language]?.content;
    
    // If no content in state, try to get it from the editor directly
    if (!content || !content.trim()) {
      console.warn(`No content found in formData for ${descriptionType} ${language}, checking editors...`);
      alert("Veuillez saisir du contenu dans l'éditeur avant d'optimiser avec l'IA");
      return;
    }

    try {
      const response = await axios.post(`${API}/optimize/description`, {
        type: descriptionType,
        content: content,
        property_type: formData.bien_general.type_bien,
        language: language
      }, { headers: getAuthHeaders() });

      // Update the description with AI optimized content
      setFormData(prev => ({
        ...prev,
        descriptif: {
          ...prev.descriptif,
          descriptions: {
            ...prev.descriptif.descriptions,
            [descriptionType]: {
              ...prev.descriptif.descriptions[descriptionType],
              [language]: {
                content: response.data.optimized,
                optimized_by_ai: true,
                last_ai_optimization: new Date().toISOString()
              }
            }
          }
        }
      }));

      alert(`Description ${language} optimisée par l'IA avec succès !`);
    } catch (error) {
      console.error("Erreur lors de l'optimisation IA:", error);
      alert("Erreur lors de l'optimisation IA");
    }
  };

  const handlePrestationChange = (prestation, checked) => {
    setFormData(prev => ({
      ...prev,
      prestations: {
        ...prev.prestations,
        [prestation]: checked
      }
    }));
  };

  // Calculation helpers for honoraires
  const calculateHonoraires = async (field, value) => {
    try {
      let calcData = {};
      
      if (field === 'prix_vente' && formData.honoraires_calcul.pourcentage) {
        calcData = {
          prix_vente: parseFloat(value),
          pourcentage: parseFloat(formData.honoraires_calcul.pourcentage)
        };
      } else if (field === 'pourcentage' && formData.honoraires_calcul.prix_vente) {
        calcData = {
          prix_vente: parseFloat(formData.honoraires_calcul.prix_vente),
          pourcentage: parseFloat(value)
        };
      } else if (field === 'honoraires' && formData.honoraires_calcul.prix_vente) {
        calcData = {
          prix_vente: parseFloat(formData.honoraires_calcul.prix_vente),
          honoraires: parseFloat(value)
        };
      } else if (field === 'net_vendeur' && formData.honoraires_calcul.pourcentage) {
        calcData = {
          net_vendeur: parseFloat(value),
          pourcentage: parseFloat(formData.honoraires_calcul.pourcentage)
        };
      }

      if (Object.keys(calcData).length >= 2) {
        const response = await axios.post(`${API}/calculate/honoraires`, calcData, { headers: getAuthHeaders() });
        setFormData(prev => ({
          ...prev,
          honoraires_calcul: response.data
        }));
      } else {
        // Just update the single field if no calculation possible
        handleInputChange('honoraires_calcul', field, value);
      }
    } catch (error) {
      console.error("Erreur lors du calcul des honoraires:", error);
      handleInputChange('honoraires_calcul', field, value);
    }
  };

  // Address autocomplete functions
  const searchAddress = async (query, type = 'property') => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    try {
      // API Adresse française officielle + Monaco
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=8`
      );
      const data = await response.json();
      
      // Add Monaco addresses if query contains "monaco"
      let suggestions = data.features || [];
      
      if (query.toLowerCase().includes('monaco')) {
        const monacoAddresses = [
          { properties: { label: "Avenue des Beaux-Arts, 98000 Monaco", city: "Monaco", postcode: "98000" }},
          { properties: { label: "Boulevard Albert Ier, 98000 Monaco", city: "Monaco", postcode: "98000" }},
          { properties: { label: "Avenue Saint-Martin, 98000 Monaco", city: "Monaco", postcode: "98000" }},
          { properties: { label: "Rue Grimaldi, 98000 Monaco", city: "Monaco", postcode: "98000" }},
          { properties: { label: "Boulevard du Larvotto, 98000 Monaco", city: "Monaco", postcode: "98000" }}
        ];
        suggestions = [...monacoAddresses, ...suggestions];
      }

      setAddressSuggestions(suggestions.slice(0, 8));
      setShowAddressSuggestions(true);
      setAddressInputFocus(type);
    } catch (error) {
      console.error("Erreur lors de la recherche d'adresse:", error);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
  };
  
  // City autocomplete functions (for localisation in onglet Général) - INTERNATIONAL
  const searchCity = async (query) => {
    if (query.length < 2) {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
      return;
    }

    try {
      // Nominatim OSM API - Base de données INTERNATIONALE
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}&` +
        `format=json&` +
        `addressdetails=1&` +
        `limit=10&` +
        `featuretype=city&` +
        `accept-language=fr`
      );
      const data = await response.json();
      
      console.log('🌍 Résultats recherche ville internationale:', data.length);

      setCitySuggestions(data);
      setShowCitySuggestions(true);
    } catch (error) {
      console.error("Erreur lors de la recherche de ville internationale:", error);
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  const selectCity = (suggestion) => {
    // Extract city name from address or display_name
    const city = suggestion.address?.city || 
                 suggestion.address?.town || 
                 suggestion.address?.village || 
                 suggestion.display_name.split(',')[0];
    
    const country = suggestion.address?.country || '';
    const displayValue = country ? `${city}, ${country}` : city;
    
    console.log('✅ Ville sélectionnée:', displayValue);
    
    handleInputChange('bien_general', 'ville_generale', displayValue);
    setShowCitySuggestions(false);
    setCitySuggestions([]);
  };


  const selectAddress = (suggestion, type) => {
    const address = suggestion.properties.label;
    const city = suggestion.properties.city;
    const postcode = suggestion.properties.postcode;

    if (type === 'property') {
      handleInputChange('bien_general', 'adresse', address);
      handleInputChange('bien_general', 'ville', city);
      handleInputChange('bien_general', 'code_postal', postcode);
    } else if (type === 'proprietaire') {
      handleNestedInputChange('informations_confidentielles', 'proprietaire', 'adresse', address);
      handleNestedInputChange('informations_confidentielles', 'proprietaire', 'ville', city);
      handleNestedInputChange('informations_confidentielles', 'proprietaire', 'code_postal', postcode);
    } else if (type === 'confidentiel') {
      handleInputChange('informations_confidentielles', 'adresse', address);
      handleInputChange('informations_confidentielles', 'ville', city);
      handleInputChange('informations_confidentielles', 'code_postal', postcode);
    }

    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
  };

  // Helper function to clean data for backend
  // ROBUST data cleaning function - BLACKLIST approach
  // Converts ALL empty strings to null by default, EXCEPT text fields that should keep empty strings
  const cleanDataRecursive = (data) => {
    if (!data) return data;
    
    const cleaned = JSON.parse(JSON.stringify(data)); // Deep copy
    
    // BLACKLIST: Fields that MUST keep empty strings (text fields, descriptions, comments)
    const textFieldsToPreserve = {
      // Text descriptions and comments
      'content': true, 'description': true, 'commentaire': true, 'commentaires': true,
      'notes': true, 'remarques': true, 'observations': true,
      
      // Names and labels
      'enseigne': true, 'nom': true, 'prenom': true, 'nom_societe': true,
      'societe': true, 'raison_sociale': true, 'title': true, 'titre': true,
      
      // Addresses and locations
      'adresse': true, 'adresse_complete': true, 'rue': true, 'complement_adresse': true,
      'ville': true, 'pays': true, 'quartier': true,
      
      // Contacts
      'email': true, 'telephone': true, 'numero': true, 'fax': true,
      
      // Free text fields
      'brasserie': true, 'fournisseur_cafe': true, 'vitrine': true,
      'fils_semaine': true, 'kms_cafe_semaine': true, 'facade': true,
      'horaires_ouverture_fermeture': true, 'fermeture_hebdomadaire': true,
      'conges_fermeture_saisonniere': true, 'avocat_notaire': true,
      'expert_comptable': true, 'negociateur': true, 'identite_bailleur': true,
      'conditions_transfert': true, 'acces_livraison': true, 'configuration_bureaux': true,
      
      // Document fields
      'url_dossier': true, 'url': true, 'video_url': true, 'visite_virtuelle_url': true,
      'name': true, 'type': true,
      
      // Propriétaire fields  
      'proprietaire': true, 'conditions_visites': true, 'modalites_vente': true,
      
      // Multilingual content - CRITICAL: Must keep empty strings for Pydantic validation
      'Français': true, 'Anglais': true, 'Espagnol': true, 'Italien': true, 'Allemand': true,
      
      // Other text fields
      'commentaires_prestations': true, 'travaux_prevoir': true,
      'classement': true, 'anciennete_activite': true,
      'type_licence': true, 'numero_licence': true, 'validite_licence': true
    };
    
    // Recursive cleaning function
    const cleanValue = (value, fieldName = '', parentKey = '') => {
      // Keep empty strings for text fields in the blacklist
      if (value === "" && textFieldsToPreserve[fieldName]) {
        return value; // Preserve empty string
      }
      
      // Convert all other empty strings to null (numeric, boolean, enum, date fields)
      if (value === "") {
        return null;
      }
      
      // Recursively clean objects
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        const cleanedObj = {};
        for (const key in value) {
          if (value.hasOwnProperty(key)) {
            cleanedObj[key] = cleanValue(value[key], key, fieldName);
          }
        }
        return cleanedObj;
      }
      
      // Recursively clean arrays
      if (Array.isArray(value)) {
        return value.map(item => cleanValue(item, fieldName, parentKey));
      }
      
      return value;
    };
    
    const result = cleanValue(cleaned);
    
    console.log('🧹 Data cleaned (BLACKLIST approach):', {
      before_composition: data?.composition,
      after_composition: result?.composition,
      before_bien_general: data?.bien_general,
      after_bien_general: result?.bien_general
    });
    
    return result;
  };

  const cleanDataForBackend = (data) => {
    console.log('🔵 AVANT NETTOYAGE - cleanDataForBackend appelé');
    console.log('📊 bien_general AVANT:', JSON.stringify(data?.bien_general, null, 2));
    console.log('📊 composition AVANT:', JSON.stringify(data?.composition, null, 2));
    console.log('📊 informations_bail AVANT:', JSON.stringify(data?.informations_bail, null, 2));
    
    // Clean the entire data structure recursively
    const recursiveCleaned = cleanDataRecursive(data);
    
    console.log('✅ APRÈS NETTOYAGE');
    console.log('📊 bien_general APRÈS:', JSON.stringify(recursiveCleaned?.bien_general, null, 2));
    console.log('📊 composition APRÈS:', JSON.stringify(recursiveCleaned?.composition, null, 2));
    console.log('📊 informations_bail APRÈS:', JSON.stringify(recursiveCleaned?.informations_bail, null, 2));
    
    // Additional specific cleaning for medias to filter out invalid entries
    if (recursiveCleaned?.medias) {
      console.log('📸 MEDIAS AVANT NETTOYAGE:', JSON.stringify(recursiveCleaned.medias, null, 2));
      ['photos', 'documents', 'plans'].forEach(mediaType => {
        if (recursiveCleaned.medias[mediaType]) {
          const before = recursiveCleaned.medias[mediaType].length;
          recursiveCleaned.medias[mediaType] = recursiveCleaned.medias[mediaType].filter(item => 
            item && item.url && typeof item.url === 'string' && item.url.length > 0
          );
          const after = recursiveCleaned.medias[mediaType].length;
          if (before !== after) {
            console.warn(`⚠️ ${mediaType}: ${before - after} éléments invalides retirés`);
          }
        }
      });
      console.log('📸 MEDIAS APRÈS NETTOYAGE:', JSON.stringify(recursiveCleaned.medias, null, 2));
    }
    
    // IMPORTANT: Nettoyage de prestations - déplacer les champs non-boolean vers les bonnes sections
    if (recursiveCleaned?.prestations && typeof recursiveCleaned.prestations === 'object') {
      console.log('🧹 NETTOYAGE PRESTATIONS - AVANT:', JSON.stringify(recursiveCleaned.prestations, null, 2));
      const cleanedPrestations = {};
      const fieldsToRelocate = [];
      
      // Charger tous les packs pour connaître les destinations des champs
      const allPacks = [packRestauration, packRetail, packEnseignes];
      
      Object.keys(recursiveCleaned.prestations).forEach(key => {
        const value = recursiveCleaned.prestations[key];
        
        // Ne garder que les vrais boolean
        if (typeof value === 'boolean') {
          cleanedPrestations[key] = value;
        } else {
          // Ce champ doit être déplacé - trouver sa destination dans les packs
          let targetLocation = null;
          
          for (const pack of allPacks) {
            if (pack && pack.champs) {
              const fieldConfig = pack.champs.find(f => f.id === key);
              if (fieldConfig && fieldConfig.groupe_data) {
                targetLocation = fieldConfig.groupe_data;
                break;
              }
            }
          }
          
          if (targetLocation) {
            fieldsToRelocate.push({ key, value, targetLocation });
            console.log(`🔄 Champ "${key}" sera déplacé de prestations vers ${targetLocation} (valeur: ${value})`);
          } else {
            console.warn(`⚠️ Champ "${key}" retiré de prestations (valeur: ${value}, type: ${typeof value}). Destination inconnue.`);
          }
        }
      });
      
      recursiveCleaned.prestations = cleanedPrestations;
      
      // Déplacer les champs vers leurs destinations correctes
      fieldsToRelocate.forEach(({ key, value, targetLocation }) => {
        // Parse le targetLocation (ex: "composition.chambre_froide" -> section: composition, field: chambre_froide)
        const [section, fieldName] = targetLocation.split('.');
        
        if (section && fieldName) {
          // Initialiser la section si elle n'existe pas
          if (!recursiveCleaned[section]) {
            recursiveCleaned[section] = {};
          }
          
          // Déplacer le champ
          recursiveCleaned[section][fieldName] = value;
          console.log(`✅ Champ "${key}" déplacé vers ${section}.${fieldName} = ${value}`);
        }
      });
      
      console.log('🧹 NETTOYAGE PRESTATIONS - APRÈS:', JSON.stringify(recursiveCleaned.prestations, null, 2));
      if (fieldsToRelocate.length > 0) {
        console.log('📊 composition APRÈS RELOCALISATION:', JSON.stringify(recursiveCleaned?.composition, null, 2));
        console.log('📊 bien_general APRÈS RELOCALISATION:', JSON.stringify(recursiveCleaned?.bien_general, null, 2));
      }
    }
    
    return recursiveCleaned;
  };

  const createProperty = async () => {
    try {
      // Clean and prepare data for backend
      const cleanedData = cleanDataForBackend({
        // Nouvelle taxonomie (Étape 2)
        operation: formData.operation,
        objet: formData.objet,
        profil: formData.profil,
        sous_profil: formData.sous_profil,
        cible: formData.cible,
        // Données principales
        bien_general: formData.bien_general,
        enseigne: formData.enseigne,
        honoraires_calcul: formData.honoraires_calcul,
        informations_bail: formData.informations_bail,
        ouverture_fermeture: formData.ouverture_fermeture,
        conseils_vendeur: formData.conseils_vendeur,
        negociateur: formData.negociateur,
        documents_google_drive: formData.documents_google_drive,
        composition: formData.composition,
        prestations: formData.prestations || {},
        packs_actifs: formData.packs_actifs || {}, // AJOUT: packs sélectionnés
        commentaires_prestations: formData.commentaires_prestations || "",
        descriptif: formData.descriptif,
        informations_confidentielles: formData.informations_confidentielles,
        informations_vente: formData.informations_vente,
        medias: formData.medias
      });

      console.log("Creating new property with cleaned data...");
      const response = await axios.post(`${API}/properties`, cleanedData, { headers: getAuthHeaders() });
      
      console.log("✅ Property created, response:", response.data);
      const newPropertyId = response.data?.id;
      
      // Create contact from proprietaire if data exists
      if (formData.informations_confidentielles?.proprietaire) {
        await createContactFromPropertyInfo(
          formData.informations_confidentielles.proprietaire,
          "Propriétaire"
        );
      }
      
      // Create contact from syndic if data exists
      if (formData.informations_confidentielles?.syndic_nom) {
        await createContactFromPropertyInfo(
          {
            syndic_nom: formData.informations_confidentielles.syndic_nom,
            syndic_telephone: formData.informations_confidentielles.syndic_telephone,
            syndic_email: formData.informations_confidentielles.syndic_email,
            syndic_adresse: formData.informations_confidentielles.syndic_adresse
          },
          "Syndic"
        );
      }
      
      alert("Bien créé avec succès!");
      
      // Appeler le callback avec l'ID du nouveau bien pour redirection
      if (onPropertyEditComplete && newPropertyId) {
        console.log("📤 Calling onPropertyEditComplete with ID:", newPropertyId);
        onPropertyEditComplete(newPropertyId);
      } else {
        // Fallback : recharger la liste
        setCurrentProperty(null);
        setShowForm(false);
        loadProperties(currentFilter);
      }
    } catch (error) {
      console.error("Erreur lors de la création:", error);
      console.error("Response data:", error.response?.data);
      if (error.response?.data?.detail) {
        console.error("Validation errors:", error.response.data.detail);
      }
      
      // Better error message formatting
      let errorMessage = "Erreur lors de la création du bien";
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage += `: ${error.response.data.detail}`;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage += `: ${error.response.data.detail.map(err => err.msg || err).join(', ')}`;
        } else {
          errorMessage += `: ${JSON.stringify(error.response.data.detail)}`;
        }
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  const updateProperty = async () => {
    if (!currentProperty) return;
    
    try {
      // Ensure required fields are always present
      const ensureRequiredFields = (data) => {
        // Ensure bien_general has required type_bien field
        if (data.bien_general && !data.bien_general.type_bien) {
          data.bien_general.type_bien = currentProperty.bien_general?.type_bien || "Commerce";
        }
        
        // Ensure all objects are properly structured 
        return {
          ...data,
          bien_general: data.bien_general || currentProperty.bien_general || { type_bien: "Commerce" },
          informations_confidentielles: data.informations_confidentielles || {}
        };
      };
      
      // Clean and prepare data for backend (same as createProperty)
      const cleanedData = cleanDataForBackend({
        transaction_type: formData.transaction_type || "Vente", // NOUVEAU
        // Nouvelle taxonomie (Étape 2)
        operation: formData.operation,
        objet: formData.objet,
        profil: formData.profil,
        sous_profil: formData.sous_profil,
        cible: formData.cible,
        // Données principales
        bien_general: formData.bien_general,
        enseigne: formData.enseigne,
        honoraires_calcul: formData.honoraires_calcul,
        informations_bail: formData.informations_bail,
        ouverture_fermeture: formData.ouverture_fermeture,
        conseils_vendeur: formData.conseils_vendeur,
        negociateur: formData.negociateur,
        documents_google_drive: formData.documents_google_drive,
        composition: formData.composition,
        prestations: formData.prestations || {},
        packs_actifs: formData.packs_actifs || {}, // AJOUT: packs sélectionnés
        commentaires_prestations: formData.commentaires_prestations || "",
        descriptif: formData.descriptif,
        informations_confidentielles: formData.informations_confidentielles,
        informations_vente: formData.informations_vente,
        medias: formData.medias
      });
      
      // Ensure required fields are present
      let finalData = ensureRequiredFields(cleanedData);

      // S'assurer que la taxonomie finale est bien présente dans le payload
      finalData = {
        ...finalData,
        operation: formData.operation || null,
        objet: formData.objet || null,
        profil: formData.profil || null,
        sous_profil: formData.sous_profil || null,
        cible: formData.cible || null
      };

      console.log('📊 TAXONOMIE FINALE envoyée depuis updateProperty:');
      console.log('  - operation:', finalData.operation);
      console.log('  - objet:', finalData.objet);
      console.log('  - profil:', finalData.profil);
      console.log('  - sous_profil:', finalData.sous_profil);
      console.log('  - cible:', finalData.cible);
      console.log('📊 formData ACTUEL au moment de updateProperty:');
      console.log('  - formData.operation:', formData.operation);
      console.log('  - formData.objet:', formData.objet);
      console.log('  - formData.profil:', formData.profil);
      console.log('  - formData.sous_profil:', formData.sous_profil);
      console.log('  - formData.cible:', formData.cible);

      console.log("Updating property with cleaned data...", {
        mediaPhotosCount: finalData.medias?.photos?.length || 0,
        mediaDocumentsCount: finalData.medias?.documents?.length || 0,
        mediaPlansCount: finalData.medias?.plans?.length || 0,
        fullDataSize: JSON.stringify(finalData).length,
        hasMedias: !!finalData.medias,
        hasRequiredFields: !!finalData.bien_general?.type_bien
      });
      
      console.log("📤 DONNÉES ENVOYÉES AU BACKEND - bien_general:", JSON.stringify(finalData.bien_general, null, 2));
      console.log("📤 DONNÉES ENVOYÉES AU BACKEND - composition:", JSON.stringify(finalData.composition, null, 2));
      console.log("📤 DONNÉES ENVOYÉES AU BACKEND - composition.chambre_froide:", finalData.composition?.chambre_froide);
      console.log("📤 DONNÉES ENVOYÉES AU BACKEND - prestations:", JSON.stringify(finalData.prestations, null, 2));
      console.log("📤 DONNÉES ENVOYÉES AU BACKEND - packs_actifs:", JSON.stringify(finalData.packs_actifs, null, 2));
      
      // Log specific sections to identify problematic data
      if (finalData.medias?.photos?.length > 0) {
        console.log("Sample photo data:", {
          id: finalData.medias.photos[0].id,
          name: finalData.medias.photos[0].name,
          type: finalData.medias.photos[0].type,
          urlLength: finalData.medias.photos[0].url?.length || 0,
          urlPrefix: finalData.medias.photos[0].url?.substring(0, 30) + "..."
        });
      }
      
      await axios.put(`${API}/properties/${currentProperty.id}`, finalData);
      
      // Recharger currentProperty pour mettre à jour le header
      const updatedPropertyResponse = await axios.get(`${API}/properties/${currentProperty.id}`);
      setCurrentProperty(updatedPropertyResponse.data);
      console.log('✅ currentProperty rechargé après mise à jour');
      
      // Create contact from proprietaire if data exists and it's a new proprietaire
      if (formData.informations_confidentielles?.proprietaire) {
        await createContactFromPropertyInfo(
          formData.informations_confidentielles.proprietaire,
          "Propriétaire"
        );
      }
      
      // Create contact from syndic if data exists
      if (formData.informations_confidentielles?.syndic_nom) {
        await createContactFromPropertyInfo(
          {
            syndic_nom: formData.informations_confidentielles.syndic_nom,
            syndic_telephone: formData.informations_confidentielles.syndic_telephone,
            syndic_email: formData.informations_confidentielles.syndic_email,
            syndic_adresse: formData.informations_confidentielles.syndic_adresse
          },
          "Syndic"
        );
      }
      
      // En mode édit via PropertyEditPage, ne pas recharger la liste pour rester sur le formulaire
      if (onPropertyEditComplete) {
        console.log("✅ Mode édition - pas de rechargement de liste après updateProperty");
      } else {
        loadProperties(currentFilter);
      }
      alert("Bien mis à jour avec succès!");
    } catch (error) {
      console.error("❌ Erreur lors de la mise à jour:", error);
      console.error("❌ Response data:", error.response?.data);
      if (error.response?.data?.detail) {
        console.error("❌ Validation errors:", error.response.data.detail);
        console.error("❌ DÉTAILS DES ERREURS:", JSON.stringify(error.response.data.detail, null, 2));
      }
      
      // Better error message formatting
      let errorMessage = "Erreur lors de la mise à jour du bien";
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage += `: ${error.response.data.detail}`;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage += `: ${error.response.data.detail.map(err => err.msg || err).join(', ')}`;
        } else {
          errorMessage += `: ${JSON.stringify(error.response.data.detail)}`;
        }
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      bien_general: {
        type_bien: "Restaurant Pizzeria Snack",
        classement: "",
        murs_vente: "Non",
        identite_bailleur: "",
        prix_estime: "",
        location_mois_licence: "",
        travaux_prevoir: "",
        anciennete_activite: "",
        forme_juridique: ""
      },
      enseigne: {
        enseigne: "",
        superficie: ""
      },
      honoraires_calcul: {
        prix_vente: "",
        pourcentage: "",
        honoraires: "",
        net_vendeur: ""
      },
      informations_bail: {
        titre_locatif: "",
        loyer_soumis_tva: "",
        date_debut_bail: "",
        loyer_mois_charges_taxes: "",
        charges_mensuelles: "",
        impot_foncier: "",
        procedure: "",
        appartement_prive: ""
      },
      ouverture_fermeture: {
        horaires_ouverture_fermeture: "",
        fermeture_hebdomadaire: "",
        conges_fermeture_saisonniere: ""
      },
      conseils_vendeur: {
        avocat_notaire: "",
        expert_comptable: ""
      },
      negociateur: {
        negociateur: ""
      },
      documents_google_drive: {
        url_dossier: "",
        description: "Collez l'URL du dossier/fichier Google Drive"
      },
      composition: {
        brasserie: "",
        fournisseur_cafe: "",
        nombre_salles: 0,
        nombre_employes: 0,
        vitrine: "",
        fils_semaine: "",
        kms_cafe_semaine: "",
        facade: "",
        reserve: "",
        places_assises_interieur: 0,
        places_assises_terrasse: 0
      },
      prestations: {},
      commentaires_prestations: "",
      
      // New tabs data - CLEAN multilingual structure
      descriptif: {
        langue_active: "Français",
        descriptions: {
          "Fiche Technique": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Affiche vitrine": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Web": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Pub": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Mandat": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Autre": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          }
        },
        proximite: {
          "Français": "",
          "Anglais": "",
          "Espagnol": "",
          "Italien": ""
        }
      },
      informations_confidentielles: {
        proprietaire: {
          societe: "",
          forme_juridique: "",
          activite: "",
          siret: "",
          nom: "",
          prenom: "",
          groupe: "",
          adresse: "",
          code_postal: "",
          ville: "",
          contacts: {
            telephones: [
              { type: "pro", value: "", label: "Professionnel" },
              { type: "perso", value: "", label: "Personnel" },
              { type: "mobile", value: "", label: "Mobile" }
            ],
            emails: [
              { type: "pro", value: "", label: "Professionnel" },
              { type: "perso", value: "", label: "Personnel" }
            ]
          }
        },
        
        adresse: "",
        code_postal: "",
        ville: "",
        coordonnees_gps: "",
        syndic_nom: "",
        syndic_adresse: "",
        syndic_contacts: {
          telephones: [
            { type: "pro", value: "", label: "Bureau" }
          ],
          emails: [
            { type: "pro", value: "", label: "Bureau" }
          ]
        },
        conditions_visites: "",
        horaires_visites: "",
        contact_visites: "",
        remarques_visites: "",
        informations_diverses: "",
        notes_particulieres: "",
        chiffre_affaires_annuel: "",
        benefices_annuels: "",
        charges_annuelles: "",
        banque: "",
        compte_bancaire: "",
        raison_vente: "",
        commentaires_confidentiels: ""
      },
      informations_vente: {
        prix_annonce: "",
        prix_minimum_accepte: "",
        date_disponibilite: "",
        delai_liberation: "",
        conditions_visite: "",
        contact_visite: "",
        documents_requis: "",
        statut_documents: ""
      },
      medias: {
        photos: [],
        documents: [],
        plans: [],
        video_url: "",
        visite_virtuelle_url: ""
      }
    });
  };

  const selectProperty = (property) => {
    setCurrentProperty(property);
    
    // Populate form with existing data - NO FALLBACK to avoid contamination
    setFormData({
      bien_general: property.bien_general || {
        type_bien: "Restaurant Pizzeria Snack",
        classement: "",
        murs_vente: "Non",
        identite_bailleur: "",
        prix_estime: "",
        location_mois_licence: "",
        travaux_prevoir: "",
        anciennete_activite: "",
        forme_juridique: ""
      },
      enseigne: property.enseigne || { enseigne: "", superficie: "" },
      honoraires_calcul: property.honoraires_calcul || { prix_vente: "", pourcentage: "", honoraires: "", net_vendeur: "" },
      informations_bail: property.informations_bail || {
        titre_locatif: "",
        loyer_soumis_tva: "",
        date_debut_bail: "",
        loyer_mois_charges_taxes: "",
        charges_mensuelles: "",
        impot_foncier: "",
        procedure: "",
        appartement_prive: ""
      },
      ouverture_fermeture: property.ouverture_fermeture || {
        horaires_ouverture_fermeture: "",
        fermeture_hebdomadaire: "",
        conges_fermeture_saisonniere: ""
      },
      conseils_vendeur: property.conseils_vendeur || { avocat_notaire: "", expert_comptable: "" },
      negociateur: property.negociateur || { negociateur: "" },
      documents_google_drive: property.documents_google_drive || { url_dossier: "", description: "Collez l'URL du dossier/fichier Google Drive" },
      composition: property.composition || {
        brasserie: "",
        fournisseur_cafe: "",
        nombre_salles: 0,
        nombre_employes: 0,
        vitrine: "",
        fils_semaine: "",
        kms_cafe_semaine: "",
        facade: "",
        reserve: "",
        places_assises_interieur: 0,
        places_assises_terrasse: 0
      },
      prestations: property.prestations || {},
      commentaires_prestations: property.commentaires_prestations || "",
      descriptif: property.descriptif || {
        langue_active: "Français",
        descriptions: {
          "Fiche Technique": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Affiche vitrine": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Web": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Pub": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Mandat": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          },
          "Autre": { 
            "Français": { content: "", optimized_by_ai: false },
            "Anglais": { content: "", optimized_by_ai: false },
            "Espagnol": { content: "", optimized_by_ai: false },
            "Italien": { content: "", optimized_by_ai: false }
          }
        },
        proximite: {
          "Français": "",
          "Anglais": "",
          "Espagnol": "",
          "Italien": ""
        }
      },
      informations_confidentielles: property.informations_confidentielles || {
        proprietaire: {
          societe: "",
          forme_juridique: "",
          activite: "",
          siret: "",
          nom: "",
          prenom: "",
          groupe: "",
          adresse: "",
          code_postal: "",
          ville: "",
          contacts: {
            telephones: [
              { type: "pro", value: "", label: "Professionnel" },
              { type: "perso", value: "", label: "Personnel" },
              { type: "mobile", value: "", label: "Mobile" }
            ],
            emails: [
              { type: "pro", value: "", label: "Professionnel" },
              { type: "perso", value: "", label: "Personnel" }
            ]
          }
        },
        adresse: "",
        code_postal: "",
        ville: "",
        coordonnees_gps: "",
        syndic_nom: "",
        syndic_adresse: "",
        syndic_contacts: {
          telephones: [{ type: "pro", value: "", label: "Bureau" }],
          emails: [{ type: "pro", value: "", label: "Bureau" }]
        },
        conditions_visites: "",
        horaires_visites: "",
        contact_visites: "",
        remarques_visites: "",
        informations_diverses: "",
        notes_particulieres: "",
        chiffre_affaires_annuel: "",
        benefices_annuels: "",
        charges_annuelles: "",
        banque: "",
        compte_bancaire: "",
        raison_vente: "",
        commentaires_confidentiels: ""
      },
      informations_vente: property.informations_vente || {
        prix_annonce: "",
        prix_minimum_accepte: "",
        date_disponibilite: "",
        delai_liberation: "",
        conditions_visite: "",
        contact_visite: "",
        documents_requis: "",
        statut_documents: ""
      },
      medias: property.medias || {
        photos: [],
        documents: [],
        plans: [],
        video_url: "",
        visite_virtuelle_url: ""
      }
    });
    
    setShowForm(true);
    setShowDashboard(false);
    setShowContacts(false);
    setActiveSection('properties');
  };

  // Start new property creation
  // ⚠️ Doit lancer le wizard via la route officielle, pas via l'ancien startWizard interne
  const startNewProperty = () => {
    console.log("🎯 startNewProperty → navigate /properties/wizard/new");
    navigate('/properties/wizard/new');
  };

  const renderGeneralTab = () => {
    // Charger la configuration dynamique
    const activiteActuelle = formData.bien_general?.activite_principale;
    const config = getFieldsConfig();
    console.log('Config complete pour activite:', activiteActuelle, config);
    
    const generalConfig = config?.fullConfig?.onglets?.general;
    console.log('GeneralConfig:', generalConfig);
    
    if (!generalConfig) {
      console.warn('Aucune configuration trouvee pour l onglet General');
      return (
        <div className="p-8 text-center text-gray-500">
          <i className="bi bi-exclamation-triangle text-4xl mb-3 block"></i>
          <p>Configuration de l'onglet Général non disponible</p>
          <p className="text-xs mt-2">Activite: {activiteActuelle || 'Non définie'}</p>
          <p className="text-xs mt-2">Config type: {config?.configType}</p>
        </div>
      );
    }
    
    console.log('Config General chargee:', generalConfig);
    
    // Icônes par section
    const sectionIcons = {
      'identification': 'bi-card-heading',
      'prix_honoraires': 'bi-currency-euro',
      'localisation': 'bi-geo-alt'
    };
    
    // Couleurs par section
    const sectionColors = {
      'identification': { from: 'blue-500', to: 'blue-600', hover: 'blue-500/10' },
      'prix_honoraires': { from: 'emerald-500', to: 'teal-600', hover: 'emerald-500/10' },
      'localisation': { from: 'orange-500', to: 'red-600', hover: 'orange-500/10' }
    };
    
    return (
      <div className="space-y-8">
        {/* Modern info header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-indigo-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/25">
              <i className="bi bi-info-circle text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-blue-600 bg-clip-text text-transparent">
                Informations Générales
              </h2>
              <p className="text-gray-600 font-medium">
                Configurez les données principales de votre bien immobilier
              </p>
            </div>
          </div>
        </div>

        {/* Taxonomie - Étape 2 */}
        <div className="relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-blue-500/5">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-purple-50/50"></div>
          <div className="relative p-8">
            {/* Section Header */}
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-indigo-500/25">
                <i className="bi bi-diagram-3 text-lg"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Classification du bien</h3>
                <p className="text-gray-600 text-sm">Opération → Objet → Profil → Sous-profil</p>
              </div>
            </div>
            
            {/* Taxonomy Selector */}
            <TaxonomySelector
              value={{
                operation: formData.operation,
                objet: formData.objet,
                profil: formData.profil,
                sous_profil: formData.sous_profil,
                cible: formData.cible
              }}
              onChange={(taxonomyData) => {
                // Mettre à jour formData avec les nouvelles valeurs de taxonomie EN UN SEUL APPEL
                console.log('📥 TaxonomySelector onChange reçu:', taxonomyData);
                setFormData(prevFormData => {
                  const updatedData = {
                    ...prevFormData,
                    operation: taxonomyData.operation,
                    objet: taxonomyData.objet,
                    profil: taxonomyData.profil,
                    sous_profil: taxonomyData.sous_profil,
                    cible: taxonomyData.cible
                  };
                  console.log('✅ formData mis à jour avec taxonomie:', {
                    operation: updatedData.operation,
                    objet: updatedData.objet,
                    profil: updatedData.profil,
                    sous_profil: updatedData.sous_profil,
                    cible: updatedData.cible
                  });
                  return updatedData;
                });
              }}
              disabled={false}
            />
          </div>
        </div>

        {/* Render dynamic sections from JSON */}
        {Object.entries(generalConfig.sections || {}).map(([sectionKey, section]) => {
          const color = sectionColors[sectionKey] || { from: 'gray-500', to: 'gray-600', hover: 'gray-500/10' };
          const icon = sectionIcons[sectionKey] || 'bi-info-circle';
          
          return (
            <div key={sectionKey} className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-blue-500/5 hover:shadow-lg transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-purple-50/50"></div>
              <div className="relative p-8">
                {/* Section Header */}
                <div className="flex items-center mb-6">
                  <div className={`w-12 h-12 bg-gradient-to-br from-${color.from} to-${color.to} rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-${color.from}/25 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`bi ${icon} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{section.label}</h3>
                    {section.description && (
                      <p className="text-gray-600 text-sm">{section.description}</p>
                    )}
                  </div>
                </div>
                
                {/* Section Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(section.champs || []).filter(champ => {
                    // Masquer les champs legacy remplacés par la taxonomie
                    const legacyFields = ['transaction_type', 'type_bien', 'activite_principale', 'category', 'subcategory'];
                    return !legacyFields.includes(champ.id);
                  }).map((champ, index) => {
                    // Get field value based on groupe_data with correct type handling
                    const parts = champ.groupe_data.split('.'); // Nécessaire pour handleInputChange
                    const value = getFieldValue(formData, champ.groupe_data, champ.type);
                    
                    // Debug logs for type_bien and activite_principale
                    if (champ.id === 'type_bien' || champ.id === 'activite_principale') {
                      console.log(`📋 Champ ${champ.id}:`, {
                        value,
                        formDataSection: formData[parts[0]],
                        fullFormData: formData.bien_general
                      });
                    }
                    
                    let champToRender = champ;
                    
                    // FILTRAGE DYNAMIQUE: Si c'est le champ type_bien, filtrer selon transaction_type
                    if (champ.id === 'type_bien' && champ.type === 'enum') {
                      const transactionType = formData.transaction_type || 'Vente';
                      const typesBienFiltres = getTypesBienByTransaction(transactionType);
                      if (typesBienFiltres.length > 0) {
                        champToRender = {
                          ...champ,
                          options: typesBienFiltres
                        };
                        console.log(`🔍 Filtrage types de biens pour ${transactionType}:`, typesBienFiltres.length, 'options');
                      }
                    }
                    
                    // FILTRAGE DYNAMIQUE: Si c'est le champ activite_principale, filtrer selon type_bien
                    if (champ.id === 'activite_principale' && champ.type === 'enum') {
                      const typeBienSelectionne = formData.bien_general?.type_bien;
                      if (typeBienSelectionne) {
                        const activitesFiltrées = getActivitiesByTypeBien(typeBienSelectionne);
                        if (activitesFiltrées.length > 0) {
                          champToRender = {
                            ...champ,
                            options: activitesFiltrées
                          };
                          console.log(`🔍 Filtrage activités pour ${typeBienSelectionne}:`, activitesFiltrées.length, 'options');
                        }
                      }
                    }
                    
                    return (
                      <div key={index} className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700">
                          {champToRender.label}
                          {champToRender.obligatoire && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {renderDynamicField(champToRender, value, (newValue) => {
                          handleInputChange(parts[0], parts[1], newValue);
                        })}
                        {champToRender.description && (
                          <span className="text-xs text-gray-500">{champToRender.description}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPrestationsTab = () => (
    <div className="space-y-8">
      {/* Modern info header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-indigo-500/10 border border-white/20 backdrop-blur-sm">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-600/10"></div>
        <div className="relative p-6 flex items-center">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25">
            <i className="bi bi-gear-wide-connected text-2xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-purple-600 bg-clip-text text-transparent">
              Prestations & Services
            </h2>
            <p className="text-gray-600 font-medium">
              Configuration des équipements et services du bien
            </p>
          </div>
        </div>
      </div>

      {/* Section Activation des Packs */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-indigo-300 shadow-2xl shadow-indigo-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-white/50 to-purple-50/50"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-indigo-500/25">
              <i className="bi bi-puzzle text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Packs d'activité</h3>
              <p className="text-gray-600 text-sm">Activez les packs spécifiques à votre activité</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Pack Restauration */}
            <label className="flex items-center space-x-3 cursor-pointer p-4 bg-white/50 backdrop-blur-sm border-2 border-orange-200 rounded-xl hover:bg-orange-50/70 hover:border-orange-400 transition-all duration-300">
              <input
                type="checkbox"
                className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                checked={formData.packs_actifs?.restauration || false}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    packs_actifs: {
                      ...prev.packs_actifs,
                      restauration: e.target.checked
                    }
                  }));
                }}
              />
              <div className="flex-1">
                <div className="flex items-center">
                  <i className="bi bi-cup-hot text-orange-600 mr-2"></i>
                  <span className="text-sm font-bold text-gray-700">Pack Restauration</span>
                </div>
                <span className="text-xs text-gray-500 block mt-1">Extraction, cuisine, terrasse...</span>
              </div>
            </label>

            {/* Pack Retail */}
            <label className="flex items-center space-x-3 cursor-pointer p-4 bg-white/50 backdrop-blur-sm border-2 border-blue-200 rounded-xl hover:bg-blue-50/70 hover:border-blue-400 transition-all duration-300">
              <input
                type="checkbox"
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                checked={formData.packs_actifs?.retail || false}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    packs_actifs: {
                      ...prev.packs_actifs,
                      retail: e.target.checked
                    }
                  }));
                }}
              />
              <div className="flex-1">
                <div className="flex items-center">
                  <i className="bi bi-shop text-blue-600 mr-2"></i>
                  <span className="text-sm font-bold text-gray-700">Pack Retail</span>
                </div>
                <span className="text-xs text-gray-500 block mt-1">Vitrine, flux, emplacement...</span>
              </div>
            </label>

            {/* Pack Enseignes */}
            <label className="flex items-center space-x-3 cursor-pointer p-4 bg-white/50 backdrop-blur-sm border-2 border-purple-200 rounded-xl hover:bg-purple-50/70 hover:border-purple-400 transition-all duration-300">
              <input
                type="checkbox"
                className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                checked={formData.packs_actifs?.enseignes || false}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    packs_actifs: {
                      ...prev.packs_actifs,
                      enseignes: e.target.checked
                    }
                  }));
                }}
              />
              <div className="flex-1">
                <div className="flex items-center">
                  <i className="bi bi-building-check text-purple-600 mr-2"></i>
                  <span className="text-sm font-bold text-gray-700">Pack Enseignes</span>
                </div>
                <span className="text-xs text-gray-500 block mt-1">Ciblage enseignes...</span>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Champs dynamiques des packs actifs */}
      {(() => {
        const packsActifs = formData.packs_actifs || {};
        const renderedPacks = [];
        
        // Pack Restauration
        if (packsActifs.restauration) {
          const pack = loadPack('restauration');
          if (pack) {
            renderedPacks.push(
              <div key="pack-restauration" className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-orange-300 shadow-2xl shadow-orange-500/10 transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 via-white/50 to-yellow-50/50"></div>
                <div className="relative p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-orange-500/25">
                      <i className={`${pack.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{pack.label}</h3>
                      <p className="text-gray-600 text-sm">{pack.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {pack.champs.map((champ, index) => {
                      const value = getFieldValue(formData, champ.groupe_data, champ.type);
                      return (
                        <div key={index} className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">
                            {champ.label}
                            {champ.obligatoire && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {renderDynamicField(champ, value, (newValue) => {
                            const parts = champ.groupe_data.split('.');
                            console.log(`✏️ Pack Restauration Edit - ${champ.label} (${champ.groupe_data}): ${value} → ${newValue}`);
                            handleInputChange(parts[0], parts[1], newValue);
                          })}
                          {champ.description && (
                            <span className="text-xs text-gray-500 block">{champ.description}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          }
        }
        
        // Pack Retail
        if (packsActifs.retail) {
          const pack = loadPack('retail');
          if (pack) {
            renderedPacks.push(
              <div key="pack-retail" className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-blue-300 shadow-2xl shadow-blue-500/10 transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-cyan-50/50"></div>
                <div className="relative p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/25">
                      <i className={`${pack.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{pack.label}</h3>
                      <p className="text-gray-600 text-sm">{pack.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {pack.champs.map((champ, index) => {
                      const value = getFieldValue(formData, champ.groupe_data, champ.type);
                      return (
                        <div key={index} className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">
                            {champ.label}
                            {champ.obligatoire && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {renderDynamicField(champ, value, (newValue) => {
                            const parts = champ.groupe_data.split('.');
                            console.log(`✏️ Pack Restauration Edit - ${champ.label} (${champ.groupe_data}): ${value} → ${newValue}`);
                            handleInputChange(parts[0], parts[1], newValue);
                          })}
                          {champ.description && (
                            <span className="text-xs text-gray-500 block">{champ.description}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          }
        }
        
        // Pack Enseignes
        if (packsActifs.enseignes) {
          const pack = loadPack('enseignes');
          if (pack) {
            renderedPacks.push(
              <div key="pack-enseignes" className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-purple-300 shadow-2xl shadow-purple-500/10 transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-white/50 to-pink-50/50"></div>
                <div className="relative p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25">
                      <i className={`${pack.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{pack.label}</h3>
                      <p className="text-gray-600 text-sm">{pack.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {pack.champs.map((champ, index) => {
                      const value = getFieldValue(formData, champ.groupe_data, champ.type);
                      return (
                        <div key={index} className={champ.type === 'text' ? 'md:col-span-2 space-y-2' : 'space-y-2'}>
                          <label className="block text-sm font-semibold text-gray-700">
                            {champ.label}
                            {champ.obligatoire && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {renderDynamicField(champ, value, (newValue) => {
                            const parts = champ.groupe_data.split('.');
                            handleInputChange(parts[0], parts[1], newValue);
                          })}
                          {champ.description && (
                            <span className="text-xs text-gray-500 block">{champ.description}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          }
        }
        
        return renderedPacks;
      })()}

      {/* Dynamic Sections Based on Property Type */}
      {(() => {
        const config = getFieldsConfig();
        
        // Section Licence - DYNAMIQUE basée sur JSON config
        if (config.specificFields?.type_licence || config.dynamicConfig) {
          // Si on a une config dynamique, l'utiliser
          if (config.dynamicConfig && config.dynamicConfig.groupes.specific) {
            const specificGroup = config.dynamicConfig.groupes.specific;
            const champs = specificGroup.champs || [];
            
            return (
              <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-purple-300 shadow-2xl shadow-purple-500/10 transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-white/50 to-indigo-50/50"></div>
                <div className="relative p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                      <i className="bi bi-file-text text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{specificGroup.label}</h3>
                      <p className="text-gray-600 text-sm">Configuration dynamique basée sur JSON</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {champs.map((champ, index) => {
                      // Rendu selon le type de champ
                      // CAS SPÉCIAL : Champ ville avec autocomplétion internationale
                      if (champToRender.id === 'ville' && (champToRender.type === 'string' || champToRender.type === 'autocomplete')) {
                        const currentValue = getFieldValue(formData, champToRender.groupe_data, champToRender.type);
                        return (
                          <div key={index} className="space-y-2 relative">
                            <label className="block text-sm font-semibold text-gray-700">
                              {champToRender.label} {champToRender.obligatoire && <span className="text-red-500">*</span>}
                            </label>
                            <input
                              type="text"
                              className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                              placeholder={champToRender.placeholder || 'Commencez à taper le nom de la ville...'}
                              value={currentValue}
                              onChange={(e) => {
                                const parts = champToRender.groupe_data.split('.');
                                if (parts.length === 1) {
                                  handleInputChange(parts[0], undefined, e.target.value);
                                } else {
                                  handleInputChange(parts[0], parts[1], e.target.value);
                                }
                                searchCity(e.target.value);
                              }}
                              onFocus={() => {
                                if (currentValue && currentValue.length >= 2) {
                                  searchCity(currentValue);
                                }
                              }}
                              onBlur={() => {
                                // Delay pour permettre le clic sur une suggestion
                                setTimeout(() => setShowCitySuggestions(false), 200);
                              }}
                            />
                            
                            {/* Suggestions internationales */}
                            {showCitySuggestions && citySuggestions.length > 0 && (
                              <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
                                {citySuggestions.map((suggestion, idx) => {
                                  const cityName = suggestion.address?.city || suggestion.address?.town || suggestion.address?.village || suggestion.display_name.split(',')[0];
                                  const country = suggestion.address?.country || '';
                                  return (
                                    <div
                                      key={idx}
                                      className="px-3 py-2 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                      onClick={() => selectCity(suggestion)}
                                    >
                                      <div className="font-medium text-gray-900">
                                        {cityName}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        {country} {suggestion.address?.state ? `- ${suggestion.address.state}` : ''}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      if (champ.type === 'string' || champ.type === 'text') {
                        return (
                          <div key={index} className={champ.type === 'text' ? 'md:col-span-2 space-y-2' : 'space-y-2'}>
                            <label className="block text-sm font-semibold text-gray-700">
                              {champ.label} {champ.obligatoire && <span className="text-red-500">*</span>}
                            </label>
                            {champ.type === 'text' ? (
                              <textarea
                                className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm resize-none"
                                rows="3"
                                placeholder={champ.placeholder || ''}
                                value={formData.bien_general?.[champ.id] || ''}
                                onChange={(e) => handleInputChange('bien_general', champ.id, e.target.value)}
                              />
                            ) : (
                              <input
                                type="text"
                                className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                                placeholder={champ.placeholder || ''}
                                value={formData.bien_general?.[champ.id] || ''}
                                onChange={(e) => handleInputChange('bien_general', champ.id, e.target.value)}
                              />
                            )}
                          </div>
                        );
                      }
                      
                      if (champ.type === 'number') {
                        return (
                          <div key={index} className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">
                              {champ.label} {champ.obligatoire && <span className="text-red-500">*</span>}
                            </label>
                            <input
                              type="number"
                              className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                              placeholder={champ.placeholder || ''}
                              value={formData.bien_general?.[champ.id] || ''}
                              onChange={(e) => handleInputChange('bien_general', champ.id, parseFloat(e.target.value) || '')}
                            />
                          </div>
                        );
                      }
                      
                      if (champ.type === 'date') {
                        return (
                          <div key={index} className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">
                              {champ.label} {champ.obligatoire && <span className="text-red-500">*</span>}
                            </label>
                            <input
                              type="date"
                              className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                              value={formData.bien_general?.[champ.id] || ''}
                              onChange={(e) => handleInputChange('bien_general', champ.id, e.target.value)}
                            />
                          </div>
                        );
                      }
                      
                      if (champ.type === 'boolean') {
                        return (
                          <div key={index} className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">
                              {champ.label} {champ.obligatoire && <span className="text-red-500">*</span>}
                            </label>
                            <select
                              className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                              value={formData.bien_general?.[champ.id] || ''}
                              onChange={(e) => handleInputChange('bien_general', champ.id, e.target.value === 'true')}
                            >
                              <option value="">Non spécifié</option>
                              <option value="true">Oui</option>
                              <option value="false">Non</option>
                            </select>
                          </div>
                        );
                      }
                      
                      if (champ.type === 'enum' && champ.options) {
                        return (
                          <div key={index} className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">
                              {champ.label} {champ.obligatoire && <span className="text-red-500">*</span>}
                            </label>
                            <select
                              className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                              value={formData.bien_general?.[champ.id] || ''}
                              onChange={(e) => handleInputChange('bien_general', champ.id, e.target.value)}
                            >
                              <option value="">Sélectionner...</option>
                              {champ.options.map((option, optIndex) => (
                                <option key={optIndex} value={option}>{option.replace(/_/g, ' ')}</option>
                              ))}
                            </select>
                          </div>
                        );
                      }
                      
                      return null;
                    })}
                  </div>
                </div>
              </div>
            );
          }
          
          // Fallback: version statique originale
          return (
            <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-purple-300 shadow-2xl shadow-purple-500/10 transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-white/50 to-indigo-50/50"></div>
              <div className="relative p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                    <i className="bi bi-file-text text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Informations Licence</h3>
                    <p className="text-gray-600 text-sm">Détails spécifiques de la licence</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Type de licence</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                      placeholder="Ex: Licence IV - Débit de boissons"
                      value={formData.bien_general?.type_licence || ''}
                      onChange={(e) => handleInputChange('bien_general', 'type_licence', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Numéro de licence</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                      placeholder="Numéro officiel"
                      value={formData.bien_general?.numero_licence || ''}
                      onChange={(e) => handleInputChange('bien_general', 'numero_licence', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Date de validité</label>
                    <input
                      type="date"
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                      value={formData.bien_general?.validite_licence || ''}
                      onChange={(e) => handleInputChange('bien_general', 'validite_licence', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Transférable</label>
                    <select
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                      value={formData.bien_general?.transferable || ''}
                      onChange={(e) => handleInputChange('bien_general', 'transferable', e.target.value)}
                    >
                      <option value="">Non spécifié</option>
                      <option value="Oui">Oui</option>
                      <option value="Non">Non</option>
                      <option value="Sous conditions">Sous conditions</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700">Conditions de transfert</label>
                    <textarea
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm resize-none"
                      rows="3"
                      placeholder="Précisez les conditions de transfert de la licence..."
                      value={formData.bien_general?.conditions_transfert || ''}
                      onChange={(e) => handleInputChange('bien_general', 'conditions_transfert', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        }
        
        // Section Bureaux
        if (config.specificFields?.postes_travail) {
          return (
            <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-blue-300 shadow-2xl shadow-blue-500/10 transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-cyan-50/50"></div>
              <div className="relative p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/25 group-hover:scale-110 transition-transform duration-300">
                    <i className="bi bi-building text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Spécifique Bureaux</h3>
                    <p className="text-gray-600 text-sm">Informations propres aux espaces de bureau</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Nombre de postes de travail</label>
                    <input
                      type="number"
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                      placeholder="Ex: 10"
                      value={formData.bien_general?.postes_travail || ''}
                      onChange={(e) => handleInputChange('bien_general', 'postes_travail', parseInt(e.target.value) || '')}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Salles de réunion</label>
                    <input
                      type="number"
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                      placeholder="Nombre de salles"
                      value={formData.bien_general?.salles_reunion || ''}
                      onChange={(e) => handleInputChange('bien_general', 'salles_reunion', parseInt(e.target.value) || '')}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Parking inclus</label>
                    <select
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                      value={formData.bien_general?.parking_inclus || ''}
                      onChange={(e) => handleInputChange('bien_general', 'parking_inclus', e.target.value)}
                    >
                      <option value="">Non spécifié</option>
                      <option value="Oui">Oui</option>
                      <option value="Non">Non</option>
                      <option value="En option">En option</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Fibre optique</label>
                    <select
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                      value={formData.bien_general?.fibre_optique || ''}
                      onChange={(e) => handleInputChange('bien_general', 'fibre_optique', e.target.value)}
                    >
                      <option value="">Non spécifié</option>
                      <option value="Oui">Oui</option>
                      <option value="Non">Non</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Climatisation</label>
                    <select
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                      value={formData.bien_general?.climatisation || ''}
                      onChange={(e) => handleInputChange('bien_general', 'climatisation', e.target.value)}
                    >
                      <option value="">Non spécifié</option>
                      <option value="Oui">Oui</option>
                      <option value="Non">Non</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        
        return null;
      })()}

      {/* État général Section - Modernized */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-purple-500/5 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-white/50 to-pink-50/50"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
              <i className="bi bi-house-gear text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">État Général</h3>
              <p className="text-gray-600 text-sm">État des travaux et informations juridiques</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Travaux à prévoir</label>
              <div className="relative">
                <select
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm appearance-none"
                  value={formData.bien_general.travaux_prevoir}
                  onChange={(e) => handleInputChange('bien_general', 'travaux_prevoir', e.target.value)}
                >
                  <option value="">Sélectionner</option>
                  <option value="Décoration">Décoration</option>
                  <option value="Aucun">Aucun</option>
                  <option value="Légers">Légers</option>
                  <option value="Importants">Importants</option>
                </select>
                <i className="bi bi-chevron-down absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"></i>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500/5 to-pink-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Ancienneté de l'activité</label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                  placeholder="10 janvier 2020"
                  value={formData.bien_general.anciennete_activite}
                  onChange={(e) => handleInputChange('bien_general', 'anciennete_activite', e.target.value)}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500/5 to-pink-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Forme juridique</label>
              <div className="relative">
                <select
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm appearance-none"
                  value={formData.bien_general.forme_juridique}
                  onChange={(e) => handleInputChange('bien_general', 'forme_juridique', e.target.value)}
                >
                  <option value="">Sélectionner</option>
                  <option value="Sarl">Sarl</option>
                  {formesJuridiques.map(forme => (
                    <option key={forme} value={forme}>{forme}</option>
                  ))}
                </select>
                <i className="bi bi-chevron-down absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"></i>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500/5 to-pink-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Commentaires Section - Modernized */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-blue-500/5 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-cyan-50/50"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/25 group-hover:scale-110 transition-transform duration-300">
              <i className="bi bi-chat-text text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Commentaires</h3>
              <p className="text-gray-600 text-sm">Notes et observations sur les prestations</p>
            </div>
          </div>
          
          <div className="relative">
            <textarea
              className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm resize-none"
              rows="4"
              placeholder="Commentaires sur les prestations..."
              value={formData.commentaires_prestations}
              onChange={(e) => setFormData(prev => ({ ...prev, commentaires_prestations: e.target.value }))}
            />
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/5 to-cyan-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDescriptifTab = () => {
    // Types de descriptions — clés CANONIQUES (identiques au backend TypeDescription)
    // + destination explicite de chaque type (routage vérifié dans le code)
    const descriptionTypes = [
      { key: "Fiche Technique", legacy: "fiche_technique", label: "Fiche Technique", icon: "bi-file-text", usage: "PDF fiche détaillée (interne) — texte maître par défaut" },
      { key: "Web", legacy: "web", label: "Web", icon: "bi-globe", usage: "Fiche publique (lien client) + portails / diffusion + export agence" },
      { key: "Pub", legacy: "pub", label: "Pub", icon: "bi-megaphone", usage: "Marketing : réseaux sociaux, emailing" },
      { key: "Mandat", legacy: "mandat", label: "Mandat", icon: "bi-file-earmark-check", usage: "PDF mandat" },
      { key: "Affiche vitrine", legacy: "affiche_vitrine", label: "Affiche vitrine", icon: "bi-shop-window", usage: "Affiche imprimée vitrine" },
      { key: "Autre", legacy: "autre", label: "Autre", icon: "bi-three-dots", usage: "Libre / notes" }
    ];
    const legacyKeyOf = Object.fromEntries(descriptionTypes.map(t => [t.key, t.legacy]));

    // UI en français uniquement — la structure descriptions[TYPE]["Français"] est conservée
    const currentLanguage = "Français";

    // Fonction de dictée vocale
    const startDictation = (typeKey) => {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        alert("La reconnaissance vocale n'est pas supportée par votre navigateur.");
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.lang = languageCodes[currentLanguage] || "fr-FR";
      recognition.continuous = false;
      recognition.interimResults = false;

      setIsRecordingDescriptif(prev => ({ ...prev, [typeKey]: true }));

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        const currentContent = getContent(typeKey);
        const newContent = currentContent ? `${currentContent} ${transcript}` : transcript;
        updateDescription(typeKey, newContent);
        setIsRecordingDescriptif(prev => ({ ...prev, [typeKey]: false }));
      };

      recognition.onerror = (event) => {
        console.error('Erreur de reconnaissance vocale:', event.error);
        setIsRecordingDescriptif(prev => ({ ...prev, [typeKey]: false }));
        
        let errorMessage = "";
        switch(event.error) {
          case 'no-speech':
            errorMessage = "Aucune parole détectée. Veuillez réessayer et parler plus fort.";
            break;
          case 'network':
            errorMessage = "Erreur de connexion. Vérifiez votre connexion internet et réessayez.\n\nNote : La dictée vocale nécessite une connexion internet active car elle utilise les services Google Speech.";
            break;
          case 'not-allowed':
            errorMessage = "Accès au microphone refusé. Veuillez autoriser l'accès au microphone dans les paramètres de votre navigateur.";
            break;
          case 'aborted':
            errorMessage = "Reconnaissance vocale annulée.";
            break;
          case 'audio-capture':
            errorMessage = "Aucun microphone détecté. Veuillez connecter un microphone et réessayer.";
            break;
          default:
            errorMessage = `Erreur: ${event.error}. Veuillez réessayer.`;
        }
        
        alert(errorMessage);
      };

      recognition.onend = () => {
        setIsRecordingDescriptif(prev => ({ ...prev, [typeKey]: false }));
      };

      recognition.start();
    };

    // Fonction de dictée pour la proximité
    const startDictationProximite = () => {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        alert("La reconnaissance vocale n'est pas supportée par votre navigateur.");
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.lang = "fr-FR";
      recognition.continuous = false;
      recognition.interimResults = false;

      setIsRecordingDescriptif(prev => ({ ...prev, proximite: true }));

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        const currentContent = getProximite();
        const newContent = currentContent ? `${currentContent} ${transcript}` : transcript;
        updateProximite(newContent);
        setIsRecordingDescriptif(prev => ({ ...prev, proximite: false }));
      };

      recognition.onerror = (event) => {
        console.error('Erreur de reconnaissance vocale:', event.error);
        setIsRecordingDescriptif(prev => ({ ...prev, proximite: false }));
        
        let errorMessage = "";
        switch(event.error) {
          case 'no-speech':
            errorMessage = "Aucune parole détectée. Veuillez réessayer et parler plus fort.";
            break;
          case 'network':
            errorMessage = "Erreur de connexion. Vérifiez votre connexion internet et réessayez.\n\nNote : La dictée vocale nécessite une connexion internet active car elle utilise les services Google Speech.";
            break;
          case 'not-allowed':
            errorMessage = "Accès au microphone refusé. Veuillez autoriser l'accès au microphone dans les paramètres de votre navigateur.";
            break;
          case 'aborted':
            errorMessage = "Reconnaissance vocale annulée.";
            break;
          case 'audio-capture':
            errorMessage = "Aucun microphone détecté. Veuillez connecter un microphone et réessayer.";
            break;
          default:
            errorMessage = `Erreur: ${event.error}. Veuillez réessayer.`;
        }
        
        alert(errorMessage);
      };

      recognition.onend = () => {
        setIsRecordingDescriptif(prev => ({ ...prev, proximite: false }));
      };

      recognition.start();
    };

    // Fonction d'optimisation IA
    const optimizeWithAI = async (typeKey) => {
      const content = getContent(typeKey);
      
      if (!content || htmlToPlainText(content).length === 0) {
        alert("Veuillez d'abord saisir du texte à optimiser.");
        return;
      }
      
      // Confirmation
      if (!window.confirm("Voulez-vous optimiser ce texte avec l'IA ? Le texte actuel sera remplacé par la version optimisée.")) {
        return;
      }
      
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/optimize-description`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {})
          },
          body: JSON.stringify({
            content: htmlToPlainText(content),
            type: legacyKeyOf[typeKey] || typeKey,
            language: currentLanguage
          })
        });
        
        const data = await response.json();
        
        if (data.success) {
          // Mise à jour avec la version optimisée (HTML formaté par l'IA, ou texte → paragraphes)
          const optimized = data.optimized_content || "";
          updateDescription(typeKey, looksLikeHtml(optimized) ? optimized : plainTextToHtml(optimized), true);
          alert(data.message || "✨ Texte optimisé avec succès !");
        } else {
          // Afficher le message d'information (pas d'erreur)
          alert(data.message || "L'optimisation IA n'est pas disponible pour le moment.");
        }
        
      } catch (error) {
        console.error('Erreur optimisation IA:', error);
        alert("Une erreur est survenue lors de l'optimisation. Veuillez réessayer.");
      }
    };

    // Initialiser la structure si nécessaire
    if (!formData.descriptif) {
      setFormData(prev => ({
        ...prev,
        descriptif: {
          langue_active: "Français",
          descriptions: {},
          proximite: {}
        }
      }));
    }

    // Fonction pour mettre à jour une description (écrit sur la clé CANONIQUE, purge la clé legacy)
    const updateDescription = (typeKey, content, fromAI = false) => {
      setFormData(prev => {
        const descriptions = { ...(prev.descriptif?.descriptions || {}) };
        const legacy = legacyKeyOf[typeKey];
        const existing = descriptions[typeKey] || (legacy ? descriptions[legacy] : undefined) || {};
        if (legacy && descriptions[legacy]) delete descriptions[legacy];
        descriptions[typeKey] = {
          ...existing,
          [currentLanguage]: {
            content: content,
            optimized_by_ai: fromAI
          }
        };
        return {
          ...prev,
          descriptif: {
            ...prev.descriptif,
            langue_active: currentLanguage,
            descriptions
          }
        };
      });
    };

    // Fonction pour mettre à jour la proximité
    const updateProximite = (content) => {
      setFormData(prev => ({
        ...prev,
        descriptif: {
          ...prev.descriptif,
          proximite: {
            ...prev.descriptif.proximite,
            [currentLanguage]: content
          }
        }
      }));
    };

    // Obtenir le contenu pour un type (clé canonique, avec fallback clé legacy — biens migrés/anciens)
    const getContent = (typeKey) => {
      const descs = formData.descriptif?.descriptions || {};
      const langMap = descs[typeKey] || (legacyKeyOf[typeKey] ? descs[legacyKeyOf[typeKey]] : undefined);
      return langMap?.[currentLanguage]?.content || "";
    };

    // Obtenir la proximité pour une langue
    const getProximite = () => {
      return formData.descriptif?.proximite?.[currentLanguage] || "";
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500/5 via-green-500/5 to-teal-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/10 to-green-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-emerald-500/25">
              <i className="bi bi-file-earmark-text text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-emerald-600 bg-clip-text text-transparent">
                Descriptions & Contenus
              </h2>
              <p className="text-gray-600 font-medium">
                Rédigez les descriptions détaillées du bien — chaque type alimente une destination précise
                {speechSupported && <span className="ml-2 text-emerald-600">🎤 Dictée vocale disponible</span>}
              </p>
            </div>
          </div>
        </div>

        {/* Descriptions par type */}
        {descriptionTypes.map((type) => {
          const content = getContent(type.key);
          const plainContent = htmlToPlainText(content);
          const charCount = plainContent.length;
          const isEmpty = charCount === 0;
          const recording = isRecordingDescriptif[type.key];
          
          return (
            <div key={type.key} data-testid={`descriptif-card-${type.legacy}`} className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/30 via-white/50 to-green-50/30"></div>
              <div className="relative p-6">
                <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
                  <div className="flex items-center min-w-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center text-white mr-3 shadow-lg shadow-emerald-500/25 shrink-0">
                      <i className={`${type.icon} text-lg`}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-base font-bold text-gray-800">{type.label}</h4>
                        {isEmpty && (
                          <span data-testid={`descriptif-empty-badge-${type.legacy}`} className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[11px] font-semibold">
                            À rédiger
                          </span>
                        )}
                      </div>
                      <p data-testid={`descriptif-usage-${type.legacy}`} className="text-xs text-slate-500 mt-0.5">
                        <i className="bi bi-arrow-right-short"></i> Utilisé pour : <span className="font-medium text-slate-600">{type.usage}</span>
                        <span className="text-slate-400"> · {charCount} caractères</span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 shrink-0">
                  {/* Bouton dictée vocale */}
                  {speechSupported && (
                    <button
                      type="button"
                      onClick={() => startDictation(type.key)}
                      disabled={recording}
                      className={`px-3 py-2 rounded-lg font-medium text-sm transition-colors flex items-center space-x-2 border ${
                        recording
                          ? 'bg-red-500 text-white border-red-500 animate-pulse'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                      title="Dictée vocale"
                    >
                      <i className={`bi ${recording ? 'bi-mic-fill' : 'bi-mic'} text-base`}></i>
                      <span>{recording ? 'Enregistrement...' : 'Dicter'}</span>
                    </button>
                  )}
                  
                  {/* Bouton optimisation IA (violet = IA) */}
                  <button
                    type="button"
                    data-testid={`descriptif-ai-btn-${type.legacy}`}
                    onClick={() => optimizeWithAI(type.key)}
                    disabled={isEmpty}
                    className="px-3 py-2 rounded-lg font-medium text-sm transition-all duration-300 flex items-center space-x-2 bg-gradient-to-r from-[#7C3AED] to-[#4338CA] hover:opacity-90 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Optimiser avec IA"
                  >
                    <i className="bi bi-stars"></i>
                    <span>Optimiser avec IA</span>
                  </button>
                  </div>
                </div>
                
                <RichTextEditor
                  value={content}
                  onChange={(html) => updateDescription(type.key, html)}
                  placeholder={`Rédigez la description « ${type.label} »…`}
                  testId={`descriptif-editor-${type.legacy}`}
                />
              </div>
            </div>
          );
        })}

        {/* Section Proximité */}
        <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-xl">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-white/50 to-cyan-50/30"></div>
          <div className="relative p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center text-white mr-3 shadow-lg shadow-blue-500/25">
                  <i className="bi bi-geo-alt text-lg"></i>
                </div>
                <div>
                  <h4 className="text-base font-bold text-gray-800">Proximité & Points d'intérêt</h4>
                  <p className="text-xs text-gray-500">{getProximite().length} caractères</p>
                </div>
              </div>
              
              {/* Bouton dictée vocale pour proximité */}
              {speechSupported && (
                <button
                  type="button"
                  onClick={startDictationProximite}
                  disabled={isRecordingDescriptif.proximite}
                  className={`px-4 py-2 rounded-xl font-medium text-sm transition-all duration-300 flex items-center space-x-2 ${
                    isRecordingDescriptif.proximite
                      ? 'bg-red-500 text-white animate-pulse'
                      : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg'
                  }`}
                  title="Dictée vocale"
                >
                  <i className={`bi ${isRecordingDescriptif.proximite ? 'bi-mic-fill' : 'bi-mic'} text-lg`}></i>
                  <span>{isRecordingDescriptif.proximite ? 'Enregistrement...' : 'Dicter'}</span>
                </button>
              )}
            </div>
            
            <textarea
              value={getProximite()}
              onChange={(e) => updateProximite(e.target.value)}
              placeholder="Décrivez les points d'intérêt et la proximité…"
              className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
              style={{ minHeight: '200px' }}
              rows={8}
            />
          </div>
        </div>

        {/* Info box */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-100/50 border border-blue-200/50 rounded-2xl">
          <div className="flex items-start">
            <i className="bi bi-lightbulb text-blue-600 text-xl mr-3 mt-0.5"></i>
            <div className="text-sm text-blue-800">
              <span className="font-semibold">💡 Conseil :</span> Chaque type de description alimente une destination précise (indiquée sous son titre). Utilisez la barre d'outils pour mettre en forme : gras, listes, titres, liens.
              {speechSupported && (
                <>
                  <span className="block mt-2"><strong>🎤 Dictée vocale :</strong> Cliquez sur "Dicter" et parlez clairement. Le texte s'ajoutera automatiquement.</span>
                  <span className="block mt-1 text-xs text-blue-700"><strong>⚠️ Important :</strong> La dictée vocale nécessite une connexion internet active. En cas d'erreur "network", vérifiez votre connexion.</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCompositionTab = () => {
    const config = getFieldsConfig();
    const compositionConfig = config?.fullConfig?.onglets?.composition;
    
    console.log('🔧 Composition - Config:', compositionConfig);
    console.log('🔧 Composition - Packs actifs:', formData.packs_actifs);
    
    return (
      <div className="space-y-8">
        {/* Modern info header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-orange-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600/10 to-yellow-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-amber-500/25">
              <i className="bi bi-diagram-3 text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-amber-600 bg-clip-text text-transparent">
                Composition & Surfaces
              </h2>
              <p className="text-gray-600 font-medium">
                Répartition des surfaces et capacités du bien
              </p>
            </div>
          </div>
        </div>

        {/* Dynamic sections from JSON config */}
        {compositionConfig && Object.entries(compositionConfig.sections || {}).map(([sectionKey, section]) => (
          <div key={sectionKey} className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-amber-300 shadow-2xl shadow-amber-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 via-white/50 to-yellow-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-amber-500/25">
                  <i className="bi bi-rulers text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{section.label}</h3>
                </div>
              </div>
              
              <div className="space-y-6">
                {(section.champs || []).map((champ, index) => {
                  // Si c'est un champ de type "info", l'afficher en pleine largeur
                  if (champ.type === 'info') {
                    return (
                      <div key={index} className="col-span-full">
                        {renderDynamicField(champ, null, () => {})}
                      </div>
                    );
                  }
                  
                  // Sinon, affichage normal dans la grille
                  const value = getFieldValue(formData, champ.groupe_data, champ.type);
                  return (
                    <div key={index} className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {champ.label}
                        {champ.obligatoire && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {renderDynamicField(champ, value, (newValue) => {
                        handleDynamicFieldChange(champ.groupe_data, newValue);
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}

        {/* Pack-specific fields that belong to composition tab */}
        {(() => {
          const packsActifs = formData.packs_actifs || {};
          const compositionPacks = [];
          
          // Check if any pack has composition fields
          Object.keys(packsActifs).forEach(packId => {
            if (packsActifs[packId] === true) {
              const pack = loadPack(packId);
              if (pack && pack.champs) {
                // Filter fields that belong to composition
                const compositionFields = pack.champs.filter(champ => 
                  champ.groupe_data && champ.groupe_data.startsWith('composition.')
                );
                
                if (compositionFields.length > 0) {
                  compositionPacks.push(
                    <div key={`pack-composition-${packId}`} className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-amber-300 shadow-2xl shadow-amber-500/10 transition-all duration-500">
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 via-white/50 to-yellow-50/50"></div>
                      <div className="relative p-8">
                        <div className="flex items-center mb-6">
                          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-amber-500/25">
                            <i className={`${pack.icon} text-lg`}></i>
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-800">{pack.label} - Surfaces</h3>
                            <p className="text-gray-600 text-sm">Champs de composition spécifiques</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {compositionFields.map((champ, index) => {
                            const value = getFieldValue(formData, champ.groupe_data, champ.type);
                            return (
                              <div key={index} className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                  {champ.label}
                                  {champ.obligatoire && <span className="text-red-500 ml-1">*</span>}
                                </label>
                                {renderDynamicField(champ, value, (newValue) => {
                                  const parts = champ.groupe_data.split('.');
                                  handleInputChange(parts[0], parts[1], newValue);
                                })}
                                {champ.description && (
                                  <span className="text-xs text-gray-500 block">{champ.description}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }
              }
            }
          });
          
          return compositionPacks;
        })()}

        {/* Fallback: Manual composition fields if no config */}
        {(!compositionConfig || !compositionConfig.sections) && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-amber-300 shadow-2xl shadow-amber-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 via-white/50 to-yellow-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-amber-500/25">
                  <i className="bi bi-rulers text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Surfaces</h3>
                  <p className="text-gray-600 text-sm">Répartition des surfaces</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Superficie totale
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                      placeholder="150"
                      value={formData.composition?.superficie_totale || ''}
                      onChange={(e) => handleInputChange('composition', 'superficie_totale', parseFloat(e.target.value) || '')}
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">m²</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Surface de vente</label>
                  <div className="relative">
                    <input
                      type="number"
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                      placeholder="80"
                      value={formData.composition?.surface_vente || ''}
                      onChange={(e) => handleInputChange('composition', 'surface_vente', parseFloat(e.target.value) || '')}
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">m²</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Réserve</label>
                  <div className="relative">
                    <input
                      type="number"
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                      placeholder="30"
                      value={formData.composition?.reserve || ''}
                      onChange={(e) => handleInputChange('composition', 'reserve', parseFloat(e.target.value) || '')}
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">m²</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Nombre d'employés</label>
                  <input
                    type="number"
                    className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                    placeholder="5"
                    value={formData.composition?.nombre_employes || ''}
                    onChange={(e) => handleInputChange('composition', 'nombre_employes', parseInt(e.target.value) || '')}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderConfidentielTab = () => {
    // Initialiser proprietaire s'il n'existe pas
    if (!formData.informations_confidentielles?.proprietaire) {
      setFormData(prev => ({
        ...prev,
        informations_confidentielles: {
          ...prev.informations_confidentielles,
          proprietaire: {
            societe: "",
            forme_juridique: "",
            activite: "",
            siret: "",
            nom: "",
            prenom: "",
            groupe: "",
            adresse: "",
            ville: "",
            code_postal: "",
            telephones: [],
            emails: []
          }
        }
      }));
      return <div className="p-8 text-center">Chargement...</div>;
    }
    
    return (
    <div className="space-y-8">
      {/* Modern info header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-red-500/5 via-orange-500/5 to-pink-500/10 border border-white/20 backdrop-blur-sm">
        <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 to-orange-600/10"></div>
        <div className="relative p-6 flex items-center">
          <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-red-500/25">
            <i className="bi bi-shield-lock text-2xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-red-600 bg-clip-text text-transparent">
              Informations Confidentielles
            </h2>
            <p className="text-gray-600 font-medium">
              Ces informations ne sont visibles que par l'équipe de l'agence
            </p>
          </div>
        </div>
      </div>

      {/* Propriétaire Section - Modernized */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-orange-500/5 hover:shadow-lg hover:shadow-orange-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 via-white/50 to-red-50/50"></div>
        <div className="relative p-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 sm:gap-0">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-orange-500/25 group-hover:scale-110 transition-transform duration-300">
                <i className="bi bi-person-vcard text-lg"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Propriétaire</h3>
                <p className="text-gray-600 text-sm">Informations détaillées du propriétaire</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setContactSelectionTarget('proprietaire');
                  setShowContactSelection(true);
                }}
                className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25 active:scale-95"
              >
                <i className="bi bi-search mr-2"></i>
                <span>Sélectionner contact</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  // Clear proprietaire data
                  setFormData(prev => ({
                    ...prev,
                    informations_confidentielles: {
                      ...prev.informations_confidentielles,
                      proprietaire: {
                        societe: "",
                        forme_juridique: "",
                        activite: "",
                        siret: "",
                        nom: "",
                        prenom: "",
                        groupe: "",
                        adresse: "",
                        ville: "",
                        code_postal: "",
                        telephones: [],
                        emails: []
                      }
                    }
                  }));
                }}
                className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gray-500/25 active:scale-95"
              >
                <i className="bi bi-trash mr-2"></i>
                <span>Vider</span>
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Société</label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                  value={formData.informations_confidentielles.proprietaire.societe}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    informations_confidentielles: {
                      ...prev.informations_confidentielles,
                      proprietaire: {
                        ...prev.informations_confidentielles.proprietaire,
                        societe: e.target.value
                      }
                    }
                  }))}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-orange-500/5 to-red-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Nom</label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                  value={formData.informations_confidentielles.proprietaire.nom}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    informations_confidentielles: {
                      ...prev.informations_confidentielles,
                      proprietaire: {
                        ...prev.informations_confidentielles.proprietaire,
                        nom: e.target.value
                      }
                    }
                  }))}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-orange-500/5 to-red-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Prénom</label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                  value={formData.informations_confidentielles.proprietaire.prenom}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    informations_confidentielles: {
                      ...prev.informations_confidentielles,
                      proprietaire: {
                        ...prev.informations_confidentielles.proprietaire,
                        prenom: e.target.value
                      }
                    }
                  }))}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-orange-500/5 to-red-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
          </div>
          
          {/* Resto des champs - version simplifiee pour corriger rapidement */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Forme juridique</label>
              <select
                className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                value={formData.informations_confidentielles.proprietaire.forme_juridique || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  informations_confidentielles: {
                    ...prev.informations_confidentielles,
                    proprietaire: {
                      ...prev.informations_confidentielles.proprietaire,
                      forme_juridique: e.target.value
                    }
                  }
                }))}
              >
                <option value="">Sélectionner une forme juridique</option>
                <option value="Association">Association</option>
                <option value="Auto-entrepreneur">Auto-entrepreneur</option>
                <option value="EIRL">EIRL</option>
                <option value="Entreprise individuelle">Entreprise individuelle</option>
                <option value="EURL">EURL</option>
                <option value="SA">SA</option>
                <option value="SARL">SARL</option>
                <option value="SARLU">SARLU</option>
                <option value="SAS">SAS</option>
                <option value="SASU">SASU</option>
                <option value="SC">SC</option>
                <option value="SCI">SCI</option>
                <option value="SCM">SCM</option>
                <option value="SCP">SCP</option>
                <option value="SNC">SNC</option>
                <option value="Autre">Autre</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Activité</label>
              <input
                type="text"
                className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                value={formData.informations_confidentielles.proprietaire.activite || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  informations_confidentielles: {
                    ...prev.informations_confidentielles,
                    proprietaire: {
                      ...prev.informations_confidentielles.proprietaire,
                      activite: e.target.value
                    }
                  }
                }))}
              />
            </div>
          </div>
          
          {/* Adresse du Propriétaire avec autocomplétion internationale */}
          <div className="mt-6 space-y-4">
            <div className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse du propriétaire</label>
              <textarea
                className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                rows="2"
                placeholder="Adresse complète du propriétaire (autocomplétion française et internationale disponible)"
                value={formData.informations_confidentielles.proprietaire.adresse || ''}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    informations_confidentielles: {
                      ...prev.informations_confidentielles,
                      proprietaire: {
                        ...prev.informations_confidentielles.proprietaire,
                        adresse: e.target.value
                      }
                    }
                  }));
                  searchAddress(e.target.value, 'proprietaire_address');
                }}
                onFocus={() => setAddressInputFocus('proprietaire_address')}
              />
              
              {/* Suggestions d'adresses pour propriétaire */}
              {showAddressSuggestions && addressInputFocus === 'proprietaire_address' && addressSuggestions.length > 0 && (
                <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
                  {addressSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="px-3 py-2 hover:bg-orange-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onClick={() => {
                        const address = suggestion.properties.label;
                        const city = suggestion.properties.city || '';
                        const postcode = suggestion.properties.postcode || '';
                        
                        setFormData(prev => ({
                          ...prev,
                          informations_confidentielles: {
                            ...prev.informations_confidentielles,
                            proprietaire: {
                              ...prev.informations_confidentielles.proprietaire,
                              adresse: address,
                              ville: city,
                              code_postal: postcode
                            }
                          }
                        }));
                        
                        setShowAddressSuggestions(false);
                        setAddressSuggestions([]);
                      }}
                    >
                      <div className="font-medium text-gray-900">
                        {suggestion.properties.label}
                      </div>
                      <div className="text-sm text-gray-500">
                        {suggestion.properties.context}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Ville</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-100 border border-gray-200/50 rounded-xl text-gray-600 cursor-not-allowed"
                  placeholder="Auto-rempli depuis l'adresse"
                  value={formData.informations_confidentielles.proprietaire.ville || ''}
                  readOnly
                  title="Ce champ est automatiquement rempli lors de la sélection d'une adresse"
                />
                <p className="text-xs text-gray-500 italic">🔒 Auto-rempli depuis l'adresse ci-dessus</p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Code postal</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-100 border border-gray-200/50 rounded-xl text-gray-600 cursor-not-allowed"
                  placeholder="Auto-rempli depuis l'adresse"
                  value={formData.informations_confidentielles.proprietaire.code_postal || ''}
                  readOnly
                  title="Ce champ est automatiquement rempli lors de la sélection d'une adresse"
                />
                <p className="text-xs text-gray-500 italic">🔒 Auto-rempli depuis l'adresse ci-dessus</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Négociateur - NOUVEAU */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-purple-500/5 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-white/50 to-indigo-50/50"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
              <i className="bi bi-person-badge text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Négociateur</h3>
              <p className="text-gray-600 text-sm">Responsable du bien dans l'agence</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">Négociateur interne</label>
            <select
              className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
              value={formData.negociateur?.negociateur || ''}
              onChange={(e) => handleInputChange('negociateur', 'negociateur', e.target.value)}
            >
              <option value="">Sélectionner un négociateur</option>
              <option value="GRIMAZ Yvan">GRIMAZ Yvan</option>
              <option value="DELOUPY Marc">DELOUPY Marc</option>
              <option value="MARTIN Pierre">MARTIN Pierre</option>
              <option value="DURAND Sophie">DURAND Sophie</option>
            </select>
          </div>
        </div>
      </div>

      {/* Adresse du Bien Section - Modernized */}
      <div className="bg-gray-50 p-6 rounded-xl shadow-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 text-orange-800">
          <i className="bi bi-buildings-fill mr-2"></i>
          Adresse du Bien
        </h3>
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">Adresse confidentielle</label>
          <textarea
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
            rows="3"
            placeholder="Adresse complète du bien (confidentielle) - Autocomplétion française disponible"
            value={formData.informations_confidentielles.adresse || formData.informations_confidentielles.adresse_bien_confidentielle || ''}
            onChange={(e) => {
              // Update both fields to ensure compatibility
              setFormData(prev => ({
                ...prev,
                informations_confidentielles: {
                  ...prev.informations_confidentielles,
                  adresse: e.target.value,
                  adresse_bien_confidentielle: e.target.value
                }
              }));
              searchAddress(e.target.value, 'bien_confidentiel_address');
            }}
          />
          
          {/* Suggestions d'adresses pour bien confidentiel */}
          {showAddressSuggestions && addressInputFocus === 'bien_confidentiel_address' && addressSuggestions.length > 0 && (
            <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
              {addressSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  onClick={() => {
                    const address = suggestion.properties.label;
                    const city = suggestion.properties.city || '';
                    const postcode = suggestion.properties.postcode || '';
                    
                    setFormData(prev => ({
                      ...prev,
                      informations_confidentielles: {
                        ...prev.informations_confidentielles,
                        adresse: address,
                        adresse_bien_confidentielle: address,
                        ville: city,
                        code_postal: postcode
                      }
                    }));
                    
                    setShowAddressSuggestions(false);
                    setAddressSuggestions([]);
                  }}
                >
                  <div className="font-medium text-gray-900">
                    {suggestion.properties.label}
                  </div>
                  <div className="text-sm text-gray-500">
                    {suggestion.properties.context}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Ville et Code postal - EN LECTURE SEULE */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
            <input
              type="text"
              className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
              value={formData.informations_confidentielles.ville || ''}
              readOnly
              placeholder="Auto-rempli depuis l'adresse"
              title="Ce champ est automatiquement rempli lors de la sélection d'une adresse"
            />
            <p className="text-xs text-gray-500 italic mt-1">🔒 Auto-rempli depuis l'adresse ci-dessus</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
            <input
              type="text"
              className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
              value={formData.informations_confidentielles.code_postal || ''}
              readOnly
              placeholder="Auto-rempli depuis l'adresse"
              title="Ce champ est automatiquement rempli lors de la sélection d'une adresse"
            />
            <p className="text-xs text-gray-500 italic mt-1">🔒 Auto-rempli depuis l'adresse ci-dessus</p>
          </div>
        </div>
      </div>

      {/* Syndic Section - Modernized */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-teal-500/5 hover:shadow-lg hover:shadow-teal-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50/50 via-white/50 to-cyan-50/50"></div>
        <div className="relative p-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 sm:gap-0">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-teal-500/25 group-hover:scale-110 transition-transform duration-300">
                <i className="bi bi-building-gear text-lg"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Syndic</h3>
                <p className="text-gray-600 text-sm">Informations du syndic de copropriété</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setContactSelectionTarget('syndic');
                  setShowContactSelection(true);
                }}
                className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-teal-500/25 active:scale-95"
              >
                <i className="bi bi-search mr-2"></i>
                <span>Sélectionner contact</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    informations_confidentielles: {
                      ...prev.informations_confidentielles,
                      syndic_nom: "",
                      syndic_telephone: "",
                      syndic_email: "",
                      syndic_adresse: ""
                    }
                  }));
                }}
                className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gray-500/25 active:scale-95"
              >
                <i className="bi bi-trash mr-2"></i>
                <span>Vider</span>
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Nom du syndic</label>
              <input
                type="text"
                className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                value={formData.informations_confidentielles.syndic_nom || ''}
                onChange={(e) => handleInputChange('informations_confidentielles', 'syndic_nom', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Téléphone syndic</label>
              <input
                type="tel"
                className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                value={formData.informations_confidentielles.syndic_telephone || ''}
                onChange={(e) => handleInputChange('informations_confidentielles', 'syndic_telephone', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Email syndic</label>
              <input
                type="email"
                className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                value={formData.informations_confidentielles.syndic_email || ''}
                onChange={(e) => handleInputChange('informations_confidentielles', 'syndic_email', e.target.value)}
              />
            </div>
          </div>
          
          <div className="mt-6 relative">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse syndic</label>
            <textarea
              className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
              rows="2"
              placeholder="Saisir l'adresse du syndic (autocomplétion française)"
              value={formData.informations_confidentielles.syndic_adresse || ''}
              onChange={(e) => {
                handleInputChange('informations_confidentielles', 'syndic_adresse', e.target.value);
                searchAddress(e.target.value, 'syndic_address');
              }}
              onFocus={() => setAddressInputFocus('syndic_address')}
            />
            
            {/* Suggestions d'adresses pour syndic */}
            {showAddressSuggestions && addressInputFocus === 'syndic_address' && addressSuggestions.length > 0 && (
              <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
                {addressSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="px-3 py-2 hover:bg-teal-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    onClick={() => {
                      const address = suggestion.properties.label;
                      handleInputChange('informations_confidentielles', 'syndic_adresse', address);
                      setShowAddressSuggestions(false);
                      setAddressSuggestions([]);
                    }}
                  >
                    <div className="font-medium text-gray-900">
                      {suggestion.properties.label}
                    </div>
                    <div className="text-sm text-gray-500">
                      {suggestion.properties.context}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Visites Section */}
      <div className="bg-gray-50 p-6 rounded-xl shadow-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 text-orange-800">
          <i className="bi bi-eye-fill mr-2"></i>
          Visites
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Conditions de visites</label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows="3"
              placeholder="Conditions particulières pour les visites..."
              value={formData.informations_confidentielles.conditions_visites}
              onChange={(e) => handleInputChange('informations_confidentielles', 'conditions_visites', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Horaires de visites</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="ex: Lundi au vendredi 14h-18h"
                value={formData.informations_confidentielles.horaires_visites}
                onChange={(e) => handleInputChange('informations_confidentielles', 'horaires_visites', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact visites</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Nom et téléphone"
                value={formData.informations_confidentielles.contact_visites}
                onChange={(e) => handleInputChange('informations_confidentielles', 'contact_visites', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarques visites</label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows="2"
              placeholder="Remarques particulières pour les visites..."
              value={formData.informations_confidentielles.remarques_visites}
              onChange={(e) => handleInputChange('informations_confidentielles', 'remarques_visites', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Informations Financières Section */}
      <div className="bg-gray-50 p-6 rounded-xl shadow-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 text-indigo-800">
          <i className="bi bi-bank2 mr-2"></i>
          Informations Financières
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Chiffre d'affaires annuel (€)</label>
            <input
              type="number"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              value={formData.informations_confidentielles.chiffre_affaires_annuel}
              onChange={(e) => handleInputChange('informations_confidentielles', 'chiffre_affaires_annuel', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bénéfices annuels (€)</label>
            <input
              type="number"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              value={formData.informations_confidentielles.benefices_annuels}
              onChange={(e) => handleInputChange('informations_confidentielles', 'benefices_annuels', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Charges annuelles (€)</label>
            <input
              type="number"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              value={formData.informations_confidentielles.charges_annuelles}
              onChange={(e) => handleInputChange('informations_confidentielles', 'charges_annuelles', e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Banque</label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              value={formData.informations_confidentielles.banque}
              onChange={(e) => handleInputChange('informations_confidentielles', 'banque', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Compte bancaire</label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              value={formData.informations_confidentielles.compte_bancaire}
              onChange={(e) => handleInputChange('informations_confidentielles', 'compte_bancaire', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Divers Section */}
      <div className="bg-gray-50 p-6 rounded-xl shadow-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 text-indigo-800">
          <i className="bi bi-card-list mr-2"></i>
          Divers
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Raison de la vente</label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows="3"
              placeholder="Motif de cession du fonds de commerce..."
              value={formData.informations_confidentielles.raison_vente}
              onChange={(e) => handleInputChange('informations_confidentielles', 'raison_vente', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Informations diverses</label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows="4"
              placeholder="Informations diverses importantes..."
              value={formData.informations_confidentielles.informations_diverses}
              onChange={(e) => handleInputChange('informations_confidentielles', 'informations_diverses', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes particulières</label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows="3"
              placeholder="Notes particulières et commentaires confidentiels..."
              value={formData.informations_confidentielles.notes_particulieres}
              onChange={(e) => handleInputChange('informations_confidentielles', 'notes_particulieres', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commentaires confidentiels</label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows="4"
              placeholder="Informations confidentielles supplémentaires..."
              value={formData.informations_confidentielles.commentaires_confidentiels}
              onChange={(e) => handleInputChange('informations_confidentielles', 'commentaires_confidentiels', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <i className="bi bi-shield-lock-fill text-blue-600"></i>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">
              <strong>Attention :</strong> Ces informations sont strictement confidentielles et ne doivent être partagées qu'avec les acquéreurs qualifiés et ayant signé un engagement de confidentialité.
            </p>
          </div>
        </div>
      </div>
    </div>
    );
  };



  const renderVenteTab = () => (
    <div className="space-y-8">
      {/* Modern info header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-500/5 via-cyan-500/5 to-blue-500/10 border border-white/20 backdrop-blur-sm">
        <div className="absolute inset-0 bg-gradient-to-r from-teal-600/10 to-cyan-600/10"></div>
        <div className="relative p-6 flex items-center">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-teal-500/25">
            <i className="bi bi-currency-euro text-2xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-teal-600 bg-clip-text text-transparent">
              Informations de Vente
            </h2>
            <p className="text-gray-600 font-medium">
              Configurez les prix, disponibilité et documents de vente
            </p>
          </div>
        </div>
      </div>

      {/* Prix et Négociation Section - Modernized */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-teal-500/5 hover:shadow-lg hover:shadow-teal-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50/50 via-white/50 to-cyan-50/50"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-teal-500/25 group-hover:scale-110 transition-transform duration-300">
              <i className="bi bi-currency-euro text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Prix & Négociation</h3>
              <p className="text-gray-600 text-sm">Configuration tarifaire et marges de négociation</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Prix d'annonce (€)</label>
              <div className="relative">
                <input
                  type="number"
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                  value={formData.informations_vente.prix_annonce}
                  onChange={(e) => handleInputChange('informations_vente', 'prix_annonce', e.target.value)}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-teal-500/5 to-cyan-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Prix minimum accepté (€)</label>
              <div className="relative">
                <input
                  type="number"
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                  value={formData.informations_vente.prix_minimum_accepte}
                  onChange={(e) => handleInputChange('informations_vente', 'prix_minimum_accepte', e.target.value)}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-teal-500/5 to-cyan-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Investissement locatif (murs occupés) — uniquement pour la vente de murs */}
      {['vente_immo', 'vente_murs'].includes(formData.operation) && (
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-amber-500/5 transition-all duration-500" data-testid="murs-invest-section">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/60 via-white/50 to-orange-50/40"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-amber-500/25">
              <i className="bi bi-graph-up-arrow text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Investissement locatif</h3>
              <p className="text-gray-600 text-sm">Murs occupés / libres, loyer en place et rendement</p>
            </div>
          </div>

          {/* Occupé / Libre */}
          <div className="flex flex-wrap gap-3 mb-6">
            {[{ v: 'occupe', label: 'Murs occupés', icon: 'bi-person-check' }, { v: 'libre', label: 'Murs libres', icon: 'bi-door-open' }].map((opt) => {
              const active = (formData.informations_vente.occupation_murs || '') === opt.v;
              return (
                <button
                  type="button"
                  key={opt.v}
                  data-testid={`murs-occupation-${opt.v}`}
                  onClick={() => handleInputChange('informations_vente', 'occupation_murs', opt.v)}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${active ? 'bg-amber-500 border-amber-500 text-white shadow-md' : 'bg-white/70 border-gray-200 text-gray-600 hover:border-amber-300'}`}
                >
                  <i className={`bi ${opt.icon}`}></i> {opt.label}
                </button>
              );
            })}
          </div>

          {formData.informations_vente.occupation_murs === 'occupe' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Loyer mensuel en place (HC HT)</label>
                  <div className="relative">
                    <input
                      type="number"
                      data-testid="murs-loyer-mensuel"
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all"
                      placeholder="ex: 1000"
                      value={formData.informations_vente.loyer_mensuel_actuel || ''}
                      onChange={(e) => handleInputChange('informations_vente', 'loyer_mensuel_actuel', e.target.value)}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€/mois</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Loyer annuel en place (HC HT)</label>
                  <div className="relative">
                    <input
                      type="number"
                      data-testid="murs-loyer-annuel"
                      className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all"
                      placeholder="ex: 12000"
                      value={formData.informations_vente.loyer_annuel_actuel || ''}
                      onChange={(e) => handleInputChange('informations_vente', 'loyer_annuel_actuel', e.target.value)}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€/an</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Rendement brut (auto)</label>
                  <div className="w-full px-4 py-3 bg-amber-50 border-2 border-amber-200 rounded-xl flex items-center justify-between" data-testid="murs-rendement-display">
                    <span className="text-2xl font-bold text-amber-600">
                      {formData.informations_vente.taux_rendement_brut ? `${formData.informations_vente.taux_rendement_brut} %` : '—'}
                    </span>
                    <i className="bi bi-percent text-amber-400 text-xl"></i>
                  </div>
                  <p className="text-[11px] text-gray-400">= loyer annuel HC / prix de vente FAI</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Début du bail en place</label>
                  <input
                    type="date"
                    data-testid="murs-bail-debut"
                    className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all"
                    value={formData.informations_vente.bail_actuel_debut || ''}
                    onChange={(e) => handleInputChange('informations_vente', 'bail_actuel_debut', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Échéance du bail en place</label>
                  <input
                    type="date"
                    data-testid="murs-bail-echeance"
                    className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all"
                    value={formData.informations_vente.bail_actuel_echeance || ''}
                    onChange={(e) => handleInputChange('informations_vente', 'bail_actuel_echeance', e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      )}


      {/* Disponibilité Section - Modernized */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-cyan-500/5 hover:shadow-lg hover:shadow-cyan-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 via-white/50 to-blue-50/50"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-cyan-500/25 group-hover:scale-110 transition-transform duration-300">
              <i className="bi bi-calendar-check text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Disponibilité</h3>
              <p className="text-gray-600 text-sm">Calendrier et délais de libération</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Date de disponibilité</label>
              <div className="relative">
                <input
                  type="date"
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                  value={formData.informations_vente.date_disponibilite}
                  onChange={(e) => handleInputChange('informations_vente', 'date_disponibilite', e.target.value)}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500/5 to-blue-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Délai de libération</label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                  placeholder="ex: 3 mois après signature"
                  value={formData.informations_vente.delai_liberation}
                  onChange={(e) => handleInputChange('informations_vente', 'delai_liberation', e.target.value)}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500/5 to-blue-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Visites Section - Modernized */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-blue-500/5 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-indigo-50/50"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/25 group-hover:scale-110 transition-transform duration-300">
              <i className="bi bi-eye text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Visites</h3>
              <p className="text-gray-600 text-sm">Organisation et modalités des visites</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Conditions de visite</label>
              <div className="relative">
                <textarea
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm resize-none"
                  rows="3"
                  placeholder="Conditions et modalités pour organiser les visites..."
                  value={formData.informations_vente.conditions_visite}
                  onChange={(e) => handleInputChange('informations_vente', 'conditions_visite', e.target.value)}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Contact pour les visites</label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                  placeholder="Nom et téléphone du contact"
                  value={formData.informations_vente.contact_visite}
                  onChange={(e) => handleInputChange('informations_vente', 'contact_visite', e.target.value)}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Documents Section - Modernized */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-indigo-500/5 hover:shadow-lg hover:shadow-indigo-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-white/50 to-purple-50/50"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-indigo-500/25 group-hover:scale-110 transition-transform duration-300">
              <i className="bi bi-file-earmark text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Documents de Vente</h3>
              <p className="text-gray-600 text-sm">Gestion documentaire et statuts administratifs</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Documents requis</label>
              <div className="relative">
                <textarea
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm resize-none"
                  rows="4"
                  placeholder="Liste des documents nécessaires pour la vente..."
                  value={formData.informations_vente.documents_requis}
                  onChange={(e) => handleInputChange('informations_vente', 'documents_requis', e.target.value)}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-indigo-500/5 to-purple-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Statut des documents</label>
              <div className="relative">
                <select
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm appearance-none"
                  value={formData.informations_vente.statut_documents}
                  onChange={(e) => handleInputChange('informations_vente', 'statut_documents', e.target.value)}
                >
                  <option value="">Sélectionner</option>
                  <option value="Complets">Complets</option>
                  <option value="En cours">En cours</option>
                  <option value="Manquants">Manquants</option>
                  <option value="À vérifier">À vérifier</option>
                </select>
                <i className="bi bi-chevron-down absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"></i>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-indigo-500/5 to-purple-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <i className="bi bi-lightbulb-fill text-green-600"></i>
          </div>
          <div className="ml-3">
            <p className="text-sm text-green-700">
              <strong>Conseil :</strong> Assurez-vous que tous les documents sont complets avant de présenter le bien aux acquéreurs potentiels pour éviter les retards dans la transaction.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
  const renderMediasTab = () => (
    <div className="space-y-8">
      {/* Modern info header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-500/5 via-amber-500/5 to-yellow-500/10 border border-white/20 backdrop-blur-sm">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-600/10 to-amber-600/10"></div>
        <div className="relative p-6 flex items-center">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-orange-500/25">
            <i className="bi bi-image text-2xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-orange-600 bg-clip-text text-transparent">
              Photos & Documents
            </h2>
            <p className="text-gray-600 font-medium">
              Gérez les photos, documents et médias du bien immobilier
            </p>
          </div>
        </div>
      </div>

      {/* Photos Section - Modernized */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-orange-500/5 hover:shadow-lg hover:shadow-orange-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 via-white/50 to-amber-50/50"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-orange-500/25 group-hover:scale-110 transition-transform duration-300">
              <i className="bi bi-camera text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Photos</h3>
              <p className="text-gray-600 text-sm">Galerie d'images avec ordre personnalisable</p>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Upload Photos (JPG, PNG, WebP)
            </label>
            <div className="relative">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => {
                  Array.from(e.target.files).forEach(file => {
                    handleFileUpload(file, 'photos');
                  });
                }}
                className="block w-full text-sm text-gray-600 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-gradient-to-r file:from-orange-500 file:to-orange-600 file:text-white hover:file:from-orange-600 hover:file:to-orange-700 file:transition-all file:duration-300 file:shadow-lg file:shadow-orange-500/25 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl p-3 hover:bg-white/90 transition-all duration-300"
              />
            </div>
          </div>
          
          {/* Photos Grid with Drag & Drop - Modernized */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {(formData.medias?.photos || []).map((photo, index) => (
              <div key={photo.id} className="relative group">
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg shadow-orange-500/10 hover:shadow-xl hover:shadow-orange-500/20 transition-all duration-300 hover:-translate-y-1">
                  <img
                    src={getMediaUrl(photo)}
                    alt={photo.name}
                    className="w-full h-32 object-cover cursor-move"
                    draggable="true"  
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", index.toString());
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const dragIndex = parseInt(e.dataTransfer.getData("text/plain"));
                      if (dragIndex !== index) {
                        reorderPhotos(dragIndex, index);
                      }
                    }}
                  />
                  
                  {/* Photo order indicator - Modernized */}
                  <div className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-lg">
                    {index + 1}
                  </div>
                  
                  {/* Remove button - Modernized */}
                  <button
                    type="button"
                    onClick={() => removeMediaFile('photos', mediaKey(photo))}
                    className="absolute top-3 right-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 shadow-lg"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                  
                  {/* Move buttons for mobile - Modernized */}
                  <div className="absolute bottom-3 left-3 flex space-x-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => reorderPhotos(index, index - 1)}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs hover:scale-110 transition-all duration-300 shadow-lg"
                        title="Déplacer vers la gauche"
                      >
                        <i className="bi bi-arrow-left"></i>
                      </button>
                    )}
                    {index < formData.medias.photos.length - 1 && (
                      <button
                        type="button"
                        onClick={() => reorderPhotos(index, index + 1)}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs hover:scale-110 transition-all duration-300 shadow-lg"
                        title="Déplacer vers la droite"
                      >
                        <i className="bi bi-arrow-right"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {formData.medias.photos.length > 0 && (
            <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100/50 border border-blue-200/50 rounded-xl">
              <div className="flex items-start">
                <i className="bi bi-lightbulb text-blue-600 text-lg mr-3 mt-0.5 flex-shrink-0"></i>
                <div className="text-sm text-blue-800">
                  <span className="font-semibold">Réorganisation :</span> Glissez-déposez les photos pour les réorganiser, ou utilisez les flèches sur mobile. La première photo sera utilisée comme aperçu dans la liste des biens.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Documents Section - Restored with modern styling */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-amber-500/5 hover:shadow-lg hover:shadow-amber-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 via-white/50 to-yellow-50/50"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-amber-500/25 group-hover:scale-110 transition-transform duration-300">
              <i className="bi bi-file-earmark-pdf text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Documents</h3>
              <p className="text-gray-600 text-sm">Fichiers PDF, DOC, XLS et autres documents</p>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">Upload Documents (PDF, DOC, XLS, etc.)</label>
            <input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
              onChange={(e) => {
                Array.from(e.target.files).forEach(file => {
                  handleFileUpload(file, 'documents');
                });
              }}
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-gradient-to-r file:from-amber-500 file:to-amber-600 file:text-white hover:file:from-amber-600 hover:file:to-amber-700 file:transition-all file:duration-300 file:shadow-lg file:shadow-amber-500/25 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl p-3 hover:bg-white/90 transition-all duration-300"
            />
          </div>
          
          <div className="space-y-3">
            {(formData.medias?.documents || []).map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-4 bg-white/50 backdrop-blur-sm border border-gray-200/50 rounded-xl hover:bg-white/70 transition-all duration-300">
                <div className="flex items-center space-x-4">
                  <i className="bi bi-file-earmark text-2xl text-amber-600"></i>
                  <div>
                    <div className="font-semibold text-gray-900">{doc.name}</div>
                    <div className="text-sm text-gray-500">
                      {(doc.size / 1024 / 1024).toFixed(2)} MB - {new Date(doc.uploadDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeMediaFile('documents', mediaKey(doc))}
                  className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:from-red-600 hover:to-red-700 transition-all duration-300"
                >
                  Supprimer
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Plans Section - Restored with modern styling */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-yellow-500/5 hover:shadow-lg hover:shadow-yellow-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-50/50 via-white/50 to-orange-50/50"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-yellow-500/25 group-hover:scale-110 transition-transform duration-300">
              <i className="bi bi-rulers text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Plans</h3>
              <p className="text-gray-600 text-sm">Plans techniques et architecturaux</p>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">Upload Plans (PDF, JPG, PNG, DWG)</label>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.dwg"
              onChange={(e) => {
                Array.from(e.target.files).forEach(file => {
                  handleFileUpload(file, 'plans');
                });
              }}
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-gradient-to-r file:from-yellow-500 file:to-yellow-600 file:text-white hover:file:from-yellow-600 hover:file:to-yellow-700 file:transition-all file:duration-300 file:shadow-lg file:shadow-yellow-500/25 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl p-3 hover:bg-white/90 transition-all duration-300"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(formData.medias?.plans || []).map((plan) => (
              <div key={plan.id} className="relative group p-4 bg-white/50 backdrop-blur-sm border border-gray-200/50 rounded-xl hover:bg-white/70 transition-all duration-300">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">📐</span>
                  <div>
                    <div className="font-semibold text-gray-900">{plan.name}</div>
                    <div className="text-sm text-gray-500">
                      {(plan.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeMediaFile('plans', mediaKey(plan))}
                  className="absolute top-2 right-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-all duration-300"
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Videos & Virtual Tours Section - Restored with modern styling */}
      <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-purple-500/5 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-500">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-white/50 to-indigo-50/50"></div>
        <div className="relative p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
              <i className="bi bi-camera-video text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Vidéos & Visites Virtuelles</h3>
              <p className="text-gray-600 text-sm">Liens vers contenus multimédias interactifs</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">URL Vidéo (YouTube, Vimeo, etc.)</label>
              <div className="relative">
                <input
                  type="url"
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={formData.medias.video_url}
                  onChange={(e) => handleInputChange('medias', 'video_url', e.target.value)}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500/5 to-indigo-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
            
            {/* URL Visite Virtuelle — masquée v1.0 (flag SHOW_VISITE_VIRTUELLE) */}
            {false && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">URL Visite Virtuelle</label>
              <div className="relative">
                <input
                  type="url"
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 shadow-sm"
                  placeholder="https://matterport.com/..."
                  value={formData.medias.visite_virtuelle_url}
                  onChange={(e) => handleInputChange('medias', 'visite_virtuelle_url', e.target.value)}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500/5 to-indigo-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Conseil Section - Restored with modern styling */}
      <div className="p-6 bg-gradient-to-r from-purple-50 to-indigo-100/50 border border-purple-200/50 rounded-3xl">
        <div className="flex items-start">
          <i className="bi bi-lightbulb text-purple-600 text-2xl mr-4 mt-1 flex-shrink-0"></i>
          <div className="text-sm text-purple-800">
            <span className="font-semibold">Conseil :</span> Les photos de qualité professionnelle augmentent l'intérêt des acquéreurs. Ajoutez des plans et des visites virtuelles pour une présentation complète du bien.
          </div>
        </div>
      </div>
    </div>
  );

  // Table sorting function
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort properties based on current sort settings
  const sortedProperties = [...properties].sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case 'id':
        aValue = a.id;
        bValue = b.id;
        break;
      case 'proprietaire':
        aValue = `${a.informations_confidentielles?.proprietaire?.prenom || ''} ${a.informations_confidentielles?.proprietaire?.nom || ''}`;
        bValue = `${b.informations_confidentielles?.proprietaire?.prenom || ''} ${b.informations_confidentielles?.proprietaire?.nom || ''}`;
        break;
      case 'type':
        aValue = a.bien_general?.type_bien || '';
        bValue = b.bien_general?.type_bien || '';
        break;
      case 'pieces':
        aValue = a.bien_general?.nb_pieces || 0;
        bValue = b.bien_general?.nb_pieces || 0;
        break;
      case 'prix':
        aValue = a.honoraires_calcul?.prix_vente || 0;
        bValue = b.honoraires_calcul?.prix_vente || 0;
        break;
      case 'ville':
        aValue = a.informations_confidentielles?.ville || '';
        bValue = b.informations_confidentielles?.ville || '';
        break;
      case 'cp':
        aValue = a.informations_confidentielles?.code_postal || '';
        bValue = b.informations_confidentielles?.code_postal || '';
        break;
      case 'surface':
        aValue = a.enseigne?.superficie || 0;
        bValue = b.enseigne?.superficie || 0;
        break;
      case 'created_at':
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
        break;
      case 'negociateur':
        // Helper unique : sans lui, le tri comparait des noms avec des identifiants.
        aValue = getNegociateurName(a, availableNegotiators, '');
        bValue = getNegociateurName(b, availableNegotiators, '');
        break;
      default:
        return 0;
    }

    // Handle string comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      return sortDirection === 'asc' ? comparison : -comparison;
    }

    // Handle numeric and date comparison
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // State for search filters - Complete legacy search
  const [searchFilters, setSearchFilters] = useState({
    // Base criteria
    affichage: "classique",
    reference: "",
    agence: "0", // 0=Mon Agence, 1=HI, 2=HRE
    negociateur: "",
    
    // NOUVEAUX: Property type criteria (remplace "type")
    transaction_type: "", // Vente / Location
    type_bien: "", // Fonds de commerce, Locaux commerciaux, Bureaux, Licences
    activite_principale: "", // Restaurant, Bar, Retail, etc.
    statut_bien: "", // Libre, Occupé, En travaux, etc.
    
    // Property criteria  
    secteur: "",
    ville: "",
    codePostal: "",
    enseigne: "", // Search by brand/enseigne
    
    // Price & surface
    budgetMin: "",
    budgetMax: "",
    surfaceMin: "",
    surfaceMax: "",
    
    // Special criteria
    libre: "",
    includeArchived: false, // Include archived properties in search
    
    // HRE/HI specific
    pdf: "0", // 0=Indifférent, 1=Oui, 2=Non
    exclusivite: "0",
    mandat: "",
    
    // Archive criteria (when isarchive=1)
    vendu: false,
    venduPar: "",
    prixVenteMin: "",
    prixVenteMax: "",
    dateVenteMin: "",
    dateVenteMax: "",
    
    // Temporal
    maxDays: "",
    
    // Other
    adresseBien: ""
  });

  // State for available cities (dynamic)
  const [availableCities, setAvailableCities] = useState([]);
  
  // State for dynamic criteria (based on real data)
  const [availableNegotiators, setAvailableNegotiators] = useState([]);
  const [availablePropertyTypes, setAvailablePropertyTypes] = useState([]);
  const [availableEnseignes, setAvailableEnseignes] = useState([]);
  
  // NOUVEAUX: States pour les nouveaux filtres
  const [availableTypeBien, setAvailableTypeBien] = useState([]);
  const [availableActivites, setAvailableActivites] = useState([]);
  const [availableStatutsBien, setAvailableStatutsBien] = useState([]);

  // State for mobile menu and dropdowns  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  
  // State for advanced filters collapse
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // States pour partage et PDF inline
  const [expandedShareProperty, setExpandedShareProperty] = useState(null); // ID du bien avec section partage ouverte
  const [propertyLinks, setPropertyLinks] = useState({}); // {propertyId: {code, url, loading}}
  const [showPDFDropdown, setShowPDFDropdown] = useState(null); // ID du bien avec dropdown PDF ouvert
  const [generatingPDF, setGeneratingPDF] = useState(null); // ID du bien en cours de génération PDF
  // Use userProfile from props if available (in modern mode), otherwise load locally (legacy mode)
  const [userProfileLocal, setUserProfileLocal] = useState({
    civilite: "",
    prenom: "",
    nom: "", 
    agence: agencySettingsProp?.nom_agence || "KOMERCES",
    supportType: "Transaction" // Transaction, Location, Location_Saisonniere
  });

  // Use prop if available, otherwise use local state
  const userProfile = userProfileProp && Object.keys(userProfileProp).some(key => userProfileProp[key]) 
    ? { ...userProfileLocal, ...userProfileProp, agence: agencySettingsProp?.nom_agence || "KOMERCES" }
    : userProfileLocal;
  
  // Create a setter function that works in both modes
  const setUserProfile = (newData) => {
    setUserProfileLocal(newData);
  };

  // Load user profile from settings on mount (only if not provided via props)
  const loadUserProfile = async () => {
    // Skip loading if userProfile is provided via props
    if (userProfileProp && Object.keys(userProfileProp).some(key => userProfileProp[key])) {
      return;
    }
    
    try {
      const response = await axios.get(API + '/settings/user');
      const userData = response.data;
      setUserProfileLocal({
        civilite: userData.civilite || "M.",
        prenom: userData.prenom || "",
        nom: userData.nom || "",
        email: userData.email || "",
        telephone: userData.telephone || "",
        agence: agencySettingsProp?.nom_agence || "KOMERCES",
        supportType: "Transaction"
      });
      
      // Apply theme from settings
      if (userData.theme && switchTheme) {
        switchTheme(userData.theme);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du profil utilisateur:', error);
    }
  };

  // Load user profile on component mount (only in legacy mode)
  useEffect(() => {
    loadUserProfile();
    
    // Check initial URL to set correct view
    const currentPath = window.location.pathname;
    if (currentPath === '/settings') {
      setShowDashboard(false);
      setShowSettings(true);
    } else if (currentPath.startsWith('/biens')) {
      setShowDashboard(false);
      // Properties view will be handled by RouterSync
    } else if (currentPath.startsWith('/contacts')) {
      setShowDashboard(false);
      // Contacts view will be handled by RouterSync
    }
  }, []);

  const handleSearchFilterChange = (field, value) => {
    const newFilters = {
      ...searchFilters,
      [field]: value
    };
    
    setSearchFilters(newFilters);
    
    // NOUVEAUTÉ: Filtres en cascade dynamiques
    if (field === 'transaction_type') {
      // Quand on change la transaction, mettre à jour les villes et activités disponibles
      updateDynamicFilters(newFilters);
    }
    
    if (field === 'type_bien') {
      // Quand on change le type de bien, mettre à jour les activités
      updateDynamicFilters(newFilters);
    }
    
    // If includeArchived checkbox changes, reload cities accordingly
    if (field === 'includeArchived') {
      const cityStatus = value ? 'all' : 'active';
      loadAvailableCities(cityStatus);
    }
    
    // Fields that need debouncing (text inputs for numbers)
    const debouncedFields = ['reference', 'budgetMin', 'budgetMax', 'surfaceMin', 'surfaceMax'];
    
    if (debouncedFields.includes(field)) {
      // Clear existing timeout if any
      if (window.searchTimeout) {
        clearTimeout(window.searchTimeout);
      }
      // Set new timeout for debounced fields
      window.searchTimeout = setTimeout(() => {
        executeSearchWithFilters(newFilters);
      }, 500); // 500ms delay for text input
    } else {
      // For dropdown selections and checkboxes, search immediately
      executeSearchWithFilters(newFilters);
    }
  };

  // Mettre à jour les filtres dynamiquement en fonction des sélections
  const updateDynamicFilters = (filters) => {
    try {
      // Filtrer allProperties en fonction des critères sélectionnés
      let filteredProperties = allProperties;

      if (filters.transaction_type) {
        filteredProperties = filteredProperties.filter(p => 
          p.bien_general?.transaction_type === filters.transaction_type
        );
      }

      if (filters.type_bien) {
        filteredProperties = filteredProperties.filter(p => 
          p.bien_general?.type_bien === filters.type_bien
        );
      }

      // Mettre à jour les villes disponibles
      const citiesSet = new Set();
      filteredProperties.forEach(property => {
        if (property.bien_general?.adresse?.ville) {
          citiesSet.add(property.bien_general.adresse.ville);
        }
      });
      const citiesList = Array.from(citiesSet).sort();
      setAvailableCities(citiesList);

      // Mettre à jour les activités disponibles
      const activitiesSet = new Set();
      filteredProperties.forEach(property => {
        if (property.bien_general?.activite_principale) {
          activitiesSet.add(property.bien_general.activite_principale);
        }
      });
      const activitiesList = Array.from(activitiesSet).sort();
      setAvailableActivites(activitiesList);

      console.log('🔄 Filtres dynamiques mis à jour:', {
        villes: citiesList.length,
        activités: activitiesList.length
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour des filtres dynamiques:', error);
    }
  };

  // Execute search with filters
  const executeSearch = async () => {
    return executeSearchWithFilters(searchFilters);
  };

  // Execute search with specific filters (used for automatic search)
  const executeSearchWithFilters = async (filters) => {
    try {
      console.log("Searching with filters:", filters);
      
      // Build query parameters from filters
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        // Ne pas envoyer les valeurs vides, false, null, undefined
        // Pour les valeurs numériques (budget, surface), envoyer même si c'est "0"
        const isNumericField = ['budgetMin', 'budgetMax', 'surfaceMin', 'surfaceMax', 'maxDays'].includes(key);
        
        if (isNumericField) {
          // Pour les champs numériques, envoyer si la valeur n'est pas vide et convertir en string
          if (value !== "" && value !== null && value !== undefined) {
            // Nettoyer la valeur : supprimer espaces et caractères non numériques sauf le point
            const cleanValue = String(value).replace(/[^0-9.]/g, '');
            if (cleanValue && cleanValue !== "." && !isNaN(parseFloat(cleanValue))) {
              params.append(key, cleanValue);
            }
          }
        } else {
          // Pour les autres champs, utiliser la logique d'origine mais exclure "0" seulement pour les champs non-numériques
          if (value !== "" && value !== false && value !== "0" && value !== null && value !== undefined) {
            params.append(key, value);
          }
        }
      });
      
      // Handle includeArchived special case
      const status = filters.includeArchived ? "all" : "active";
      params.append('status', status);
      
      const response = await axios.get(`${API}/properties/search?${params.toString()}`);
      setProperties(response.data);
      
      console.log(`Recherche effectuée : ${response.data.length} biens trouvés`);
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
      // Fallback to normal property loading if search fails
      loadProperties(currentFilter);
    }
  };

  // Critères de recherche : villes, négociateurs, types de biens et enseignes réellement
  // présents. Deux téléchargements COMPLETS du portefeuille (2 × 831 Ko en production, sur
  // CHAQUE écran puisque ce composant est monté par la route attrape-tout) servaient
  // uniquement à en extraire ces listes de valeurs distinctes. `/properties/criteria` les
  // calcule côté serveur.
  const loadDynamicCriteria = async () => {
    try {
      const { data } = await axios.get(`${API}/properties/criteria`);
      setAvailableNegotiators((data.negotiators || []).map(n => n.name).filter(Boolean));
      setAvailablePropertyTypes(data.types_bien || []);
      setAvailableEnseignes(data.enseignes || []);
      setAvailableCities(data.cities || []);
    } catch (error) {
      console.error("Error loading dynamic criteria:", error);
      setAvailableNegotiators([]);
      setAvailablePropertyTypes([]);
      setAvailableEnseignes([]);
    }
  };

  // Photo Gallery Modal Component
  const PhotoGalleryModal = () => {
    if (!photoGalleryOpen || !galleryProperty?.medias?.photos) return null;

    const photos = galleryProperty.medias.photos;
    const currentPhoto = photos[currentPhotoIndex];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
        <div className="relative max-w-6xl w-full h-full flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center text-white mb-4">
            <h3 className="text-xl font-semibold">
              {galleryProperty.bien_general?.type_bien || 'Bien immobilier'} - Galerie photos
            </h3>
            <div className="flex items-center space-x-4">
              <span className="text-sm">
                {currentPhotoIndex + 1} / {photos.length}
              </span>
              <button
                onClick={closePhotoGallery}
                className="text-white hover:text-gray-300 text-2xl font-bold"
              >
                ×
              </button>
            </div>
          </div>

          {/* Main Photo */}
          <div className="flex-1 flex items-center justify-center relative">
            <img
              src={currentPhoto.url}
              alt={currentPhoto.name || `Photo ${currentPhotoIndex + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
            />
            
            {/* Navigation Arrows */}
            {photos.length > 1 && (
              <>
                <button
                  onClick={prevPhoto}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-opacity"
                >
                  <i className="bi bi-chevron-left text-xl"></i>
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-opacity"
                >
                  <i className="bi bi-chevron-right text-xl"></i>
                </button>
              </>
            )}
          </div>

          {/* Thumbnail Strip */}
          {photos.length > 1 && (
            <div className="mt-4 flex justify-center space-x-2 pb-2 tabs-scroll-x">
              {photos.map((photo, index) => (
                <button
                  key={photo.id}
                  onClick={() => goToPhoto(index)}
                  className={`flex-shrink-0 w-16 h-12 rounded border-2 overflow-hidden transition-all ${
                    index === currentPhotoIndex 
                      ? 'border-white shadow-lg' 
                      : 'border-gray-600 hover:border-gray-400'
                  }`}
                >
                  <img
                    src={getMediaUrl(photo)}
                    alt={`Miniature ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          {/* Photo Info */}
          <div className="mt-2 text-center text-gray-300 text-sm">
            {currentPhoto.name && (
              <p className="font-medium">{currentPhoto.name}</p>
            )}
            {currentPhoto.size && (
              <p>{(currentPhoto.size / 1024 / 1024).toFixed(2)} MB</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Reset search filters
  const resetSearchFilters = () => {
    setSearchFilters({
      affichage: "classique", reference: "", agence: "0", negociateur: "",
      transaction_type: "", type_bien: "", activite_principale: "", statut_bien: "",
      secteur: "", ville: "", codePostal: "", enseigne: "", 
      budgetMin: "", budgetMax: "", surfaceMin: "", surfaceMax: "",
      libre: "", includeArchived: false, pdf: "0", exclusivite: "0", 
      mandat: "", vendu: false, venduPar: "", prixVenteMin: "", prixVenteMax: "", 
      dateVenteMin: "", dateVenteMax: "", maxDays: "", adresseBien: ""
    });
    // Reload all properties after reset
    loadProperties(currentFilter);
  };

  // Property consultation (read-only view)
  const consultProperty = (property) => {
    console.log('🔍 Consultation du bien:', property.id);
    setConsultedProperty(property);
    setShowConsultation(true);
    setShowDashboard(false);
    setShowForm(false);
    setShowContacts(false);
    setShowDocuments(false);
    setActiveSection('consultation');
  };

  // Consultation par ID (charge le bien depuis l'API puis l'affiche)
  const consultPropertyById = async (propertyId) => {
    console.log('🔍 Consultation du bien par ID:', propertyId);
    try {
      const response = await axios.get(`${API}/properties/${propertyId}`);
      if (response.data) {
        consultProperty(response.data);
      } else {
        console.error('Bien non trouvé:', propertyId);
        alert('Bien non trouvé');
      }
    } catch (error) {
      console.error('Erreur chargement bien:', error);
      alert('Erreur lors du chargement du bien');
    }
  };

  // Sélection (édition) par ID (charge le bien depuis l'API puis l'ouvre en édition)
  const selectPropertyById = async (propertyId) => {
    console.log('✏️ Édition du bien par ID:', propertyId);
    try {
      const response = await axios.get(`${API}/properties/${propertyId}`);
      if (response.data) {
        selectProperty(response.data);
      } else {
        console.error('Bien non trouvé:', propertyId);
        alert('Bien non trouvé');
      }
    } catch (error) {
      console.error('Erreur chargement bien:', error);
      alert('Erreur lors du chargement du bien');
    }
  };

  // Print property web format
  const printPropertyWeb = (property, format) => {
    console.log('🖨️ Impression web:', property.id, format);
    
    // Créer une nouvelle fenêtre pour l'impression
    const printWindow = window.open('', '_blank');
    
    // Générer le contenu HTML selon le format
    let htmlContent = '';
    
    switch (format) {
      case 'fiche_technique':
        htmlContent = generateFicheTechniqueHTML(property);
        break;
      case 'vitrine':
        htmlContent = generateVitrineHTML(property);
        break;
      case 'web':
        htmlContent = generateWebHTML(property);
        break;
      default:
        htmlContent = generateFicheTechniqueHTML(property);
    }
    
    // Écrire le contenu dans la nouvelle fenêtre
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Déclencher l'impression automatiquement
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Actions toolbar functions
  const openEmailModal = (property) => {
    console.log('📧 Ouverture modal email pour:', property.id);
    // TODO: Implement email modal
    alert('Fonctionnalité E-mailing à implémenter');
  };

  const openClientAffectation = (property) => {
    console.log('👤 Affectation client pour:', property.id);
    // TODO: Implement client affectation
    alert('Fonctionnalité Affectation client à implémenter');
  };

  const openHistorique = (property) => {
    console.log('📊 Historique pour:', property.id);
    // TODO: Implement historique modal
    alert('Fonctionnalité Historique à implémenter - sera le point 3');
  };

  const openBonVisite = (property) => {
    console.log('📋 Bon de visite pour:', property.id);
    // TODO: Implement bon de visite
    alert('Fonctionnalité Bon de visite à implémenter');
  };

  const openAjouterEvenement = (property) => {
    console.log('📅 Ajouter événement pour:', property.id);
    // TODO: Implement ajouter événement
    alert('Fonctionnalité Ajouter événement à implémenter');
  };

  const openAjouterTache = (property) => {
    console.log('✅ Ajouter tâche pour:', property.id);
    // TODO: Implement ajouter tâche
    alert('Fonctionnalité Ajouter tâche à implémenter');
  };

  // CONSULTATION READ-ONLY VERSIONS - Réutilisent le design modernisé
  const renderGeneralTabReadOnly = () => {
    const property = consultedProperty;
    
    // Créer un config spécifique pour le mode consultation basé sur consultedProperty
    const getReadOnlyConfig = () => {
      const activityType = property?.bien_general?.activite_principale || 
                           property?.subcategory || 
                           property?.category;
      
      console.log('🔍 [ReadOnly] Activité détectée:', activityType);
      
      // IMPORTANT : mapper l'activité vers le type de config
      const configType = getConfigTypeByActivity(activityType);
      console.log('📄 [ReadOnly] Type de config à charger:', configType);
      
      // Charger la config spécifique
      const specificConfig = loadConfigByType(configType);
      if (!specificConfig) {
        console.warn('⚠️ Aucune config trouvée, utilisation de base-config');
        return baseConfigJson;
      }
      
      const mergedConfig = mergeConfigWithBase(specificConfig);
      return mergeConfigWithPacks(mergedConfig);
    };
    
    const config = getReadOnlyConfig();
    const generalConfig = config?.onglets?.general;
    
    if (!generalConfig) {
      console.error('❌ [ReadOnly] Aucune config generale trouvée');
      return <div className="p-8 text-center text-gray-500">Configuration non disponible</div>;
    }
    
    console.log('✅ [ReadOnly] Config chargée:', Object.keys(generalConfig.sections || {}));
    
    return (
      <div className="space-y-8">
        {/* Modern info header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-indigo-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/25">
              <i className="bi bi-info-circle text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-blue-600 bg-clip-text text-transparent">
                Informations Générales
              </h2>
              <p className="text-gray-600 font-medium">
                Vue consultation des données principales de votre bien immobilier
              </p>
            </div>
          </div>
        </div>
        
        {/* Sections dynamiques depuis JSON config */}
        {Object.entries(generalConfig.sections || {}).map(([sectionKey, section]) => (
          <div key={sectionKey} className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-blue-500/5 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-purple-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/25">
                  <i className={`bi ${section.icon || 'bi-card-checklist'} text-lg`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{section.label}</h3>
                  {section.description && (
                    <p className="text-gray-600 text-sm">{section.description}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(section.champs || []).map((champ, index) => {
                  // Get field value based on groupe_data - Support multi-level paths
                  let value;
                  if (champ.groupe_data) {
                    const parts = champ.groupe_data.split('.');
                    if (parts.length === 1) {
                      // Root-level field (e.g., "transaction_type")
                      value = property[parts[0]];
                    } else if (parts.length === 2) {
                      // Nested field (e.g., "bien_general.type_bien")
                      value = property[parts[0]]?.[parts[1]];
                    } else if (parts.length === 3) {
                      // Deep nested field (e.g., "informations_confidentielles.proprietaire.nom")
                      value = property[parts[0]]?.[parts[1]]?.[parts[2]];
                    }
                  }
                  
                  return (
                    <div key={index} className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {champ.label}
                        {champ.obligatoire && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                        {value !== undefined && value !== null && value !== '' ? (
                          champ.type === 'boolean' ? (value ? 'Oui' : 'Non') :
                          champ.unite ? `${value} ${champ.unite}` :
                          value
                        ) : (
                          <span className="text-gray-400">Non renseigné</span>
                        )}
                      </div>
                      {champ.description && (
                        <span className="text-xs text-gray-500">{champ.description}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Autres onglets read-only (versions simplifiées pour éviter les erreurs)

  const renderPrestationsTabReadOnly = () => {
    const property = consultedProperty;
    const bien_general = property.bien_general || {};
    const packsActifs = property.packs_actifs || {};
    const commentaires_prestations = property.commentaires_prestations || "";

    return (
      <div className="space-y-8">
        {/* Modern info header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-indigo-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25">
              <i className="bi bi-gear-wide-connected text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-purple-600 bg-clip-text text-transparent">
                Prestations & Services
              </h2>
              <p className="text-gray-600 font-medium">
                Équipements et services du bien immobilier
              </p>
            </div>
          </div>
        </div>

        {/* Section Packs Actifs */}
        <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-indigo-300 shadow-2xl shadow-indigo-500/10 transition-all duration-500">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-white/50 to-purple-50/50"></div>
          <div className="relative p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-indigo-500/25">
                <i className="bi bi-puzzle text-lg"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Packs d'activité</h3>
                <p className="text-gray-600 text-sm">Packs actifs pour ce bien</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Pack Restauration */}
              <div className={`p-4 bg-white/50 backdrop-blur-sm border-2 rounded-xl transition-all duration-300 ${
                packsActifs.restauration 
                  ? 'border-orange-400 bg-orange-50/70' 
                  : 'border-gray-200 opacity-50'
              }`}>
                <div className="flex items-center">
                  <i className={`bi ${packsActifs.restauration ? 'bi-check-circle-fill text-orange-600' : 'bi-circle text-gray-400'} mr-2 text-xl`}></i>
                  <div>
                    <span className="text-sm font-bold text-gray-700">Pack Restauration</span>
                    <span className="text-xs text-gray-500 block mt-1">Extraction, cuisine, terrasse...</span>
                  </div>
                </div>
              </div>

              {/* Pack Retail */}
              <div className={`p-4 bg-white/50 backdrop-blur-sm border-2 rounded-xl transition-all duration-300 ${
                packsActifs.retail 
                  ? 'border-blue-400 bg-blue-50/70' 
                  : 'border-gray-200 opacity-50'
              }`}>
                <div className="flex items-center">
                  <i className={`bi ${packsActifs.retail ? 'bi-check-circle-fill text-blue-600' : 'bi-circle text-gray-400'} mr-2 text-xl`}></i>
                  <div>
                    <span className="text-sm font-bold text-gray-700">Pack Retail</span>
                    <span className="text-xs text-gray-500 block mt-1">Vitrine, flux, emplacement...</span>
                  </div>
                </div>
              </div>

              {/* Pack Enseignes */}
              <div className={`p-4 bg-white/50 backdrop-blur-sm border-2 rounded-xl transition-all duration-300 ${
                packsActifs.enseignes 
                  ? 'border-purple-400 bg-purple-50/70' 
                  : 'border-gray-200 opacity-50'
              }`}>
                <div className="flex items-center">
                  <i className={`bi ${packsActifs.enseignes ? 'bi-check-circle-fill text-purple-600' : 'bi-circle text-gray-400'} mr-2 text-xl`}></i>
                  <div>
                    <span className="text-sm font-bold text-gray-700">Pack Enseignes</span>
                    <span className="text-xs text-gray-500 block mt-1">Ciblage enseignes...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Affichage dynamique des champs des packs actifs */}
        {(() => {
          const renderedPacks = [];
          
          // Pack Restauration
          if (packsActifs.restauration) {
            const pack = loadPack('restauration');
            if (pack) {
              renderedPacks.push(
                <div key="pack-restauration" className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-orange-300 shadow-2xl shadow-orange-500/10 transition-all duration-500">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 via-white/50 to-yellow-50/50"></div>
                  <div className="relative p-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-orange-500/25">
                        <i className={`${pack.icon} text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">{pack.label}</h3>
                        <p className="text-gray-600 text-sm">{pack.description}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {pack.champs.map((champ, index) => {
                        const value = getFieldValue(property, champ.groupe_data, champ.type);
                        console.log(`🔍 Pack Restauration ReadOnly - ${champ.label} (${champ.groupe_data}):`, value);
                        
                        // Formater la valeur pour l'affichage
                        let displayValue;
                        if (value === null || value === undefined || value === '') {
                          displayValue = "Non renseigné";
                        } else if (typeof value === 'boolean') {
                          displayValue = value ? "Oui" : "Non";
                        } else {
                          displayValue = value;
                        }
                        
                        return (
                          <div key={index} className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">{champ.label}</label>
                            <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                              {displayValue}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }
          }
          
          // Pack Retail
          if (packsActifs.retail) {
            const pack = loadPack('retail');
            if (pack) {
              renderedPacks.push(
                <div key="pack-retail" className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-blue-300 shadow-2xl shadow-blue-500/10 transition-all duration-500">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-cyan-50/50"></div>
                  <div className="relative p-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/25">
                        <i className={`${pack.icon} text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">{pack.label}</h3>
                        <p className="text-gray-600 text-sm">{pack.description}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {pack.champs.map((champ, index) => {
                        const value = getFieldValue(property, champ.groupe_data, champ.type);
                        
                        // Formater la valeur pour l'affichage
                        let displayValue;
                        if (value === null || value === undefined || value === '') {
                          displayValue = "Non renseigné";
                        } else if (typeof value === 'boolean') {
                          displayValue = value ? "Oui" : "Non";
                        } else {
                          displayValue = value;
                        }
                        
                        return (
                          <div key={index} className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">{champ.label}</label>
                            <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                              {displayValue}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }
          }
          
          // Pack Enseignes
          if (packsActifs.enseignes) {
            const pack = loadPack('enseignes');
            if (pack) {
              renderedPacks.push(
                <div key="pack-enseignes" className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-purple-300 shadow-2xl shadow-purple-500/10 transition-all duration-500">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-white/50 to-pink-50/50"></div>
                  <div className="relative p-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25">
                        <i className={`${pack.icon} text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">{pack.label}</h3>
                        <p className="text-gray-600 text-sm">{pack.description}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {pack.champs.map((champ, index) => {
                        const value = getFieldValue(property, champ.groupe_data, champ.type);
                        
                        // Formater la valeur pour l'affichage
                        let displayValue;
                        if (value === null || value === undefined || value === '') {
                          displayValue = "Non renseigné";
                        } else if (typeof value === 'boolean') {
                          displayValue = value ? "Oui" : "Non";
                        } else {
                          displayValue = value;
                        }
                        
                        return (
                          <div key={index} className={champ.type === 'text' ? 'md:col-span-2 space-y-2' : 'space-y-2'}>
                            <label className="block text-sm font-semibold text-gray-700">{champ.label}</label>
                            <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                              {displayValue}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }
          }
          
          return renderedPacks;
        })()}

        {/* État général Section - Read Only */}
        <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-purple-500/5 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-500">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-white/50 to-pink-50/50"></div>
          <div className="relative p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25">
                <i className="bi bi-house-gear text-lg"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">État Général</h3>
                <p className="text-gray-600 text-sm">État des travaux et informations juridiques</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Travaux à prévoir</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {bien_general.travaux_prevoir || "Non renseigné"}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Ancienneté de l'activité</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {bien_general.anciennete_activite || "Non renseigné"}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Forme juridique</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {bien_general.forme_juridique || "Non renseigné"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Commentaires Section - Read Only */}
        {commentaires_prestations && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-blue-500/5 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-cyan-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/25">
                  <i className="bi bi-chat-text text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Commentaires</h3>
                  <p className="text-gray-600 text-sm">Notes et observations sur les prestations</p>
                </div>
              </div>
              
              <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                {commentaires_prestations}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };


  // Version dynamique pour Licences et Investissements
  const renderPrestationsTabReadOnlyDynamic = () => {
    const property = consultedProperty;
    
    // Créer un config spécifique pour le mode consultation
    const getReadOnlyConfig = () => {
      const activityType = property?.bien_general?.activite_principale || 
                           property?.subcategory || 
                           property?.category;
      
      console.log('🔍 [ReadOnly Prestations] Activité détectée:', activityType);
      
      // IMPORTANT : mapper l'activité vers le type de config
      const configType = getConfigTypeByActivity(activityType);
      console.log('📄 [ReadOnly Prestations] Type de config à charger:', configType);
      
      // Charger la config spécifique
      const specificConfig = loadConfigByType(configType);
      if (!specificConfig) {
        return baseConfigJson;
      }
      
      const mergedConfig = mergeConfigWithBase(specificConfig);
      return mergeConfigWithPacks(mergedConfig);
    };
    
    const config = getReadOnlyConfig();
    const prestationsConfig = config?.onglets?.prestations;
    
    if (!prestationsConfig) {
      return <div className="p-8 text-center text-gray-500">Configuration non disponible</div>;
    }

    return (
      <div className="space-y-8">
        {/* Modern info header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-indigo-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25">
              <i className="bi bi-gear-wide-connected text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-purple-600 bg-clip-text text-transparent">
                Prestations & Services
              </h2>
              <p className="text-gray-600 font-medium">
                {prestationsConfig.description || "Équipements et services du bien immobilier"}
              </p>
            </div>
          </div>
        </div>

        {/* Sections dynamiques depuis JSON config */}
        {Object.entries(prestationsConfig.sections || {}).map(([sectionKey, section]) => (
          <div key={sectionKey} className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-purple-500/5 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-white/50 to-pink-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25">
                  <i className={`bi ${section.icon || 'bi-gear-wide-connected'} text-lg`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{section.label}</h3>
                  {section.description && (
                    <p className="text-gray-600 text-sm">{section.description}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(section.champs || []).map((champ, index) => {
                  // Get field value based on groupe_data
                  const parts = champ.groupe_data.split('.');
                  let value;
                  if (parts.length === 1) {
                    value = property[parts[0]];
                  } else {
                    value = property[parts[0]]?.[parts[1]];
                  }
                  
                  // N'afficher que les champs remplis (sauf pour les champs de type "info")
                  if (champ.type !== 'info' && (value === undefined || value === null || value === '')) {
                    return null;
                  }
                  
                  // Rendu spécial pour les champs "info"
                  if (champ.type === 'info') {
                    return (
                      <div key={index} className="col-span-full">
                        <div className="p-4 bg-blue-50/70 border border-blue-200/50 rounded-xl">
                          <div className="flex items-start space-x-3">
                            <i className="bi bi-info-circle text-blue-600 text-lg mt-0.5"></i>
                            <div>
                              <p className="text-sm font-semibold text-blue-900">{champ.label}</p>
                              <p className="text-sm text-blue-800 mt-1">{champ.content}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div key={index} className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {champ.label}
                      </label>
                      <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                        {champ.type === 'boolean' ? (value ? 'Oui' : 'Non') :
                         champ.type === 'date' ? (value ? new Date(value).toLocaleDateString('fr-FR') : 'Non renseigné') :
                         champ.type === 'text' ? (
                           <div className="whitespace-pre-wrap">{value}</div>
                         ) :
                         champ.unite ? `${value} ${champ.unite}` :
                         value}
                      </div>
                      {champ.description && (
                        <span className="text-xs text-gray-500">{champ.description}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
        
        {/* Commentaires Section - Read Only (si présent) */}
        {property.commentaires_prestations && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-blue-500/5 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-cyan-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/25">
                  <i className="bi bi-chat-text text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Commentaires</h3>
                  <p className="text-gray-600 text-sm">Notes et observations sur les prestations</p>
                </div>
              </div>
              
              <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 whitespace-pre-wrap">
                {property.commentaires_prestations}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDescriptifTabReadOnly = () => {
    const property = consultedProperty;
    const descriptif = property.descriptif || {};
    const descriptions = descriptif.descriptions || {};
    const proximite = descriptif.proximite || {};

    // Types de descriptions — clés canoniques + fallback legacy, destination affichée
    const descriptionTypes = [
      { key: "Fiche Technique", legacy: "fiche_technique", label: "Fiche Technique", icon: "bi-file-text", usage: "PDF fiche détaillée (interne)" },
      { key: "Web", legacy: "web", label: "Web", icon: "bi-globe", usage: "Diffusion portails (CessionPME, LeBonCoin) + export agence" },
      { key: "Pub", legacy: "pub", label: "Pub", icon: "bi-megaphone", usage: "Fiche publique partagée (lien client)" },
      { key: "Mandat", legacy: "mandat", label: "Mandat", icon: "bi-file-earmark-check", usage: "PDF mandat" },
      { key: "Affiche vitrine", legacy: "affiche_vitrine", label: "Affiche vitrine", icon: "bi-shop-window", usage: "Affiche imprimée vitrine" },
      { key: "Autre", legacy: "autre", label: "Autre", icon: "bi-three-dots", usage: "Libre / notes" }
    ];

    // Vérifier si on a du contenu
    const hasAnyContent = Object.keys(descriptions).length > 0 || Object.keys(proximite).length > 0;

    if (!hasAnyContent) {
      return (
        <div className="p-12 text-center">
          <i className="bi bi-file-earmark-text text-6xl text-gray-300 mb-4"></i>
          <p className="text-gray-500 text-lg">Aucune description disponible pour ce bien</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500/5 via-green-500/5 to-teal-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/10 to-green-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-emerald-500/25">
              <i className="bi bi-file-earmark-text text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-emerald-600 bg-clip-text text-transparent">
                Descriptions & Contenus
              </h2>
              <p className="text-gray-600 font-medium">
                Descriptions détaillées du bien
              </p>
            </div>
          </div>
        </div>

        {/* Descriptions par type (Français) */}
        {descriptionTypes.map((type) => {
          const typeDescriptions = descriptions[type.key] || descriptions[type.legacy] || {};
          const descObj = typeDescriptions["Français"] || typeDescriptions.fr;
          const content = descObj?.content || "";
          const hasContent = content.trim().length > 0;

          if (!hasContent) return null;

          return (
            <div key={type.key} className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/30 via-white/50 to-green-50/30"></div>
              <div className="relative p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center text-white mr-3 shadow-lg shadow-emerald-500/25">
                    <i className={`${type.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">{type.label}</h4>
                    <p className="text-xs text-slate-500">Utilisé pour : {type.usage}</p>
                  </div>
                  {descObj?.optimized_by_ai && (
                    <span className="ml-auto px-3 py-1 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800 rounded-lg text-xs font-semibold">
                      ✨ Optimisé par IA
                    </span>
                  )}
                </div>

                <div
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 prose prose-sm max-w-none [&>p]:mb-2 [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>h3]:font-bold [&>h3]:text-base"
                  dangerouslySetInnerHTML={{ __html: renderDescriptionHtml(content) }}
                />
              </div>
            </div>
          );
        })}

        {/* Section Proximité */}
        {Object.keys(proximite).some(lang => proximite[lang] && proximite[lang].trim().length > 0) && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-white/50 to-cyan-50/30"></div>
            <div className="relative p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center text-white mr-3 shadow-lg shadow-blue-500/25">
                  <i className="bi bi-geo-alt text-lg"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-800">Proximité & Points d'intérêt</h4>
              </div>

              {(() => {
                const content = proximite["Français"] || proximite.fr || "";
                if (!content.trim()) return null;
                return (
                  <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 whitespace-pre-wrap">
                    {content}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCompositionTabReadOnly = () => {
    const property = consultedProperty;
    
    // Créer un config spécifique pour le mode consultation
    const getReadOnlyConfig = () => {
      const activityType = property?.bien_general?.activite_principale || 
                           property?.subcategory || 
                           property?.category;
      
      console.log('🔍 [ReadOnly Composition] Activité détectée:', activityType);
      
      // IMPORTANT : mapper l'activité vers le type de config
      const configType = getConfigTypeByActivity(activityType);
      console.log('📄 [ReadOnly Composition] Type de config à charger:', configType);
      
      // Charger la config spécifique
      const specificConfig = loadConfigByType(configType);
      if (!specificConfig) {
        return baseConfigJson;
      }
      
      const mergedConfig = mergeConfigWithBase(specificConfig);
      return mergeConfigWithPacks(mergedConfig);
    };
    
    const config = getReadOnlyConfig();
    const compositionConfig = config?.onglets?.composition;
    
    if (!compositionConfig) {
      return <div className="p-8 text-center text-gray-500">Configuration non disponible</div>;
    }
    
    return (
      <div className="space-y-8">
        {/* Modern info header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-orange-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600/10 to-yellow-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-amber-500/25">
              <i className="bi bi-diagram-3 text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-amber-600 bg-clip-text text-transparent">
                Composition & Surfaces
              </h2>
              <p className="text-gray-600 font-medium">
                Répartition des surfaces et capacités du bien
              </p>
            </div>
          </div>
        </div>
        
        {/* Sections dynamiques depuis JSON config */}
        {Object.entries(compositionConfig.sections || {}).map(([sectionKey, section]) => (
          <div key={sectionKey} className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-amber-500/5 hover:shadow-lg hover:shadow-amber-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 via-white/50 to-yellow-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-amber-500/25">
                  <i className={`bi ${section.icon || 'bi-rulers'} text-lg`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{section.label}</h3>
                  {section.description && (
                    <p className="text-gray-600 text-sm">{section.description}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(section.champs || []).map((champ, index) => {
                  // Rendu spécial pour les champs "info" (sans groupe_data)
                  if (champ.type === 'info') {
                    return (
                      <div key={index} className="col-span-full">
                        <div className="p-4 bg-blue-50/70 border border-blue-200/50 rounded-xl">
                          <div className="flex items-start space-x-3">
                            <i className="bi bi-info-circle text-blue-600 text-lg mt-0.5"></i>
                            <div>
                              <p className="text-sm font-semibold text-blue-900">{champ.label}</p>
                              <p className="text-sm text-blue-800 mt-1">{champ.content}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  // Vérifier que groupe_data existe avant de split
                  if (!champ.groupe_data) {
                    return null;
                  }
                  
                  // Get field value based on groupe_data
                  const parts = champ.groupe_data.split('.');
                  const value = property[parts[0]]?.[parts[1]];
                  
                  // N'afficher que les champs remplis
                  if (value === undefined || value === null || value === '') {
                    return null;
                  }
                  
                  return (
                    <div key={index} className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {champ.label}
                      </label>
                      <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                        {champ.type === 'boolean' ? (value ? 'Oui' : 'Non') :
                         champ.unite ? `${value} ${champ.unite}` :
                         value}
                      </div>
                      {champ.description && (
                        <span className="text-xs text-gray-500">{champ.description}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderConfidentielTabReadOnly = () => {
    const property = consultedProperty;
    const informations_confidentielles = property.informations_confidentielles || {};
    const proprietaire = informations_confidentielles.proprietaire || {};
    
    return (
      <div className="space-y-8">
        {/* Modern info header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-red-500/5 via-orange-500/5 to-pink-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 to-orange-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-red-500/25">
              <i className="bi bi-shield-lock text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-red-600 bg-clip-text text-transparent">
                Informations Confidentielles
              </h2>
              <p className="text-gray-600 font-medium">
                Données réservées à l'équipe de l'agence
              </p>
            </div>
          </div>
        </div>

        {/* Propriétaire Section - Read Only */}
        <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-orange-500/5 hover:shadow-lg hover:shadow-orange-500/10 transition-all duration-500">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 via-white/50 to-red-50/50"></div>
          <div className="relative p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-orange-500/25">
                <i className="bi bi-person-vcard text-lg"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Propriétaire</h3>
                <p className="text-gray-600 text-sm">Informations du propriétaire du bien</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Société</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {proprietaire.societe || "Non renseigné"}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Nom</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {proprietaire.nom || "Non renseigné"}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Prénom</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {proprietaire.prenom || "Non renseigné"}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Forme juridique</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {proprietaire.forme_juridique || "Non renseigné"}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Activité</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {proprietaire.activite || "Non renseigné"}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">SIRET</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {proprietaire.siret || "Non renseigné"}
                </div>
              </div>
            </div>
            
            {/* Adresse */}
            {proprietaire.adresse && (
              <div className="mt-6 space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Adresse complète</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                  {proprietaire.adresse}
                  {proprietaire.ville && `, ${proprietaire.ville}`}
                  {proprietaire.code_postal && ` ${proprietaire.code_postal}`}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section Négociateur - Read Only */}
        {property.negociateur?.negociateur && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-purple-500/5 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-white/50 to-indigo-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25">
                  <i className="bi bi-person-badge text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Négociateur</h3>
                  <p className="text-gray-600 text-sm">Responsable du bien dans l'agence</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Négociateur interne</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {getNegociateurName(property, availableNegotiators)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Adresse du Bien Section - Read Only */}
        {(informations_confidentielles.adresse || informations_confidentielles.ville) && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-red-500/5 hover:shadow-lg hover:shadow-red-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-red-50/50 via-white/50 to-orange-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-red-500/25">
                  <i className="bi bi-geo-alt-fill text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Adresse du Bien</h3>
                  <p className="text-gray-600 text-sm">Localisation confidentielle du bien</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Adresse complète confidentielle</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                  {informations_confidentielles.adresse || informations_confidentielles.adresse_bien_confidentielle || "Non renseigné"}
                  {informations_confidentielles.ville && `, ${informations_confidentielles.ville}`}
                  {informations_confidentielles.code_postal && ` ${informations_confidentielles.code_postal}`}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section Syndic - Read Only */}
        {(informations_confidentielles.syndic_nom || informations_confidentielles.syndic_telephone || informations_confidentielles.syndic_email) && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-teal-500/5 hover:shadow-lg hover:shadow-teal-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-50/50 via-white/50 to-cyan-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-teal-500/25">
                  <i className="bi bi-building-gear text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Syndic</h3>
                  <p className="text-gray-600 text-sm">Informations du syndic de copropriété</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {informations_confidentielles.syndic_nom && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Nom du syndic</label>
                    <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                      {informations_confidentielles.syndic_nom}
                    </div>
                  </div>
                )}
                {informations_confidentielles.syndic_telephone && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Téléphone</label>
                    <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                      {informations_confidentielles.syndic_telephone}
                    </div>
                  </div>
                )}
                {informations_confidentielles.syndic_email && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Email</label>
                    <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                      {informations_confidentielles.syndic_email}
                    </div>
                  </div>
                )}
              </div>
              
              {informations_confidentielles.syndic_adresse && (
                <div className="mt-6 space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Adresse du syndic</label>
                  <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                    {informations_confidentielles.syndic_adresse}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section Visites - Read Only */}
        {(informations_confidentielles.conditions_visites || informations_confidentielles.horaires_visites || informations_confidentielles.contact_visites || informations_confidentielles.remarques_visites) && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-amber-500/5 hover:shadow-lg hover:shadow-amber-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 via-white/50 to-yellow-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-amber-500/25">
                  <i className="bi bi-eye-fill text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Visites</h3>
                  <p className="text-gray-600 text-sm">Conditions et modalités de visite</p>
                </div>
              </div>
              
              {informations_confidentielles.conditions_visites && (
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-semibold text-gray-700">Conditions de visites</label>
                  <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                    {informations_confidentielles.conditions_visites}
                  </div>
                </div>
              )}
              
              {informations_confidentielles.horaires_visites && (
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-semibold text-gray-700">Horaires de visites</label>
                  <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                    {informations_confidentielles.horaires_visites}
                  </div>
                </div>
              )}
              
              {informations_confidentielles.contact_visites && (
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-semibold text-gray-700">Contact pour visites</label>
                  <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                    {informations_confidentielles.contact_visites}
                  </div>
                </div>
              )}
              
              {informations_confidentielles.remarques_visites && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Remarques sur les visites</label>
                  <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                    {informations_confidentielles.remarques_visites}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section Informations Diverses - Read Only */}
        {informations_confidentielles.informations_diverses && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-gray-500/5 hover:shadow-lg hover:shadow-gray-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 via-white/50 to-slate-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-gray-500/25">
                  <i className="bi bi-info-circle text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Informations Diverses</h3>
                  <p className="text-gray-600 text-sm">Notes et remarques complémentaires</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                  {informations_confidentielles.informations_diverses}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section Informations Financières - Read Only */}
        {(informations_confidentielles.chiffre_affaires_annuel || informations_confidentielles.benefices_annuels || informations_confidentielles.charges_annuelles || informations_confidentielles.banque || informations_confidentielles.compte_bancaire) && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-indigo-500/5 hover:shadow-lg hover:shadow-indigo-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-white/50 to-blue-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-indigo-500/25">
                  <i className="bi bi-bank2 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Informations Financières</h3>
                  <p className="text-gray-600 text-sm">Données financières et bancaires</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {informations_confidentielles.chiffre_affaires_annuel && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Chiffre d'affaires annuel (€)</label>
                    <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                      {parseInt(informations_confidentielles.chiffre_affaires_annuel).toLocaleString()} €
                    </div>
                  </div>
                )}
                {informations_confidentielles.benefices_annuels && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Bénéfices annuels (€)</label>
                    <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                      {parseInt(informations_confidentielles.benefices_annuels).toLocaleString()} €
                    </div>
                  </div>
                )}
                {informations_confidentielles.charges_annuelles && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Charges annuelles (€)</label>
                    <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                      {parseInt(informations_confidentielles.charges_annuelles).toLocaleString()} €
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {informations_confidentielles.banque && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Banque</label>
                    <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                      {informations_confidentielles.banque}
                    </div>
                  </div>
                )}
                {informations_confidentielles.compte_bancaire && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Compte bancaire</label>
                    <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-mono">
                      {informations_confidentielles.compte_bancaire}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Section Divers - Read Only */}
        {(informations_confidentielles.raison_vente || informations_confidentielles.notes_particulieres || informations_confidentielles.commentaires_confidentiels) && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-slate-500/5 hover:shadow-lg hover:shadow-slate-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-white/50 to-gray-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-slate-500 to-slate-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-slate-500/25">
                  <i className="bi bi-card-text text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Divers</h3>
                  <p className="text-gray-600 text-sm">Informations complémentaires et notes</p>
                </div>
              </div>
              
              {informations_confidentielles.raison_vente && (
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-semibold text-gray-700">Raison de la vente</label>
                  <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                    {informations_confidentielles.raison_vente}
                  </div>
                </div>
              )}
              
              {informations_confidentielles.notes_particulieres && (
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-semibold text-gray-700">Notes particulières</label>
                  <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                    {informations_confidentielles.notes_particulieres}
                  </div>
                </div>
              )}
              
              {informations_confidentielles.commentaires_confidentiels && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Commentaires confidentiels</label>
                  <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800">
                    {informations_confidentielles.commentaires_confidentiels}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Message de confidentialité */}
        <div className="p-6 bg-gradient-to-r from-red-50 to-orange-100/50 border border-red-200/50 rounded-3xl">
          <div className="flex items-start">
            <i className="bi bi-shield-exclamation text-red-600 text-2xl mr-4 mt-1 flex-shrink-0"></i>
            <div className="text-sm text-red-800">
              <span className="font-semibold">Confidentialité :</span>
              <br />
              Ces informations sont strictement confidentielles et ne doivent pas être partagées avec des tiers sans autorisation explicite du propriétaire et de l'agence.
            </div>
          </div>
        </div>
      </div>
    );
  };
  const renderConfidentielTabReadOnlyDynamic = () => {
    const property = consultedProperty;
    
    // Créer un config spécifique pour le mode consultation
    const getReadOnlyConfig = () => {
      const activityType = property?.bien_general?.activite_principale || 
                           property?.subcategory || 
                           property?.category;
      
      console.log('🔍 [ReadOnly Confidentiel] Activité détectée:', activityType);
      
      // IMPORTANT : mapper l'activité vers le type de config
      const configType = getConfigTypeByActivity(activityType);
      console.log('📄 [ReadOnly Confidentiel] Type de config à charger:', configType);
      
      // Charger la config spécifique
      const specificConfig = loadConfigByType(configType);
      if (!specificConfig) {
        return baseConfigJson;
      }
      
      const mergedConfig = mergeConfigWithBase(specificConfig);
      return mergeConfigWithPacks(mergedConfig);
    };
    
    const config = getReadOnlyConfig();
    const confidentielConfig = config?.onglets?.confidentiel;
    
    if (!confidentielConfig) {
      return <div className="p-8 text-center text-gray-500">Configuration non disponible</div>;
    }
    
    return (
      <div className="space-y-8">
        {/* Modern info header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-red-500/5 via-orange-500/5 to-pink-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 to-orange-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-red-500/25">
              <i className="bi bi-shield-lock text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-red-600 bg-clip-text text-transparent">
                Informations Confidentielles
              </h2>
              <p className="text-gray-600 font-medium">
                {confidentielConfig.description || "Données réservées à l'équipe de l'agence"}
              </p>
            </div>
          </div>
        </div>

        {/* Sections dynamiques depuis JSON config */}
        {Object.entries(confidentielConfig.sections || {}).map(([sectionKey, section]) => {
          // Vérifier si la section a une condition (transaction_type)
          if (section.condition) {
            const transactionType = property?.transaction_type;
            const conditionMatch = section.condition.includes('Vente') ? transactionType === 'Vente' : transactionType === 'Location';
            if (!conditionMatch) {
              return null; // Ne pas afficher cette section
            }
          }
          
          return (
            <div key={sectionKey} className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-red-500/5 hover:shadow-lg hover:shadow-red-500/10 transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-red-50/50 via-white/50 to-orange-50/50"></div>
              <div className="relative p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-red-500/25">
                    <i className={`bi ${section.icon || 'bi-shield-lock'} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{section.label}</h3>
                    {section.description && (
                      <p className="text-gray-600 text-sm">{section.description}</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(section.champs || []).map((champ, index) => {
                    // Rendu spécial pour les champs "info" (sans groupe_data)
                    if (champ.type === 'info') {
                      return (
                        <div key={index} className="col-span-full">
                          <div className="p-4 bg-blue-50/70 border border-blue-200/50 rounded-xl">
                            <div className="flex items-start space-x-3">
                              <i className="bi bi-info-circle text-blue-600 text-lg mt-0.5"></i>
                              <div>
                                <p className="text-sm font-semibold text-blue-900">{champ.label}</p>
                                <p className="text-sm text-blue-800 mt-1">{champ.content}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Vérifier que groupe_data existe avant de split
                    if (!champ.groupe_data) {
                      return null;
                    }
                    
                    // Get field value based on groupe_data
                    const parts = champ.groupe_data.split('.');
                    let value;
                    if (parts.length === 1) {
                      value = property[parts[0]];
                    } else if (parts.length === 2) {
                      value = property[parts[0]]?.[parts[1]];
                    } else {
                      // Pour les champs plus profondément imbriqués (ex: proprietaire.nom)
                      value = property;
                      for (const part of parts) {
                        value = value?.[part];
                      }
                    }
                    
                    // N'afficher que les champs remplis ou les objets
                    if (value === undefined || value === null || value === '') {
                      return null;
                    }
                    
                    // Ne pas afficher les objets ou tableaux complexes (sauf pour contact_selector)
                    if (typeof value === 'object' && champ.type !== 'contact_selector') {
                      return null;
                    }
                    
                    return (
                      <div key={index} className={champ.type === 'text' ? "col-span-full space-y-2" : "space-y-2"}>
                        <label className="block text-sm font-semibold text-gray-700">
                          {champ.label}
                        </label>
                        <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                          {champ.type === 'boolean' ? (value ? 'Oui' : 'Non') :
                           champ.type === 'date' ? (value ? new Date(value).toLocaleDateString('fr-FR') : 'Non renseigné') :
                           champ.type === 'text' ? (
                             <div className="whitespace-pre-wrap">{value}</div>
                           ) :
                           champ.type === 'number' && champ.unite ? `${parseInt(value).toLocaleString()} ${champ.unite}` :
                           champ.unite ? `${value} ${champ.unite}` :
                           String(value)}
                        </div>
                        {champ.description && (
                          <span className="text-xs text-gray-500">{champ.description}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderVenteTabReadOnly = () => {
    const property = consultedProperty;
    const informations_vente = property.informations_vente || {};
    
    return (
      <div className="space-y-8">
        {/* Modern info header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-500/5 via-cyan-500/5 to-blue-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-600/10 to-cyan-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-teal-500/25">
              <i className="bi bi-currency-euro text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-teal-600 bg-clip-text text-transparent">
                Informations de Vente
              </h2>
              <p className="text-gray-600 font-medium">
                Prix, disponibilité et documents de vente
              </p>
            </div>
          </div>
        </div>

        {/* Prix et Négociation Section - Read Only */}
        <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-teal-500/5 hover:shadow-lg hover:shadow-teal-500/10 transition-all duration-500">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-50/50 via-white/50 to-cyan-50/50"></div>
          <div className="relative p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-teal-500/25">
                <i className="bi bi-currency-euro text-lg"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Prix & Négociation</h3>
                <p className="text-gray-600 text-sm">Configuration tarifaire et marges de négociation</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Prix d'annonce (€)</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {informations_vente.prix_annonce ? `${parseInt(informations_vente.prix_annonce).toLocaleString()} €` : "Non renseigné"}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Prix minimum accepté (€)</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {informations_vente.prix_minimum_accepte ? `${parseInt(informations_vente.prix_minimum_accepte).toLocaleString()} €` : "Non renseigné"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Disponibilité Section - Read Only */}
        <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-cyan-500/5 hover:shadow-lg hover:shadow-cyan-500/10 transition-all duration-500">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 via-white/50 to-blue-50/50"></div>
          <div className="relative p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-cyan-500/25">
                <i className="bi bi-calendar-check text-lg"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Disponibilité</h3>
                <p className="text-gray-600 text-sm">Calendrier et délais de libération</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Date de disponibilité</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {informations_vente.date_disponibilite ? new Date(informations_vente.date_disponibilite).toLocaleDateString('fr-FR') : "Non renseigné"}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Délai de libération</label>
                <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                  {informations_vente.delai_liberation || "Non renseigné"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Visites Section - Read Only */}
        {(informations_vente.conditions_visite || informations_vente.contact_visite) && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-blue-500/5 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-indigo-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/25">
                  <i className="bi bi-eye text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Visites</h3>
                  <p className="text-gray-600 text-sm">Organisation et modalités des visites</p>
                </div>
              </div>
              
              <div className="space-y-6">
                {informations_vente.conditions_visite && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Conditions de visite</label>
                    <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 min-h-20">
                      {informations_vente.conditions_visite}
                    </div>
                  </div>
                )}
                
                {informations_vente.contact_visite && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Contact pour les visites</label>
                    <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                      {informations_vente.contact_visite}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Documents Section - Read Only */}
        {(informations_vente.documents_requis || informations_vente.statut_documents) && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-indigo-500/5 hover:shadow-lg hover:shadow-indigo-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-white/50 to-purple-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-indigo-500/25">
                  <i className="bi bi-file-earmark text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Documents de Vente</h3>
                  <p className="text-gray-600 text-sm">Gestion documentaire et statuts administratifs</p>
                </div>
              </div>
              
              <div className="space-y-6">
                {informations_vente.documents_requis && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Documents requis</label>
                    <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 min-h-24">
                      {informations_vente.documents_requis}
                    </div>
                  </div>
                )}
                
                {informations_vente.statut_documents && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Statut des documents</label>
                    <div className={`w-full px-4 py-3 backdrop-blur-sm border rounded-xl font-medium ${
                      informations_vente.statut_documents === 'Complets' 
                        ? 'bg-green-100/70 border-green-200/50 text-green-800'
                        : informations_vente.statut_documents === 'En cours'
                        ? 'bg-yellow-100/70 border-yellow-200/50 text-yellow-800'
                        : 'bg-red-100/70 border-red-200/50 text-red-800'
                    }`}>
                      {informations_vente.statut_documents}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  const renderVenteTabReadOnlyDynamic = () => {
    const property = consultedProperty;
    
    // Créer un config spécifique pour le mode consultation
    const getReadOnlyConfig = () => {
      const activityType = property?.bien_general?.activite_principale || 
                           property?.subcategory || 
                           property?.category;
      
      console.log('🔍 [ReadOnly Vente] Activité détectée:', activityType);
      
      // IMPORTANT : mapper l'activité vers le type de config
      const configType = getConfigTypeByActivity(activityType);
      console.log('📄 [ReadOnly Vente] Type de config à charger:', configType);
      
      // Charger la config spécifique
      const specificConfig = loadConfigByType(configType);
      if (!specificConfig) {
        return baseConfigJson;
      }
      
      const mergedConfig = mergeConfigWithBase(specificConfig);
      return mergeConfigWithPacks(mergedConfig);
    };
    
    const config = getReadOnlyConfig();
    const venteConfig = config?.onglets?.vente;
    
    if (!venteConfig) {
      return <div className="p-8 text-center text-gray-500">Configuration non disponible</div>;
    }
    
    return (
      <div className="space-y-8">
        {/* Modern info header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-500/5 via-cyan-500/5 to-blue-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-600/10 to-cyan-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-teal-500/25">
              <i className="bi bi-currency-euro text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-teal-600 bg-clip-text text-transparent">
                {venteConfig.label || "Informations de Vente"}
              </h2>
              <p className="text-gray-600 font-medium">
                {venteConfig.description || "Prix, disponibilité et documents"}
              </p>
            </div>
          </div>
        </div>

        {/* Sections dynamiques depuis JSON config */}
        {Object.entries(venteConfig.sections || {}).map(([sectionKey, section]) => {
          // Vérifier si la section a une condition
          if (section.condition) {
            const transactionType = property?.transaction_type;
            const conditionMatch = section.condition.includes('Vente') ? transactionType === 'Vente' : transactionType === 'Location';
            if (!conditionMatch) {
              return null; // Ne pas afficher cette section
            }
          }
          
          return (
            <div key={sectionKey} className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-teal-500/5 hover:shadow-lg hover:shadow-teal-500/10 transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-50/50 via-white/50 to-cyan-50/50"></div>
              <div className="relative p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-teal-500/25">
                    <i className={`bi ${section.icon || 'bi-currency-euro'} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{section.label}</h3>
                    {section.description && (
                      <p className="text-gray-600 text-sm">{section.description}</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(section.champs || []).map((champ, index) => {
                    // Rendu spécial pour les champs "info" (sans groupe_data)
                    if (champ.type === 'info') {
                      return (
                        <div key={index} className="col-span-full">
                          <div className="p-4 bg-blue-50/70 border border-blue-200/50 rounded-xl">
                            <div className="flex items-start space-x-3">
                              <i className="bi bi-info-circle text-blue-600 text-lg mt-0.5"></i>
                              <div>
                                <p className="text-sm font-semibold text-blue-900">{champ.label}</p>
                                <p className="text-sm text-blue-800 mt-1">{champ.content}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Vérifier que groupe_data existe avant de split
                    if (!champ.groupe_data) {
                      return null;
                    }
                    
                    // Get field value based on groupe_data
                    const parts = champ.groupe_data.split('.');
                    let value;
                    if (parts.length === 1) {
                      value = property[parts[0]];
                    } else if (parts.length === 2) {
                      value = property[parts[0]]?.[parts[1]];
                    } else {
                      // Pour les champs plus profondément imbriqués
                      value = property;
                      for (const part of parts) {
                        value = value?.[part];
                      }
                    }
                    
                    // N'afficher que les champs remplis ou les objets
                    if (value === undefined || value === null || value === '') {
                      return null;
                    }
                    
                    // Ne pas afficher les objets ou tableaux complexes
                    if (typeof value === 'object') {
                      return null;
                    }
                    
                    return (
                      <div key={index} className={champ.type === 'text' ? "col-span-full space-y-2" : "space-y-2"}>
                        <label className="block text-sm font-semibold text-gray-700">
                          {champ.label}
                        </label>
                        <div className="w-full px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium">
                          {champ.type === 'boolean' ? (value ? 'Oui' : 'Non') :
                           champ.type === 'date' ? (value ? new Date(value).toLocaleDateString('fr-FR') : 'Non renseigné') :
                           champ.type === 'text' ? (
                             <div className="whitespace-pre-wrap">{value}</div>
                           ) :
                           champ.type === 'number' && champ.unite ? `${parseInt(value).toLocaleString()} ${champ.unite}` :
                           champ.unite ? `${value} ${champ.unite}` :
                           String(value)}
                        </div>
                        {champ.description && (
                          <span className="text-xs text-gray-500">{champ.description}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };


  const renderMediasTabReadOnly = () => {
    const property = consultedProperty;
    const medias = property.medias || {};
    const photos = medias.photos || [];
    const documents = medias.documents || [];
    const plans = medias.plans || [];
    
    return (
      <div className="space-y-8">
        {/* Modern info header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-500/5 via-amber-500/5 to-yellow-500/10 border border-white/20 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/10 to-amber-600/10"></div>
          <div className="relative p-6 flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-orange-500/25">
              <i className="bi bi-image text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-orange-600 bg-clip-text text-transparent">
                Photos & Documents
              </h2>
              <p className="text-gray-600 font-medium">
                Galerie de médias du bien immobilier
              </p>
            </div>
          </div>
        </div>

        {/* Photos Section - Read Only */}
        {photos.length > 0 && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-orange-500/5 hover:shadow-lg hover:shadow-orange-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 via-white/50 to-amber-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-orange-500/25">
                  <i className="bi bi-camera text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Photos ({photos.length})</h3>
                  <p className="text-gray-600 text-sm">Galerie d'images du bien</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {photos.map((photo, index) => (
                  <div key={photo.id} className="relative group">
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg shadow-orange-500/10 hover:shadow-xl hover:shadow-orange-500/20 transition-all duration-300 hover:-translate-y-1">
                      <img
                        src={getMediaUrl(photo)}
                        alt={photo.name}
                        className="w-full h-32 object-cover"
                      />
                      
                      {/* Photo order indicator */}
                      <div className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-lg">
                        {index + 1}
                      </div>
                      
                      {/* Photo name */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-2 rounded-b-2xl">
                        <div className="text-xs font-medium truncate">{photo.name}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Documents Section - Read Only */}
        {documents.length > 0 && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-amber-500/5 hover:shadow-lg hover:shadow-amber-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 via-white/50 to-yellow-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-amber-500/25">
                  <i className="bi bi-file-earmark-pdf text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Documents ({documents.length})</h3>
                  <p className="text-gray-600 text-sm">Fichiers PDF, DOC, XLS et autres documents</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-4 bg-white/50 backdrop-blur-sm border border-gray-200/50 rounded-xl">
                    <div className="flex items-center space-x-4">
                      <i className="bi bi-file-earmark text-2xl text-amber-600"></i>
                      <div>
                        <div className="font-semibold text-gray-900">{doc.name}</div>
                        <div className="text-sm text-gray-500">
                          {(doc.size / 1024 / 1024).toFixed(2)} MB - {new Date(doc.uploadDate).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => window.open(doc.url, '_blank')}
                      className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-300"
                    >
                      Ouvrir
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Plans Section - Read Only */}
        {plans.length > 0 && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-yellow-500/5 hover:shadow-lg hover:shadow-yellow-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-50/50 via-white/50 to-orange-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-yellow-500/25">
                  <i className="bi bi-rulers text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Plans ({plans.length})</h3>
                  <p className="text-gray-600 text-sm">Plans techniques et architecturaux</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans.map((plan) => (
                  <div key={plan.id} className="relative group p-4 bg-white/50 backdrop-blur-sm border border-gray-200/50 rounded-xl hover:bg-white/70 transition-all duration-300">
                    <div className="flex items-center space-x-3">
                      <span className="text-3xl">📐</span>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 truncate">{plan.name}</div>
                        <div className="text-sm text-gray-500">
                          {(plan.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => window.open(plan.url, '_blank')}
                      className="mt-2 w-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-3 py-2 rounded-xl text-sm font-medium hover:from-yellow-600 hover:to-yellow-700 transition-all duration-300"
                    >
                      Voir le plan
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Vidéos & Visites Virtuelles Section - Read Only */}
        {(medias.video_url || medias.visite_virtuelle_url) && (
          <div className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-purple-500/5 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-white/50 to-indigo-50/50"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/25">
                  <i className="bi bi-camera-video text-lg"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Vidéos & Visites Virtuelles</h3>
                  <p className="text-gray-600 text-sm">Contenus multimédias interactifs</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {medias.video_url && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">URL Vidéo</label>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium truncate">
                        {medias.video_url}
                      </div>
                      <button
                        onClick={() => window.open(medias.video_url, '_blank')}
                        className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-3 rounded-xl text-sm font-medium hover:from-purple-600 hover:to-purple-700 transition-all duration-300"
                      >
                        Voir
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Visite virtuelle — masquée v1.0 (flag SHOW_VISITE_VIRTUELLE) */}
                {false && medias.visite_virtuelle_url && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">URL Visite Virtuelle</label>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 px-4 py-3 bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-800 font-medium truncate">
                        {medias.visite_virtuelle_url}
                      </div>
                      <button
                        onClick={() => window.open(medias.visite_virtuelle_url, '_blank')}
                        className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-4 py-3 rounded-xl text-sm font-medium hover:from-indigo-600 hover:to-indigo-700 transition-all duration-300"
                      >
                        Visiter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Message si aucun média */}
        {photos.length === 0 && documents.length === 0 && plans.length === 0 && !medias.video_url && !medias.visite_virtuelle_url && (
          <div className="p-8 bg-gradient-to-r from-gray-50 to-gray-100/50 border border-gray-200/50 rounded-3xl text-center">
            <i className="bi bi-image text-4xl text-gray-400 mb-4"></i>
            <h3 className="text-lg font-semibold text-gray-600 mb-2">Aucun média disponible</h3>
            <p className="text-gray-500">Aucune photo, document ou média n'a été ajouté pour ce bien.</p>
          </div>
        )}
      </div>
    );
  };

  const renderHistorique = () => (
    <div className="p-6 bg-white">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Historique des demandes de contact</h3>
        <p className="text-sm text-gray-600">
          Toutes les demandes reçues pour ce bien
        </p>
      </div>
      
      {demandesBien.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <i className="bi bi-inbox text-4xl text-gray-400 mb-3 block"></i>
          <p className="text-gray-500 font-medium">Aucune demande pour ce bien</p>
        </div>
      ) : (
        <div className="space-y-4">
          {demandesBien.map((demande) => (
            <div 
              key={demande.id} 
              className="bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-bold text-gray-900 text-lg">{demande.nom}</h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      demande.statut === 'nouveau' || demande.statut === 'en_attente'
                        ? 'bg-green-100 text-green-700'
                        : demande.statut === 'en_cours'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {demande.statut === 'nouveau' || demande.statut === 'en_attente' ? '🆕 Nouveau' : 
                       demande.statut === 'en_cours' ? '🔄 En cours' : '✓ Traité'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p className="flex items-center gap-2">
                      <i className="bi bi-envelope"></i>
                      <a href={`mailto:${demande.email}`} className="text-blue-600 hover:underline">
                        {demande.email}
                      </a>
                    </p>
                    {demande.telephone && (
                      <p className="flex items-center gap-2">
                        <i className="bi bi-telephone"></i>
                        <a href={`tel:${demande.telephone}`} className="text-blue-600 hover:underline">
                          {formatPhoneFR(demande.telephone)}
                        </a>
                      </p>
                    )}
                    <p className="flex items-center gap-2">
                      <i className="bi bi-tag"></i>
                      {demande.type_demande}
                    </p>
                    <p className="flex items-center gap-2 text-xs text-gray-500">
                      <i className="bi bi-clock"></i>
                      {new Date(demande.created_at).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
              
              {demande.message && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm text-gray-700 italic bg-gray-50 p-3 rounded-lg">
                    "{demande.message}"
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
  const renderUltraModernTabContent = () => {
    const property = consultedProperty;
    const bien_general = property.bien_general || {};
    const informations_confidentielles = property.informations_confidentielles || {};
    const enseigne = property.enseigne || {};
    const composition = property.composition || {};
    const prestations = property.prestations || {};
    const descriptif = property.descriptif || {};
    const honoraires_calcul = property.honoraires_calcul || {};
    const informations_vente = property.informations_vente || {};
    const informations_bail = property.informations_bail || {};
    const medias = property.medias || {};

    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-8">
            {/* Hero card avec glassmorphism et data visualization */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-indigo-500/10 border border-white/20 backdrop-blur-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-indigo-600/10"></div>
              <div className="relative z-10 p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-2xl shadow-blue-500/25">
                        <i className="bi bi-building"></i>
                      </div>
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                        <i className="bi bi-check text-white text-xs font-bold"></i>
                      </div>
                    </div>
                    <div>
                      <h2 className="text-3xl font-semibold bg-gradient-to-r from-gray-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent">
                        {bien_general.type_bien || 'Commerce'}
                      </h2>
                      <p className="text-lg text-gray-600 font-medium">Informations principales</p>
                    </div>
                  </div>
                  
                  {/* Score visual moderne */}
                  <div className="text-right">
                    <div className="inline-flex items-center bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-2xl shadow-2xl shadow-emerald-500/25">
                      <div className="text-2xl font-semibold tabular-nums">98</div>
                      <div className="ml-2 text-sm font-semibold opacity-90">/100</div>
                    </div>
                    <div className="text-sm text-gray-600 mt-1 font-medium">Score qualité</div>
                  </div>
                </div>

                {/* Grid moderne avec micro-interactions */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Surface avec visualisation */}
                  <div className="group relative overflow-hidden bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-500/25">
                        <i className="bi bi-arrows-fullscreen text-lg"></i>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-3xl font-semibold text-gray-900 tabular-nums">
                        {enseigne.superficie || '--'} <span className="text-lg text-gray-500 font-medium">m²</span>
                      </div>
                      <div className="text-sm font-semibold text-blue-600">Superficie totale</div>
                      {enseigne.superficie && (
                        <div className="text-xs text-gray-500 bg-blue-50 px-3 py-1 rounded-full inline-block font-medium">
                          Loi Carrez certifiée
                        </div>
                      )}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Classement avec badge dynamique */}
                  <div className="group relative overflow-hidden bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:shadow-2xl hover:shadow-purple-500/10 transition-all duration-500 hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-purple-500/25">
                        <i className="bi bi-award text-lg"></i>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-xl font-bold text-gray-900">
                        {bien_general.classement || 'Standard'}
                      </div>
                      <div className="text-sm font-semibold text-purple-600">Classement</div>
                      <div className="flex items-center space-x-1">
                        {[1,2,3,4,5].map((star) => (
                          <i key={star} className={`bi bi-star-fill text-sm ${star <= 4 ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Murs en vente avec toggle visual */}
                  <div className="group relative overflow-hidden bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:shadow-2xl hover:shadow-emerald-500/10 transition-all duration-500 hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-emerald-500/25">
                        <i className="bi bi-house-door text-lg"></i>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="text-sm font-semibold text-emerald-600">Murs en vente</div>
                      <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold ${
                        bien_general.murs_vente === 'Oui' 
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/25' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          bien_general.murs_vente === 'Oui' ? 'bg-white' : 'bg-gray-400'
                        }`}></div>
                        {bien_general.murs_vente || 'Non défini'}
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Zone avec map indicator */}
                  <div className="group relative overflow-hidden bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-500 hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-orange-500/25">
                        <i className="bi bi-geo-alt text-lg"></i>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm font-semibold text-orange-600">Zone d'attractivité</div>
                      <div className="text-lg font-bold text-gray-900">
                        {bien_general.zone_attractivite || 'À définir'}
                      </div>
                      <div className="text-xs text-gray-500 font-medium">
                        Secteur: {bien_general.secteur || 'Non spécifié'}
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Composition avec data viz moderne */}
            {(composition.nombre_salles || composition.places_assises_interieur || composition.places_assises_terrasse) && (
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-rose-500/10 border border-white/20 backdrop-blur-sm">
                <div className="relative z-10 p-8">
                  <div className="flex items-center space-x-4 mb-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center text-white text-xl shadow-2xl shadow-purple-500/25">
                      <i className="bi bi-diagram-3"></i>
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold bg-gradient-to-r from-purple-900 to-pink-900 bg-clip-text text-transparent">
                        Composition & Agencement
                      </h3>
                      <p className="text-gray-600 font-medium">Répartition des espaces</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {composition.nombre_salles && (
                      <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-xl transition-all duration-300">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center text-white text-2xl font-semibold mx-auto mb-4 shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                            {composition.nombre_salles}
                          </div>
                          <div className="text-sm font-bold text-purple-600 uppercase tracking-wide">Salles</div>
                        </div>
                        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-purple-500 to-pink-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                      </div>
                    )}

                    {composition.places_assises_interieur && (
                      <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-xl transition-all duration-300">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-2xl font-semibold mx-auto mb-4 shadow-lg shadow-blue-500/25 group-hover:scale-110 transition-transform duration-300">
                            {composition.places_assises_interieur}
                          </div>
                          <div className="text-sm font-bold text-blue-600 uppercase tracking-wide">Places intérieur</div>
                        </div>
                        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                      </div>
                    )}

                    {composition.places_assises_terrasse && (
                      <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-xl transition-all duration-300">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center text-white text-2xl font-semibold mx-auto mb-4 shadow-lg shadow-emerald-500/25 group-hover:scale-110 transition-transform duration-300">
                            {composition.places_assises_terrasse}
                          </div>
                          <div className="text-sm font-bold text-emerald-600 uppercase tracking-wide">Places terrasse</div>
                        </div>
                        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                      </div>
                    )}
                  </div>

                  {composition.vitrine && (
                    <div className="mt-6 bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-amber-500/25">
                          <i className="bi bi-shop"></i>
                        </div>
                        <div>
                          <div className="text-sm font-bold text-amber-600 uppercase tracking-wide">Vitrine</div>
                          <div className="text-gray-900 font-semibold">{composition.vitrine}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case 'prestations':
        return (
          <div className="space-y-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-500/5 via-violet-500/5 to-fuchsia-500/10 border border-white/20 backdrop-blur-sm">
              <div className="relative z-10 p-8">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-fuchsia-600 rounded-2xl flex items-center justify-center text-white text-2xl shadow-2xl shadow-purple-500/25">
                    <i className="bi bi-stars"></i>
                  </div>
                  <div>
                    <h2 className="text-3xl font-semibold bg-gradient-to-r from-purple-900 via-violet-900 to-fuchsia-900 bg-clip-text text-transparent">
                      Prestations & Équipements
                    </h2>
                    <p className="text-gray-600 font-medium">Services et commodités inclus</p>
                  </div>
                </div>
                
                {prestations && Object.keys(prestations).some(key => prestations[key]) ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(prestations).map(([key, value]) => 
                      value ? (
                        <div key={key} className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-2xl hover:shadow-purple-500/10 transition-all duration-500 hover:-translate-y-1">
                          <div className="flex items-center space-x-4">
                            <div className="relative">
                              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                                <i className="bi bi-check-circle-fill text-lg"></i>
                              </div>
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                                <i className="bi bi-check text-white text-xs font-bold"></i>
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="font-bold text-gray-900 group-hover:text-purple-900 transition-colors duration-300">
                                {key.replace(/_/g, ' ')}
                              </div>
                              <div className="text-sm text-gray-600 font-medium">Inclus</div>
                            </div>
                          </div>
                          <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-purple-500 to-fuchsia-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                        </div>
                      ) : null
                    )}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="relative mx-auto w-24 h-24 mb-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center">
                        <i className="bi bi-star text-gray-400 text-4xl"></i>
                      </div>
                      <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500/20 to-fuchsia-500/20 animate-pulse"></div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Aucune prestation renseignée</h3>
                    <p className="text-gray-600 max-w-md mx-auto">Les équipements et services n'ont pas encore été détaillés pour ce bien.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'descriptif':
        return (
          <div className="space-y-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500/5 via-teal-500/5 to-cyan-500/10 border border-white/20 backdrop-blur-sm">
              <div className="relative z-10 p-8">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-cyan-600 rounded-2xl flex items-center justify-center text-white text-2xl shadow-2xl shadow-emerald-500/25">
                    <i className="bi bi-file-text"></i>
                  </div>
                  <div>
                    <h2 className="text-3xl font-semibold bg-gradient-to-r from-emerald-900 via-teal-900 to-cyan-900 bg-clip-text text-transparent">
                      Description & Marketing
                    </h2>
                    <p className="text-gray-600 font-medium">Textes promotionnels et descriptions</p>
                  </div>
                </div>

                {descriptif && Object.keys(descriptif).some(key => descriptif[key]) ? (
                  <div className="space-y-6">
                    {Object.entries(descriptif).map(([key, value]) => 
                      value ? (
                        <div key={key} className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-2xl hover:shadow-emerald-500/10 transition-all duration-500">
                          <div className="flex items-start space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-emerald-500/25 group-hover:scale-110 transition-transform duration-300 flex-shrink-0 mt-1">
                              <i className="bi bi-chat-quote-fill text-lg"></i>
                            </div>
                            <div className="flex-1">
                              <div className="font-bold text-emerald-900 text-lg mb-3 group-hover:text-emerald-700 transition-colors duration-300">
                                {key.replace(/_/g, ' ').toUpperCase()}
                              </div>
                              <div className="text-gray-700 leading-relaxed whitespace-pre-wrap bg-emerald-50/50 p-4 rounded-xl">
                                {value}
                              </div>
                            </div>
                          </div>
                          <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-emerald-500 to-cyan-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                        </div>
                      ) : null
                    )}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="relative mx-auto w-24 h-24 mb-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center">
                        <i className="bi bi-file-text text-gray-400 text-4xl"></i>
                      </div>
                      <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 animate-pulse"></div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Aucune description renseignée</h3>
                    <p className="text-gray-600 max-w-md mx-auto">Les textes de description n'ont pas encore été rédigés pour ce bien.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'honoraires':
        return (
          <div className="space-y-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-500/5 via-orange-500/5 to-red-500/10 border border-white/20 backdrop-blur-sm">
              <div className="relative z-10 p-8">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-red-600 rounded-2xl flex items-center justify-center text-white text-2xl shadow-2xl shadow-amber-500/25">
                    <i className="bi bi-calculator"></i>
                  </div>
                  <div>
                    <h2 className="text-3xl font-semibold bg-gradient-to-r from-amber-900 via-orange-900 to-red-900 bg-clip-text text-transparent">
                      Honoraires & Calculs
                    </h2>
                    <p className="text-gray-600 font-medium">Tarification et commissions</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Prix de vente */}
                  <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-500 hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-amber-500/25">
                        <i className="bi bi-currency-euro"></i>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-3xl font-semibold text-gray-900 tabular-nums">
                        {honoraires_calcul.prix_vente ? `${honoraires_calcul.prix_vente.toLocaleString()} €` : '--'}
                      </div>
                      <div className="text-sm font-semibold text-amber-600">Prix de vente</div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-orange-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Pourcentage */}
                  <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-500 hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-orange-500/25">
                        <i className="bi bi-percent"></i>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-3xl font-semibold text-gray-900 tabular-nums">
                        {honoraires_calcul.pourcentage || '--'}%
                      </div>
                      <div className="text-sm font-semibold text-orange-600">Commission</div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Honoraires */}
                  <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-2xl hover:shadow-red-500/10 transition-all duration-500 hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-red-500/25">
                        <i className="bi bi-cash-stack"></i>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-3xl font-semibold text-gray-900 tabular-nums">
                        {honoraires_calcul.honoraires ? `${honoraires_calcul.honoraires.toLocaleString()} €` : '--'}
                      </div>
                      <div className="text-sm font-semibold text-red-600">Honoraires</div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-rose-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Net vendeur */}
                  <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-2xl hover:shadow-emerald-500/10 transition-all duration-500 hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-emerald-500/25">
                        <i className="bi bi-wallet2"></i>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-3xl font-semibold text-gray-900 tabular-nums">
                        {honoraires_calcul.net_vendeur ? `${honoraires_calcul.net_vendeur.toLocaleString()} €` : '--'}
                      </div>
                      <div className="text-sm font-semibold text-emerald-600">Net vendeur</div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'medias':
        return (
          <div className="space-y-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/10 border border-white/20 backdrop-blur-sm">
              <div className="relative z-10 p-8">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-pink-600 rounded-2xl flex items-center justify-center text-white text-2xl shadow-2xl shadow-indigo-500/25">
                    <i className="bi bi-images"></i>
                  </div>
                  <div>
                    <h2 className="text-3xl font-semibold bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 bg-clip-text text-transparent">
                      Médias & Photos
                    </h2>
                    <p className="text-gray-600 font-medium">Galerie visuelle du bien</p>
                  </div>
                </div>

                {medias && medias.photos && medias.photos.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {medias.photos.map((photo, index) => (
                      <div key={index} className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl border border-white/40 hover:shadow-2xl hover:shadow-purple-500/10 transition-all duration-500 hover:-translate-y-1">
                        <div className="aspect-w-16 aspect-h-12">
                          <img
                            src={photo}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="absolute bottom-4 left-4 right-4">
                            <div className="text-white font-bold text-sm">Photo {index + 1}</div>
                          </div>
                        </div>
                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                            <i className="bi bi-zoom-in text-white text-sm"></i>
                          </div>
                        </div>
                        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-indigo-500 to-pink-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="relative mx-auto w-24 h-24 mb-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center">
                        <i className="bi bi-images text-gray-400 text-4xl"></i>
                      </div>
                      <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-indigo-500/20 to-pink-500/20 animate-pulse"></div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Aucune photo ajoutée</h3>
                    <p className="text-gray-600 max-w-md mx-auto">La galerie photos n'a pas encore été configurée pour ce bien.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'vente':
        return (
          <div className="space-y-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-green-500/5 via-emerald-500/5 to-teal-500/10 border border-white/20 backdrop-blur-sm">
              <div className="relative z-10 p-8">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl flex items-center justify-center text-white text-2xl shadow-2xl shadow-green-500/25">
                    <i className="bi bi-shop"></i>
                  </div>
                  <div>
                    <h2 className="text-3xl font-semibold bg-gradient-to-r from-green-900 via-emerald-900 to-teal-900 bg-clip-text text-transparent">
                      Informations de Vente
                    </h2>
                    <p className="text-gray-600 font-medium">Prix et conditions commerciales</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Prix annoncé */}
                  <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-2xl hover:shadow-green-500/10 transition-all duration-500 hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-green-500/25">
                        <i className="bi bi-tag-fill"></i>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm font-semibold text-green-600 uppercase tracking-wide">Prix annoncé</div>
                      <div className="text-3xl font-semibold text-gray-900 tabular-nums">
                        {informations_vente.prix_annonce ? `${informations_vente.prix_annonce.toLocaleString()} €` : '--'}
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Loyer mensuel */}
                  <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-2xl hover:shadow-teal-500/10 transition-all duration-500 hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-teal-500/25">
                        <i className="bi bi-calendar-month"></i>  
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm font-semibold text-teal-600 uppercase tracking-wide">Loyer mensuel</div>
                      <div className="text-3xl font-semibold text-gray-900 tabular-nums">
                        {informations_bail.loyer_mois_charges_taxes ? `${informations_bail.loyer_mois_charges_taxes.toLocaleString()} €` : '--'}
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 to-cyan-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'composition':
        return (
          <div className="space-y-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-500/5 via-purple-500/5 to-fuchsia-500/10 border border-white/20 backdrop-blur-sm">
              <div className="relative z-10 p-8">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-2xl flex items-center justify-center text-white text-2xl shadow-2xl shadow-violet-500/25">
                    <i className="bi bi-layout-three-columns"></i>
                  </div>
                  <div>
                    <h2 className="text-3xl font-semibold bg-gradient-to-r from-violet-900 via-purple-900 to-fuchsia-900 bg-clip-text text-transparent">
                      Composition & Agencement
                    </h2>
                    <p className="text-gray-600 font-medium">Distribution et organisation des espaces</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Nombre de salles */}
                  {composition.nombre_salles && (
                    <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-500 hover:-translate-y-1">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl flex items-center justify-center text-white text-2xl font-semibold mx-auto mb-4 shadow-lg shadow-violet-500/25 group-hover:scale-110 transition-transform duration-300">
                          {composition.nombre_salles}
                        </div>
                        <div className="text-sm font-bold text-violet-600 uppercase tracking-wide">Salles</div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-violet-500 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                    </div>
                  )}

                  {/* Places intérieur */}
                  {composition.places_assises_interieur && (
                    <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-2xl hover:shadow-purple-500/10 transition-all duration-500 hover:-translate-y-1">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center text-white text-2xl font-semibold mx-auto mb-4 shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                          {composition.places_assises_interieur}
                        </div>
                        <div className="text-sm font-bold text-purple-600 uppercase tracking-wide">Places intérieur</div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-purple-500 to-fuchsia-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                    </div>
                  )}

                  {/* Places terrasse */}
                  {composition.places_assises_terrasse && (
                    <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-2xl hover:shadow-fuchsia-500/10 transition-all duration-500 hover:-translate-y-1">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-fuchsia-500 to-fuchsia-600 rounded-2xl flex items-center justify-center text-white text-2xl font-semibold mx-auto mb-4 shadow-lg shadow-fuchsia-500/25 group-hover:scale-110 transition-transform duration-300">
                          {composition.places_assises_terrasse}
                        </div>
                        <div className="text-sm font-bold text-fuchsia-600 uppercase tracking-wide">Places terrasse</div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-fuchsia-500 to-pink-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                    </div>
                  )}

                  {/* Vitrine */}
                  {composition.vitrine && (
                    <div className="md:col-span-2 lg:col-span-3 group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/40 hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-500">
                      <div className="flex items-center space-x-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-500/25 group-hover:scale-110 transition-transform duration-300">
                          <i className="bi bi-shop-window text-xl"></i>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-bold text-indigo-600 uppercase tracking-wide mb-2">Vitrine</div>
                          <div className="text-lg font-bold text-gray-900">{composition.vitrine}</div>
                        </div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                    </div>
                  )}
                </div>

                {/* Message si pas de composition */}
                {!composition.nombre_salles && !composition.places_assises_interieur && !composition.places_assises_terrasse && !composition.vitrine && (
                  <div className="text-center py-16">
                    <div className="relative mx-auto w-24 h-24 mb-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center">
                        <i className="bi bi-layout-three-columns text-gray-400 text-4xl"></i>
                      </div>
                      <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 animate-pulse"></div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Aucune composition renseignée</h3>
                    <p className="text-gray-600 max-w-md mx-auto">Les détails de composition n'ont pas encore été configurés pour ce bien.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return renderModernTabContent();
    }
  };

  // Render modern tab content (Bootstrap style)
  const renderModernTabContent = () => {
    const property = consultedProperty;
    const bien_general = property.bien_general || {};
    const informations_confidentielles = property.informations_confidentielles || {};
    const enseigne = property.enseigne || {};
    const composition = property.composition || {};
    const prestations = property.prestations || {};
    const descriptif = property.descriptif || {};
    const honoraires_calcul = property.honoraires_calcul || {};
    const informations_vente = property.informations_vente || {};
    const informations_bail = property.informations_bail || {};
    const medias = property.medias || {};

    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            {/* Informations principales */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <h3 className="text-xl font-semibold text-blue-900 mb-4 flex items-center">
                <i className="bi bi-info-circle-fill mr-3 text-blue-600"></i>
                Informations principales
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Type de bien</label>
                    <div className="bg-white p-3 rounded-lg border border-gray-200 text-gray-800 font-medium">
                      {bien_general.type_bien || 'Non spécifié'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Classement</label>
                    <div className="bg-white p-3 rounded-lg border border-gray-200 text-gray-800">
                      {bien_general.classement || 'Non spécifié'}
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Superficie</label>
                    <div className="bg-white p-3 rounded-lg border border-gray-200 text-gray-800 flex items-center">
                      <span className="font-semibold text-blue-600 text-lg">
                        {enseigne.superficie ? formatSurfaceFR(enseigne.superficie) : 'N/A'}
                      </span>
                      {enseigne.superficie && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          Loi Carrez
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Murs en vente</label>
                    <div className="bg-white p-3 rounded-lg border border-gray-200 text-gray-800">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        bien_general.murs_vente === 'Oui' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {bien_general.murs_vente || 'Non spécifié'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Secteur</label>
                    <div className="bg-white p-3 rounded-lg border border-gray-200 text-gray-800">
                      {bien_general.secteur || 'Non spécifié'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Zone d'attractivité</label>
                    <div className="bg-white p-3 rounded-lg border border-gray-200 text-gray-800">
                      {bien_general.zone_attractivite || 'Non spécifiée'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Composition */}
            {(composition.nombre_salles || composition.places_assises_interieur || composition.places_assises_terrasse) && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-xl font-semibold text-purple-900 mb-4 flex items-center">
                  <i className="bi bi-layout-text-window-reverse mr-3 text-purple-600"></i>
                  Composition
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {composition.nombre_salles && (
                    <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
                      <div className="text-2xl font-bold text-purple-600 mb-1">{composition.nombre_salles}</div>
                      <div className="text-sm text-gray-600">Salles</div>
                    </div>
                  )}
                  {composition.places_assises_interieur && (
                    <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
                      <div className="text-2xl font-bold text-purple-600 mb-1">{composition.places_assises_interieur}</div>
                      <div className="text-sm text-gray-600">Places intérieur</div>
                    </div>
                  )}
                  {composition.places_assises_terrasse && (
                    <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
                      <div className="text-2xl font-bold text-purple-600 mb-1">{composition.places_assises_terrasse}</div>
                      <div className="text-sm text-gray-600">Places terrasse</div>
                    </div>
                  )}
                </div>
                {composition.vitrine && (
                  <div className="mt-4 bg-white p-4 rounded-lg border border-gray-200">
                    <span className="text-sm font-semibold text-gray-700">Vitrine: </span>
                    <span className="text-gray-800">{composition.vitrine}</span>
                  </div>
                )}
              </div>
            )}

            {/* Enseigne */}
            {enseigne.enseigne && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-xl font-semibold text-green-900 mb-4 flex items-center">
                  <i className="bi bi-shop mr-3 text-green-600"></i>
                  Enseigne
                </h3>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="text-lg font-semibold text-green-800">{enseigne.enseigne}</div>
                </div>
              </div>
            )}
          </div>
        );

      case 'prestations':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-200">
              <h3 className="text-xl font-semibold text-purple-900 mb-6 flex items-center">
                <i className="bi bi-star-fill mr-3 text-purple-600"></i>
                Prestations et équipements
              </h3>
              
              {prestations && Object.keys(prestations).some(key => prestations[key]) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(prestations).map(([key, value]) => 
                    value ? (
                      <div key={key} className="bg-white p-4 rounded-lg border border-gray-200 flex items-center space-x-3 hover:shadow-md transition-shadow">
                        <div className="bg-purple-100 p-2 rounded-lg">
                          <i className="bi bi-check-circle-fill text-purple-600"></i>
                        </div>
                        <span className="text-gray-800 font-medium">{key.replace(/_/g, ' ')}</span>
                      </div>
                    ) : null
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <i className="bi bi-star text-gray-400 text-2xl"></i>
                  </div>
                  <p className="text-gray-500">Aucune prestation renseignée</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'descriptif':
        return (
          <div className="space-y-6">
            {descriptif.descriptions && Object.entries(descriptif.descriptions).map(([type, langues]) => (
              <div key={type} className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-xl font-semibold text-green-900 mb-4 flex items-center">
                  <i className="bi bi-text-paragraph mr-3 text-green-600"></i>
                  {type}
                </h3>
                {Object.entries(langues).map(([langue, contenu]) => (
                  <div key={langue} className="mb-4">
                    <div className="flex items-center mb-2">
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        {langue}
                      </span>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="text-gray-800 whitespace-pre-wrap">
                        {contenu.content || 'Aucun contenu disponible'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
            
            {descriptif.proximite && (
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-xl font-semibold text-blue-900 mb-4 flex items-center">
                  <i className="bi bi-geo-alt-fill mr-3 text-blue-600"></i>
                  Proximité
                </h3>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="text-gray-800 whitespace-pre-wrap">
                    {descriptif.proximite.Français || 'Aucune information de proximité'}
                  </div>
                </div>
              </div>
            )}
            
            {(!descriptif.descriptions || Object.keys(descriptif.descriptions).length === 0) && !descriptif.proximite && (
              <div className="text-center py-12">
                <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <i className="bi bi-text-paragraph text-gray-400 text-3xl"></i>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun descriptif disponible</h3>
                <p className="text-gray-500">Les informations descriptives n'ont pas été renseignées pour ce bien.</p>
              </div>
            )}
          </div>
        );

      case 'confidentiel':
        return (
          <div className="space-y-6">
            {/* Avertissement */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <div className="bg-red-100 p-3 rounded-lg">
                  <i className="bi bi-shield-exclamation text-red-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-red-900 mb-2">Informations confidentielles</h3>
                  <p className="text-red-800">Ces informations sont sensibles et réservées aux utilisateurs autorisés. Leur divulgation est strictement contrôlée.</p>
                </div>
              </div>
            </div>

            {/* Informations confidentielles */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <i className="bi bi-lock-fill mr-3 text-red-600"></i>
                Localisation précise
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Ville</label>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-gray-800 font-medium">
                    {informations_confidentielles.ville || 'Non spécifiée'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Code postal</label>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-gray-800 font-medium">
                    {informations_confidentielles.code_postal || 'Non spécifié'}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse complète</label>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-gray-800">
                    {informations_confidentielles.adresse || 'Non spécifiée'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'photos':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-200">
              <h3 className="text-xl font-semibold text-orange-900 mb-6 flex items-center">
                <i className="bi bi-images mr-3 text-orange-600"></i>
                Galerie photos
                {medias.photos && (
                  <span className="ml-2 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">
                    {medias.photos.length} photo{medias.photos.length > 1 ? 's' : ''}
                  </span>
                )}
              </h3>
              
              {medias.photos && medias.photos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {medias.photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                        <img
                          src={photo.url}
                          alt={photo.description || `Photo ${index + 1}`}
                          className="w-full h-48 object-cover cursor-pointer group-hover:scale-105 transition-transform duration-300"
                          onClick={() => openPhotoGallery(medias.photos, index)}
                        />
                        <div className="p-3">
                          <div className="text-sm font-medium text-gray-900">Photo {index + 1}</div>
                          {photo.description && (
                            <div className="text-xs text-gray-500 mt-1 truncate">{photo.description}</div>
                          )}
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-xl transition-opacity flex items-center justify-center">
                        <i className="bi bi-zoom-in text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <i className="bi bi-camera text-gray-400 text-3xl"></i>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune photo disponible</h3>
                  <p className="text-gray-500">Les photos n'ont pas encore été ajoutées pour ce bien.</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'vente':
        return (
          <div className="space-y-6">
            {/* Informations financières */}
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-200">
              <h3 className="text-xl font-semibold text-emerald-900 mb-6 flex items-center">
                <i className="bi bi-currency-euro mr-3 text-emerald-600"></i>
                Informations financières
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {honoraires_calcul.prix_vente && (
                  <div className="bg-white p-6 rounded-xl border border-gray-200 text-center">
                    <div className="text-3xl font-bold text-emerald-600 mb-2">
                      {formatPriceFR(honoraires_calcul.prix_vente)}
                    </div>
                    <div className="text-sm text-gray-600">Prix de vente</div>
                  </div>
                )}
                {honoraires_calcul.honoraires && (
                  <div className="bg-white p-6 rounded-xl border border-gray-200 text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-2">
                      {formatPriceFR(honoraires_calcul.honoraires)}
                    </div>
                    <div className="text-sm text-gray-600">Honoraires</div>
                  </div>
                )}
                {honoraires_calcul.net_vendeur && (
                  <div className="bg-white p-6 rounded-xl border border-gray-200 text-center">
                    <div className="text-2xl font-bold text-purple-600 mb-2">
                      {formatPriceFR(honoraires_calcul.net_vendeur)}
                    </div>
                    <div className="text-sm text-gray-600">Net vendeur</div>
                  </div>
                )}
                {informations_vente.prix_annonce && (
                  <div className="bg-white p-6 rounded-xl border border-gray-200 text-center">
                    <div className="text-2xl font-bold text-orange-600 mb-2">
                      {formatPriceFR(informations_vente.prix_annonce)}
                    </div>
                    <div className="text-sm text-gray-600">Prix annoncé</div>
                  </div>
                )}
              </div>
            </div>

            {/* Informations bail */}
            {(informations_bail.loyer_mois_charges_taxes || informations_bail.charges_mensuelles) && (
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-xl font-semibold text-blue-900 mb-6 flex items-center">
                  <i className="bi bi-house-door mr-3 text-blue-600"></i>
                  Informations locataires
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {informations_bail.loyer_mois_charges_taxes && (
                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <div className="text-2xl font-bold text-blue-600 mb-2">
                        {formatPriceFR(informations_bail.loyer_mois_charges_taxes)}
                      </div>
                      <div className="text-sm text-gray-600">Loyer mensuel (charges incluses)</div>
                    </div>
                  )}
                  {informations_bail.charges_mensuelles && (
                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <div className="text-2xl font-bold text-gray-600 mb-2">
                        {formatPriceFR(informations_bail.charges_mensuelles)}
                      </div>
                      <div className="text-sm text-gray-600">Charges mensuelles</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case 'historique':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
              <h3 className="text-xl font-semibold text-indigo-900 mb-6 flex items-center">
                <i className="bi bi-clock-history mr-3 text-indigo-600"></i>
                Historique du bien
              </h3>
              
              <div className="text-center py-12">
                <div className="bg-indigo-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <i className="bi bi-clock-history text-indigo-600 text-3xl"></i>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Historique en cours de développement</h3>
                <p className="text-gray-500 mb-4">Cette fonctionnalité sera bientôt disponible pour consulter toutes les interactions avec ce bien.</p>
                <button
                  onClick={() => openHistorique(consultedProperty)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  Ouvrir l'historique détaillé
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="bi bi-question-circle text-gray-400 text-2xl"></i>
            </div>
            <p className="text-gray-500">Onglet non trouvé</p>
          </div>
        );
    }
  };

  const generateFicheTechniqueHTML = (property) => {
    const bien_general = property.bien_general || {};
    const location_info = property.informations_confidentielles || {};
    const enseigne = property.enseigne || {};
    const honoraires = property.honoraires_calcul || {};
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Fiche Technique - ${bien_general.type_bien || 'Bien'}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
          .header { text-align: center; border-bottom: 2px solid #007BFF; padding-bottom: 20px; margin-bottom: 30px; }
          .title { font-size: 24px; font-weight: bold; color: #007BFF; }
          .section { margin-bottom: 25px; }
          .section-title { font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 10px; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; }
          .info-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          .info-table td { padding: 8px; border: 1px solid #e5e7eb; }
          .info-table td:first-child { background-color: #f9fafb; font-weight: bold; width: 30%; }
          .price { font-size: 20px; font-weight: bold; color: #059669; text-align: center; padding: 15px; background-color: #f0f9ff; border: 2px solid #0ea5e9; margin: 20px 0; }
          @media print { body { margin: 0; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">FICHE TECHNIQUE</div>
          <div style="margin-top: 10px;">
            <strong>KOMERCES IMMOBILIER</strong><br>
            Référence: #${property.id?.slice(0,8)}<br>
            Date: ${new Date().toLocaleDateString('fr-FR')}
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">INFORMATIONS GÉNÉRALES</div>
          <table class="info-table">
            <tr><td>Type de bien</td><td>${bien_general.type_bien || 'Non spécifié'}</td></tr>
            <tr><td>Classement</td><td>${bien_general.classement || 'Non spécifié'}</td></tr>
            <tr><td>Superficie</td><td>${enseigne.superficie ? enseigne.superficie + ' m²' : 'Non spécifiée'}</td></tr>
            <tr><td>Ville</td><td>${location_info.ville || 'Non spécifiée'}</td></tr>
            <tr><td>Code postal</td><td>${location_info.code_postal || 'Non spécifié'}</td></tr>
          </table>
        </div>
        
        ${honoraires.prix_vente ? `
        <div class="price">
          Prix de vente: ${formatPriceFR(honoraires.prix_vente)}
        </div>
        ` : ''}
        
        <div class="section">
          <div class="section-title">CONTACT</div>
          <table class="info-table">
            <tr><td>Agence</td><td>KOMERCES IMMOBILIER</td></tr>
            <tr><td>Email</td><td>contact@komerces-immobilier.com</td></tr>
            <tr><td>Téléphone</td><td>+33 4 XX XX XX XX</td></tr>
          </table>
        </div>
        
        <div style="text-align: center; margin-top: 40px; font-size: 12px; color: #6b7280;">
          Document généré le ${formatDateTimeFR(new Date())} - KOMERCES IMMOBILIER
        </div>
      </body>
      </html>
    `;
  };

  // Generate HTML for Vitrine format
  const generateVitrineHTML = (property) => {
    const bien_general = property.bien_general || {};
    const location_info = property.informations_confidentielles || {};
    const enseigne = property.enseigne || {};
    const honoraires = property.honoraires_calcul || {};
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Affichage Vitrine - ${bien_general.type_bien || 'Bien'}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; }
          .vitrine-card { background: rgba(255,255,255,0.95); color: #1f2937; border-radius: 15px; padding: 30px; max-width: 800px; margin: 0 auto; box-shadow: 0 20px 40px rgba(0,0,0,0.1); }
          .vitrine-title { font-size: 36px; font-weight: bold; text-align: center; color: #007BFF; margin-bottom: 20px; }
          .vitrine-price { font-size: 48px; font-weight: bold; text-align: center; color: #059669; margin: 30px 0; text-shadow: 2px 2px 4px rgba(0,0,0,0.1); }
          .vitrine-details { display: flex; justify-content: space-around; margin: 30px 0; }
          .vitrine-detail { text-align: center; }
          .vitrine-detail-value { font-size: 24px; font-weight: bold; color: #007BFF; }
          .vitrine-detail-label { font-size: 14px; color: #6b7280; margin-top: 5px; }
          .vitrine-contact { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 2px solid #e5e7eb; }
          @media print { body { background: white !important; color: black !important; } }
        </style>
      </head>
      <body>
        <div class="vitrine-card">
          <div class="vitrine-title">
            ${bien_general.type_bien || 'COMMERCE'}
            ${location_info.ville ? ` - ${location_info.ville}` : ''}
          </div>
          
          ${honoraires.prix_vente ? `
          <div class="vitrine-price">
            ${formatPriceFR(honoraires.prix_vente)}
          </div>
          ` : ''}
          
          <div class="vitrine-details">
            ${enseigne.superficie ? `
            <div class="vitrine-detail">
              <div class="vitrine-detail-value">${formatSurfaceFR(enseigne.superficie)}</div>
              <div class="vitrine-detail-label">Surface</div>
            </div>
            ` : ''}
            
            ${location_info.ville ? `
            <div class="vitrine-detail">
              <div class="vitrine-detail-value">${location_info.ville}</div>
              <div class="vitrine-detail-label">Ville</div>
            </div>
            ` : ''}
            
            ${bien_general.type_bien ? `
            <div class="vitrine-detail">
              <div class="vitrine-detail-value">${bien_general.type_bien}</div>
              <div class="vitrine-detail-label">Type</div>
            </div>
            ` : ''}
          </div>
          
          <div class="vitrine-contact">
            <div style="font-size: 20px; font-weight: bold; margin-bottom: 10px;">KOMERCES IMMOBILIER</div>
            <div>📧 contact@komerces-immobilier.com</div>
            <div>📞 +33 4 XX XX XX XX</div>
            <div style="margin-top: 10px; font-size: 12px; color: #6b7280;">
              Réf: #${property.id?.slice(0,8)}
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Generate HTML for Web format
  const generateWebHTML = (property) => {
    const bien_general = property.bien_general || {};
    const location_info = property.informations_confidentielles || {};
    const enseigne = property.enseigne || {};
    const honoraires = property.honoraires_calcul || {};
    const descriptif = property.descriptif || {};
    
    // Get description
    let description = '';
    if (descriptif.descriptions) {
      const descriptions = descriptif.descriptions;
      const webDesc = descriptions.Web?.Français?.content ||
                     descriptions['Fiche Technique']?.Français?.content ||
                     descriptions.Pub?.Français?.content ||
                     '';
      description = webDesc;
    }
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Annonce Web - ${bien_general.type_bien || 'Bien'}</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 20px; background-color: #f8fafc; }
          .web-container { max-width: 1000px; margin: 0 auto; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,0.1); }
          .web-header { background: linear-gradient(135deg, #007BFF, #003F82); color: white; padding: 30px; text-align: center; }
          .web-title { font-size: 32px; font-weight: bold; margin-bottom: 10px; }
          .web-subtitle { font-size: 18px; opacity: 0.9; }
          .web-content { padding: 30px; }
          .web-price { font-size: 36px; font-weight: bold; color: #059669; text-align: center; margin: 20px 0; }
          .web-details { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin: 30px 0; }
          .web-detail { background: #f8fafc; padding: 20px; border-radius: 8px; text-align: center; }
          .web-detail-value { font-size: 20px; font-weight: bold; color: #007BFF; margin-bottom: 5px; }
          .web-detail-label { color: #6b7280; font-size: 14px; }
          .web-description { background: #fafafa; padding: 20px; border-radius: 8px; margin: 20px 0; line-height: 1.8; }
          .web-contact { background: #007BFF; color: white; padding: 20px; text-align: center; margin-top: 30px; border-radius: 8px; }
          @media print { body { background: white !important; } }
        </style>
      </head>
      <body>
        <div class="web-container">
          <div class="web-header">
            <div class="web-title">
              ${bien_general.type_bien || 'COMMERCE À VENDRE'}
            </div>
            <div class="web-subtitle">
              ${location_info.ville ? `${location_info.ville} ${location_info.code_postal ? '(' + location_info.code_postal + ')' : ''}` : 'Emplacement privilégié'}
            </div>
          </div>
          
          <div class="web-content">
            ${honoraires.prix_vente ? `
            <div class="web-price">
              ${formatPriceFR(honoraires.prix_vente)}
            </div>
            ` : ''}
            
            <div class="web-details">
              ${enseigne.superficie ? `
              <div class="web-detail">
                <div class="web-detail-value">${formatSurfaceFR(enseigne.superficie)}</div>
                <div class="web-detail-label">Surface</div>
              </div>
              ` : ''}
              
              ${bien_general.type_bien ? `
              <div class="web-detail">
                <div class="web-detail-value">${bien_general.type_bien}</div>
                <div class="web-detail-label">Type de bien</div>
              </div>
              ` : ''}
              
              ${bien_general.classement ? `
              <div class="web-detail">
                <div class="web-detail-value">${bien_general.classement}</div>
                <div class="web-detail-label">Classement</div>
              </div>
              ` : ''}
              
              <div class="web-detail">
                <div class="web-detail-value">#${property.id?.slice(0,8)}</div>
                <div class="web-detail-label">Référence</div>
              </div>
            </div>
            
            ${description ? `
            <div class="web-description">
              <h3 style="margin-top: 0; color: #007BFF;">Description</h3>
              <p>${description}</p>
            </div>
            ` : ''}
            
            <div class="web-contact">
              <h3 style="margin-top: 0;">Contactez-nous</h3>
              <div style="font-size: 18px; margin-bottom: 10px;"><strong>KOMERCES IMMOBILIER</strong></div>
              <div>📧 contact@komerces-immobilier.com</div>
              <div>📞 +33 4 XX XX XX XX</div>
              <div style="margin-top: 15px; font-size: 14px; opacity: 0.9;">
                Immobilier Commercial Premium
              </div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Property selection handlers
  const handlePropertySelection = (propertyId, isSelected) => {
    console.log('🔥 handlePropertySelection CALLED:', { propertyId, isSelected });
    console.log('🔥 Type de propertyId:', typeof propertyId);
    console.log('🔥 Longueur propertyId:', propertyId ? propertyId.length : 'null');
    console.log('🔥 propertyId complet:', propertyId);
    console.log('🔥 selectedProperties BEFORE:', selectedProperties);
    
    if (isSelected) {
      setSelectedProperties(prev => {
        const newSelection = [...prev, propertyId];
        console.log('🔥 selectedProperties AFTER ADD:', newSelection);
        console.log('🔥 Longueurs des IDs:', newSelection.map(id => id ? id.length : 0));
        return newSelection;
      });
    } else {
      setSelectedProperties(prev => {
        const newSelection = prev.filter(id => id !== propertyId);
        console.log('🔥 selectedProperties AFTER REMOVE:', newSelection);
        return newSelection;
      });
    }
  };

  const handleSelectAllProperties = (isSelected) => {
    if (isSelected) {
      const visiblePropertyIds = sortedProperties.map(property => property.id);
      setSelectedProperties(visiblePropertyIds);
    } else {
      setSelectedProperties([]);
    }
  };

  // Favorite properties handlers
  const toggleFavorite = (propertyId) => {
    setFavoriteProperties(prev => {
      const newFavorites = prev.includes(propertyId)
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId];
      
      // Save to localStorage
      localStorage.setItem('favoriteProperties', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  const showFavoritesView = () => {
    setShowFavorites(true);
    setShowDashboard(false);
    setShowForm(false);
    setShowConsultation(false);
    setShowContacts(false);
    setShowDocuments(false);
    setActiveSection('favorites');
  };

  // Affecter des biens à des contacts
  const affectPropertiesToContacts = async () => {
    if (selectedFavorites.length === 0 || selectedContactsForAffectation.length === 0) {
      alert('Veuillez sélectionner au moins un bien et un contact.');
      return;
    }

    try {
      console.log('🚀 Début affectation...');
      console.log('📦 Biens sélectionnés:', selectedFavorites);
      console.log('👥 Contacts sélectionnés:', selectedContactsForAffectation);
      
      let successCount = 0;
      let errorCount = 0;
      let duplicateCount = 0;
      
      // Pour chaque contact sélectionné, créer une affectation avec chaque bien sélectionné
      for (const contactId of selectedContactsForAffectation) {
        console.log(`\n👤 Traitement du contact: ${contactId}`);
        for (const propertyId of selectedFavorites) {
          try {
            console.log(`  🏠 Affectation du bien: ${propertyId}`);
            const response = await axios.post(`${API}/contacts/${contactId}/biens-presentes`, {
              bien_id: propertyId,
              statut: 'sélectionné',
              negociateur_id: userProfile?.id || 'system'
            });
            console.log(`  ✅ Succès:`, response.data);
            successCount++;
          } catch (err) {
            // Vérifier si c'est une erreur de doublon
            if (err.response?.status === 400 && err.response?.data?.detail?.includes('déjà affecté')) {
              console.log(`  ⚠️ Bien déjà affecté: ${propertyId}`);
              duplicateCount++;
            } else {
              console.error(`  ❌ Erreur pour bien ${propertyId}:`, err.response?.data || err.message);
              errorCount++;
            }
          }
        }
      }

      console.log(`\n📊 Résultat: ${successCount} succès, ${duplicateCount} doublons ignorés, ${errorCount} erreurs`);
      
      if (errorCount === 0 && duplicateCount === 0) {
        alert(`✅ ${successCount} bien(s) affecté(s) avec succès !`);
      } else if (errorCount === 0 && duplicateCount > 0) {
        alert(`✅ ${successCount} bien(s) affecté(s) avec succès !\n⚠️ ${duplicateCount} bien(s) déjà affecté(s) (ignorés)`);
      } else {
        alert(`⚠️ Affectation: ${successCount} succès, ${duplicateCount} doublons, ${errorCount} échecs`);
      }
      
      setShowAffectationModal(false);
      setSelectedContactsForAffectation([]);
      setSelectedFavorites([]);
    } catch (error) {
      console.error('❌ Erreur générale lors de l\'affectation:', error);
      alert('Erreur lors de l\'affectation des biens aux contacts.');
    }
  };

  // Contact selection handlers
  const handleContactSelection = (contactId, isSelected) => {
    if (isSelected) {
      setSelectedContacts(prev => [...prev, contactId]);
    } else {
      setSelectedContacts(prev => prev.filter(id => id !== contactId));
    }
  };

  const handleSelectAllContacts = (isSelected) => {
    if (isSelected) {
      const visibleContactIds = filteredContacts.map(contact => contact.id);
      setSelectedContacts(visibleContactIds);
    } else {
      setSelectedContacts([]);
    }
  };

  // Helper function pour wrapper le contenu avec SidebarLayout si thème Moderne
  const wrapWithLayoutIfModerne = (content) => {
    if (isModerne) {
      return (
        <SidebarLayout 
          userProfile={userProfile}
          showPropertiesView={showPropertiesView}
          showContactsView={showContactsView}
          showDemandesContactView={showDemandesContactView}
          showFavoritesView={showFavoritesView}
          favoriteProperties={favoriteProperties}
          showMarketingView={showMarketingView}
          showOutilsView={showOutilsView}
          setActiveMarketingTab={setActiveMarketingTab}
          setActiveOutilsTab={setActiveOutilsTab}
          startNewProperty={startNewProperty}
          startNewContact={startNewContact}
          showDashboardView={showDashboardView}
          showAvisDeValeurView={showAvisDeValeurView}
        >
          {content}
        </SidebarLayout>
      );
    }
    return content;
  };

  // Exposer les fonctions de navigation via ref
  React.useImperativeHandle(ref, () => ({
    showPropertiesView,
    showContactsView,
    showDemandesContactView,
    showFavoritesView,
    showMarketingView,
    showOutilsView,
    showDocumentsView,
    setActiveMarketingTab,
    setActiveOutilsTab,
    setActiveDocumentsTab,
    startNewProperty,
    startNewContact,
    showDashboardView,
    showDashboard,
    setShowDashboard,
    showAvisDeValeurView,
    showSettingsView,
    showProfileView,
    setCurrentFilter,
    userProfile,
    demandesContact,
    favoriteProperties,
    setShowContacts,
    setShowForm,
    setContactSearchFilters,
    loadProperties,
    globalSearch,
    setGlobalSearch,
    handleSearchFilterChange,
    executeSearch,
    activeDropdown,
    setActiveDropdown,
    activeSection,
    setActiveSection,
    // Nouvelles fonctions pour navigation par ID
    consultPropertyById,
    selectPropertyById,
    consultProperty,
    selectProperty
  }));

  // RENDERING LOGIC
  // Section Demandes de Contact - REFACTORISÉE
  if (activeSection === 'demandes-contact') {
    const content = (
      <>
      {/* Header standard KOMERCES - Afficher UNIQUEMENT en mode Joueur */}
      {isJoueurs && (
        <>
        <Header 
            showContactsView={showContactsView}
            showDemandesContactView={showDemandesContactView}
            showFavoritesView={showFavoritesView}
            favoriteProperties={favoriteProperties}
            showDashboardView={showDashboardView}
            showDocumentsView={showDocumentsView}
            showMarketingView={showMarketingView}
            showPropertiesView={showPropertiesView}
            showOutilsView={showOutilsView}
            setActiveMarketingTab={setActiveMarketingTab}
            setActiveOutilsTab={setActiveOutilsTab}
            setActiveDocumentsTab={setActiveDocumentsTab}
            startNewContact={startNewContact}
            startNewProperty={startNewProperty}
            setShowContacts={setShowContacts}
            setShowForm={setShowForm}
            setContactSearchFilters={setContactSearchFilters}
            loadProperties={loadProperties}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            handleSearchFilterChange={handleSearchFilterChange}
            executeSearch={executeSearch}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            userProfile={userProfile}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          />
        </>
      )}

      {/* Contenu Demandes de Contact - Composant refactorisé */}
      <div className={isModerne ? "p-6" : "min-h-screen bg-slate-50"}>
        <div className={isModerne ? "" : "max-w-7xl mx-auto p-6"}>
          <ContactRequests
            demandesContact={demandesContact}
            demandesArchivees={demandesArchivees}
            onChangeStatus={marquerDemandeTraitee}
            onAffecter={(demande) => {
              setDemandeToAffect(demande);
              setShowAffectationModal(true);
              setAffectationSearch(demande.email);
              searchContactForAffectation(demande.email);
            }}
            onDelete={supprimerDemande}
            onArchive={archiverDemande}
            onUnarchive={desarchiverDemande}
            onMarkAsProcessed={marquerDemandeTraitee}
          />
          
          {/* Modal d'affectation de contact */}
          {showAffectationModal && demandeToAffect && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Affecter le bien à un contact</h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Demande de <strong>{demandeToAffect.nom}</strong> pour <strong>{demandeToAffect.property_titre || demandeToAffect.property_reference || 'un bien non identifié'}</strong>
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setShowAffectationModal(false);
                        setDemandeToAffect(null);
                        setAffectationSearch('');
                        setAffectationResults([]);
                        setSelectedContactForAffectation(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <i className="bi bi-x-lg text-2xl"></i>
                    </button>
                  </div>
                  
                  {/* Recherche */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Rechercher un contact existant (par email ou nom)
                    </label>
                    <input
                      type="text"
                      value={affectationSearch}
                      onChange={(e) => {
                        setAffectationSearch(e.target.value);
                        searchContactForAffectation(e.target.value);
                      }}
                      placeholder="Tapez un email ou un nom..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* Résultats de recherche */}
                  {affectationResults.length > 0 ? (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">
                        {affectationResults.length} contact(s) trouvé(s)
                      </h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {affectationResults.map((contact) => (
                          <div
                            key={contact.id}
                            onClick={() => setSelectedContactForAffectation(contact)}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              selectedContactForAffectation?.id === contact.id
                                ? 'border-purple-600 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900">
                                  {contact.prenom} {contact.nom}
                                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                    {contact.type_contact}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  <i className="bi bi-envelope mr-1"></i>
                                  {contact.emails?.[0]?.email || 'Pas d\'email'}
                                </div>
                                {contact.telephones?.[0]?.numero && (
                                  <div className="text-sm text-gray-600">
                                    <i className="bi bi-telephone mr-1"></i>
                                    {contact.telephones[0].numero}
                                  </div>
                                )}
                              </div>
                              {selectedContactForAffectation?.id === contact.id && (
                                <i className="bi bi-check-circle-fill text-2xl text-purple-600"></i>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : affectationSearch.length >= 2 ? (
                    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-yellow-800">
                        <i className="bi bi-info-circle mr-2"></i>
                        Aucun contact trouvé. Un nouveau contact sera créé lors de l'affectation.
                      </p>
                    </div>
                  ) : null}
                  
                  {/* Actions */}
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => {
                        setShowAffectationModal(false);
                        setDemandeToAffect(null);
                        setAffectationSearch('');
                        setAffectationResults([]);
                        setSelectedContactForAffectation(null);
                      }}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={affecterBienAContact}
                      className="flex-1 px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
                    >
                      <i className="bi bi-person-check-fill"></i>
                      {selectedContactForAffectation 
                        ? `Affecter à ${selectedContactForAffectation.prenom} ${selectedContactForAffectation.nom}`
                        : 'Créer nouveau contact et affecter'
                      }
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </>
    );
    
    // En mode Moderne, le layout (sidebar + topbar) est géré par le parent App
    // Donc on retourne juste le contenu sans wrapper supplémentaire
    // En mode Joueur, on retourne le contenu avec son propre Header
    return content;
  }

  // PROFILE VIEW - Must be before showDashboard check
  if (showProfile) {
    return <Profile userProfile={userProfile} setUserProfile={setUserProfile} />;
  }

  // SETTINGS VIEW - Must be before showDashboard check
  if (showSettings) {
    return <Settings userProfile={userProfile} setUserProfile={setUserProfile} />;
  }

  if (showDashboard) {
    return (
      <>
      <div className="min-h-screen bg-slate-50">
        {/* Header standard KOMERCES - Afficher UNIQUEMENT en mode Joueur */}
        {isJoueurs && (
          <Header 
            showContactsView={showContactsView}
            showDemandesContactView={showDemandesContactView}
            showFavoritesView={showFavoritesView}
            favoriteProperties={favoriteProperties}
            showDashboardView={showDashboardView}
            showDocumentsView={showDocumentsView}
            showMarketingView={showMarketingView}
            showPropertiesView={showPropertiesView}
            showOutilsView={showOutilsView}
            setActiveMarketingTab={setActiveMarketingTab}
            setActiveOutilsTab={setActiveOutilsTab}
            setActiveDocumentsTab={setActiveDocumentsTab}
            startNewContact={startNewContact}
            startNewProperty={startNewProperty}
            setShowContacts={setShowContacts}
            setShowForm={setShowForm}
            setContactSearchFilters={setContactSearchFilters}
            loadProperties={loadProperties}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            handleSearchFilterChange={handleSearchFilterChange}
            executeSearch={executeSearch}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            userProfile={userProfile}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          />
        )}

        {/* Dashboard Content */}
        {renderDashboard()}
      </div>
      </>
    );
  }

  if (showContacts) {
    return (
      <>
      <div className="min-h-screen bg-slate-50">
        {/* Header standard KOMERCES - Afficher UNIQUEMENT en mode Joueur */}
        {isJoueurs && (
          <Header 
            showContactsView={showContactsView}
            showDemandesContactView={showDemandesContactView}
            showFavoritesView={showFavoritesView}
            favoriteProperties={favoriteProperties}
            showDashboardView={showDashboardView}
            showDocumentsView={showDocumentsView}
            showMarketingView={showMarketingView}
            showPropertiesView={showPropertiesView}
            showOutilsView={showOutilsView}
            setActiveMarketingTab={setActiveMarketingTab}
            setActiveOutilsTab={setActiveOutilsTab}
            setActiveDocumentsTab={setActiveDocumentsTab}
            startNewContact={startNewContact}
            startNewProperty={startNewProperty}
            setShowContacts={setShowContacts}
            setShowForm={setShowForm}
            setContactSearchFilters={setContactSearchFilters}
            loadProperties={loadProperties}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            handleSearchFilterChange={handleSearchFilterChange}
            executeSearch={executeSearch}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            userProfile={userProfile}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          />
        )}

        {/* DEMANDES DE CONTACT - Version thème Joueur (désactivée, utiliser version complète) */}
        {false && activeSection === 'demandes-contact' && (
          <div className="max-w-7xl mx-auto p-6">
            <div className="bg-white rounded-lg shadow-sm">
              {/* Header */}
              <div className="border-b border-gray-200 px-6 py-4">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <i className="bi bi-envelope-fill text-blue-600"></i>
                  Demandes de contact
                </h2>
              </div>
              
              {/* Filtres */}
              <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap gap-2 sm:gap-3">
                <button
                  onClick={() => setDemandesContactFilter('tous')}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-sm sm:text-base transition-colors whitespace-nowrap ${
                    demandesContactFilter === 'tous'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Tous ({demandesContact.length})
                </button>
                <button
                  onClick={() => setDemandesContactFilter('nouveau')}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-sm sm:text-base transition-colors whitespace-nowrap ${
                    demandesContactFilter === 'nouveau'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Nouveaux ({demandesContact.filter(d => d.statut === 'nouveau').length})
                </button>
                <button
                  onClick={() => setDemandesContactFilter('traité')}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-sm sm:text-base transition-colors whitespace-nowrap ${
                    demandesContactFilter === 'traité'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Traités ({demandesContact.filter(d => d.statut === 'traité').length})
                </button>
              </div>
              
              {/* Liste des demandes */}
              <div className="p-6">
                {demandesContact
                  .filter(d => demandesContactFilter === 'tous' || d.statut === demandesContactFilter)
                  .length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <i className="bi bi-inbox text-4xl mb-3 block"></i>
                    <p>Aucune demande de contact</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {demandesContact
                      .filter(d => demandesContactFilter === 'tous' || d.statut === demandesContactFilter)
                      .map(demande => (
                        <div
                          key={demande.id}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => {
                            setSelectedDemande(demande);
                            setShowDemandeDetail(true);
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-semibold text-gray-900">{demande.nom}</h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                  demande.statut === 'nouveau'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {demande.statut === 'nouveau' ? '🆕 Nouveau' : '✓ Traité'}
                                </span>
                                <span className="px-3 py-1 rounded-lg text-xs bg-blue-100 text-blue-700 font-medium">
                                  {demande.type_demande === 'visite' ? '🏠 Visite' : 
                                   demande.type_demande === 'question' ? '❓ Question' : 
                                   demande.type_demande === 'info' ? '📋 Infos' : '💬 Autre'}
                                </span>
                              </div>
                              
                              <p className="text-gray-600 text-sm mb-2">
                                <i className="bi bi-envelope mr-2"></i>{demande.email}
                                {demande.telephone && (
                                  <span className="ml-4">
                                    <i className="bi bi-telephone mr-2"></i>{formatPhoneFR(demande.telephone)}
                                  </span>
                                )}
                              </p>
                              
                              <p className="text-gray-700 line-clamp-2 mb-3">{demande.message}</p>
                              
                              <div className="flex items-center gap-4 text-sm">
                                <div className="bg-purple-50 px-3 py-1 rounded-lg text-purple-700 font-medium">
                                  <i className="bi bi-building mr-2"></i>
                                  {demande.bien_titre}
                                </div>
                                {demande.bien_reference && (
                                  <span className="text-gray-500">
                                    Réf: {demande.bien_reference}
                                  </span>
                                )}
                                {demande.bien_prix && (
                                  <span className="text-emerald-600 font-semibold">
                                    {demande.bien_prix.toLocaleString()} €
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="text-right text-sm text-gray-500">
                              {new Date(demande.created_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Modal détail demande */}
            {showDemandeDetail && selectedDemande && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-6">
                      <h2 className="text-2xl font-bold text-gray-900">Détail de la demande</h2>
                      <button
                        onClick={() => setShowDemandeDetail(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <i className="bi bi-x-lg text-2xl"></i>
                      </button>
                    </div>
                    
                    {/* Statut */}
                    <div className="mb-6">
                      <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                        selectedDemande.statut === 'nouveau'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {selectedDemande.statut === 'nouveau' ? '🆕 Nouveau' : '✓ Traité'}
                      </span>
                    </div>
                    
                    {/* Info contact */}
                    <div className="bg-gray-50 p-4 rounded-lg mb-6">
                      <h3 className="font-bold text-gray-900 mb-3">Informations du contact</h3>
                      <div className="space-y-2">
                        <p><strong>Nom:</strong> {selectedDemande.nom}</p>
                        <p><strong>Email:</strong> <a href={`mailto:${selectedDemande.email}`} className="text-blue-600">{selectedDemande.email}</a></p>
                        <p><strong>Téléphone:</strong> <span className="num">{selectedDemande.telephone ? formatPhoneFR(selectedDemande.telephone) : 'Non renseigné'}</span></p>
                        <p><strong>Type de demande:</strong> {selectedDemande.type_demande}</p>
                      </div>
                    </div>
                    
                    {/* Bien concerné */}
                    <div className="bg-blue-50 p-4 rounded-lg mb-6">
                      <h3 className="font-bold text-gray-900 mb-3">Bien concerné</h3>
                      <p className="font-semibold text-lg">{selectedDemande.bien_titre}</p>
                      {selectedDemande.bien_reference && (
                        <p className="text-gray-600">Réf: {selectedDemande.bien_reference}</p>
                      )}
                      {selectedDemande.bien_ville && (
                        <p className="text-gray-600">Ville: {selectedDemande.bien_ville}</p>
                      )}
                      {selectedDemande.bien_prix && (
                        <p className="text-emerald-600 font-semibold text-xl mt-2">
                          {selectedDemande.bien_prix.toLocaleString()} €
                        </p>
                      )}
                    </div>
                    
                    {/* Message */}
                    <div className="mb-6">
                      <h3 className="font-bold text-gray-900 mb-3">Message</h3>
                      <p className="text-gray-700 whitespace-pre-line bg-gray-50 p-4 rounded-lg">
                        {selectedDemande.message}
                      </p>
                    </div>
                    
                    {/* Date */}
                    <p className="text-sm text-gray-500 mb-6">
                      Reçu le {new Date(selectedDemande.created_at).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    
                    {/* Actions */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowDemandeDetail(false)}
                        className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                      >
                        Fermer
                      </button>
                      {selectedDemande.statut === 'nouveau' && (
                        <button
                          onClick={async () => {
                            await marquerDemandeTraitee(selectedDemande.id);
                            setShowDemandeDetail(false);
                          }}
                          className="flex-1 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                        >
                          <i className="bi bi-check-circle-fill"></i>
                          Marquer comme traité
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* CONTACT FORM OR VIEW OR LIST - Ne pas afficher si on est sur Demandes de contact */}
        {activeSection !== 'demandes-contact' && (showContactForm ? (
          contactViewMode === 'view' ? (
            <ContactViewMode 
              contact={currentContact}
              onEdit={() => setContactViewMode('edit')}
              onClose={handleContactFormCancel}
              onArchive={(contactId) => {
                // Recharger les contacts après archivage
                loadContacts();
              }}
              onDelete={(contactId) => {
                // Recharger les contacts après suppression
                loadContacts();
              }}
              userProfile={userProfile}
            />
          ) : (
            <ContactForm 
              contact={currentContact}
              onSave={handleContactFormSave}
              onCancel={handleContactFormCancel}
              onContactUpdate={(updatedContact) => {
                setCurrentContact(updatedContact);
                // IMPORTANT : Mettre à jour aussi la liste de contacts
                setContacts(prevContacts => 
                  prevContacts.map(c => c.id === updatedContact.id ? updatedContact : c)
                );
              }}
              userProfile={userProfile}
            />
          )
        ) : (
          /* CONTACTS LIST - NOUVEAU COMPOSANT MODERNE */
          <ContactList 
            contacts={filteredContacts}
            chargementComplet={contactsCarnetComplet}
            selectedContacts={selectedContacts}
            setSelectedContacts={setSelectedContacts}
            startNewContact={startNewContact}
            editContact={editContact}
            viewContact={viewContact}
            deleteContact={deleteContact}
            showArchivedContacts={showArchivedContacts}
            setShowArchivedContacts={setShowArchivedContacts}
            loadContacts={loadContacts}
            contactSearchFilters={contactSearchFilters}
            setContactSearchFilters={setContactSearchFilters}
            contactTypes={contactTypes}
            contactGroupes={contactGroups}
            handleContactSelection={handleContactSelection}
            handleSelectAllContacts={handleSelectAllContacts}
            bulkArchiveContacts={bulkArchiveContacts}
            bulkUnarchiveContacts={bulkUnarchiveContacts}
            bulkDeleteContacts={bulkDeleteContacts}
          />
        ))}

        {/* Fin du bloc CONTACT FORM OR LIST */}
      </div>
      </>
    );
  }

  if (showConsultation && consultedProperty) {
    return (
      <>
      <div className="min-h-screen bg-slate-50">
        {/* Header standard KOMERCES - Afficher UNIQUEMENT en mode Joueur */}
        {isJoueurs && (
          <Header 
            showContactsView={showContactsView}
            showDemandesContactView={showDemandesContactView}
            showFavoritesView={showFavoritesView}
            favoriteProperties={favoriteProperties}
            showDashboardView={showDashboardView}
            showDocumentsView={showDocumentsView}
            showMarketingView={showMarketingView}
            showPropertiesView={showPropertiesView}
            showOutilsView={showOutilsView}
            setActiveMarketingTab={setActiveMarketingTab}
            setActiveOutilsTab={setActiveOutilsTab}
            setActiveDocumentsTab={setActiveDocumentsTab}
            startNewContact={startNewContact}
            startNewProperty={startNewProperty}
            setShowContacts={setShowContacts}
            setShowForm={setShowForm}
            setContactSearchFilters={setContactSearchFilters}
            loadProperties={loadProperties}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            handleSearchFilterChange={handleSearchFilterChange}
            executeSearch={executeSearch}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            userProfile={userProfile}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          />
        )}

        {/* Consultation Content */}
        {renderConsultation()}
      </div>
      </>
    );
  }

  if (showDocuments) {
    return (
      <>
      <div className="min-h-screen bg-slate-50">
        {/* Header standard KOMERCES - Afficher UNIQUEMENT en mode Joueur */}
        {isJoueurs && (
          <Header 
            showContactsView={showContactsView}
            showDemandesContactView={showDemandesContactView}
            showFavoritesView={showFavoritesView}
            favoriteProperties={favoriteProperties}
            showDashboardView={showDashboardView}
            showDocumentsView={showDocumentsView}
            showMarketingView={showMarketingView}
            showPropertiesView={showPropertiesView}
            showOutilsView={showOutilsView}
            setActiveMarketingTab={setActiveMarketingTab}
            setActiveOutilsTab={setActiveOutilsTab}
            setActiveDocumentsTab={setActiveDocumentsTab}
            startNewContact={startNewContact}
            startNewProperty={startNewProperty}
            setShowContacts={setShowContacts}
            setShowForm={setShowForm}
            setContactSearchFilters={setContactSearchFilters}
            loadProperties={loadProperties}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            handleSearchFilterChange={handleSearchFilterChange}
            executeSearch={executeSearch}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            userProfile={userProfile}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          />
        )}

        {/* Documents Content */}
        {renderDocuments()}
      </div>
      </>
    );
  }

  if (showMarketing) {
    return (
      <>
      <div className="min-h-screen bg-slate-50">
        {/* Header standard KOMERCES - Afficher UNIQUEMENT en mode Joueur */}
        {isJoueurs && (
          <Header 
            showContactsView={showContactsView}
            showDemandesContactView={showDemandesContactView}
            showFavoritesView={showFavoritesView}
            favoriteProperties={favoriteProperties}
            showDashboardView={showDashboardView}
            showDocumentsView={showDocumentsView}
            showMarketingView={showMarketingView}
            showPropertiesView={showPropertiesView}
            showOutilsView={showOutilsView}
            setActiveMarketingTab={setActiveMarketingTab}
            setActiveOutilsTab={setActiveOutilsTab}
            setActiveDocumentsTab={setActiveDocumentsTab}
            startNewContact={startNewContact}
            startNewProperty={startNewProperty}
            setShowContacts={setShowContacts}
            setShowForm={setShowForm}
            setContactSearchFilters={setContactSearchFilters}
            loadProperties={loadProperties}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            handleSearchFilterChange={handleSearchFilterChange}
            executeSearch={executeSearch}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            userProfile={userProfile}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          />
        )}

        {/* Marketing Dashboard Content */}
        {renderMarketingDashboard()}
      </div>
      </>
    );
  }

  if (showOutils && !showAvisDeValeur) {
    return (
      <>
      <div className="min-h-screen bg-slate-50">
        {/* Header standard KOMERCES - Afficher UNIQUEMENT en mode Joueur */}
        {isJoueurs && (
          <Header 
            showContactsView={showContactsView}
            showDemandesContactView={showDemandesContactView}
            showFavoritesView={showFavoritesView}
            favoriteProperties={favoriteProperties}
            showDashboardView={showDashboardView}
            showDocumentsView={showDocumentsView}
            showMarketingView={showMarketingView}
            showPropertiesView={showPropertiesView}
            showOutilsView={showOutilsView}
            setActiveMarketingTab={setActiveMarketingTab}
            setActiveOutilsTab={setActiveOutilsTab}
            setActiveDocumentsTab={setActiveDocumentsTab}
            startNewContact={startNewContact}
            startNewProperty={startNewProperty}
            setShowContacts={setShowContacts}
            setShowForm={setShowForm}
            setContactSearchFilters={setContactSearchFilters}
            loadProperties={loadProperties}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            handleSearchFilterChange={handleSearchFilterChange}
            executeSearch={executeSearch}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            userProfile={userProfile}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          />
        )}

        {/* Outils Dashboard Content */}
        {renderOutilsDashboard()}
      </div>
      </>
    );
  }

  // AVIS DE VALEUR VIEW
  if (showAvisDeValeur) {
    return (
      <>
      <div className="min-h-screen bg-slate-50">
        {/* Header standard KOMERCES - Afficher UNIQUEMENT en mode Joueur */}
        {isJoueurs && (
          <Header 
            showContactsView={showContactsView}
            showDemandesContactView={showDemandesContactView}
            showFavoritesView={showFavoritesView}
            favoriteProperties={favoriteProperties}
            showDashboardView={showDashboardView}
            showDocumentsView={showDocumentsView}
            showMarketingView={showMarketingView}
            showPropertiesView={showPropertiesView}
            showOutilsView={showOutilsView}
            setActiveMarketingTab={setActiveMarketingTab}
            setActiveOutilsTab={setActiveOutilsTab}
            setActiveDocumentsTab={setActiveDocumentsTab}
            startNewContact={startNewContact}
            startNewProperty={startNewProperty}
            setShowContacts={setShowContacts}
            setShowForm={setShowForm}
            setContactSearchFilters={setContactSearchFilters}
            loadProperties={loadProperties}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            handleSearchFilterChange={handleSearchFilterChange}
            executeSearch={executeSearch}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            userProfile={userProfile}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          />
        )}

        {/* Avis de Valeur Component */}
        <AvisDeValeur />
      </div>
      </>
    );
  }

  // FAVORITES VIEW
  if (showFavorites) {
    // Filter favorites from dedicated favorites data
    const favoritePropertiesList = favoritePropertiesData.filter(p => favoriteProperties.includes(p.id));
    
    // Debug log to see what data we have
    if (favoritePropertiesList.length > 0) {
      console.log('🔍 Premier bien favori:', favoritePropertiesList[0]);
      console.log('  - bien_general:', favoritePropertiesList[0].bien_general);
      console.log('  - descriptif:', favoritePropertiesList[0].descriptif);
      console.log('  - status:', favoritePropertiesList[0].status);
      console.log('  - type_bien:', favoritePropertiesList[0].type_bien);
      console.log('  - numero_mandat:', favoritePropertiesList[0].numero_mandat);
    }
    
    // VERSION JOUEUR (utilisée pour tous les thèmes pour l'instant)
    return (
      <>
      <div className="min-h-screen bg-slate-50">
        {/* Header standard KOMERCES - Afficher UNIQUEMENT en mode Joueur */}
        {isJoueurs && (
          <Header 
            showContactsView={showContactsView}
            showDemandesContactView={showDemandesContactView}
            showFavoritesView={showFavoritesView}
            favoriteProperties={favoriteProperties}
            showDashboardView={showDashboardView}
            showDocumentsView={showDocumentsView}
            showMarketingView={showMarketingView}
            showPropertiesView={showPropertiesView}
            showOutilsView={showOutilsView}
            setActiveMarketingTab={setActiveMarketingTab}
            setActiveOutilsTab={setActiveOutilsTab}
            setActiveDocumentsTab={setActiveDocumentsTab}
            startNewContact={startNewContact}
            startNewProperty={startNewProperty}
            setShowContacts={setShowContacts}
            setShowForm={setShowForm}
            setContactSearchFilters={setContactSearchFilters}
            loadProperties={loadProperties}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            handleSearchFilterChange={handleSearchFilterChange}
            executeSearch={executeSearch}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            userProfile={userProfile}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          />
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20">
            
            {/* Header Favoris */}
            <div className="bg-gradient-to-r from-pink-100/80 via-rose-100/60 to-pink-100/80 backdrop-blur-sm text-gray-800 p-6 rounded-t-2xl border-b border-pink-200/30">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-red-600 rounded-xl flex items-center justify-center shadow-xl">
                    <i className="bi bi-heart-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">Mes Favoris</h2>
                    <p className="text-gray-600 text-sm">Biens sélectionnés pour un suivi rapide</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <button 
                    onClick={() => {
                      setShowFavorites(false);
                      setShowDashboard(false);
                      setShowForm(false);
                      setActiveSection('properties');
                    }}
                    className="bg-gradient-to-r from-slate-400 to-gray-500 hover:from-slate-500 hover:to-gray-600 text-white px-4 py-3 rounded-xl transition-all duration-200 border border-gray-300/20 flex items-center space-x-2 shadow-md hover:shadow-lg"
                  >
                    <i className="bi bi-arrow-left"></i>
                    <span className="hidden sm:inline">Retour aux biens</span>
                  </button>
                  
                  {/* Status indicator */}
                  <div className="hidden lg:flex items-center space-x-2 text-gray-600">
                    <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium">{favoritePropertiesList.length} favori(s)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Barre d'actions groupées */}
            {favoritePropertiesList.length > 0 && (
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-6 py-4 flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
                <div className="flex items-center space-x-3">
                  <div className="text-sm font-medium text-gray-700 bg-white px-4 py-2 rounded-lg border-2 border-gray-200">
                    <i className="bi bi-check2-square text-pink-500 mr-2"></i>
                    <span>{selectedFavorites.length} bien(s) sélectionné(s)</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button 
                    className={`px-4 py-2 rounded-lg text-sm font-medium shadow-md transition-all ${
                      selectedFavorites.length > 0 
                        ? 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white hover:shadow-lg cursor-pointer' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-40'
                    }`}
                    disabled={selectedFavorites.length === 0}
                    onClick={() => {
                      if (selectedFavorites.length > 0) {
                        setShowAffectationModal(true);
                      }
                    }}
                  >
                    <i className="bi bi-people mr-2"></i>
                    Affecter à des acquéreurs
                  </button>
                  <button 
                    className={`px-4 py-2 rounded-lg text-sm font-medium shadow-md transition-all ${
                      selectedFavorites.length > 0 
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white hover:shadow-lg cursor-pointer' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-40'
                    }`}
                    disabled={selectedFavorites.length === 0}
                    onClick={() => {
                      if (selectedFavorites.length > 0) {
                        alert(`Imprimer ${selectedFavorites.length} bien(s)`);
                        // TODO: Générer PDF et imprimer
                      }
                    }}
                  >
                    <i className="bi bi-printer mr-2"></i>
                    Imprimer
                  </button>
                  <button 
                    className={`px-4 py-2 rounded-lg text-sm font-medium shadow-md transition-all ${
                      selectedFavorites.length > 0 
                        ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white hover:shadow-lg cursor-pointer' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-40'
                    }`}
                    disabled={selectedFavorites.length === 0}
                    onClick={async (e) => {
                      console.log('🖱️ Clic sur Partager');
                      console.log('Biens sélectionnés:', selectedFavorites.length);
                      console.log('IDs:', selectedFavorites);
                      
                      if (selectedFavorites.length > 0) {
                        setShareLoading(true);
                        console.log('⏳ Création du lien de partage...');
                        
                        try {
                          const payload = {
                            property_ids: selectedFavorites,
                            title: `Collection de ${selectedFavorites.length} bien(s)`,
                            created_by: userProfile?.id || 'system'
                          };
                          console.log('📤 Envoi:', payload);
                          
                          const response = await axios.post(`${API}/shared-collections`, payload);
                          console.log('📥 Réponse:', response.data);
                          
                          const fullUrl = `${window.location.origin}/shared/${response.data.share_token}`;
                          setShareLink(fullUrl);
                          setShowShareModal(true);
                          console.log('✅ Lien de partage créé:', fullUrl);
                        } catch (error) {
                          console.error('❌ Erreur création lien:', error);
                          alert('❌ Erreur lors de la création du lien de partage: ' + (error.response?.data?.detail || error.message));
                        } finally {
                          setShareLoading(false);
                        }
                      } else {
                        console.log('⚠️ Aucun bien sélectionné');
                      }
                    }}
                  >
                    <i className={`bi ${shareLoading ? 'bi-hourglass-split' : 'bi-share'} mr-2`}></i>
                    {shareLoading ? 'Génération...' : 'Partager'}
                  </button>
                  <button 
                    className={`px-4 py-2 rounded-lg text-sm font-medium shadow-md transition-all ${
                      selectedFavorites.length > 0 
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white hover:shadow-lg cursor-pointer' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-40'
                    }`}
                    disabled={selectedFavorites.length === 0}
                    onClick={() => {
                      if (selectedFavorites.length > 0) {
                        alert(`Envoyer ${selectedFavorites.length} bien(s) par email`);
                        // TODO: Ouvrir modal d'envoi email
                      }
                    }}
                  >
                    <i className="bi bi-envelope mr-2"></i>
                    Envoyer par email
                  </button>
                </div>
              </div>
            )}

            {/* Liste des favoris */}
            <div className="p-6">
              {favoritePropertiesList.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-24 h-24 bg-gradient-to-br from-pink-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="bi bi-heart text-5xl text-pink-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">Aucun favori</h3>
                  <p className="text-gray-600 mb-6">Ajoutez des biens à vos favoris pour les retrouver facilement</p>
                  <button
                    onClick={() => {
                      setShowFavorites(false);
                      setShowDashboard(false);
                      setShowForm(false);
                      setActiveSection('properties');
                    }}
                    className="bg-gradient-to-r from-pink-500 to-red-600 hover:from-pink-600 hover:to-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    <i className="bi bi-search mr-2"></i>
                    Parcourir les biens
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {favoritePropertiesList.map((property) => (
                    <div 
                      key={property.id} 
                      className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 overflow-hidden group cursor-pointer hover:scale-[1.02]"
                      onClick={() => {
                        setCurrentProperty(property);
                        setShowConsultation(true);
                        setConsultedProperty(property);
                        setShowFavorites(false);
                        setShowDashboard(false);
                        setShowForm(false);
                      }}
                    >
                      {/* Image */}
                      <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200">
                        {(() => {
                          const photos = property.medias?.photos || property.photos || [];
                          const firstPhoto = photos.length > 0 ? (photos[0].url || photos[0]) : null;
                          return firstPhoto ? (
                            <img 
                              src={firstPhoto} 
                              alt={property.bien_general?.titre || property.descriptif?.titre_annonce || 'Photo du bien'}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <i className="bi bi-image text-6xl text-gray-400"></i>
                            </div>
                          );
                        })()}
                        
                        {/* Checkbox for selection */}
                        <div 
                          className="absolute top-3 left-3 z-10 flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selectedFavorites.includes(property.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (e.target.checked) {
                                setSelectedFavorites(prev => [...prev, property.id]);
                              } else {
                                setSelectedFavorites(prev => prev.filter(id => id !== property.id));
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-5 h-5 rounded-lg focus:ring-pink-500 text-pink-600 border-2 border-white shadow-lg cursor-pointer"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(property.id);
                            }}
                            className="w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110"
                            title="Retirer des favoris"
                          >
                            <i className="bi bi-heart-fill text-red-500 text-base"></i>
                          </button>
                        </div>

                        {/* Badge statut */}
                        <div className="absolute top-3 right-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            property.status === 'active' ? 'bg-emerald-500 text-white' :
                            property.status === 'draft' ? 'bg-gray-500 text-white' :
                            property.status === 'archived' ? 'bg-amber-500 text-white' :
                            'bg-blue-600 text-white'
                          }`}>
                            {property.status === 'active' ? 'Actif' :
                             property.status === 'draft' ? 'Brouillon' :
                             property.status === 'archived' ? 'Archivé' : property.status}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-4">
                        {/* Title */}
                        <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">
                          {property.descriptif?.descriptions?.titre || 
                           property.enseigne?.enseigne || 
                           property.bien_general?.type_bien || 
                           'Sans titre'}
                        </h3>
                        
                        <div className="space-y-2 mb-3">
                          {/* Prix */}
                          {(() => {
                            const prix = property.honoraires_calcul?.prix_vente || 
                                       property.bien_general?.prix_estime || 
                                       property.bien_general?.prix_vente;
                            return prix ? (
                              <div className="flex items-center text-emerald-600 font-bold text-xl">
                                <i className="bi bi-currency-euro mr-2"></i>
                                {prix.toLocaleString()} €
                              </div>
                            ) : null;
                          })()}
                          
                          {/* Ville */}
                          {(() => {
                            const ville = property.localisation?.ville || property.ville;
                            return ville ? (
                              <div className="flex items-center text-gray-600 text-sm">
                                <i className="bi bi-geo-alt-fill mr-2 text-purple-500"></i>
                                {ville}
                              </div>
                            ) : null;
                          })()}

                          {/* Référence */}
                          {property.numero_mandat && (
                            <div className="flex items-center text-gray-600 text-sm">
                              <i className="bi bi-tag-fill mr-2 text-blue-500"></i>
                              {property.numero_mandat}
                            </div>
                          )}
                          
                          {/* Type et activité */}
                          {property.bien_general?.type_bien && (
                            <div className="flex items-center text-gray-600 text-sm">
                              <i className="bi bi-shop mr-2 text-indigo-500"></i>
                              {property.bien_general.type_bien}
                            </div>
                          )}
                        </div>

                        {/* Footer avec statut */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            property.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                            property.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                            property.status === 'archived' ? 'bg-amber-100 text-amber-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {property.status === 'active' ? 'Actif' :
                             property.status === 'draft' ? 'Brouillon' :
                             property.status === 'archived' ? 'Archivé' : 
                             property.status || 'Actif'}
                          </span>
                          
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            className="text-pink-500 hover:text-pink-700 transition-colors flex items-center gap-1 text-sm font-medium"
                          >
                            <span>Voir détails</span>
                            <i className="bi bi-arrow-right-circle text-lg"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal d'affectation */}
        {showAffectationModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-6 border-b border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Affecter à des acquéreurs</h3>
                    <p className="text-sm text-gray-600 mt-1">{selectedFavorites.length} bien(s) sélectionné(s)</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowAffectationModal(false);
                      setSelectedContactsForAffectation([]);
                      setContactSearchQuery('');
                    }}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <i className="bi bi-x-lg text-2xl"></i>
                  </button>
                </div>
              </div>

              {/* Liste des contacts */}
              <div className="p-6 overflow-y-auto max-h-[50vh]">
                <p className="text-sm text-gray-600 mb-4">Sélectionnez les contacts auxquels affecter les biens</p>
                
                {/* Barre de recherche */}
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Rechercher un contact (nom, prénom, email)..."
                      value={contactSearchQuery}
                      onChange={(e) => setContactSearchQuery(e.target.value)}
                      className="w-full px-4 py-3 pl-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <i className="bi bi-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg"></i>
                    {contactSearchQuery && (
                      <button
                        onClick={() => setContactSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <i className="bi bi-x-circle-fill"></i>
                      </button>
                    )}
                  </div>
                </div>
                
                {(() => {
                  const filteredContacts = contacts.filter(contact => {
                    if (!contactSearchQuery) return true;
                    const searchLower = contactSearchQuery.toLowerCase();
                    const fullName = `${contact.prenom} ${contact.nom}`.toLowerCase();
                    const email = contact.emails?.[0]?.email?.toLowerCase() || '';
                    const type = contact.type_contact?.toLowerCase() || '';
                    return fullName.includes(searchLower) || email.includes(searchLower) || type.includes(searchLower);
                  });

                  return filteredContacts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <i className="bi bi-search text-4xl mb-2 block"></i>
                      <p>{contactSearchQuery ? 'Aucun contact trouvé' : 'Aucun contact disponible'}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredContacts.map((contact) => (
                      <label
                        key={contact.id}
                        className="flex items-center p-4 bg-gray-50 hover:bg-purple-50 rounded-lg cursor-pointer transition-colors border-2 border-transparent hover:border-purple-200"
                      >
                        <input
                          type="checkbox"
                          checked={selectedContactsForAffectation.includes(contact.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedContactsForAffectation(prev => [...prev, contact.id]);
                            } else {
                              setSelectedContactsForAffectation(prev => prev.filter(id => id !== contact.id));
                            }
                          }}
                          className="w-5 h-5 rounded text-purple-600 focus:ring-blue-500 mr-3"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">
                            {contact.prenom} {contact.nom}
                          </div>
                          <div className="text-sm text-gray-600">
                            {contact.type_contact} • {contact.emails?.[0]?.email || 'Pas d\'email'}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  );
                })()}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 p-6 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <i className="bi bi-check2-square text-purple-500 mr-2"></i>
                  {selectedContactsForAffectation.length} contact(s) sélectionné(s)
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowAffectationModal(false);
                      setSelectedContactsForAffectation([]);
                      setContactSearchQuery('');
                    }}
                    className="px-6 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={affectPropertiesToContacts}
                    disabled={selectedContactsForAffectation.length === 0}
                    className={`px-6 py-2 rounded-lg font-medium transition-all ${
                      selectedContactsForAffectation.length > 0
                        ? 'bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white shadow-lg hover:shadow-xl'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <i className="bi bi-check-lg mr-2"></i>
                    Affecter
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* MODAL PARTAGE MULTI-BIENS - FAVORIS */}
      <ShareModal 
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setShareLink('');
        }}
        shareLink={shareLink}
        propertyCount={selectedFavorites.length || selectedProperties.length}
      />
      </>
    );
  }

  if (!showForm) {
    return (
      <>
      {/* MODAUX GLOBAUX - Toujours disponibles dans la vue liste */}
      
      {/* Modal d'affectation SIMPLE d'un bien */}
      {showAffectationBienModal && bienToAffect && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Affecter un bien à un client</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Réf : <strong className="text-purple-700">{bienToAffect.property_reference || bienToAffect.id?.substring(0, 8) || 'N/A'}</strong>
                  </p>
                  <p className="text-sm text-gray-600">
                    Bien : <strong>{bienToAffect.bien_general?.type_bien || 'N/A'}</strong> - 
                    <strong> {bienToAffect.informations_confidentielles?.ville || bienToAffect.localisation?.ville || 'N/A'}</strong>
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAffectationBienModal(false);
                    setBienToAffect(null);
                    setAffectationSearch('');
                    setAffectationResults([]);
                    setSelectedContactForAffectation(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="bi bi-x-lg text-2xl"></i>
                </button>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Rechercher un client (par email ou nom)
                </label>
                <input
                  type="text"
                  value={affectationSearch}
                  onChange={(e) => {
                    setAffectationSearch(e.target.value);
                    searchContactForAffectation(e.target.value);
                  }}
                  placeholder="Tapez un email ou un nom..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {affectationResults.length > 0 ? (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {affectationResults.length} client(s) trouvé(s)
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {affectationResults.map((contact) => (
                      <div
                        key={contact.id}
                        onClick={() => setSelectedContactForAffectation(contact)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedContactForAffectation?.id === contact.id
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">
                              {contact.prenom} {contact.nom}
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                {contact.type_contact}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              <i className="bi bi-envelope mr-1"></i>
                              {contact.emails?.[0]?.email || 'Pas d\'email'}
                            </div>
                            {contact.telephones?.[0]?.numero && (
                              <div className="text-sm text-gray-600">
                                <i className="bi bi-telephone mr-1"></i>
                                {contact.telephones[0].numero}
                              </div>
                            )}
                          </div>
                          {selectedContactForAffectation?.id === contact.id && (
                            <i className="bi bi-check-circle-fill text-2xl text-purple-600"></i>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : affectationSearch.length >= 2 ? (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800">
                    <i className="bi bi-info-circle mr-2"></i>
                    Aucun client trouvé avec cette recherche.
                  </p>
                </div>
              ) : null}
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAffectationBienModal(false);
                    setBienToAffect(null);
                    setAffectationSearch('');
                    setAffectationResults([]);
                    setSelectedContactForAffectation(null);
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    if (!selectedContactForAffectation) {
                      alert('⚠️ Veuillez sélectionner un client');
                      return;
                    }
                    
                    try {
                      await axios.post(
                        `${API}/contacts/${selectedContactForAffectation.id}/biens-presentes`,
                        {
                          bien_id: bienToAffect.id,
                          statut: "presente",
                          negociateur_id: userProfile?.id || "system"
                        }
                      );
                      
                      alert(`✅ Bien affecté à ${selectedContactForAffectation.prenom} ${selectedContactForAffectation.nom} !`);
                      
                      const contactId = selectedContactForAffectation.id;
                      
                      setShowAffectationBienModal(false);
                      setBienToAffect(null);
                      setAffectationSearch('');
                      setAffectationResults([]);
                      setSelectedContactForAffectation(null);
                      
                      const contact = await axios.get(`${API}/contacts/${contactId}`);
                      setCurrentContact(contact.data);
                      
                      // Recharger les biens présentés
                      await loadPresentedProperties(contactId);
                      
                      setShowContactForm(true);
                      setShowContacts(true);
                      setShowDashboard(false);
                      setActiveContactTab("biens-presentes");
                      setActiveSection('contacts');
                    } catch (error) {
                      console.error('Erreur affectation:', error);
                      if (error.response?.status === 400) {
                        alert('⚠️ Ce bien est déjà affecté à ce client');
                      } else {
                        alert('❌ Erreur lors de l\'affectation du bien');
                      }
                    }
                  }}
                  disabled={!selectedContactForAffectation}
                  className={`flex-1 px-6 py-3 font-semibold rounded-lg flex items-center justify-center gap-2 ${
                    selectedContactForAffectation
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <i className="bi bi-person-check-fill"></i>
                  {selectedContactForAffectation 
                    ? `Affecter à ${selectedContactForAffectation.prenom} ${selectedContactForAffectation.nom}`
                    : 'Sélectionnez un client'
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal d'affectation MULTIPLE */}
      {showAffectationMultipleBiensModal && selectedProperties.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Affecter {selectedProperties.length} bien(s) à un client</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Sélectionnez le client qui recevra ces {selectedProperties.length} biens
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAffectationMultipleBiensModal(false);
                    setAffectationSearch('');
                    setAffectationResults([]);
                    setSelectedContactForAffectation(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="bi bi-x-lg text-2xl"></i>
                </button>
              </div>
              
              <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <h3 className="text-sm font-semibold text-indigo-900 mb-2">Biens sélectionnés :</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedProperties.slice(0, 5).map(propId => {
                    const prop = properties.find(p => p.id === propId);
                    return prop ? (
                      <span key={propId} className="text-xs bg-white px-3 py-1.5 rounded border border-indigo-300 text-indigo-700">
                        <strong className="text-purple-700">#{prop.property_reference || prop.id?.substring(0, 8)}</strong>
                        {' '}- {prop.bien_general?.type_bien || 'N/A'} - {prop.informations_confidentielles?.ville || prop.localisation?.ville || 'N/A'}
                      </span>
                    ) : null;
                  })}
                  {selectedProperties.length > 5 && (
                    <span className="text-xs bg-indigo-200 px-2 py-1 rounded text-indigo-800 font-semibold">
                      +{selectedProperties.length - 5} autres
                    </span>
                  )}
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Rechercher un client (par email ou nom)
                </label>
                <input
                  type="text"
                  value={affectationSearch}
                  onChange={(e) => {
                    setAffectationSearch(e.target.value);
                    searchContactForAffectation(e.target.value);
                  }}
                  placeholder="Tapez un email ou un nom..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {affectationResults.length > 0 ? (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {affectationResults.length} client(s) trouvé(s)
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {affectationResults.map((contact) => (
                      <div
                        key={contact.id}
                        onClick={() => setSelectedContactForAffectation(contact)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedContactForAffectation?.id === contact.id
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">
                              {contact.prenom} {contact.nom}
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                {contact.type_contact}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              <i className="bi bi-envelope mr-1"></i>
                              {contact.emails?.[0]?.email || 'Pas d\'email'}
                            </div>
                            {contact.telephones?.[0]?.numero && (
                              <div className="text-sm text-gray-600">
                                <i className="bi bi-telephone mr-1"></i>
                                {contact.telephones[0].numero}
                              </div>
                            )}
                          </div>
                          {selectedContactForAffectation?.id === contact.id && (
                            <i className="bi bi-check-circle-fill text-2xl text-purple-600"></i>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : affectationSearch.length >= 2 ? (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800">
                    <i className="bi bi-info-circle mr-2"></i>
                    Aucun client trouvé avec cette recherche.
                  </p>
                </div>
              ) : null}
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAffectationMultipleBiensModal(false);
                    setAffectationSearch('');
                    setAffectationResults([]);
                    setSelectedContactForAffectation(null);
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    if (!selectedContactForAffectation) {
                      alert('⚠️ Veuillez sélectionner un client');
                      return;
                    }
                    
                    try {
                      let successCount = 0;
                      let errorCount = 0;
                      
                      for (const propId of selectedProperties) {
                        try {
                          await axios.post(
                            `${API}/contacts/${selectedContactForAffectation.id}/biens-presentes`,
                            {
                              bien_id: propId,
                              statut: "presente",
                              negociateur_id: userProfile?.id || "system"
                            }
                          );
                          successCount++;
                        } catch (error) {
                          if (error.response?.status !== 400) {
                            errorCount++;
                          }
                        }
                      }
                      
                      if (successCount > 0) {
                        alert(`✅ ${successCount} bien(s) affecté(s) à ${selectedContactForAffectation.prenom} ${selectedContactForAffectation.nom} !${errorCount > 0 ? `\n⚠️ ${errorCount} bien(s) n'ont pas pu être affectés.` : ''}`);
                      } else {
                        alert('⚠️ Tous les biens sont déjà affectés à ce client');
                      }
                      
                      const contactId = selectedContactForAffectation.id;
                      
                      setShowAffectationMultipleBiensModal(false);
                      setAffectationSearch('');
                      setAffectationResults([]);
                      setSelectedContactForAffectation(null);
                      setSelectedProperties([]);
                      
                      const contact = await axios.get(`${API}/contacts/${contactId}`);
                      setCurrentContact(contact.data);
                      
                      // Recharger les biens présentés
                      await loadPresentedProperties(contactId);
                      
                      setShowContactForm(true);
                      setShowContacts(true);
                      setShowDashboard(false);
                      setActiveContactTab("biens-presentes");
                      setActiveSection('contacts');
                    } catch (error) {
                      console.error('Erreur affectation:', error);
                      alert('❌ Erreur lors de l\'affectation des biens');
                    }
                  }}
                  disabled={!selectedContactForAffectation}
                  className={`flex-1 px-6 py-3 font-semibold rounded-lg flex items-center justify-center gap-2 ${
                    selectedContactForAffectation
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <i className="bi bi-person-check-fill"></i>
                  {selectedContactForAffectation 
                    ? `Affecter ${selectedProperties.length} bien(s) à ${selectedContactForAffectation.prenom} ${selectedContactForAffectation.nom}`
                    : 'Sélectionnez un client'
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      

      <div className="min-h-screen bg-slate-50">
        {/* Header standard KOMERCES - Ne pas afficher sur la page Liste des biens */}
        {(showDashboard || showContacts || showForm) && (
          <Header 
            showContactsView={showContactsView}
            showDemandesContactView={showDemandesContactView}
            showDashboardView={showDashboardView}
            showDocumentsView={showDocumentsView}
            showMarketingView={showMarketingView}
            showPropertiesView={showPropertiesView}
            showOutilsView={showOutilsView}
            setActiveMarketingTab={setActiveMarketingTab}
            setActiveOutilsTab={setActiveOutilsTab}
            setActiveDocumentsTab={setActiveDocumentsTab}
            startNewContact={startNewContact}
            startNewProperty={startNewProperty}
            setShowContacts={setShowContacts}
            setShowForm={setShowForm}
            setContactSearchFilters={setContactSearchFilters}
            loadProperties={loadProperties}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            handleSearchFilterChange={handleSearchFilterChange}
            executeSearch={executeSearch}
            showFavoritesView={showFavoritesView}
            favoriteProperties={favoriteProperties}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            userProfile={userProfile}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            
          />
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
            
            {/* NOUVEAU: Filters Section Compacte */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm mb-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <i className="bi bi-funnel-fill text-indigo-600 text-lg"></i>
                  <h2 className="text-base font-semibold text-gray-900">Recherche rapide</h2>
                </div>
                <button 
                  onClick={resetSearchFilters}
                  className="bg-white hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg transition-all duration-200 border border-gray-300 flex items-center space-x-1.5 text-xs font-medium" 
                  title="Réinitialiser tous les filtres"
                >
                  <i className="bi bi-x-circle text-sm"></i>
                  <span>Réinitialiser</span>
                </button>
              </div>

              {/* Filtres principaux (toujours visibles) */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                {/* Référence ou mot-clé */}
                <div className="lg:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Référence ou mot-clé</label>
                  <input
                    type="text"
                    value={searchFilters.reference}
                    onChange={(e) => handleSearchFilterChange('reference', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Rechercher..."
                  />
                </div>

                {/* Négociateur */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Négociateur</label>
                  <select
                    value={searchFilters.negociateur}
                    onChange={(e) => handleSearchFilterChange('negociateur', e.target.value)}
                    onFocus={() => {
                      if (availableNegotiators.length === 0) {
                        console.log('🔄 Rechargement des négociateurs...');
                        loadDynamicCriteria();
                      }
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Tous</option>
                    {availableNegotiators.length > 0 ? (
                      availableNegotiators.map(negotiator => (
                        <option key={negotiator} value={negotiator}>{negotiator}</option>
                      ))
                    ) : (
                      <option disabled>Chargement...</option>
                    )}
                  </select>
                </div>

                {/* Transaction */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Transaction</label>
                  <select
                    value={searchFilters.transaction_type}
                    onChange={(e) => handleSearchFilterChange('transaction_type', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Toutes</option>
                    <option value="Vente">Vente</option>
                    <option value="Location">Location</option>
                  </select>
                </div>

                {/* Type de bien */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Type de bien</label>
                  <select
                    value={searchFilters.type_bien}
                    onChange={(e) => handleSearchFilterChange('type_bien', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Tous</option>
                    <option value="Fonds de commerce">Fonds de commerce</option>
                    <option value="Locaux commerciaux">Locaux commerciaux</option>
                    <option value="Bureaux">Bureaux</option>
                    <option value="Licences">Licences</option>
                    <option value="Investissement">Investissement</option>
                  </select>
                </div>

                {/* Ville */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Ville</label>
                  <select
                    value={searchFilters.ville}
                    onChange={(e) => handleSearchFilterChange('ville', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Toutes</option>
                    {availableCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>

                {/* Bouton Filtres avancés */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 invisible">Action</label>
                  <button
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className="w-full px-3 py-2 bg-gradient-to-r from-indigo-50 to-blue-50 hover:from-indigo-100 hover:to-blue-100 text-indigo-700 border border-indigo-200 rounded-lg text-sm font-semibold flex items-center justify-center space-x-2 transition-all"
                  >
                    <i className={`bi bi-chevron-down transition-transform duration-300 ${showAdvancedFilters ? 'rotate-180' : ''}`}></i>
                    <span>{showAdvancedFilters ? 'Masquer' : 'Filtres avancés'}</span>
                  </button>
                </div>
              </div>

              {/* Filtres avancés (collapsibles) */}
              {showAdvancedFilters && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-200">
                  {/* Activité */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Activité</label>
                    <select
                      value={searchFilters.activite_principale}
                      onChange={(e) => handleSearchFilterChange('activite_principale', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Toutes</option>
                      <option value="Restaurant">Restaurant</option>
                      <option value="Bar">Bar</option>
                      <option value="Retail">Retail</option>
                      <option value="Hôtellerie">Hôtellerie</option>
                      <option value="Bureaux">Bureaux</option>
                      <option value="Industriel">Industriel</option>
                    </select>
                  </div>

                  {/* Budget Min */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Budget Min (€)</label>
                    <input
                      type="number"
                      value={searchFilters.budgetMin}
                      onChange={(e) => handleSearchFilterChange('budgetMin', e.target.value)}
                      placeholder="Min"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Budget Max */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Budget Max (€)</label>
                    <input
                      type="number"
                      value={searchFilters.budgetMax}
                      onChange={(e) => handleSearchFilterChange('budgetMax', e.target.value)}
                      placeholder="Max"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Surface Min */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Surface Min (m²)</label>
                    <input
                      type="number"
                      value={searchFilters.surfaceMin}
                      onChange={(e) => handleSearchFilterChange('surfaceMin', e.target.value)}
                      placeholder="Min"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Surface Max */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Surface Max (m²)</label>
                    <input
                      type="number"
                      value={searchFilters.surfaceMax}
                      onChange={(e) => handleSearchFilterChange('surfaceMax', e.target.value)}
                      placeholder="Max"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Statut */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Statut</label>
                    <select
                      value={searchFilters.statut_bien}
                      onChange={(e) => handleSearchFilterChange('statut_bien', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Tous</option>
                      <option value="Libre">Libre</option>
                      <option value="Occupé">Occupé</option>
                      <option value="En travaux">En travaux</option>
                    </select>
                  </div>

                  {/* Disponibilité */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Disponibilité</label>
                    <select
                      value={searchFilters.libre}
                      onChange={(e) => handleSearchFilterChange('libre', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Toutes</option>
                      <option value="1">Immédiate</option>
                      <option value="0">À convenir</option>
                    </select>
                  </div>

                  {/* Checkboxes */}
                  <div className="flex flex-col justify-end space-y-2">
                    <label className="flex items-center text-xs text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={searchFilters.includeArchived || false}
                        onChange={(e) => handleSearchFilterChange('includeArchived', e.target.checked)}
                        className="mr-2 w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                      />
                      <span className="font-medium">Biens archivés</span>
                    </label>

                    <label className="flex items-center text-xs text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={searchFilters.exclusivite === "1"}
                        onChange={(e) => handleSearchFilterChange('exclusivite', e.target.checked ? "1" : "0")}
                        className="mr-2 w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                      />
                      <span className="font-medium">Exclusivité</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Tabs + View Toggle */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex items-center justify-between mb-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => {
                    loadProperties("all");
                    navigate('/biens-v2?filter=all');
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                    currentFilter === 'all'
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span>Tous</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    currentFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'
                  }`}>{allPropertiesCounts.all}</span>
                </button>

                <button
                  onClick={() => {
                    loadProperties("draft");
                    navigate('/biens-v2?filter=draft');
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                    currentFilter === 'draft'
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span>Brouillons</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    currentFilter === 'draft' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'
                  }`}>{allPropertiesCounts.draft}</span>
                </button>

                <button
                  onClick={() => {
                    loadProperties("active");
                    navigate('/biens-v2?filter=active');
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                    currentFilter === 'active'
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span>En ligne</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    currentFilter === 'active' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'
                  }`}>{allPropertiesCounts.active}</span>
                </button>

                <button
                  onClick={() => {
                    loadProperties("archive");
                    navigate('/biens-v2?filter=archive');
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                    currentFilter === 'archive'
                      ? 'bg-amber-500 text-white' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span>Archivés</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    currentFilter === 'archive' ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-700'
                  }`}>{allPropertiesCounts.archive}</span>
                </button>

                <button
                  onClick={() => {
                    loadProperties("sold");
                    navigate('/biens-v2?filter=sold');
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                    currentFilter === 'sold'
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span>Vendus</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    currentFilter === 'sold' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'
                  }`}>{allPropertiesCounts.sold}</span>
                </button>

                <button
                  onClick={() => {
                    loadProperties("shared");
                    navigate('/biens-v2?filter=shared');
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                    currentFilter === 'shared'
                      ? 'bg-emerald-500 text-white' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <i className="bi bi-share-fill text-xs"></i>
                  <span>Partagés</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    currentFilter === 'shared' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'
                  }`}>{allPropertiesCounts.shared || 0}</span>
                </button>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1.5 flex items-center gap-2 rounded-md transition-all text-sm font-medium ${
                    viewMode === 'grid'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <i className="bi bi-grid-3x3-gap"></i>
                  <span>Grille</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 flex items-center gap-2 rounded-md transition-all text-sm font-medium ${
                    viewMode === 'list'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <i className="bi bi-list-ul"></i>
                  <span>Liste</span>
                </button>
              </div>
            </div>

            {/* Barre d'actions */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 rounded focus:ring-blue-500 text-blue-600 border-slate-300"
                    checked={selectedProperties.length > 0 && selectedProperties.length === sortedProperties.length}
                    onChange={(e) => handleSelectAllProperties(e.target.checked)}
                  />
                  <span className="font-medium">Tout sélectionner</span>
                </label>
                
                {/* Bouton Tout désélectionner - version compacte avec icône */}
                {selectedProperties.length > 0 && (
                  <button
                    onClick={() => setSelectedProperties([])}
                    className="text-sm text-slate-500 hover:text-slate-700 transition-colors flex items-center gap-1"
                    title="Tout désélectionner"
                  >
                    <i className="bi bi-x-circle text-xs"></i>
                  </button>
                )}
                
                <span className="text-sm text-slate-500">
                  {(() => {
                    const totalCount = allPropertiesCounts[currentFilter] || sortedProperties.length;
                    console.log('🔢 Compteur - totalCount:', totalCount, 'currentFilter:', currentFilter, 'selectedProperties.length:', selectedProperties.length);
                    return selectedProperties.length > 0 
                      ? `${selectedProperties.length} bien${selectedProperties.length > 1 ? 's' : ''} sélectionné${selectedProperties.length > 1 ? 's' : ''}`
                      : `${totalCount} bien${totalCount > 1 ? 's' : ''}`;
                  })()}
                </span>
              </div>

              <div className="flex items-center gap-2 relative">
                <PropertyActions 
                  selectedProperties={selectedProperties}
                  userProfile={userProfile}
                  currentFilter={currentFilter}
                  properties={sortedProperties}
                  onShareSuccess={(shareLink) => {
                    console.log('📥 [App.js] onShareSuccess appelé avec:', shareLink);
                    setShareLink(shareLink);
                    setShowShareModal(true);
                  }}
                  onUnshareSuccess={async () => {
                    await loadProperties(currentFilter);
                    await loadAllPropertiesCounts();
                    setSelectedProperties([]);
                  }}
                  onArchiveSuccess={async () => {
                    await loadProperties(currentFilter);
                    setSelectedProperties([]);
                  }}
                  onDeleteSuccess={async () => {
                    await loadProperties(currentFilter);
                    setSelectedProperties([]);
                  }}
                />

                <div 
                  onMouseEnter={() => selectedProperties.length > 0 && setShowActionsDropdown(true)}
                  onMouseLeave={() => setShowActionsDropdown(false)}
                  className="relative"
                >
                  <button 
                    className="bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-medium transition-all border border-gray-300 flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={selectedProperties.length === 0}
                    title="Plus d'actions"
                  >
                    <i className="bi bi-three-dots-vertical"></i>
                    <span>Plus d'actions</span>
                  </button>
                  
                  {/* Dropdown Menu Desktop */}
                  {showActionsDropdown && selectedProperties.length > 0 && (
                    <div className="hidden sm:block absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-50" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                      <div className="py-2">
                        <button className="w-full text-left px-4 py-2 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center space-x-2">
                          <i className="bi bi-calendar-plus text-indigo-500"></i>
                          <span>Ajouter à l'agenda</span>
                        </button>
                        <button className="w-full text-left px-4 py-2 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center space-x-2">
                          <i className="bi bi-check-square text-indigo-500"></i>
                          <span>Créer une tâche</span>
                        </button>
                        <button className="w-full text-left px-4 py-2 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center space-x-2">
                          <i className="bi bi-file-earmark-text text-indigo-500"></i>
                          <span>Bon de visite</span>
                        </button>
                        <button className="w-full text-left px-4 py-2 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center space-x-2">
                          <i className="bi bi-envelope-paper text-indigo-500"></i>
                          <span>Lettre d'intention</span>
                        </button>
                        <hr className="my-2" />
                        <button 
                        onClick={() => {
                          console.log('🎯 [LISTE] Bouton Affecter cliqué!', { selectedCount: selectedProperties.length });
                          if (selectedProperties.length === 0) {
                            alert('⚠️ Veuillez sélectionner au moins un bien');
                            return;
                          }
                          // Ouvrir le modal d'affectation multiple
                          console.log('🎯 [LISTE] Avant setShowAffectationMultipleBiensModal(true)');
                          setShowAffectationMultipleBiensModal(true);
                          setAffectationSearch('');
                          setAffectationResults([]);
                          setSelectedContactForAffectation(null);
                          setShowActionsDropdown(false);
                          console.log('🎯 [LISTE] Après tous les setState');
                        }}
                        className="w-full text-left px-4 py-2 text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors flex items-center space-x-2"
                      >
                        <i className="bi bi-person-check text-purple-500"></i>
                        <span>Affecter à un client</span>
                      </button>
                      
                    </div>
                  </div>
                  )}
                </div>

                {/* Boutons rectangulaires : Archiver (si onglet non archivés) / Restaurer (si archivés) */}
                {currentFilter === 'archive' ? (
                  <button 
                    onClick={async () => {
                      if (window.confirm(`Êtes-vous sûr de vouloir restaurer ${selectedProperties.length} bien(s) ?`)) {
                        try {
                          for (const propertyId of selectedProperties) {
                            await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${propertyId}/restore`);
                          }
                          await loadProperties(currentFilter);
                          await loadAllPropertiesCounts();
                          setSelectedProperties([]);
                          alert(`${selectedProperties.length} bien(s) restauré(s) avec succès`);
                        } catch (error) {
                          console.error("Erreur restauration:", error);
                          alert("Erreur lors de la restauration: " + (error.response?.data?.detail || error.message));
                        }
                      }
                    }}
                    className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                    disabled={selectedProperties.length === 0}
                    title="Restaurer les biens sélectionnés"
                  >
                    <i className="bi bi-arrow-counterclockwise"></i>
                    <span>Restaurer</span>
                  </button>
                ) : (
                  <button 
                    onClick={async () => {
                      if (window.confirm(`Êtes-vous sûr de vouloir archiver ${selectedProperties.length} bien(s) ?`)) {
                        try {
                          for (const propertyId of selectedProperties) {
                            await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${propertyId}/archive`);
                          }
                          await loadProperties(currentFilter);
                          await loadAllPropertiesCounts();
                          setSelectedProperties([]);
                          alert(`${selectedProperties.length} bien(s) archivé(s) avec succès`);
                        } catch (error) {
                          console.error("Erreur archivage:", error);
                          alert("Erreur lors de l'archivage: " + (error.response?.data?.detail || error.message));
                        }
                      }
                    }}
                    className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                    disabled={selectedProperties.length === 0}
                    title="Archiver les biens sélectionnés"
                  >
                    <i className="bi bi-archive"></i>
                    <span>Archiver</span>
                  </button>
                )}

                {/* Bouton rectangulaire : Supprimer (rouge) */}
                <button 
                  onClick={async () => {
                    if (window.confirm(messageGroupe('supprimer', selectedProperties, undefined))) {
                      try {
                        for (const propertyId of selectedProperties) {
                          await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${propertyId}`);
                        }
                        await loadProperties(currentFilter);
                        await loadAllPropertiesCounts();
                        setSelectedProperties([]);
                        alert(`${selectedProperties.length} bien(s) supprimé(s) avec succès`);
                      } catch (error) {
                        console.error("Erreur suppression:", error);
                        alert("Erreur lors de la suppression: " + (error.response?.data?.detail || error.message));
                      }
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  disabled={selectedProperties.length === 0}
                  title="Supprimer les biens sélectionnés"
                >
                  <i className="bi bi-trash"></i>
                  <span>Supprimer</span>
                </button>
                    
                    {/* Dropdown Menu Mobile - only visible on mobile */}
                    {showActionsDropdown && selectedProperties.length > 0 && (
                      <div className="sm:hidden fixed inset-0 bg-black bg-opacity-50 z-[9999]" onClick={() => setShowActionsDropdown(false)}>
                        <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
                          <div className="p-4">
                            {/* Handle bar */}
                            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4"></div>
                            
                            <h3 className="text-lg font-bold text-gray-900 mb-4 px-2">
                              Actions ({selectedProperties.length} bien{selectedProperties.length > 1 ? 's' : ''})
                            </h3>
                            
                            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                              <button className="w-full text-left px-4 py-3 text-gray-700 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-3">
                                <i className="bi bi-calendar-plus text-indigo-500 text-xl"></i>
                                <span className="font-medium">Ajouter à l'agenda</span>
                              </button>
                              <button className="w-full text-left px-4 py-3 text-gray-700 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-3">
                                <i className="bi bi-check-square text-indigo-500 text-xl"></i>
                                <span className="font-medium">Créer une tâche</span>
                              </button>
                              <button className="w-full text-left px-4 py-3 text-gray-700 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-3">
                                <i className="bi bi-file-earmark-text text-indigo-500 text-xl"></i>
                                <span className="font-medium">Bon de visite</span>
                              </button>
                              <button className="w-full text-left px-4 py-3 text-gray-700 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-3">
                                <i className="bi bi-envelope-paper text-indigo-500 text-xl"></i>
                                <span className="font-medium">Lettre d'intention</span>
                              </button>
                              
                              <div className="h-2"></div>
                              
                              <button 
                                onClick={() => {
                                  console.log('🎯 [LISTE] Bouton Affecter cliqué!', { selectedCount: selectedProperties.length });
                                  if (selectedProperties.length === 0) {
                                    alert('⚠️ Veuillez sélectionner au moins un bien');
                                    return;
                                  }
                                  setShowAffectationMultipleBiensModal(true);
                                  setAffectationSearch('');
                                  setAffectationResults([]);
                                  setSelectedContactForAffectation(null);
                                  setShowActionsDropdown(false);
                                }}
                                className="w-full text-left px-4 py-3 text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors flex items-center space-x-3"
                              >
                                <i className="bi bi-person-check text-purple-600 text-xl"></i>
                                <span className="font-semibold">Affecter à un client</span>
                              </button>
                            </div>
                            
                            <button 
                              onClick={() => setShowActionsDropdown(false)}
                              className="w-full mt-4 px-4 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            {/* GRID VIEW - Modern Property Cards */}
            {viewMode === 'grid' && (
              <div className="p-6 bg-gradient-to-br from-gray-50 to-white">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {sortedProperties.map((property) => (
                    <div 
                      key={property.id}
                      className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-indigo-200 relative"
                    >
                      {/* Checkbox and Favorite - Top Left */}
                      <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
                        <input 
                          type="checkbox" 
                          className="w-5 h-5 rounded-lg focus:ring-indigo-500 text-indigo-600 border-2 border-white shadow-lg cursor-pointer"
                          checked={selectedProperties.includes(property.id)}
                          onChange={(e) => {
                            console.log('✅ [CHECKBOX INLINE] onChange déclenché !');
                            console.log('✅ [CHECKBOX INLINE] property.id:', property.id);
                            console.log('✅ [CHECKBOX INLINE] Longueur ID:', property.id ? property.id.length : 0);
                            console.log('✅ [CHECKBOX INLINE] Objet property (keys):', Object.keys(property));
                            console.log('✅ [CHECKBOX INLINE] checked:', e.target.checked);
                            e.stopPropagation();
                            handlePropertySelection(property.id, e.target.checked);
                          }}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(property.id);
                          }}
                          className="w-7 h-7 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110"
                          title={favoriteProperties.includes(property.id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                        >
                          <i className={`bi ${favoriteProperties.includes(property.id) ? 'bi-heart-fill text-red-500' : 'bi-heart text-gray-600'} text-lg`}></i>
                        </button>
                      </div>

                      {/* Photo Section (60% height) */}
                      <div className="relative h-48 bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden cursor-pointer group"
                           onClick={() => consultProperty(property)}>
                        {property.medias?.photos?.[0]?.url ? (
                          <img 
                            src={property.medias.photos[0].url} 
                            alt={property.bien_general?.titre_annonce || 'Bien'}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-400/10 to-amber-500/10">
                            <img 
                              src="/new_logo_komerces_transparent.webp" 
                              alt="KOMERCES" 
                              className="w-24 h-24 opacity-20 object-contain"
                            />
                          </div>
                        )}
                        
                        {/* Overlay gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                        
                        {/* Shared Badge - Top Right (if property is shared) */}
                        {property.is_public && property.public_code && (
                          <div className="absolute top-3 right-3">
                            <span className="px-3 py-1.5 rounded-lg text-xs font-semibold shadow-lg bg-emerald-500 text-white flex items-center gap-1.5 animate-pulse">
                              <i className="bi bi-share-fill"></i>
                              Partagé
                            </span>
                          </div>
                        )}
                        
                        {/* Transaction Type Badge - Top Right OR below if shared */}
                        <div className={`absolute ${property.is_public && property.public_code ? 'top-12' : 'top-3'} right-3`}>
                          <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-lg ${
                            (property.transaction_type || 'Vente') === 'Vente' 
                              ? 'bg-green-500 text-white' 
                              : 'bg-blue-600 text-white'
                          }`}>
                            <i className={`bi ${(property.transaction_type || 'Vente') === 'Vente' ? 'bi-tag-fill' : 'bi-key-fill'} mr-1`}></i>
                            {property.transaction_type || 'Vente'}
                          </span>
                        </div>

                        {/* Prix Badge - Bottom Left on Photo - Version douce */}
                        <div className="absolute bottom-3 left-3">
                          <div className="bg-white/90 backdrop-blur-md text-gray-800 px-4 py-2.5 rounded-xl shadow-lg border border-white/50">
                            <div className="text-lg font-semibold leading-tight">
                              {property.honoraires_calcul?.prix_vente 
                                ? formatPriceFR(property.honoraires_calcul.prix_vente)
                                : property.informations_bail?.loyer_mensuel
                                ? `${property.informations_bail.loyer_mensuel.toLocaleString()} €/mois`
                                : 'Prix NC'}
                            </div>
                          </div>
                        </div>

                        {/* Photos count badge */}
                        {property.medias?.photos?.length > 1 && (
                          <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 rounded-lg text-xs font-semibold flex items-center space-x-1">
                            <i className="bi bi-images"></i>
                            <span>{property.medias.photos.length}</span>
                          </div>
                        )}
                      </div>

                      {/* Info Section */}
                      <div className="p-4 space-y-3">
                        {/* Référence + Statut */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-600">
                            #{property.reference || property.id.slice(-8)}
                          </span>
                          <span className={`text-xs px-2.5 py-1 rounded-lg font-medium ${
                            property.status === 'draft' ? 'bg-amber-50 text-amber-700 border border-amber-100' :
                            property.status === 'active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                            property.status === 'archived' ? 'bg-gray-50 text-gray-600 border border-gray-100' :
                            'bg-blue-50 text-blue-700 border border-blue-100'
                          }`}>
                            {property.status === 'draft' ? 'Brouillon' :
                             property.status === 'active' ? 'En ligne' :
                             property.status === 'archived' ? 'Archivé' :
                             'Vendu'}
                          </span>
                        </div>

                        {/* Titre / Type de bien - Sans troncature */}
                        <div>
                          <h3 className="font-semibold text-gray-800 text-sm leading-tight mb-1">
                            {property.bien_general?.type_bien || 'Type non défini'}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {property.bien_general?.activite_principale || 'Activité non définie'}
                          </p>
                        </div>

                        {/* Infos clés avec icônes - Version douce */}
                        <div className="space-y-2 text-sm">
                          {/* Ville */}
                          <div className="flex items-center space-x-2 text-gray-600">
                            <i className="bi bi-geo-alt text-indigo-400"></i>
                            <span className="font-medium">{property.informations_confidentielles?.ville || 'Ville NC'}</span>
                          </div>
                          
                          {/* Surface */}
                          {property.bien_general?.surface_totale && (
                            <div className="flex items-center space-x-2 text-gray-600">
                              <i className="bi bi-rulers text-indigo-400"></i>
                              <span>{formatSurfaceFR(property.bien_general.surface_totale)}</span>
                            </div>
                          )}

                          {/* Disponibilité */}
                          <div className="flex items-center space-x-2">
                            <i className={`bi ${property.bien_general?.statut_bien === 'Libre' ? 'bi-check-circle text-emerald-400' : 'bi-dash-circle text-amber-400'}`}></i>
                            <span className="text-sm font-medium text-gray-600">
                              {property.bien_general?.statut_bien || 'Libre'}
                            </span>
                          </div>
                        </div>

                        {/* Date de création */}
                        <div className="pt-2 border-t border-gray-100 text-xs text-gray-500 flex items-center space-x-1">
                          <i className="bi bi-calendar3"></i>
                          <span>{new Date(property.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>

                        {/* Actions - Version douce */}
                        <div className="pt-3 border-t border-gray-100 flex items-center justify-between space-x-2 relative z-10">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              consultProperty(property);
                            }}
                            className="flex-1 bg-gradient-to-r from-indigo-50 to-blue-50 hover:from-indigo-100 hover:to-blue-100 text-indigo-700 py-2 rounded-lg transition-all duration-200 flex items-center justify-center space-x-1 text-sm font-medium shadow-sm hover:shadow-md border border-indigo-100"
                            title="Consulter"
                          >
                            <i className="bi bi-eye"></i>
                            <span>Voir</span>
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              selectProperty(property);
                            }}
                            className="flex-1 bg-gradient-to-r from-orange-50 to-red-50 hover:from-orange-100 hover:to-red-100 text-orange-700 py-2 rounded-lg transition-all duration-200 flex items-center justify-center space-x-1 text-sm font-medium shadow-sm hover:shadow-md border border-orange-100"
                            title="Modifier"
                          >
                            <i className="bi bi-pencil"></i>
                            <span>Éditer</span>
                          </button>

                          {/* More actions dropdown - Fix erreur nextElementSibling */}
                          <div className="relative group/actions">
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="bg-gray-50 hover:bg-gray-100 text-gray-700 p-2 rounded-lg transition-colors border border-gray-200"
                              title="Plus d'actions"
                            >
                              <i className="bi bi-three-dots-vertical"></i>
                            </button>
                            
                            {/* Dropdown Menu - Positionné au-dessus avec group hover CSS uniquement */}
                            <div className="absolute right-0 bottom-full mb-1 sm:bottom-full md:bottom-full bg-white border border-gray-200 rounded-lg shadow-2xl z-[100] opacity-0 invisible group-hover/actions:opacity-100 group-hover/actions:visible transition-all duration-200 min-w-[180px]" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                              <div className="py-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    alert('Génération PDF en cours d\'implémentation');
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center space-x-2"
                                >
                                  <i className="bi bi-file-pdf text-red-500"></i>
                                  <span>Générer PDF</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (expandedShareProperty === property.id) {
                                      setExpandedShareProperty(null);
                                    } else {
                                      // Si le bien a déjà un lien public
                                      if (property.is_public && property.public_code) {
                                        // Vérifier si on a déjà le lien en mémoire
                                        if (!propertyLinks[property.id]) {
                                          setPropertyLinks(prev => ({
                                            ...prev,
                                            [property.id]: {
                                              code: property.public_code,
                                              url: `${window.location.origin}/p/${property.public_code}`,
                                              loading: false
                                            }
                                          }));
                                        }
                                        setExpandedShareProperty(property.id);
                                      } else {
                                        // Sinon, générer un nouveau lien
                                        generatePublicLink(property.id);
                                      }
                                    }
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors flex items-center space-x-2"
                                >
                                  <i className="bi bi-link-45deg text-blue-500"></i>
                                  <span>{expandedShareProperty === property.id ? 'Masquer partage' : (property.is_public ? 'Voir le lien' : 'Partager (lien)')}</span>
                                </button>
                                {/* Bouton Désactiver le partage - visible uniquement si le bien est partagé */}
                                {property.is_public && property.public_code && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (window.confirm("Êtes-vous sûr de vouloir désactiver le partage de ce bien ?\n\nLe lien public ne sera plus accessible.")) {
                                        try {
                                          await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${property.id}/public-link`);
                                          // Reload properties to update the display
                                          await loadProperties(currentFilter);
                                          await loadAllPropertiesCounts();
                                          alert("Partage désactivé avec succès");
                                        } catch (error) {
                                          console.error("Erreur désactivation partage:", error);
                                          alert("Erreur lors de la désactivation du partage: " + (error.response?.data?.detail || error.message));
                                        }
                                      }
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center space-x-2"
                                  >
                                    <i className="bi bi-link-45deg-slash text-red-500"></i>
                                    <span>Désactiver le partage</span>
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFavorite(property.id);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-700 transition-colors flex items-center space-x-2"
                                >
                                  <i className={`bi ${favoriteProperties.includes(property.id) ? 'bi-heart-fill text-red-500' : 'bi-heart text-pink-500'}`}></i>
                                  <span>{favoriteProperties.includes(property.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('🎯 Clic Affecter - Setting states...', { property: property.id });
                                    // Ouvrir le modal d'affectation pour ce bien
                                    setBienToAffect(property);
                                    setShowAffectationBienModal(true);
                                    setAffectationSearch('');
                                    setAffectationResults([]);
                                    setSelectedContactForAffectation(null);
                                    console.log('✅ States set - Modal should show');
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors flex items-center space-x-2"
                                >
                                  <i className="bi bi-person-check text-purple-500"></i>
                                  <span>Affecter à un client</span>
                                </button>
                                <div className="border-t border-gray-200 my-1"></div>
                                {/* Archiver/Restaurer selon status */}
                                {property.status === 'archived' ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      restoreProperty(property.id);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors flex items-center space-x-2"
                                  >
                                    <i className="bi bi-arrow-counterclockwise text-teal-500"></i>
                                    <span>Restaurer</span>
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      archiveProperty(property.id);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors flex items-center space-x-2"
                                  >
                                    <i className="bi bi-archive text-amber-500"></i>
                                    <span>Archiver</span>
                                  </button>
                                )}
                                {/* lot BY — admin, et bien DÉJÀ archivé. */}
                                {estAdminBY && property.status === 'archived' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteProperty(property.id);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-2"
                                >
                                  <i className="bi bi-trash"></i>
                                  <span>Supprimer</span>
                                </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Section Partage Expandable - PROPRE */}
                      {expandedShareProperty === property.id && (
                        <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-t-2 border-blue-300 rounded-b-lg">
                          {propertyLinks[property.id]?.loading ? (
                            <div className="flex items-center justify-center py-3">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                              <span className="text-sm text-blue-800 font-medium">Génération du lien...</span>
                            </div>
                          ) : propertyLinks[property.id]?.url ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold text-gray-700">🔗 Lien de partage</span>
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">Actif</span>
                              </div>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={propertyLinks[property.id].url}
                                  readOnly
                                  className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs font-mono text-gray-700"
                                  onClick={(e) => e.target.select()}
                                />
                              </div>
                              <div className="flex gap-2 pt-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(propertyLinks[property.id].url);
                                    const btn = e.currentTarget;
                                    const originalText = btn.innerHTML;
                                    btn.innerHTML = '<i class="bi bi-check-lg"></i> Copié!';
                                    setTimeout(() => btn.innerHTML = originalText, 2000);
                                  }}
                                  className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
                                >
                                  <i className="bi bi-clipboard mr-1"></i>Copier
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(propertyLinks[property.id].url, '_blank');
                                  }}
                                  className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors"
                                >
                                  <i className="bi bi-box-arrow-up-right mr-1"></i>Ouvrir
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm('Désactiver le partage de ce bien ?')) {
                                      disablePublicLink(property.id);
                                    }
                                  }}
                                  className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors"
                                >
                                  <i className="bi bi-x-circle"></i>
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-3 text-red-600 text-sm">
                              ❌ Erreur lors de la génération
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Empty State */}
                {sortedProperties.length === 0 && (
                  <div className="text-center py-20">
                    <i className="bi bi-inbox text-6xl text-gray-300 mb-4"></i>
                    <p className="text-xl text-gray-500 font-medium">Aucun bien trouvé</p>
                    <p className="text-sm text-gray-400 mt-2">Modifiez vos filtres ou créez un nouveau bien</p>
                  </div>
                )}
              </div>
            )}

            {/* LIST VIEW - Table View */}
            {viewMode === 'list' && (
              <>
            {/* Properties Cards - Mobile View */}
            <div className="md:hidden space-y-3 p-3">
              {sortedProperties.map((property) => (
                <div key={property.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  {/* Card Header with Photo and Checkbox */}
                  <div className="p-3 bg-gradient-to-r from-indigo-50/50 to-blue-50/50">
                    <div className="flex items-start space-x-3">
                      <input 
                        type="checkbox"
                        className="mt-1 w-4 h-4 rounded focus:ring-indigo-500 text-indigo-600"
                        checked={selectedProperties.includes(property.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handlePropertySelection(property.id, e.target.checked);
                        }}
                      />
                      {property.medias?.photos?.[0]?.url ? (
                        <img 
                          src={property.medias.photos[0].url} 
                          alt={property.enseigne?.enseigne || 'Bien'}
                          className="w-20 h-20 object-cover rounded-lg shadow-sm"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                          <i className="bi bi-camera text-gray-400 text-xl"></i>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 text-sm leading-tight mb-1">
                          {property.enseigne?.enseigne || property.bien_general?.type_bien || 'Sans nom'}
                        </h4>
                        <p className="text-xs text-gray-600 mb-1">
                          Réf: #{property.id.substring(0, 8)}
                        </p>
                        <div className="text-xs text-gray-500">
                          {property.bien_general?.type_bien}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Card Content - Infos essentielles */}
                  <div className="p-3 space-y-2 text-xs border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Prix:</span>
                      <span className="font-bold text-emerald-600 text-sm">
                        {property.honoraires_calcul?.prix_vente ? `${property.honoraires_calcul.prix_vente.toLocaleString()}€` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ville:</span>
                      <span className="font-medium text-gray-900">{property.informations_confidentielles?.ville || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Surface:</span>
                      <span className="font-medium text-gray-900">{property.enseigne?.superficie ? `${property.enseigne.superficie}m²` : 'N/A'}</span>
                    </div>
                  </div>
                  
                  {/* Card Actions - Grille 2x4 */}
                  <div className="p-3 bg-gray-50 border-t grid grid-cols-4 gap-2">
                    {/* Consulter */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        consultProperty(property);
                      }}
                      className="flex flex-col items-center justify-center p-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                      title="Consulter"
                    >
                      <i className="bi bi-search text-sm mb-1"></i>
                      <span className="text-xs font-medium">Voir</span>
                    </button>
                    
                    {/* Modifier */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        selectProperty(property);
                      }}
                      className="flex flex-col items-center justify-center p-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <i className="bi bi-pencil-square text-sm mb-1"></i>
                      <span className="text-xs font-medium">Éditer</span>
                    </button>
                    
                    {/* PDF */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        generatePropertyPDF(property, 'complete');
                      }}
                      disabled={generatingPDF}
                      className="flex flex-col items-center justify-center p-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg transition-colors disabled:opacity-50"
                      title="Générer PDF"
                    >
                      <i className="bi bi-filetype-pdf text-sm mb-1"></i>
                      <span className="text-xs font-medium">PDF</span>
                    </button>
                    
                    {/* Fiche Bien */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openFicheBienModal(property);
                      }}
                      className="flex flex-col items-center justify-center p-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
                      title="Fiche Bien"
                    >
                      <i className="bi bi-file-earmark-pdf text-sm mb-1"></i>
                      <span className="text-xs font-medium">Fiche</span>
                    </button>
                    
                    {/* Row 2 */}
                    {/* Imprimer */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        printPropertyWeb(property, 'fiche_technique');
                      }}
                      className="flex flex-col items-center justify-center p-2 bg-violet-500 hover:bg-violet-600 text-white rounded-lg transition-colors"
                      title="Imprimer"
                    >
                      <i className="bi bi-printer-fill text-sm mb-1"></i>
                      <span className="text-xs font-medium">Print</span>
                    </button>
                    
                    {/* Archiver/Restaurer */}
                    {property.status === 'archived' ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          restoreProperty(property.id);
                        }}
                        className="flex flex-col items-center justify-center p-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors"
                        title="Restaurer"
                      >
                        <i className="bi bi-arrow-counterclockwise text-sm mb-1"></i>
                        <span className="text-xs font-medium">Resto</span>
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          archiveProperty(property.id);
                        }}
                        className="flex flex-col items-center justify-center p-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
                        title="Archiver"
                      >
                        <i className="bi bi-archive-fill text-sm mb-1"></i>
                        <span className="text-xs font-medium">Arch</span>
                      </button>
                    )}
                    
                    {/* Supprimer — lot BY : admin, et bien DÉJÀ archivé. */}
                    {estAdminBY && property.status === 'archived' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteProperty(property.id);
                      }}
                      className="flex flex-col items-center justify-center p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <i className="bi bi-trash3-fill text-sm mb-1"></i>
                      <span className="text-xs font-medium">Suppr</span>
                    </button>
                    )}
                    
                    {/* Plus d'options (vide pour l'instant ou peut contenir menu) */}
                    <button
                      className="flex flex-col items-center justify-center p-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
                      title="Plus"
                    >
                      <i className="bi bi-three-dots text-sm mb-1"></i>
                      <span className="text-xs font-medium">Plus</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Properties Table - Desktop - Optimized */}
            <div className="hidden md:block bg-white rounded-b-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-indigo-50/70 via-blue-50/50 to-purple-50/70 border-b border-indigo-100 sticky top-0 z-20"
>
                  <tr>
                    {/* Checkbox */}
                    <th className="w-4 px-3 py-3">
                      <input 
                        type="checkbox" 
                        className="w-4 h-4 rounded-lg focus:ring-indigo-500 text-indigo-600 border-2 border-gray-300"
                        checked={selectedProperties.length > 0 && selectedProperties.length === sortedProperties.length}
                        onChange={(e) => handleSelectAllProperties(e.target.checked)}
                      />
                    </th>
                    
                    {/* Photo */}
                    <th className="text-left py-3 px-3 font-medium text-gray-700 w-16">
                      <i className="bi bi-image text-indigo-500"></i>
                    </th>
                    
                    {/* Référence */}
                    <th 
                      className="text-left py-3 px-3 font-medium text-gray-700 cursor-pointer hover:bg-indigo-100/50 rounded-lg transition-colors"
                      onClick={() => handleSort('id')}
                    >
                      <span className="flex items-center space-x-1">
                        <i className="bi bi-hash text-indigo-500"></i>
                        <span>Réf</span>
                        {sortField === 'id' && (
                          <i className={`bi ${sortDirection === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down'} text-xs`}></i>
                        )}
                      </span>
                    </th>
                    
                    {/* Bien (Type + Activité + Transaction + Surface) */}
                    <th 
                      className="text-left py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-indigo-100/50 rounded-lg transition-colors"
                      onClick={() => handleSort('type')}
                    >
                      <span className="flex items-center space-x-1">
                        <i className="bi bi-building text-indigo-500"></i>
                        <span>Bien</span>
                        {sortField === 'type' && (
                          <i className={`bi ${sortDirection === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down'} text-xs`}></i>
                        )}
                      </span>
                    </th>
                    
                    {/* Prix */}
                    <th 
                      className="text-left py-3 px-3 font-medium text-gray-700 cursor-pointer hover:bg-indigo-100/50 rounded-lg transition-colors"
                      onClick={() => handleSort('prix')}
                    >
                      <span className="flex items-center space-x-1">
                        <i className="bi bi-currency-euro text-indigo-500"></i>
                        <span>Prix</span>
                        {sortField === 'prix' && (
                          <i className={`bi ${sortDirection === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down'} text-xs`}></i>
                        )}
                      </span>
                    </th>
                    
                    {/* Localisation (Ville) */}
                    <th 
                      className="text-left py-3 px-3 font-medium text-gray-700 cursor-pointer hover:bg-indigo-100/50 rounded-lg transition-colors"
                      onClick={() => handleSort('ville')}
                    >
                      <span className="flex items-center space-x-1">
                        <i className="bi bi-geo-alt text-indigo-500"></i>
                        <span>Ville</span>
                        {sortField === 'ville' && (
                          <i className={`bi ${sortDirection === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down'} text-xs`}></i>
                        )}
                      </span>
                    </th>
                    
                    {/* Statut */}
                    <th className="text-left py-3 px-3 font-medium text-gray-700">
                      <span className="flex items-center space-x-1">
                        <i className="bi bi-flag text-indigo-500"></i>
                        <span>Statut</span>
                      </span>
                    </th>
                    
                    {/* Date */}
                    <th 
                      className="text-left py-3 px-3 font-medium text-gray-700 cursor-pointer hover:bg-indigo-100/50 rounded-lg transition-colors"
                      onClick={() => handleSort('created_at')}
                    >
                      <span className="flex items-center space-x-1">
                        <i className="bi bi-calendar3 text-indigo-500"></i>
                        <span>Date</span>
                        {sortField === 'created_at' && (
                          <i className={`bi ${sortDirection === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down'} text-xs`}></i>
                        )}
                      </span>
                    </th>
                    
                    {/* Actions */}
                    <th className="text-center py-3 px-3 font-medium text-gray-700">
                      <i className="bi bi-gear text-indigo-500"></i>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedProperties.map((property, index) => (
                    <tr key={property.id} className="hover:bg-gradient-to-r hover:from-indigo-50/30 hover:to-blue-50/30 transition-all duration-200 cursor-pointer group">
                      {/* Checkbox */}
                      <td className="px-3 py-4 align-middle">
                        <input 
                          type="checkbox" 
                          className="w-4 h-4 rounded focus:ring-indigo-500 text-indigo-600"
                          checked={selectedProperties.includes(property.id)}
                          onChange={(e) => handlePropertySelection(property.id, e.target.checked)}
                        />
                      </td>
                      
                      {/* Photo */}
                      <td className="py-4 px-3 align-middle">
                        {property.medias?.photos && property.medias.photos.length > 0 ? (
                          <div 
                            className="relative cursor-pointer" 
                            onClick={(e) => {
                              e.stopPropagation();
                              openPhotoGallery(property, 0);
                            }}
                          >
                            <img
                              src={property.medias.photos[0].url}
                              alt="Aperçu"
                              className="w-14 h-10 object-cover rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                            />
                            {property.medias.photos.length > 1 && (
                              <span className="absolute -top-1 -right-1 bg-indigo-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-semibold text-[10px]">
                                {property.medias.photos.length}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="w-14 h-10 bg-gray-50 rounded-lg border flex items-center justify-center">
                            <i className="bi bi-image text-gray-300 text-lg"></i>
                          </div>
                        )}
                      </td>
                      
                      {/* Référence */}
                      <td className="py-4 px-3 align-middle">
                        <span className="font-mono text-xs bg-gray-50 px-2 py-1 rounded border border-gray-200 text-gray-700">
                          #{property.reference || property.id.slice(-6)}
                        </span>
                      </td>
                      
                      {/* Bien (Type + Activité + Transaction + Surface avec icônes) */}
                      <td className="py-4 px-4 align-top">
                        <div className="space-y-2">
                          {/* Type de bien + Activité */}
                          <div className="border-l-2 border-indigo-300 pl-2">
                            <div className="font-medium text-gray-800 text-sm">
                              {property.bien_general?.type_bien || 'Type NC'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {property.bien_general?.activite_principale || 'Activité NC'}
                            </div>
                          </div>
                          
                          {/* Transaction + Surface */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Transaction badge - Couleurs TRÈS différenciées avec fallback */}
                            <span className={`inline-flex items-center space-x-1 text-xs px-2 py-0.5 rounded font-semibold ${
                              (property.transaction_type || 'Vente') === 'Vente' 
                                ? 'bg-green-500 text-white' 
                                : 'bg-blue-600 text-white'
                            }`}>
                              <i className={`bi ${(property.transaction_type || 'Vente') === 'Vente' ? 'bi-tag-fill' : 'bi-key-fill'} text-[10px]`}></i>
                              <span>{property.transaction_type || 'Vente'}</span>
                            </span>
                            
                            {/* Surface avec icône */}
                            {property.bien_general?.surface_totale && (
                              <span className="inline-flex items-center space-x-1 text-xs text-gray-600 bg-gray-50 px-2 py-0.5 rounded">
                                <i className="bi bi-rulers text-indigo-400"></i>
                                <span>{formatSurfaceFR(property.bien_general.surface_totale)}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      
                      {/* Prix */}
                      <td className="py-4 px-3 align-middle">
                        <div className="font-semibold text-gray-800">
                          {property.honoraires_calcul?.prix_vente 
                            ? formatPriceFR(property.honoraires_calcul.prix_vente)
                            : property.informations_bail?.loyer_mensuel
                            ? `${property.informations_bail.loyer_mensuel.toLocaleString()} €`
                            : 'NC'}
                        </div>
                        {property.informations_bail?.loyer_mensuel && (
                          <div className="text-xs text-gray-500">/mois</div>
                        )}
                      </td>
                      
                      {/* Localisation */}
                      <td className="py-4 px-3 align-middle">
                        <div className="flex items-center space-x-1 text-sm text-gray-700">
                          <i className="bi bi-geo-alt text-indigo-400"></i>
                          <span>{property.informations_confidentielles?.ville || 'Ville NC'}</span>
                        </div>
                      </td>
                      
                      {/* Statut */}
                      <td className="py-4 px-3 align-middle">
                        <span className={`inline-block text-xs px-2.5 py-1 rounded-lg font-medium ${
                          property.status === 'draft' ? 'bg-amber-50 text-amber-700 border border-amber-100' :
                          property.status === 'active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                          property.status === 'archived' ? 'bg-gray-50 text-gray-600 border border-gray-100' :
                          'bg-blue-50 text-blue-700 border border-blue-100'
                        }`}>
                          {property.status === 'draft' ? 'Brouillon' :
                           property.status === 'active' ? 'En ligne' :
                           property.status === 'archived' ? 'Archivé' :
                           'Vendu'}
                        </span>
                        {/* Disponibilité en dessous */}
                        {property.bien_general?.statut_bien && (
                          <div className="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                            <i className={`bi ${property.bien_general.statut_bien === 'Libre' ? 'bi-check-circle text-emerald-400' : 'bi-dash-circle text-amber-400'}`}></i>
                            <span>{property.bien_general.statut_bien}</span>
                          </div>
                        )}
                      </td>
                      
                      {/* Date */}
                      <td className="py-4 px-3 text-xs text-gray-500 align-middle">
                        <div className="flex items-center space-x-1">
                          <i className="bi bi-calendar3 text-indigo-400"></i>
                          <span>{new Date(property.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                        </div>
                      </td>
                      
                      {/* Actions */}
                      <td className="py-4 px-3 align-middle">
                        <div className="flex items-center justify-center space-x-1">
                          {/* Consulter */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              consultProperty(property);
                            }}
                            className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                            title="Consulter"
                          >
                            <i className="bi bi-eye text-sm"></i>
                          </button>
                          
                          {/* Modifier */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              selectProperty(property);
                            }}
                            className="p-1.5 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors"
                            title="Modifier"
                          >
                            <i className="bi bi-pencil text-sm"></i>
                          </button>
                          
                          {/* Plus - Dropdown fonctionnel */}
                          <div className="relative group/more">
                            <button
                              className="p-1.5 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors"
                              title="Plus d'actions"
                            >
                              <i className="bi bi-three-dots-vertical text-sm"></i>
                            </button>
                            
                            {/* Dropdown Menu */}
                            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 opacity-0 invisible group-hover/more:opacity-100 group-hover/more:visible transition-all duration-200 min-w-[180px]" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                              <div className="py-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    alert('Génération PDF en cours d\'implémentation');
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center space-x-2"
                                >
                                  <i className="bi bi-file-pdf text-red-500"></i>
                                  <span>Générer PDF</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (expandedShareProperty === property.id) {
                                      setExpandedShareProperty(null);
                                    } else {
                                      // Si le bien a déjà un lien public
                                      if (property.is_public && property.public_code) {
                                        // Vérifier si on a déjà le lien en mémoire
                                        if (!propertyLinks[property.id]) {
                                          setPropertyLinks(prev => ({
                                            ...prev,
                                            [property.id]: {
                                              code: property.public_code,
                                              url: `${window.location.origin}/p/${property.public_code}`,
                                              loading: false
                                            }
                                          }));
                                        }
                                        setExpandedShareProperty(property.id);
                                      } else {
                                        // Sinon, générer un nouveau lien
                                        generatePublicLink(property.id);
                                      }
                                    }
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors flex items-center space-x-2"
                                >
                                  <i className="bi bi-link-45deg text-blue-500"></i>
                                  <span>{expandedShareProperty === property.id ? 'Masquer partage' : (property.is_public ? 'Voir le lien' : 'Partager (lien)')}</span>
                                </button>
                                {/* Bouton Désactiver le partage - visible uniquement si le bien est partagé */}
                                {property.is_public && property.public_code && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (window.confirm("Êtes-vous sûr de vouloir désactiver le partage de ce bien ?\n\nLe lien public ne sera plus accessible.")) {
                                        try {
                                          await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${property.id}/public-link`);
                                          // Reload properties to update the display
                                          await loadProperties(currentFilter);
                                          await loadAllPropertiesCounts();
                                          alert("Partage désactivé avec succès");
                                        } catch (error) {
                                          console.error("Erreur désactivation partage:", error);
                                          alert("Erreur lors de la désactivation du partage: " + (error.response?.data?.detail || error.message));
                                        }
                                      }
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center space-x-2"
                                  >
                                    <i className="bi bi-link-45deg-slash text-red-500"></i>
                                    <span>Désactiver le partage</span>
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFavorite(property.id);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-700 transition-colors flex items-center space-x-2"
                                >
                                  <i className={`bi ${favoriteProperties.includes(property.id) ? 'bi-heart-fill text-red-500' : 'bi-heart text-pink-500'}`}></i>
                                  <span>{favoriteProperties.includes(property.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('🎯 Clic Affecter - Setting states...', { property: property.id });
                                    // Ouvrir le modal d'affectation pour ce bien
                                    setBienToAffect(property);
                                    setShowAffectationBienModal(true);
                                    setAffectationSearch('');
                                    setAffectationResults([]);
                                    setSelectedContactForAffectation(null);
                                    console.log('✅ States set - Modal should show');
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors flex items-center space-x-2"
                                >
                                  <i className="bi bi-person-check text-purple-500"></i>
                                  <span>Affecter à un client</span>
                                </button>
                                <div className="border-t border-gray-200 my-1"></div>
                                {/* Archiver/Restaurer selon status */}
                                {property.status === 'archived' ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      restoreProperty(property.id);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors flex items-center space-x-2"
                                  >
                                    <i className="bi bi-arrow-counterclockwise text-teal-500"></i>
                                    <span>Restaurer</span>
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      archiveProperty(property.id);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors flex items-center space-x-2"
                                  >
                                    <i className="bi bi-archive text-amber-500"></i>
                                    <span>Archiver</span>
                                  </button>
                                )}
                                {/* lot BY — admin, et bien DÉJÀ archivé. */}
                                {estAdminBY && property.status === 'archived' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteProperty(property.id);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-2"
                                >
                                  <i className="bi bi-trash"></i>
                                  <span>Supprimer</span>
                                </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
              </>
            )}

            {/* Pagination */}
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t rounded-b-xl">
              <div className="text-sm text-gray-700">
                Affichage de 1 à {properties.length} sur {properties.length} résultats
              </div>
              <div className="flex space-x-1">
                <button className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50">
                  Précédent
                </button>
                <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded">
                  1
                </button>
                <button className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50">
                  Suivant
                </button>
              </div>
            </div>
        </div>
      
      {/* Photo Gallery Modal */}
      {photoGalleryOpen && <PhotoGalleryModal />}
      
      {/* MODAL PARTAGE MULTI-BIENS */}
      {(() => {
        console.log('🔍 [App.js IF] Avant ShareModal - showShareModal:', showShareModal, 'shareLink:', shareLink);
        return null;
      })()}
      <ShareModal 
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setShareLink('');
        }}
        shareLink={shareLink}
        propertyCount={selectedFavorites.length || selectedProperties.length}
      />
      </>
    );
  }

  console.log("📝 États UI:", { showForm, showDashboard, showContacts });
  console.log("🔍 Modal states:", { showAffectationBienModal, bienToAffect: !!bienToAffect, showAffectationMultipleBiensModal, selectedPropertiesCount: selectedProperties.length });

  // REMOVED: La vue des collections partagées est maintenant gérée par React Router et SharedCollectionView.js

  return (
    <>
      {/* MODAUX GLOBAUX - Toujours disponibles */}
      
      {/* Modal d'affectation SIMPLE d'un bien */}
      {showAffectationBienModal && bienToAffect && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Affecter un bien à un client</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Réf : <strong className="text-purple-700">{bienToAffect.property_reference || bienToAffect.id?.substring(0, 8) || 'N/A'}</strong>
                  </p>
                  <p className="text-sm text-gray-600">
                    Bien : <strong>{bienToAffect.bien_general?.type_bien || 'N/A'}</strong> - 
                    <strong> {bienToAffect.informations_confidentielles?.ville || bienToAffect.localisation?.ville || 'N/A'}</strong>
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAffectationBienModal(false);
                    setBienToAffect(null);
                    setAffectationSearch('');
                    setAffectationResults([]);
                    setSelectedContactForAffectation(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="bi bi-x-lg text-2xl"></i>
                </button>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Rechercher un client (par email ou nom)
                </label>
                <input
                  type="text"
                  value={affectationSearch}
                  onChange={(e) => {
                    setAffectationSearch(e.target.value);
                    searchContactForAffectation(e.target.value);
                  }}
                  placeholder="Tapez un email ou un nom..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {affectationResults.length > 0 ? (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {affectationResults.length} client(s) trouvé(s)
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {affectationResults.map((contact) => (
                      <div
                        key={contact.id}
                        onClick={() => setSelectedContactForAffectation(contact)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedContactForAffectation?.id === contact.id
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">
                              {contact.prenom} {contact.nom}
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                {contact.type_contact}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              <i className="bi bi-envelope mr-1"></i>
                              {contact.emails?.[0]?.email || 'Pas d\'email'}
                            </div>
                            {contact.telephones?.[0]?.numero && (
                              <div className="text-sm text-gray-600">
                                <i className="bi bi-telephone mr-1"></i>
                                {contact.telephones[0].numero}
                              </div>
                            )}
                          </div>
                          {selectedContactForAffectation?.id === contact.id && (
                            <i className="bi bi-check-circle-fill text-2xl text-purple-600"></i>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : affectationSearch.length >= 2 ? (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800">
                    <i className="bi bi-info-circle mr-2"></i>
                    Aucun client trouvé avec cette recherche.
                  </p>
                </div>
              ) : null}
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAffectationBienModal(false);
                    setBienToAffect(null);
                    setAffectationSearch('');
                    setAffectationResults([]);
                    setSelectedContactForAffectation(null);
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    if (!selectedContactForAffectation) {
                      alert('⚠️ Veuillez sélectionner un client');
                      return;
                    }
                    
                    try {
                      await axios.post(
                        `${API}/contacts/${selectedContactForAffectation.id}/biens-presentes`,
                        {
                          bien_id: bienToAffect.id,
                          statut: "presente",
                          negociateur_id: userProfile?.id || "system"
                        }
                      );
                      
                      alert(`✅ Bien affecté à ${selectedContactForAffectation.prenom} ${selectedContactForAffectation.nom} !`);
                      
                      const contactId = selectedContactForAffectation.id;
                      
                      setShowAffectationBienModal(false);
                      setBienToAffect(null);
                      setAffectationSearch('');
                      setAffectationResults([]);
                      setSelectedContactForAffectation(null);
                      
                      const contact = await axios.get(`${API}/contacts/${contactId}`);
                      setCurrentContact(contact.data);
                      
                      // Recharger les biens présentés
                      await loadPresentedProperties(contactId);
                      
                      setShowContactForm(true);
                      setShowContacts(true);
                      setShowDashboard(false);
                      setActiveContactTab("biens-presentes");
                      setActiveSection('contacts');
                    } catch (error) {
                      console.error('Erreur affectation:', error);
                      if (error.response?.status === 400) {
                        alert('⚠️ Ce bien est déjà affecté à ce client');
                      } else {
                        alert('❌ Erreur lors de l\'affectation du bien');
                      }
                    }
                  }}
                  disabled={!selectedContactForAffectation}
                  className={`flex-1 px-6 py-3 font-semibold rounded-lg flex items-center justify-center gap-2 ${
                    selectedContactForAffectation
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <i className="bi bi-person-check-fill"></i>
                  {selectedContactForAffectation 
                    ? `Affecter à ${selectedContactForAffectation.prenom} ${selectedContactForAffectation.nom}`
                    : 'Sélectionnez un client'
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal d'affectation MULTIPLE */}
      {showAffectationMultipleBiensModal && selectedProperties.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Affecter {selectedProperties.length} bien(s) à un client</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Sélectionnez le client qui recevra ces {selectedProperties.length} biens
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAffectationMultipleBiensModal(false);
                    setAffectationSearch('');
                    setAffectationResults([]);
                    setSelectedContactForAffectation(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="bi bi-x-lg text-2xl"></i>
                </button>
              </div>
              
              <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <h3 className="text-sm font-semibold text-indigo-900 mb-2">Biens sélectionnés :</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedProperties.slice(0, 5).map(propId => {
                    const prop = properties.find(p => p.id === propId);
                    return prop ? (
                      <span key={propId} className="text-xs bg-white px-3 py-1.5 rounded border border-indigo-300 text-indigo-700">
                        <strong className="text-purple-700">#{prop.property_reference || prop.id?.substring(0, 8)}</strong>
                        {' '}- {prop.bien_general?.type_bien || 'N/A'} - {prop.informations_confidentielles?.ville || prop.localisation?.ville || 'N/A'}
                      </span>
                    ) : null;
                  })}
                  {selectedProperties.length > 5 && (
                    <span className="text-xs bg-indigo-200 px-2 py-1 rounded text-indigo-800 font-semibold">
                      +{selectedProperties.length - 5} autres
                    </span>
                  )}
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Rechercher un client (par email ou nom)
                </label>
                <input
                  type="text"
                  value={affectationSearch}
                  onChange={(e) => {
                    setAffectationSearch(e.target.value);
                    searchContactForAffectation(e.target.value);
                  }}
                  placeholder="Tapez un email ou un nom..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {affectationResults.length > 0 ? (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {affectationResults.length} client(s) trouvé(s)
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {affectationResults.map((contact) => (
                      <div
                        key={contact.id}
                        onClick={() => setSelectedContactForAffectation(contact)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedContactForAffectation?.id === contact.id
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">
                              {contact.prenom} {contact.nom}
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                {contact.type_contact}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              <i className="bi bi-envelope mr-1"></i>
                              {contact.emails?.[0]?.email || 'Pas d\'email'}
                            </div>
                            {contact.telephones?.[0]?.numero && (
                              <div className="text-sm text-gray-600">
                                <i className="bi bi-telephone mr-1"></i>
                                {contact.telephones[0].numero}
                              </div>
                            )}
                          </div>
                          {selectedContactForAffectation?.id === contact.id && (
                            <i className="bi bi-check-circle-fill text-2xl text-purple-600"></i>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : affectationSearch.length >= 2 ? (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800">
                    <i className="bi bi-info-circle mr-2"></i>
                    Aucun client trouvé avec cette recherche.
                  </p>
                </div>
              ) : null}
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAffectationMultipleBiensModal(false);
                    setAffectationSearch('');
                    setAffectationResults([]);
                    setSelectedContactForAffectation(null);
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    if (!selectedContactForAffectation) {
                      alert('⚠️ Veuillez sélectionner un client');
                      return;
                    }
                    
                    try {
                      let successCount = 0;
                      let errorCount = 0;
                      
                      for (const propId of selectedProperties) {
                        try {
                          await axios.post(
                            `${API}/contacts/${selectedContactForAffectation.id}/biens-presentes`,
                            {
                              bien_id: propId,
                              statut: "presente",
                              negociateur_id: userProfile?.id || "system"
                            }
                          );
                          successCount++;
                        } catch (error) {
                          if (error.response?.status !== 400) {
                            errorCount++;
                          }
                        }
                      }
                      
                      if (successCount > 0) {
                        alert(`✅ ${successCount} bien(s) affecté(s) à ${selectedContactForAffectation.prenom} ${selectedContactForAffectation.nom} !${errorCount > 0 ? `\n⚠️ ${errorCount} bien(s) n'ont pas pu être affectés.` : ''}`);
                      } else {
                        alert('⚠️ Tous les biens sont déjà affectés à ce client');
                      }
                      
                      const contactId = selectedContactForAffectation.id;
                      
                      setShowAffectationMultipleBiensModal(false);
                      setAffectationSearch('');
                      setAffectationResults([]);
                      setSelectedContactForAffectation(null);
                      setSelectedProperties([]);
                      
                      const contact = await axios.get(`${API}/contacts/${contactId}`);
                      setCurrentContact(contact.data);
                      
                      // Recharger les biens présentés
                      await loadPresentedProperties(contactId);
                      
                      setShowContactForm(true);
                      setShowContacts(true);
                      setShowDashboard(false);
                      setActiveContactTab("biens-presentes");
                      setActiveSection('contacts');
                    } catch (error) {
                      console.error('Erreur affectation:', error);
                      alert('❌ Erreur lors de l\'affectation des biens');
                    }
                  }}
                  disabled={!selectedContactForAffectation}
                  className={`flex-1 px-6 py-3 font-semibold rounded-lg flex items-center justify-center gap-2 ${
                    selectedContactForAffectation
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <i className="bi bi-person-check-fill"></i>
                  {selectedContactForAffectation 
                    ? `Affecter ${selectedProperties.length} bien(s) à ${selectedContactForAffectation.prenom} ${selectedContactForAffectation.nom}`
                    : 'Sélectionnez un client'
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Modern background decoration */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-purple-600/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-emerald-400/10 to-teal-600/10 rounded-full blur-3xl"></div>

      {/* Header standard - Ne pas afficher si hideHeader est true (mode PropertyEditPage) */}
      {!hideHeader && (
        <Header 
          showContactsView={showContactsView}
          showDemandesContactView={showDemandesContactView}
          showFavoritesView={showFavoritesView}
          favoriteProperties={favoriteProperties}
          showDashboardView={showDashboardView}
          showDocumentsView={showDocumentsView}
          showMarketingView={showMarketingView}
          showPropertiesView={showPropertiesView}
          showOutilsView={showOutilsView}
          setActiveMarketingTab={setActiveMarketingTab}
          setActiveOutilsTab={setActiveOutilsTab}
          setActiveDocumentsTab={setActiveDocumentsTab}
          startNewContact={startNewContact}
          startNewProperty={startNewProperty}
          setShowContacts={setShowContacts}
          setShowForm={setShowForm}
          setContactSearchFilters={setContactSearchFilters}
          loadProperties={loadProperties}
          globalSearch={globalSearch}
          setGlobalSearch={setGlobalSearch}
          handleSearchFilterChange={handleSearchFilterChange}
          executeSearch={executeSearch}
          activeDropdown={activeDropdown}
          setActiveDropdown={setActiveDropdown}
          userProfile={userProfile}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          
        />
      )}

      {/* MODAL PARTAGE MULTI-BIENS */}
      {(() => {
        console.log('🔍 [App.js] Avant ShareModal - showShareModal:', showShareModal, 'shareLink:', shareLink);
        return null;
      })()}
      <ShareModal 
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setShareLink('');
        }}
        shareLink={shareLink}
        propertyCount={selectedFavorites.length || selectedProperties.length}
      />

      <div className="relative z-10 container mx-auto px-2 xs:px-3 sm:px-4 py-3 sm:py-4 md:py-6">
        <div className="backdrop-blur-xl bg-white/80 border border-white/20 rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl sm:shadow-2xl shadow-blue-500/10 overflow-hidden">
          {/* Modern Form Header with glassmorphism */}
          <div className="relative bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 text-white overflow-hidden">
            {/* Pattern decoration */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
            <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="grid-form" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#grid-form)"/>
            </svg>
            
            <div className="relative z-10 p-3 sm:p-4 md:p-6">
              <div className="flex flex-col space-y-3 sm:space-y-4 lg:space-y-0 lg:flex-row lg:items-center lg:justify-between">
                {/* Top row on mobile: Back button and title */}
                <div className="flex flex-col space-y-3 lg:flex-row lg:items-center lg:space-y-0 lg:space-x-4">
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setCurrentProperty(null);
                    }}
                    className="group inline-flex items-center justify-center sm:justify-start px-3 py-2 sm:px-4 sm:py-2.5 bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 hover:bg-white/20 hover:scale-105 border border-white/20"
                  >
                    <i className="bi bi-arrow-left mr-1 sm:mr-2 text-sm sm:text-base group-hover:-translate-x-0.5 transition-transform duration-200"></i>
                    <span className="hidden sm:inline">Retour à la liste</span>
                    <span className="sm:hidden">Retour</span>
                  </button>
                  
                  <div className="text-center lg:text-left">
                    <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent leading-tight">
                      {currentProperty ? (
                        <>
                          <span className="block sm:hidden">Modifier</span>
                          <span className="hidden sm:block">Modification du Bien</span>
                        </>
                      ) : (
                        <>
                          <span className="block sm:hidden">Nouveau Bien</span>
                          <span className="hidden sm:block">Création d'un Nouveau Bien</span>
                        </>
                      )}
                    </h1>
                    <p className="text-blue-200/80 font-medium text-xs sm:text-sm mt-1">
                      {currentProperty 
                        ? `Réf: CASASOFT${currentProperty.id?.slice(0,8)}` 
                        : (
                          <>
                            <span className="block sm:hidden">Informations du bien</span>
                            <span className="hidden sm:block">Renseignez les informations du bien immobilier</span>
                          </>
                        )
                      }
                    </p>
                  </div>
                </div>
                
                {/* Action buttons */}
                <div className="flex flex-wrap justify-center lg:justify-end gap-3">
                  {/* Bouton Mode Consultation (uniquement en édition) */}
                  {currentProperty && (
                    <button
                      onClick={() => {
                        setConsultedProperty(currentProperty);
                        setShowForm(false);
                        setShowConsultation(true);
                      }}
                      className="group inline-flex items-center justify-center px-4 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25 active:scale-95"
                    >
                      <i className="bi bi-eye mr-1 sm:mr-2 text-sm sm:text-base group-hover:scale-110 transition-transform duration-200"></i>
                      <span className="hidden sm:inline">Mode Consultation</span>
                      <span className="sm:hidden">Consulter</span>
                    </button>
                  )}
                  
                  {/* Bouton Enregistrer/Créer */}
                  <button
                    onClick={currentProperty ? updateProperty : createProperty}
                    className="group inline-flex items-center justify-center px-4 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-emerald-500/25 active:scale-95 w-full sm:w-auto"
                  >
                    <i className={`${currentProperty ? 'bi bi-check-circle' : 'bi bi-plus-circle'} mr-1 sm:mr-2 text-sm sm:text-base group-hover:scale-110 transition-transform duration-200`}></i>
                    <span>{currentProperty ? 'Mettre à jour' : 'Créer le bien'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Property Info Header - Type + Transaction */}
          {currentProperty && (
            <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white px-4 py-4 shadow-lg">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  {/* Left: Type + Transaction */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <i className="bi bi-building text-2xl"></i>
                      <div>
                        <div className="font-bold text-lg">
                          {currentProperty.bien_general?.activite_principale || currentProperty.subcategory || currentProperty.category || currentProperty.bien_general?.type_bien || 'Type non défini'}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${
                            currentProperty.transaction_type === 'Vente' || !currentProperty.transaction_type
                              ? 'bg-green-500 text-white' 
                              : 'bg-blue-600 text-white'
                          }`}>
                            <i className={`bi ${currentProperty.transaction_type === 'Location' ? 'bi-house-door-fill' : 'bi-tag-fill'}`}></i>
                            {currentProperty.transaction_type || 'Vente'}
                          </span>
                          {currentProperty.status && (
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              currentProperty.status === 'active' ? 'bg-white/20' :
                              currentProperty.status === 'draft' ? 'bg-yellow-400/30' :
                              currentProperty.status === 'archived' ? 'bg-gray-400/30' :
                              'bg-red-400/30'
                            }`}>
                              {currentProperty.status === 'active' ? 'Actif' :
                               currentProperty.status === 'draft' ? 'Brouillon' :
                               currentProperty.status === 'archived' ? 'Archivé' :
                               'Vendu'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right: Key Info */}
                  <div className="flex items-center gap-4 text-sm">
                    {currentProperty.informations_confidentielles?.ville && (
                      <div className="flex items-center gap-1 bg-white/20 px-3 py-1.5 rounded-lg">
                        <i className="bi bi-geo-alt-fill"></i>
                        <span className="font-semibold">{currentProperty.informations_confidentielles.ville}</span>
                      </div>
                    )}
                    {currentProperty.honoraires_calcul?.prix_vente && (
                      <div className="flex items-center gap-1 bg-white/20 px-3 py-1.5 rounded-lg">
                        <i className="bi bi-currency-euro"></i>
                        <span className="font-bold">
                          {currentProperty.honoraires_calcul.prix_vente.toLocaleString()} €
                          {currentProperty.transaction_type === 'Location' && (
                            <span className="text-xs ml-1">/mois</span>
                          )}
                        </span>
                      </div>
                    )}
                    {(currentProperty.composition?.superficie_totale || currentProperty.bien_general?.superficie) && (
                      <div className="flex items-center gap-1 bg-white/20 px-3 py-1.5 rounded-lg">
                        <i className="bi bi-rulers"></i>
                        <span className="font-semibold">
                          {formatSurfaceFR(currentProperty.composition?.superficie_totale || currentProperty.bien_general?.superficie)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modern Tabs - Style consultation (simple desktop) */}
          <div className="border-b border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white/50 backdrop-blur-sm">
            <nav className="flex" aria-label="Tabs">
              {[
                { id: 'general', name: 'Général', icon: 'bi-info-circle', gradient: 'from-blue-500 to-blue-600', hover: 'hover:bg-blue-50' },
                { id: 'prestations', name: 'Prestations', icon: 'bi-gear-wide-connected', gradient: 'from-purple-500 to-purple-600', hover: 'hover:bg-purple-50' },
                { id: 'composition', name: 'Composition', icon: 'bi-diagram-3', gradient: 'from-amber-500 to-orange-600', hover: 'hover:bg-amber-50' },
                { id: 'descriptif', name: 'Descriptif', icon: 'bi-file-earmark-text', gradient: 'from-emerald-500 to-emerald-600', hover: 'hover:bg-emerald-50' },
                { id: 'confidentiel', name: 'Confidentiel', icon: 'bi-shield-lock', gradient: 'from-red-500 to-red-600', hover: 'hover:bg-red-50' },
                { id: 'vente', name: 'Vente', icon: 'bi-currency-euro', gradient: 'from-teal-500 to-teal-600', hover: 'hover:bg-teal-50' },
                { id: 'medias', name: 'Médias', icon: 'bi-image', gradient: 'from-orange-500 to-orange-600', hover: 'hover:bg-orange-50' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex-1 px-4 py-4 text-sm font-semibold flex items-center justify-center space-x-2 transition-all duration-300 group ${
                    activeTab === tab.id
                      ? `bg-gradient-to-r ${tab.gradient} text-white shadow-lg hover:shadow-xl transform -translate-y-0.5`
                      : `text-gray-600 ${tab.hover} hover:text-gray-800`
                  }`}
                >
                  <i className={`${tab.icon} text-lg ${activeTab === tab.id ? 'animate-pulse' : 'group-hover:scale-110'} transition-transform duration-200`}></i>
                  <span className="font-medium">{tab.name}</span>
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-white/30 rounded-full"></div>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Modern Tab Content with animations - Enhanced Mobile Responsive Padding */}
          <div className="p-3 xs:p-4 sm:p-5 md:p-6 lg:p-8 bg-gradient-to-br from-gray-50/50 to-white/50 min-h-[60vh] sm:min-h-[50vh]">
            <div className="animate-fade-in max-w-full" key={`tab-${activeTab}-config-${configKey}`}>
              {/* Étape 1.6: Intégration des sections avec ctx */}
              {(() => {
                // Créer l'objet ctx avec toutes les dépendances
                const ctx = {
                  // Données
                  formData,
                  setFormData,
                  // Handlers
                  handleInputChange,
                  handlePrestationChange,
                  handleNestedChange: (section, field, value) => {
                    setFormData(prev => ({
                      ...prev,
                      [section]: {
                        ...prev[section],
                        [field]: value
                      }
                    }));
                  },
                  handleDeepNestedChange: (section, subsection, field, value) => {
                    setFormData(prev => ({
                      ...prev,
                      [section]: {
                        ...prev[section],
                        [subsection]: {
                          ...prev[section]?.[subsection],
                          [field]: value
                        }
                      }
                    }));
                  },
                  // Config functions
                  getFieldsConfig,
                  getFieldValue,
                  getTypesBienByTransaction,
                  getActivitiesByTypeBien,
                  renderDynamicField,
                  // Media handlers
                  handleFileUpload,
                  handleFileDelete: (type, index) => {
                    setFormData(prev => ({
                      ...prev,
                      medias: {
                        ...prev.medias,
                        [type]: prev.medias?.[type]?.filter((_, i) => i !== index)
                      }
                    }));
                  },
                  isUploading,
                  // Constants
                  PRESTATIONS_LIST: [
                    'Agence immobilière', 'Artisan', 'Bar à vin Cave à vin', 'Bar pur', 'Bar Tabac',
                    'Boulangerie Pâtisserie', 'Brasserie', 'Bureau', 'Crêperie',
                    'Droit au bail commercial', 'Entrepôt industriel', 'Esthétique',
                    'Fleuriste Déco Vêtements etc', 'Fruits Légumes Épicerie', 'Glacier',
                    'Hôtel Hôtel Meublé', 'Licence 2', 'Licence 3', 'Licence 4',
                    'Location commerciale avec droit d\'entrée', 'Location commerciale pure',
                    'Loto', 'Murs commerciaux libres', 'Murs commerciaux occupés',
                    'Pizzeria', 'Plage', 'Pmu', 'Point chaud', 'Presse',
                    'Restaurant', 'Salon de thé', 'Sandwichs Saladerie', 'Snack', 'Tabac'
                  ],
                  DESCRIPTION_TYPES: ['Fiche Technique', 'Affiche vitrine', 'Web', 'Pub', 'Mandat', 'Autre'],
                  LANGUAGES: ['Français', 'Anglais', 'Espagnol', 'Italien'],
                  // Description state
                  activeDescriptionType: formData.descriptif?.langue_active || 'Fiche Technique',
                  setActiveDescriptionType: (type) => setFormData(prev => ({
                    ...prev,
                    descriptif: { ...prev.descriptif, langue_active: type }
                  })),
                  activeDescriptionLang: 'Français',
                  setActiveDescriptionLang: () => {} // TODO: implement if needed
                };
                
                return (
                  <>
                    {activeTab === "general" && <GeneralSection ctx={ctx} />}
                    {activeTab === "prestations" && <PrestationsSection ctx={ctx} />}
                    {activeTab === "composition" && <CompositionSection ctx={ctx} />}
                    {activeTab === "descriptif" && <DescriptifSection ctx={ctx} />}
                    {activeTab === "confidentiel" && <ConfidentialSection ctx={ctx} />}
                    {activeTab === "vente" && <VenteSection ctx={ctx} />}
                    {activeTab === "medias" && <MediasSection ctx={ctx} />}
                    {activeTab === "demandes" && renderDemandesTab()}
                    {activeTab === "historique" && renderDemandesTab()}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Photo Gallery Modal */}
      {photoGalleryOpen && <PhotoGalleryModal />}
      
      {/* Modal de sélection de contact */}
      {showContactSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Sélectionner un contact existant</h3>
              <button
                onClick={() => {
                  setShowContactSelection(false);
                  setContactSearchQuery("");
                  setFoundContacts([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            
            <div className="mb-4">
              <input
                type="text"
                placeholder="Rechercher par nom ou prénom..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={contactSearchQuery}
                onChange={(e) => {
                  setContactSearchQuery(e.target.value);
                  searchContactsForProprietaire(e.target.value);
                }}
              />
            </div>
            
            {foundContacts.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {foundContacts.map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => selectContactForProperty(contact, contactSelectionTarget)}
                    className="p-3 border border-gray-200 rounded-md hover:bg-blue-50 cursor-pointer"
                  >
                    <div className="font-medium text-gray-900">
                      {contact.prenom} {contact.nom}
                      {contact.nom_societe && (
                        <span className="text-sm text-gray-500 ml-2">({contact.nom_societe})</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {contact.adresse && `${contact.adresse}, `}
                      {contact.ville || ''} {contact.code_postal || ''}
                    </div>
                    <div className="text-xs text-gray-400">
                      Type: {contact.type_contact}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {contactSearchQuery && foundContacts.length === 0 && (
              <div className="text-center text-gray-500 py-4">
                Aucun contact trouvé pour "{contactSearchQuery}"
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Modal Fiche Bien PDF */}
      {showFicheBienModal && selectedPropertyForFiche && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <i className="bi bi-file-earmark-pdf text-2xl"></i>
                  <h3 className="text-xl font-bold">Fiche Bien PDF</h3>
                </div>
                <button 
                  onClick={closeFicheBienModal}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <i className="bi bi-x-lg text-2xl"></i>
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 space-y-6">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <i className="bi bi-info-circle text-orange-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="text-sm text-orange-900 font-medium">
                      Bien sélectionné
                    </p>
                    <p className="text-sm text-orange-700 mt-1">
                      {selectedPropertyForFiche.bien_general?.type_bien || 'Commerce'} - 
                      {selectedPropertyForFiche.enseigne?.enseigne || selectedPropertyForFiche.id.substring(0, 8)}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Checkbox pour infos confidentielles */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <label className="flex items-start space-x-3 cursor-pointer group">
                  <div className="flex items-center h-6">
                    <input
                      type="checkbox"
                      checked={includeConfidential}
                      onChange={(e) => setIncludeConfidential(e.target.checked)}
                      className="w-5 h-5 text-orange-600 bg-white border-gray-300 rounded focus:ring-orange-500 focus:ring-2 cursor-pointer"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900 group-hover:text-orange-600 transition-colors">
                      Inclure informations confidentielles
                    </span>
                    <p className="text-xs text-gray-600 mt-1">
                      Ajoute une page supplémentaire avec les données confidentielles (propriétaire, finances, adresse exacte, etc.)
                    </p>
                  </div>
                </label>
                
                {includeConfidential && (
                  <div className="flex items-start space-x-2 text-xs text-red-600 bg-red-50 p-3 rounded-lg border border-red-200 animate-fadeIn">
                    <i className="bi bi-shield-lock mt-0.5"></i>
                    <span>Document strictement réservé aux négociateurs - Ne pas diffuser</span>
                  </div>
                )}
              </div>
              
              {/* Preview des sections */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  📄 Contenu du PDF:
                </p>
                <ul className="space-y-1 text-sm text-blue-700">
                  <li className="flex items-center space-x-2">
                    <i className="bi bi-check-circle-fill text-xs"></i>
                    <span>Fiche standard (infos publiques)</span>
                  </li>
                  {includeConfidential && (
                    <li className="flex items-center space-x-2">
                      <i className="bi bi-check-circle-fill text-xs"></i>
                      <span>+ Page confidentielle (négociateur)</span>
                    </li>
                  )}
                </ul>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={closeFicheBienModal}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={downloadFicheBien}
                  className="px-6 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all font-medium flex items-center space-x-2 shadow-lg shadow-orange-500/25"
                >
                  <i className="bi bi-download"></i>
                  <span>Télécharger</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
});

export default PropertyForm;
